
import * as THREE from '../assets/js/engines/three/three.module';
import { computeBoundsTree, disposeBoundsTree, acceleratedRaycast } from '../assets/js/engines/three/bvh/index.module';
import base58_to_binary from './crypto/base58_to_binary.ts';

const frameTime = 16.67;
const fps = 1000 / frameTime;

const FRONT_LEFT = 0;
const FRONT_RIGHT = 1;
const BACK_LEFT = 2;
const BACK_RIGHT = 3;


const ACTIVE_TAG = 1
const ISLAND_SLEEPING = 2
const WANTS_DEACTIVATION = 3
const DISABLE_DEACTIVATION = 4
const DISABLE_SIMULATION = 5

const CF_NO_CONTACT_RESPONSE = 4;
const CF_CUSTOM_MATERIAL_CALLBACK = 8;
const CF_CHARACTER_OBJECT = 16;

const BT_CONSTRAINT_ERP = 1;
const BT_CONSTRAINT_STOP_ERP = 2;
const BT_CONSTRAINT_CFM = 3;
const BT_CONSTRAINT_STOP_CFM = 4;

const worldID = 1;
const carID = 2;
const ballsID = 4;
const groundID = 4;

const worldGroup = 1<<worldID;
const carGroup = 2<<carID;
const ballGroup = 4<<ballsID;
const groundGroup = 4<<groundID;

const groundMask = 4<<groundID;
const worldMask = 1<<worldID | groundMask;
const carMask = 2<<carID;
const ballMask = 4<<ballsID;


const PI2 = Math.PI * 2;
var Ammo = null;

class KeyFrame {
    quat;
    time;

    constructor(quat, time){
        this.quat = new THREE.Quaternion(quat.x, quat.y, quat.z, quat.w);
        this.time = time;
    }
}

class BoneAnim {

    name;
    keyframes;
    endTime;

    constructor(name, quat = null) {

        this.name = name;
        this.keyframes = [];
        if (quat) {
            const newKey = new KeyFrame(quat, 0);
            this.keyframes.push(newKey);
            this.endTime = this.keyframes[0].time;
        }

        this.cq = new THREE.Quaternion();
        
    }

    addKey(quat, time){

        if(time === null)
            time = this.endTime + 1.0;
        
        const newKey = new KeyFrame(quat, time);
        this.keyframes.push(newKey);

        this.keyframes.sort(function(a,b){

            if(a.time < b.time)
                return -1;

            if(a.time > b.time)
                return 1;

            return 0;
        });

        this.endTime = this.keyframes[this.keyframes.length - 1].time;
    }

    getValue(it){

        const t = it % this.endTime;

        if(t <= this.keyframes[0].time){
            return this.keyframes[0].quat;
        }

        for(let n = 0; n < this.keyframes.length; n++) {

            if(this.keyframes[n].time > t){
                const tt = this.keyframes[n].time - this.keyframes[n-1].time;
                const dt = t - this.keyframes[n-1].time;
                this.cq.slerpQuaternions(this.keyframes[n-1].quat, this.keyframes[n].quat, dt / tt);
                return this.cq;
            }
        }

        return this.keyframes[0].quat;
    }

}

class SkelAnimation {
    bones;

    constructor(bone_names = []){

        this.bone_names = bone_names;
        this.bones = [];
    }


    getEndTime(){

        this.endTime = 0;
        for(let b of this.bones){

            this.endTime = Math.max(this.endTime, b.endTime)

        }
    }

    toPhys() {

        const anim = [];

        for(let b of this.bones){

            const a = {name : b.name, keyframes: []}

            for(let k of b.keyframes){
                a.keyframes.push({q : {x: k.quat.x, y: k.quat.y, z: k.quat.z, w: k.quat.w}, t: k.time});
            }
            anim.push(a);
        }
        return {bones : anim};
    }
    serialize() {

        const anim = [];

        for(let b of this.bones){

            const a = {name : b.name, keyframes: []}

            for(let k of b.keyframes){
                a.keyframes.push({q : {x: k.quat.x, y: k.quat.y, z: k.quat.z, w: k.quat.w}, t: k.time});
            }
            anim.push(a);
        }
        return JSON.stringify(anim);
    }

    scaleTime(scale){
        for(let b of this.bones){
            for(let k of b.keyframes){
                k.time *= scale;
            }
            b.endTime*= scale;
        }
    }

    load(data){

        this.bone_names = [];

        this.bones = [];

        for(let dbone of data){
            this.bone_names.push(dbone.name);
            const a = new BoneAnim(dbone.name);
            for(let k of dbone.keyframes) {
                a.addKey(k.q, k.t);
            }
            this.bones.push(a);
        }
    }

    fromSkeleton(skeleton) {
        
        for(let c of skeleton.root.children) {
            this.addBone(c);
        }
    }

    hasBone(bone_name){

        for(let b of this.bones){
            if(b.name == bone_name){
                return true;
            }
        }

        return false;
    }

    addBone(bone){

        if((this.bone_names.length == 0) || (this.bone_names.indexOf(bone.name) >= 0 )){

            const banim = new BoneAnim(bone.name, bone.quat);
            this.bones.push(banim);
        }

        for(let c of bone.children){
            this.addBone(c);
        }
    }

    addSingleBone(bone_name, skeleton){

        const bone = skeleton.findBone(bone_name);
        const quat = bone ? bone.quat : new THREE.Quaternion();

        const banim = new BoneAnim(bone_name, quat);
        this.bones.push(banim);

    }

    addBoneKey(bone_name, quat, t = null){

        for(let b of this.bones){
            if(b.name == bone_name){
                b.addKey(quat, t);

            }
        }
    }

    getKey(bone_name, t){
        for(let b of this.bones){
            if(b.name == bone_name){
                for(let k of b.keyframes){
                    if(k.time == t)
                        return k.quat;
                }
            }
        }

        return null;
    }

    setKey(bone_name, q , t){

        for(let b of this.bones){
            if(b.name == bone_name){
                for(let k of b.keyframes){
                    if(k.time == t)
                        k.quat.set(q.x, q.y, q.z, q.w);
                }
            }
        }
    }


}

function absAngle(a) {
  // this yields correct counter-clock-wise numbers, like 350deg for -370
  return (360 + (a % 360)) % 360;
}

function angleDelta(a, b) {
  // https://gamedev.stackexchange.com/a/4472
  let delta = Math.abs(absAngle(a) - absAngle(b));
  let sign = absAngle(a) > absAngle(b) || delta >= 180 ? -1 : 1;
  return (180 - Math.abs(delta - 180)) * sign;
}

function amod(a,n){
      return a - Math.floor(a/n) * n
    }


function smallestSignedAngleBetween(x, y){
    const a = amod((x - y) , (Math.PI * 2))
    const b = amod((y - x) , (Math.PI * 2))
     if (a < b )
      return -a  
    else 
      return b;
}

const repair_time = 1200;
//let Ammo = null;

const meshsInfos = {
    'car2':      { suspensionRestLength: 0.60, wheelOffset : -0.2, chassisOffset: 0.50, rollInfluence: 0.50, wheelRadius: 0.3, wheelWidth: 0.2, extents: { size: { x: 1.5025140047073364, y: 1.2076700329780579, z: 3.6953179836273193 }, center: { x: 0, y: 0.1090900778770447, "z": -0.19572603702545166 } } },

    'car3':      { suspensionRestLength: 0.48, wheelOffset : -0.4, chassisOffset: 0.55, rollInfluence: 0.10, wheelRadius: 0.3, wheelWidth: 0.2, extents: { "size": { "x": 2.9, "y": 1.7, "z": 5.2 }, "center": { "x": 0.0356920063495636, "y": 0.1461500197649002, "z": -0.28802005052566528 } } },
    'cocc':      { suspensionRestLength: 0.38, wheelOffset : -0.4, chassisOffset: 0.35,rollInfluence: 0.1, wheelRadius: 0.3, wheelWidth: 0.2, extents: { "size": { "x": 1.7839179635047913, "y": 1.4801300466060638, "z": 4.243614077568054 }, "center": { "x": 0.0356920063495636, "y": 0.4461500197649002, "z": -0.18802005052566528 } } },
    'car4':      { suspensionRestLength: 0.40,  wheelOffset : -0.3, chassisOffset: 0.35, rollInfluence: 0.10, wheelRadius: 0.3, wheelWidth: 0.2, extents: {"size":{"x": 3.0367984771728516, "y" : 1.8123443245887756, "z" : 5.698250770568848},"center":{"x":0.00014850497245788574,"y": 0.17746801674365997,"z":0.08306896686553955}} },
    'viper':     { suspensionRestLength: 0.40,  wheelOffset : -0.3, chassisOffset: 0.45, rollInfluence: 0.10, wheelRadius: 0.3, wheelWidth: 0.2, extents: {"size":{"x": 3.0367984771728516, "y" : 1.8123443245887756, "z" : 5.698250770568848},"center":{"x":0.00014850497245788574,"y": 0.27746801674365997,"z":0.08306896686553955}} },
    'buggy':     { suspensionRestLength: 0.40,  wheelOffset : -0.3, chassisOffset: 1.05, rollInfluence: 0.10, wheelRadius: 0.3, wheelWidth: 0.2, extents: {"size":{"x": 3.0367984771728516, "y" : 1.8123443245887756, "z" : 5.698250770568848},"center":{"x":0.00014850497245788574,"y": 0.47746801674365997,"z":0.08306896686553955}} },

    'wuf':       { suspensionRestLength: 0.55,  wheelOffset : -0.8, chassisOffset: 0.80, rollInfluence: 0.10, wheelRadius: 0.3, wheelWidth: 0.2, extents: {"size":{"x":2.208293914794922,"y":1.80595018863678,"z":4.746110916137695},"center":{"x":0.010540008544921875,"y":0.2,"z":0.011483430862426758}}  },
    'davinci':   { suspensionRestLength: 0.35,  wheelOffset : -0.2, chassisOffset: 0.40, rollInfluence: 0.10, wheelRadius: 0.3, wheelWidth: 0.2, extents: {"size":{"x":2.208293914794922,"y":1.390595018863678,"z":4.746110916137695},"center":{"x":0.010540008544921875,"y":0.23492050766944885,"z":0.011483430862426758}}  },
    'buggyB':    { suspensionRestLength: 0.70,  wheelOffset : -0.2, chassisOffset: 0.30, rollInfluence: 0.40, wheelRadius: 0.4, wheelWidth: 0.4, extents: {"size":{"x":2.062350034713745,"y":1.6933359801769257,"z":3.5725300312042236},"center":{"x":0.03965401649475098,"y":0.4374619871377945,"z":-0.12764596939086914}} },
    'lambo':     { suspensionRestLength: 0.40,  wheelOffset : -0.2, chassisOffset: 0.30, rollInfluence: 0.50, wheelRadius: 0.5, wheelWidth: 0.4, extents: { "size": { "x": 2.007147967815399, "y": 1.115267038345337, "z": 4.371685147285461 }, "center": { "x": 0.0113619863986969, "y": 0.08561050891876221, "z": 0.3787035346031189 } } },
    'toradyne': { suspensionRestLength: 0.50,  wheelOffset : -0.2, chassisOffset: 0.50, rollInfluence: 0.50, wheelRadius: 0.5, wheelWidth: 0.4, extents: {"size":{"x":1.761555016040802,"y":1.4558629989624023,"z":3.878015160560608},"center":{"x":0.0038565099239349365,"y":0.20069849491119385,"z":-0.07079952955245972}} },
    'car5':      { suspensionRestLength: 0.20,  wheelOffset : -0.2, chassisOffset: 0.10, rollInfluence: 0.10, wheelRadius: 0.3, wheelWidth: 0.2, extents: { "size": { "x": 1.7839179635047913, "y": 1.4801300466060638, "z": 4.243614077568054 }, "center": { "x": 0.0356920063495636, "y": 0.4461500197649002, "z": -0.18802005052566528 } } }
  };
  

  const profiles = 
  [
    {name : "profile2",
      centerForce : 120,
      angleForce : 40,
      angleThresold: 0.4 ,
      speedFactor : 1.0,
      offset : 0,
      id : 1
    },{name : "profile1",
      centerForce : 160,
      angleForce : 60,
      angleThresold: 0.3 ,
      speedFactor : 1.0,
      offset : 2,
      id : 2
    },{name : "profile3",
      centerForce : 140,
      angleForce : 50,
      angleThresold: 0.2 ,
      speedFactor : 1.0,
      offset : -2,
      id : 3

    }
  ];

  const SplitArray = 
  [   [[[0, 1 , 2]], []],                                 //0
      [[[0, 1 , 2]], []],                                 //1  
      [[[0, 4, 5], [0, 1, 4]], [[2, 5 , 4]]],             //2  
      [[], []],                                       //3  
      [[[0, 1 , 2]], []],                                 //4  
      [[[0, 1 , 2]], []],                                 //5  
      [[[0, 1 , 5]], [[1, 2, 5]]],                        //6  
      [[], []],                                        //7  
      [[[2, 3, 4],[2, 0, 3]], [[1, 4 , 3]]],              //8  
      [[[2, 0, 3]], [[1, 2 , 3]]],                        //9 
      [[[0, 3, 5]], [[1, 5 , 3], [1, 2 , 5]]],            //10 
      [[], []],                                       //11  
      [[], []],                                       //12
      [[], []],                                       //13
      [[], []],                                       //14
      [[], []],                                       //15
      [[[0, 1 , 2]], []],                                 //16
      [[[0, 1 , 2]], []],                                 //17
      [[[0, 1 , 4]], [[2, 0, 4]]],                          //18
      [[], []],                                       //19
      [[[0, 1 , 2]], []],                                 //20
      [[[0, 1 , 2]], [[0, 1 , 2]]],                       //21 on triangle
      [[], [[0, 1 , 2]]],                                 //22
      [[], []],                                       //23  
      [[[2, 0, 4]], [[0, 1, 4]]],                         //24  
      [[], [[0, 1 , 2]]],                                 //25
      [[], [[0, 1 , 2]]],                                 //26
      [[], []],                                       //27
      [[], []],                                       //28
      [[], []],                                       //29
      [[], []],                                       //30
      [[], []],                                       //31
      [[[1, 5, 3], [1, 2, 5]], [[0, 3, 5]]],              //32
      [[[1, 2, 3]], [[2, 0, 3]]],                         //33  
      [[[1, 4, 3]], [[0, 3, 4], [2, 0, 4]]],              //34
      [[], []],                                       //35
      [[[1, 2, 5]], [[0, 1, 5]]],                         //36
      [[], [[0, 1 , 2]]],                                 //37
      [[], [[0, 1 , 2]]],                                 //38
      [[], []],                                       //39
      [[[2, 5, 4]], [[0, 4, 5], [0, 1, 4]]],              //40
      [[], [[0, 1 , 2]]],                                 //41
      [[], [[0, 1 , 2]]]                                   //42
  ];
class PathFinder {

    visited;
    distance;
    predecessors;
    numNodes;

    constructor(numNodes) {

        this.numNodes = numNodes;
        this.visited = new Uint8Array(this.numNodes);
        this.distance = new Float32Array(this.numNodes);
        this.predecessors = new Uint8Array(this.numNodes);
    }

    reset(){

        this.predecessors = [];

        this.visited.fill(0);
        this.distance.fill(Infinity);
        this.predecessors.fill(255);


    }
}

  class PhysicMain {

    constructor(DAmmo) {

      Ammo = DAmmo;
      this.rigidBody_List = new Array();
      this.syncList = [];
      this.character_List = [];
      this.ragdoll_List = [];
      this.movingObjects = [];
      this.states = [];
      this.totalBufferSents = 0;
      this.wallObjPhy2 = null;
      this.roadObjPhy = null;
      this.removeCubes = [];

      this.btFrom  = new Ammo.btVector3();
      this.btTo  = new Ammo.btVector3();
      this.closestRayResultCallback = new Ammo.ClosestRayResultCallback( this.btFrom, this.btTo );

      this.upVector = new THREE.Vector3(0,1,0);
      this.reccord = {startTime : 0, samples : []};
      this.lastReccord = 0;
      
      this.nFrames = 0;

      this.triggerEvents = [];

      this.raycaster = new THREE.Raycaster();
        
      this.tmpTransformation = new Ammo.btTransform();
      this.tmpTransformationNPC = new Ammo.btTransform();
      this.tmpTransformation2 = new Ammo.btTransform();
      this.tmpTransformation3 = new Ammo.btTransform();
      this.tmpVec3 = new Ammo.btVector3(0.0, 0.0, 0.0);
      this.tmpbtQ = new Ammo.btQuaternion(0.0, 0.0, 0.0, 1.0);
      this.tmpEUL = new THREE.Euler();
      this.tmpQ = new THREE.Quaternion();
      this.tmpQ2 = new THREE.Quaternion();

      this.tmpV3 = new THREE.Vector3();
      this.tmpV2 = new THREE.Vector3();
      this.tmpV1 = new THREE.Vector3();
      
      

      this.xAxis = new Ammo.btVector3(1,0,0);
      this.yAxis = new Ammo.btVector3(0,1,0);
      this.zAxis = new Ammo.btVector3(0,0,1);
      this.zVec = new Ammo.btVector3(0.0, 0.0, 0.0);
      
      this.collisionConfiguration = new Ammo.btDefaultCollisionConfiguration();
      this.dispatcher = new Ammo.btCollisionDispatcher(this.collisionConfiguration);
      this.overlappingPairCache = new Ammo.btDbvtBroadphase();
      this.solver = new Ammo.btSequentialImpulseConstraintSolver();
      this.softBodySolver = new Ammo.btDefaultSoftBodySolver();
      this.physicsUniverse = new Ammo.btSoftRigidDynamicsWorld(this.dispatcher, this.overlappingPairCache, this.solver, this.collisionConfiguration, this.softBodySolver);

      
      this.btCB = new Ammo.btGhostPairCallback();
      this.physicsUniverse.getBroadphase().getOverlappingPairCache().setInternalGhostPairCallback(this.btCB);	
  
      this.from = new THREE.Vector3();
      this.to = new THREE.Vector3();
      this.pts = [];

      this.tmpPos = new THREE.Vector3();
      this.tmpPos2 = new THREE.Vector3();
  
      this.upVec = new THREE.Vector3(0.0, 1.0, 0.0);
      this.forwardVec = new THREE.Vector3(0,0,1);
      this.clock = new THREE.Clock(true);
  
      this.lapTime = 0;
  
      this.cars = [];
      this.characters = [];
      this.npcs = [];
      this.woods = [];
      this.bonuses = [];
      this.jumpers = [];

      this.turrets = [];
      this.interestPoints = []
      this.springs = [];
      this.ropes = [];
      this.drones = [];
      this.mbgrids = [];
      this.blastImpacts = [];
      this.xmeshes = [];
      this.splits = [];
      this.pathes = [];
      this.nextSyncId = 1;

      THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
      THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
      THREE.Mesh.prototype.raycast = acceleratedRaycast;
    }

    findClosestSeg2(track, carPos, ocp = null, delta = null){


      
      let dist = 1000000000000;
      let cur = -1;
  
      for(let n=0;n< track.AILines.length; n++){
        track.AILines[n].closestPointToPoint (carPos, true, this.tmpPos);
        const d = this.tmpPos.distanceTo(carPos);
  
        if(d<dist){

          if(delta)
            delta.set(this.tmpPos.x, this.tmpPos.y, this.tmpPos.z).sub(carPos);

          if(ocp)
            ocp.set(this.tmpPos.x, this.tmpPos.y, this.tmpPos.z);
          dist = d;
          cur = n;
        }
      }
      return cur;
    }

    findClosestSeg(track, carPos, ocp = null, delta = null){

      
      let dist = 1000000000000;
      let cur = -1;
  
      for(let n=0;n< track.trackPoints.length; n++){
        track.trackPoints[n].line.closestPointToPoint (carPos, true, this.tmpPos);
        const d = this.tmpPos.distanceTo(carPos);
  
        if(d<dist){

          if(delta)
            delta.set(this.tmpPos.x, this.tmpPos.y, this.tmpPos.z).sub(carPos);

          if(ocp)
            ocp.set(this.tmpPos.x, this.tmpPos.y, this.tmpPos.z);
          dist = d;
          cur = n;
        }
      }
      return cur;
    }

    isBefore(a, b, c) {
      return (b.x - a.x)*(c.z - a.z) - (b.z - a.z)*(c.x - a.x) > 0;
    }

    getDistanceOnCurve(track, car, delta = null){
      const searchPoint = car.pos;
      //let ocp = new THREE.Vector3();
      const curve = track.curveC;

      if(!track.AILines)
        return -1;


      car.curve2.points = [];   // Array for second curve
      const cs = this.findClosestSeg2(track, searchPoint, this.tmpV2, delta)* curve.points.length / track.AILines.length;
;

      if(cs<0)
        return -1;

      for(var i = 0; i <= cs ; i++){
        car.curve2.points.push(curve.points[i]);
      }
      car.curve2.points.push(this.tmpV2);
      car.curve2.needsUpdate = true;

      track.lastCurve2 = car.curve2;

      return car.curve2.getLength() / curve.getLength();
    }
  
    angularDamping(phycar, dt)
    {
      const delta = dt * 100.8;
      
  
      /*
      const upVector= new THREE.Vector3(0,1,0);
      const trans = phycar.physicsBody.getCenterOfMassTransform();
      const oqrot = trans.getRotation();
      const nnr = new THREE.Vector3(oqrot.x(),oqrot.y(),oqrot.z());
      const nr = new THREE.Vector3().lerpVectors(nnr, upVector, 0.9);
      const oq = new THREE.Quaternion(oqrot.x(),oqrot.y(),oqrot.z(),oqrot.w());
      const nq = new THREE.Quaternion(nr.x, nr.y, nr.z, oqrot.w());
      nq.multiply( oq.conjugate());
      const adelta =  new THREE.Euler(0,0,0,"XYZ").setFromQuaternion(nq);
      phycar.physicsBody.applyTorqueImpulse(new Ammo.btVector3(adelta.x * 25,adelta.y* 25, adelta.z* 25));
      return;
      */
  
     
      const avel =  phycar.physicsBody.getAngularVelocity();

      const x = Math.max(-1, Math.min(1, avel.x()));
      const z = Math.max(-1, Math.min(1, avel.z()));

      avel.setValue(x * 0.90, avel.y() , z * 0.90);
   
      
      /*
      const angles = new THREE.Euler(0,0,0,"XYZ").setFromQuaternion(new THREE.Quaternion(q.x(),q.y(),q.z(),q.w()));
      
      if(Math.abs(angles.x)< Math.PI)
      {
        if(angles.x < -delta)
          x += delta;
        else if(angles.x > delta)
          x -= delta;
        else 
          x = 0;
  
      }else{
  
        if(angles.x < -delta)
          x -= delta;
        else if(angles.x > delta)
          x += delta;
        else 
          x = 0;        
      }
      */
  
      /*
      x *= 0.75;
      
      if(Math.abs(angles.z)< Math.PI)
      {
        if(angles.z < -delta)
          z += delta;
        else if(angles.z > delta)
          z -= delta;        
        else 
          z = 0;        
      }
      else{
  
        if(angles.z < -delta)
          z -= delta;
        else if(angles.z > delta)
          z += delta;        
        else 
          z = 0;        
  
      }
      */
      /*
      x *= Math.pow(0.002, dt);
      z *= Math.pow(0.002, dt);
     */
      
      
      //x *= 0.90;
      //z *= 0.99;
      
      phycar.physicsBody.setAngularVelocity(avel);     
      //phycar.physicsBody.setAngularVelocity(new Ammo.btVector3( x , avel.y(), z));
    }
  
  
    delete() {
  
      Ammo.destroy(this.tmpTransformation);
      Ammo.destroy(this.collisionConfiguration);
      Ammo.destroy(this.dispatcher);
      Ammo.destroy(this.overlappingPairCache);
      Ammo.destroy(this.solver);
      Ammo.destroy(this.gravity);
      //Ammo.destroy(this.physicsUniverse);
    }
  
  
    takeBonus(carid, bonusId) {
      for (let n = 0; n < this.bonuses.length; n++) {
  
        if (this.bonuses[n].id === bonusId) {
  
          if (this.bonuses[n].taken !== 0)
            return;
  
          this.bonuses[n].taken = 1;


          for (let c = 0; c < this.cars.length; c++) {
            if (this.cars[c].netID === carid) {
              switch(this.bonuses[n].type){
                case 0:this.cars[c].bonuses.push(1);break;
                case 1:this.cars[c].blueprints |= this.bonuses[n].bpMask; break;
                case 2:this.cars[c].techs.push(this.bonuses[n].amount);break;
              }
              return true;
            }
          }
          break;
        }
      }
    }

    bodyHit(shot , body, time){

      const spider = body.userData.spider;
      const bone = body.userData.obj;
      const car = shot.userData.turret;
      const sshot = shot.userData.obj;

      if(!spider.active)
        spider.active = true;

      if(( time - bone.lastSnd)> 0.2){
        spider.sndBits = this.addSndBit(spider.sndBits, 1);
        bone.lastSnd = time;
      }

      for(let si = 0;si<car.shots.length;si++){
        if(car.shots[si].id == sshot.id){
          this.removePhyShot(car.shots[si]);
          car.shots.splice(si , 1);
          break;
        }
      }
    }

    addImpact(pos, type){

      if((this.totalBufferSents> 100)&&(this.blastImpacts.length < 8))
        this.blastImpacts.push({pos : {x : pos.x, y : pos.y, z : pos.z}, type: type});
    }

    
    droneBodyHit(shot , body, time, wp){

      const drone = body.userData.drone;
      const bone = body.userData.obj;
      const car = shot.userData.turret;
      const sshot = shot.userData.obj;

      if(!drone.active)
        drone.active = true;

      if(( time - bone.lastSnd)> 0.2){
        drone.sndBits = this.addSndBit(drone.sndBits, 1);
        bone.lastSnd = time;
      }

      
      if(wp != null) {
        
        this.tmpPos.set(wp.x(), wp.y(), wp.z());
        /*
        const dir = new THREE.Vector3().subVectors(drone.pos, impactPos).normalize();
        const pts = [];
        this.raycaster.set(impactPos, dir);
        this.raycaster.intersectObject(drone.object, false, pts);
        if(pts.length > 0)
        */

          this.addImpact(this.tmpPos, 1);
      }

      for(let si = 0;si<car.shots.length;si++){
        if(car.shots[si].id == sshot.id){
          this.removePhyShot(car.shots[si]);
          car.shots.splice(si , 1);
          break;
        }
      }
    }

    removeBoneSubSystem(target, boneName){

      for(let ss =0;ss < target.subsystems.length; ss++){
        if(target.subsystems[ss].name == boneName){
           target.subsystems.splice(ss, 1);
           return ss;
         }
       }

       return -1;
    }

    
    paleHit(shot , pale, time){

      const palebone = pale.userData.obj;
      const car = shot.userData.turret;
      const ids = shot.userData.name.substr(9);
      const id = parseInt(ids);

      if(palebone.life == 0)
        return;

      if(palebone.life > shot.userData.dmg)
        palebone.life -= shot.userData.dmg;
      else
        palebone.life = 0;

      const drone = pale.userData.drone;

      if(!drone.active)
        drone.active = true;

      for(let si = 0;si<car.shots.length;si++){
        if(car.shots[si].id == id){
          this.removePhyShot(car.shots[si]);
          car.shots.splice(si , 1);
          break;
        }
      }

      if( palebone.life > 0 ){

        if(( time - palebone.lastSnd)> 0.2){
          drone.sndBits = this.addSndBit(drone.sndBits, 2);
          palebone.lastSnd = time;
        }
        return;
      }

      if(palebone.constraint){

        palebone.ammoData.geometry.calculateLocalInertia(20, palebone.ammoData.localInertia);
        pale.setMassProps(20, palebone.ammoData.localInertia);

        this.physicsUniverse.removeConstraint(palebone.constraint);
        Ammo.destroy(palebone.constraint);
        palebone.constraint=null;
        this.removeBoneSubSystem(drone.target, palebone.name);
        
        drone.sndBits = this.addSndBit(drone.sndBits, 3);
      }
    }


    legHit(shot , leg, time){

      const legbone = leg.userData.obj;
      legbone.life -= shot.userData.dmg;

      const spider = leg.userData.spider;

      if(!spider.active)
        spider.active = true;

      if( legbone.life > 0 ){

        if(( time - legbone.lastSnd)> 0.2){
          spider.sndBits = this.addSndBit(spider.sndBits, 2);
          legbone.lastSnd = time;
        }

        return;
      }

      if(legbone.constraint){

        const ids = shot.userData.name.substr(9);
        const id = parseInt(ids);
        const car = shot.userData.turret;

        this.physicsUniverse.removeConstraint(legbone.constraint);
        Ammo.destroy(legbone.constraint);
        legbone.constraint=null;

        //console.log('destroyed leg before '+legbone.name, spider.target.subsystems);

        const rss = this.removeBoneSubSystem(spider.target, legbone.name);

        if(car.selectedSubsystemId === rss)
            car.selectedSubsystemId = 0;


        this.tmpVec3.setValue(1.2 , 1.2, 1.2 );  
        legbone.ammoData.geometry.setLocalScaling(this.tmpVec3);
        legbone.ammoData.geometry.setMargin(0.2);


        //console.log('destroyed leg after '+legbone.name, spider.target.subsystems);

        if(legbone.children.length>0){
          const fleg = legbone.children[0];
          if(fleg.constraint !== null){
            this.physicsUniverse.removeConstraint(fleg.constraint);
            Ammo.destroy(fleg.constraint);
            fleg.constraint=null;


            this.tmpVec3.setValue(1.2 , 1.2, 1.2 );  
            fleg.ammoData.geometry.setLocalScaling(this.tmpVec3);
            fleg.ammoData.geometry.setMargin(0.2);

            //console.log('destroyed sub leg before '+fleg.name, spider.target.subsystems);

            const rss = this.removeBoneSubSystem(spider.target, fleg.name);

            if(car.selectedSubsystemId === rss)
              car.selectedSubsystemId = 0;

            //console.log('destroyed sub leg after '+fleg.name, spider.target.subsystems);
          }
        }

        spider.sndBits = this.addSndBit(spider.sndBits, 3);

        for(let si = 0;si<car.shots.length;si++){
          if(car.shots[si].id == id){
            this.removePhyShot(car.shots[si]);
            car.shots.splice(si , 1);
            break;
          }
        }
      }
    }

    takeFallenPale(rbCar, rbPale){

      if(rbPale.userData.taken === true)
        return;

    const carid = rbCar.userData.name.substr(4);
    const car = this.getNetCar(carid);
    const pale = rbPale.userData.obj;

    pale.carConstraint = this.attachObject(car, pale.physicsBody, -pale.radius * 1.2, 0, 0);

    car.newTakenObjects++;
    car.TakenObjects.push({type : "tech", amount : 1.0, obj : pale});

    rbPale.userData.taken = true;

    }

    takeFallenLeg(rbCar, rbLeg, time){

      if(rbLeg.userData.taken === true)
          return;

      console.log('take leg '+rbLeg.userData.name+' '+rbCar.userData.name);

      const carid = rbCar.userData.name.substr(4);
      const car = this.getNetCar(carid);
      const leg = rbLeg.userData.obj;

      const offset = leg.size ? -leg.size.x : -leg.length;
      leg.carConstraint = this.attachObject(car, leg.physicsBody, 0 ,0, offset);

      car.newTakenObjects++;
      car.TakenObjects.push({type : "steel", amount : 0.5, obj : leg});

      rbLeg.userData.taken = true;

      if((car.targetNode & 0x00FF0000) != 0){
          const tagetType = (car.targetNode & 0x00FF0000) >> 16;
          if(tagetType == 1){
            const npcId = (car.targetNode & 0x0000FF00) >> 8;
            const targetId = (car.targetNode & 0x000000FF);
            if(rbLeg.userData.char.target && rbLeg.userData.char.target.id === npcId){
              if(rbLeg.userData.char.target.subsystems.length>0){
                for(let ss=0;ss<rbLeg.userData.char.target.subsystems.length;ss++){
                  if(rbLeg.userData.char.target.subsystems[ss].id == targetId){
                      const phychar = this.getNPC(car.questNPC.netID);
                      this.executeScript(car.script, car.scriptInstance, car.questNPC, phychar, car.questNPC.ppos, time, {car : car} );
                  }
                }
              }/*else{
                const phychar = this.getNPC(rbLeg.userData.char.netID);
                this.executeScript(rbLeg.userData.char, phychar, rbLeg.userData.char.ppos, time, {car : car} );
              }*/
            }
          }
      }

      /*
      car.bonuses.push(0.5);
      car.newbonuses.push(0.5);
      this.physicsUniverse.removeRigidBody(leg.userData.obj.physicsBody);

      for (let rbi = 0; rbi < this.rigidBody_List.length; rbi++) {
        if(this.rigidBody_List[rbi] == leg.userData.obj.physicsBody){
          this.rigidBody_List.splice(rbi,1);
          break;
        }
      }

      Ammo.destroy(leg.userData.obj.physicsBody);
      leg.userData.obj.physicsBody = null;
      */
    }

    nameStartWithinArray(array, name){
      for(let i =0;i<array.length;i++){
        if(name.startsWith(array[i]))
            return true;
      }

      return false;
    }

    
    removeTakenObjects(car){

      if(car.TakenObjects.length<=0)
          return;

      let totalBonus = 0;
      let totalTech = 0;

      for(let TakenObject of car.TakenObjects) {
          const tobj = TakenObject.obj;
          this.physicsUniverse.removeConstraint(tobj.carConstraint);
          Ammo.destroy(tobj.carConstraint);
          tobj.carConstraint = null;

          if(TakenObject.type === 'steel')
            totalBonus += TakenObject.amount;
          else if(TakenObject.type === 'tech')
            totalTech += TakenObject.amount;

          this.physicsUniverse.removeRigidBody(tobj.physicsBody);
    
          for (let rbi = 0; rbi < this.rigidBody_List.length; rbi++) {
            if(this.rigidBody_List[rbi] == tobj.physicsBody){
              this.rigidBody_List.splice(rbi,1);
              break;
            }
          }
          Ammo.destroy(tobj.physicsBody);
          tobj.physicsBody = null;
      }

      if(totalBonus>0){
        car.bonuses.push(totalBonus);
        car.newbonuses.push(totalBonus);
      }

      if(totalTech > 0 ){
        car.techs.push(totalTech);
        car.newtechs.push(totalTech);
      }

      car.TakenObjects = [];
    }
  
    carHit(rbCar, rbObj, time){

      const carid = rbCar.userData.name.substr(4);
      const car = this.getNetCar(carid);

      if(!car)
          return null;

      let hasBonus = false;
      let ipid = -1;
      let bonusid = -1;
      let hasCol = false;
      let dmg = 0;


      if (rbObj.userData.name.startsWith('rope-obj-')) {

        const pvel = rbObj.getLinearVelocity();
        rbCar.applyCentralImpulse(pvel);
        /*
        const carvel = rbCar.physicsBody.getLinearVelocity();
        this.tmpVec3.setValue(pvel.x() + carvel.x(), pvel.y() + carvel.y(), pvel.z() + carvel.z());
        rbCar.setLinearVelocity(this.tmpVec3);
        */
     }

      if (rbObj.userData.name.startsWith('trigger-')) {
         car.hasTrigger = this.carTriggerEnter(car, rbObj.userData.obj.type, rbObj.userData.idx);
      }

      if (rbObj.userData.name.startsWith('ip-')) {
        ipid = parseInt(rbObj.userData.name.substr(3));
        this.addCarObject(carid, ipid);
      }
      if (rbObj.userData.name.startsWith('bonus')) {
          hasBonus = true;
          bonusid = parseInt(rbObj.userData.name.substr(6));
      }

      if ((rbObj.userData.name.startsWith('rope-obj'))||(rbObj.userData.name.startsWith('spring-obj'))) {
        hasCol = true;
      }

      if ((rbObj.userData.name == 'track')||(rbObj.userData.name == 'ground')) {
        
        this.tmpQ.set(car.quat.x, car.quat.y, car.quat.z, car.quat.w);
        this.tmpEUL.setFromQuaternion(this.tmpQ);

        if ((Math.abs(this.tmpEUL.z) > Math.PI / 2)||(Math.abs(this.tmpEUL.x) > Math.PI / 2)) {
          hasCol = true;
        }
      }else if((rbObj.userData.name.startsWith('cyl'))||(rbObj.userData.name.startsWith('cube'))){
        hasCol = true;
      }

      if (rbObj.userData.name == 'shot') {
        hasCol = true;
        dmg = rbObj.userData.dmg;
      }

      if (rbObj.userData.name.startsWith('npc-')){

        if(rbObj.userData.obj && rbObj.userData.obj.life === 0){
          this.takeFallenLeg(rbCar, rbObj, time);
        }else{
          const limb=rbObj.userData.name.substr(4);
          if(rbObj.userData.char.scriptInstance.state === 'attack' && 
            (rbObj.userData.char.stopStand != 0) && 
            ((time - rbObj.userData.char.lastAttack)<0.5) && rbObj.userData.char.avatarDef.attackLimb){
            for(let l=0;l<rbObj.userData.char.avatarDef.attackLimb.length;l++){
              if(limb.startsWith(rbObj.userData.char.avatarDef.attackLimb[l])){
                hasCol = true;
                dmg = 0.2;
              }
            }
          }
        }
      }

      if (rbObj.userData.name.startsWith('wood')) {
        hasCol = true;
      }

      return {carid: carid, hasCol: hasCol, hasBonus: hasBonus, bonusid: bonusid, ipid : ipid, dmg : dmg};
    }

    shotHit(shot, obj, wp){

      const origin = shot.userData.turret;
      const id = shot.userData.obj.id;
      const dmg = shot.userData.dmg;
      const tobj = obj.userData.obj;

      if(tobj === undefined){
        console.log(obj);
        return;
      }
        

      if((tobj !== null)&&(origin.id == tobj.id))
        return;

      let blastRadius = 0;
      let blastForce = 0;
      let BlastImpact = 0;

      if(origin.weapons){

        if(origin.weapons.length){
          blastRadius = origin.weapons[0].blastRadius;
          blastForce = origin.weapons[0].blastForce;
          BlastImpact =origin.weapons[0].blastImpact?origin.weapons[0].blastImpact : 1;
        }
      }else{
        blastRadius = origin.blastRadius;
        if(blastRadius>0){
          blastForce = origin.blastForce;
          BlastImpact = origin.blastImpact;
        }else
          blastRadius = 0;
      }


      if(blastRadius>0){

        this.tmpPos.set(wp.x(), wp.y(), wp.z());
        this.addImpact(this.tmpPos, BlastImpact);
  
        const pw = 2;

        for(let ci =0; ci< this.rigidBody_List.length;ci++){

          if( (this.rigidBody_List[ci].userData.name.startsWith('cube'))||
              (this.rigidBody_List[ci].userData.name.startsWith('cyl'))){

            const obj = this.rigidBody_List[ci].userData.obj;

            this.tmpPos2.subVectors(obj.pos, this.tmpPos);
            const d =this.tmpPos2.length();
            if(d<blastRadius){
    
              const intensity = (Math.pow(blastRadius, pw) - Math.pow(d, pw)) /Math.pow(blastRadius, pw) ;
    
              this.tmpPos2.normalize().multiplyScalar(blastForce * intensity);
              this.tmpVec3.setValue(this.tmpPos2.x, Math.max(0, this.tmpPos2.y), this.tmpPos2.z);

              this.rigidBody_List[ci].activate();
              this.rigidBody_List[ci].applyCentralImpulse(this.tmpVec3);
            }
          }
        }

        for(let car of this.cars){
          this.tmpPos2.subVectors(car.pos, this.tmpPos);
          const d =this.tmpPos2.length();
          if(d<blastRadius){
  
            const intensity = (Math.pow(blastRadius, pw) - Math.pow(d, pw)) /Math.pow(blastRadius, pw) ;
  
            this.tmpPos2.normalize().multiplyScalar(blastForce * intensity);
            this.tmpVec3.setValue(this.tmpPos2.x, this.tmpPos2.y, this.tmpPos2.z);
            car.physicsBody.applyCentralImpulse(this.tmpVec3);
  
            this.addCarDmg(car.id, intensity * dmg, wp);  
          }
        }


        for(let si = 0;si<origin.shots.length;si++){
          if(origin.shots[si].id == id){
            this.removePhyShot(origin.shots[si]);
            origin.shots.splice(si , 1);
            break;
          }
        }
      }
    }


    markSplitBone(bone){

      bone.isBroken = true;

      for(let c of bone.children){

        this.markSplitBone(c);

      }

    }

    npcHit(npc, obj, time){

      const objName =  obj.userData.name;
      const character = npc.userData.char;
      const bone = npc.userData.obj;
      let hitVel;

      if(bone === null){

        if((character.avatarDef.attackTime>0) &&
           (character.scriptInstance.state !== 'attack') && 
           ((objName.startsWith('car'))||objName.startsWith('shot-car'))){

          let car = null;

          if(objName.startsWith('car')){
            const ar = objName.split('-');
            const id = ar[1]; 
            car = this.getCar(id);

           

            if(Math.abs(car.speed) < 10)
              return;

          }else{
            car = obj.userData.turret;
          }

          if(car){
              character.currentTarget = car;
              character.scriptInstance.prevState = character.scriptInstance.state;
              character.scriptInstance.pauseTime = time;
              character.scriptInstance.state = 'attack';
          }
        }
        return;
      }

      if(bone.isBroken == true)
        return;
      
      if(objName.startsWith('shot-car')){

        if(bone.life>=0){

          const shot = obj;
              
          if(bone.life == 0)
            return;
    
          if(bone.life > shot.userData.dmg)
            bone.life -= shot.userData.dmg;
          else
            bone.life = 0;
        }
      }

      if(objName.startsWith('car')){

        const ar = objName.split('-');
        const id = ar[1]; 
        const car = this.getCar(id);

        if(bone.life>=0){
          const e = Math.abs((car.massVehicle * car.speed * car.speed)) ;

          const dmg = e / 10000000;

          if(bone.life > dmg)
            bone.life -= dmg;
          else
            bone.life = 0;
        }

        character.stopStand = time + 2;

        const root = character.skeleton.root;

        if(car.speed > 50){
          root.ammoData.localInertia.setValue(0,0,0);
          root.ammoData.shape.calculateLocalInertia(character.mass, root.ammoData.localInertia);
          root.physicsBody.setMassProps(character.mass, root.ammoData.localInertia);
          root.physicsBody.setDamping(0.2, 0.1);

          if((character.scriptInstance.state !== 'attack')&&(character.avatarDef.attackTime>0)){
            character.currentTarget = car;
            character.scriptInstance.prevState = character.scriptInstance.state;
            character.scriptInstance.pauseTime = time;
            character.scriptInstance.state = 'attack';
          }
        }
      }

      if(bone.life == 0){

        if(bone.constraint){

          this.markSplitBone(bone);

          bone.pos = new THREE.Vector3();

          /*
          bone.ammoData.shape.calculateLocalInertia(20, bone.ammoData.localInertia);
          bone.physicsBody.setMassProps(20, bone.ammoData.localInertia);
          */


          const hitVel = obj.getLinearVelocity();

          this.physicsUniverse.removeConstraint(bone.constraint);
          Ammo.destroy(bone.constraint);
          bone.constraint=null;

          bone.physicsBody.applyCentralImpulse(hitVel);
          
          //this.removeBoneSubSystem(drone.target, bone.name);
          //drone.sndBits = this.addSndBit(drone.sndBits, 3);
        }
      }
    }



    detectCollision(time, dt) {
  
      let dispatcher = this.physicsUniverse.getDispatcher();
      let numManifolds = dispatcher.getNumManifolds();


      for (let i = 0; i < numManifolds; i++) {
  
        let contactManifold = dispatcher.getManifoldByIndexInternal(i);
        let numContacts = contactManifold.getNumContacts();
  
        const body0 = contactManifold.getBody0();
        const body1 = contactManifold.getBody1();
  
        const rb0 = Ammo.castObject(body0, Ammo.btRigidBody);
        const rb1 = Ammo.castObject(body1, Ammo.btRigidBody);
  
        var hasCol = false;
        var hasBonus = false;
        var carid = 0;
        var ipid = 0;
        let dmg = 0;
        var bonusid;
  
        
        /*
        if ((rb0.userData.name.startsWith('car')) && (rb1.userData.name.startsWith('hm'))) {
  
          console.log('hm');
  
          rb0.setLinearVelocity(new Ammo.btVector3(0.0, 0.0, 0.0));
          rb0.setAngularVelocity(new Ammo.btVector3(0.0, 0.0, 0.0));
          rb0.clearForces();
        }
  
        if ((rb1.userData.name.startsWith('car')) && (rb0.userData.name.startsWith('hm'))) {
  
          console.log('hm');
  
        }
        */
  
        if(!rb0.userData)
          continue;
  
        if(!rb1.userData)
          continue;       
            
        
        if ((rb0.userData.name.startsWith('cube-'))||(rb0.userData.name.startsWith('cyl-'))) {
          if(rb0.userData.obj.mass >0 && numContacts>0){
            let contactPoint = contactManifold.getContactPoint(0);
            const wp = contactPoint.getPositionWorldOnB();
            this.tmpPos.set(wp.x(), wp.y(), wp.z());
            rb0.userData.contacts.push({name: rb1.userData.name, lpos : new THREE.Vector3(contactPoint.m_localPointA.x(),contactPoint.m_localPointA.y(), contactPoint.m_localPointA.z()), pos : this.tmpPos.clone()});
          }
        }

        if ((rb1.userData.name.startsWith('cube-'))||(rb1.userData.name.startsWith('cyl-'))) {
          if(rb1.userData.obj.mass >0 && numContacts>0){
            let contactPoint = contactManifold.getContactPoint(0);
            const wp = contactPoint.getPositionWorldOnB();
            this.tmpPos.set(wp.x(), wp.y(), wp.z());
            rb1.userData.contacts.push({name: rb0.userData.name, lpos : new THREE.Vector3(contactPoint.m_localPointB.x(),contactPoint.m_localPointB.y(), contactPoint.m_localPointB.z()), pos : this.tmpPos.clone()});
          }
        }
        if (rb0.userData.name.startsWith('xmesh-')) {

          if(numContacts>0){
            let contactPoint = contactManifold.getContactPoint(0);
            const wp = contactPoint.m_localPointA;
            this.xmeshHit(rb0, rb1, wp );
          }
        }

        if (rb1.userData.name.startsWith('xmesh-')) {
          if(numContacts>0){
            let contactPoint = contactManifold.getContactPoint(0);
            const wp = contactPoint.m_localPointB;
            this.xmeshHit(rb1, rb0, wp );
          }
        }

        if (rb0.userData.name.startsWith('car')) {
          const res = this.carHit(rb0, rb1, time);

          if(res){
            carid = res.carid;
            hasCol = res.hasCol;
            hasBonus = res.hasBonus;
            bonusid = res.bonusid;
            ipid = res.ipid;
            dmg = res.dmg;
          }
        }
        if (rb1.userData.name.startsWith('car')) {
          const res= this.carHit(rb1, rb0, time);
          if(res){
            carid = res.carid;
            hasCol = res.hasCol;
            hasBonus = res.hasBonus;
            bonusid = res.bonusid;
            ipid = res.ipid;
            dmg = res.dmg;
          }
        }

        if (rb1.userData.name.startsWith('npc')) {
          this.npcHit(rb1, rb0, time);
        }
  
        if (rb0.userData.name.startsWith('npc')) {
          this.npcHit(rb0, rb1, time);
        }

        if(rb0.userData.name.startsWith('shot')){

          if(numContacts>0){
            let contactPoint = contactManifold.getContactPoint(0);
            const wp = contactPoint.getPositionWorldOnB();
            this.shotHit(rb0, rb1, wp);
          }else{
            /*this.tmpVec3.setValue(rb0.userData.obj.pos.x, rb0.userData.obj.pos.y, rb0.userData.obj.pos.z);
            this.shotHit(rb0, rb1, this.tmpVec3);*/
          }
        }

        if(rb1.userData.name.startsWith('shot')){
          if(numContacts>0){
            let contactPoint = contactManifold.getContactPoint(0);
            const wp = contactPoint.getPositionWorldOnA();
            this.shotHit(rb1, rb0, wp);
          }else{
            /*this.tmpVec3.setValue(rb1.userData.obj.pos.x, rb1.userData.obj.pos.y, rb1.userData.obj.pos.z);
            this.shotHit(rb1, rb0, this.tmpVec3);*/
          }
        }
        
        if(rb1.userData.drone){
          if(rb0.userData.name.startsWith('shot-car')){
            if(rb1.userData.name.startsWith('drone-')){

              if(numContacts>0){
                let contactPoint = contactManifold.getContactPoint(0);
                const wp = contactPoint.getPositionWorldOnB();
                this.droneBodyHit(rb0, rb1, time, wp);
              }
            }else if(rb1.userData.name.startsWith('pale-')){
              this.paleHit(rb0, rb1, time);
            }
          }else if ((rb0.userData.name.startsWith('car'))  && (rb1.userData.name.startsWith('pale-')) && ((rb1.userData.obj.constraint == null)&&( rb1.userData.obj.physicsBody !== null))) {
            this.takeFallenPale(rb0, rb1);
          }
        }

        if(rb0.userData.drone){
          if(rb1.userData.name.startsWith('shot-car')){
            if(rb0.userData.name.startsWith('drone-')){
              if(numContacts>0){
                let contactPoint = contactManifold.getContactPoint(0);
                const wp = contactPoint.getPositionWorldOnA();
                this.droneBodyHit(rb1, rb0, time, wp);
              }
            }else if(rb0.userData.name.startsWith('pale-')){
              this.paleHit(rb1, rb0, time);
            }
          }else if ((rb1.userData.name.startsWith('car'))  && (rb0.userData.name.startsWith('pale-')) && ((rb0.userData.obj.constraint == null)&&( rb0.userData.obj.physicsBody !== null))) {
            this.takeFallenPale(rb1, rb0);
          }

        }

        if(rb1.userData.spider){
          const spider = rb1.userData.spider;

          if (rb1.userData.name.startsWith('root')){
            if (rb0.userData.name.startsWith('shot-car'))
              this.bodyHit(rb0, rb1, time);
          }
    
          if ((rb1.userData.name.startsWith('leg'))||(rb1.userData.name.startsWith('fleg'))||(rb1.userData.name.startsWith('head'))) {

            const legBone = rb1.userData.obj;

            //console.log('hit leg '+rb0.userData.name);
            
            if (((legBone.constraint == null)&&( legBone.physicsBody !== null)) && (rb0.userData.name.startsWith('car')) ) {
              this.takeFallenLeg(rb0, rb1, time);
            }else if (rb0.userData.name.startsWith('shot-car'))
              this.legHit(rb0, rb1, time);
            else if(numContacts>0) {
              if(rb1.userData.name.startsWith('fleg')){
                const filters = ['fleg', 'leg','body', 'root'];
                if(!this.nameStartWithinArray(filters, rb0.userData.name)){
                  spider.inContact = true;
                  legBone.inContact = true;

                  /*
                  if((spider.id == 2)&&(legBone.constraint !== null))
                    console.log('contact '+spider.id+' '+rb1.userData.name+' '+rb0.userData.name)
                  */
                }
              }
            }
          }
        }

        if(rb0.userData.spider){
          const spider = rb0.userData.spider;

          if (rb0.userData.name.startsWith('root')){
            if (rb1.userData.name.startsWith('shot-car'))
              this.bodyHit(rb1, rb0, time);
          }
  
          if ((rb0.userData.name.startsWith('leg'))||(rb0.userData.name.startsWith('fleg'))) {
            const legBone = rb0.userData.obj;

            //console.log('hit leg '+rb1.userData.name);

            if(((legBone.constraint == null)&&( legBone.physicsBody !== null))&& (rb1.userData.name.startsWith('car'))) {
              this.takeFallenLeg(rb1, rb0, time);
            } else if (rb1.userData.name.startsWith('shot-car')){
              this.legHit(rb1, rb0, time);
            } else if(numContacts>0) {
              if(rb0.userData.name.startsWith('fleg')){
                const filters = ['fleg', 'leg','body', 'root']
                if(!this.nameStartWithinArray(filters, rb1.userData.name)){
                  spider.inContact = true;
                  legBone.inContact = true;

                  /*
                  if((spider.id == 2)&&(legBone.constraint !== null))
                    console.log('contact '+spider.id+' '+rb0.userData.name+' '+rb1.userData.name)
                  */
                }
              }
            }
          }
        }

    
  
        if (hasBonus) {
          this.takeBonus(carid, bonusid);
        }
  
        if (hasCol) {
          for (let j = 0; j < numContacts; j++) {
  
            let contactPoint = contactManifold.getContactPoint(j);
            let distance = contactPoint.getDistance();
  
            const wp = contactPoint.getPositionWorldOnA();
  
            this.addCarDmg(carid, (Math.abs(distance) + dmg) * 20 * dt, wp);  
          }
        }
      }

    }
    getNetCar(id) {
      for (let n = 0; n < this.cars.length; n++) {
        if (this.cars[n].netID === id)
          return this.cars[n];
      }
      return null;
    }
  
    getCar(id) {
      for (let n = 0; n < this.cars.length; n++) {
        if (this.cars[n].id === id)
          return this.cars[n];
      }
      return null;
    }
  
    
    getChar(id) {
      for (let n = 0; n < this.characters.length; n++) {
        if (this.characters[n].netID === id)
          return this.characters[n];
      }
      return null;
    }
  
      
    getNPC(id) {
      for (let n = 0; n < this.npcs.length; n++) {
        if(!this.npcs[n])
          continue;
        if (this.npcs[n].netID === id)
          return this.npcs[n];
      }
      return null;
    }
  
    getCarDmg(carid) {
      for (let n = 0; n < this.cars.length; n++) {
        if (this.cars[n].id === carid) {
          return this.cars[n].damage;
        }
      }
  
      return null;
    }
   
    addCarDmg(carid, damage, p) {
      for (let n = 0; n < this.cars.length; n++) {
        if (this.cars[n].netID === carid) {
          
          this.cars[n].damage += damage;
          this.cars[n].lapDmg += damage;
          
          this.cars[n].contacts.push({ pos: p, hit: damage });
          return true;
        }
      }
  
      return false;
    }
  
    setCarTrans(carid, pos, quat, speed) {
      for (let n = 0; n < this.cars.length; n++) {
        if (this.cars[n].id === carid) {
          this.cars[n].speed = speed;
  
          this.cars[n].pos.x = pos.x();
          this.cars[n].pos.y = pos.y();
          this.cars[n].pos.z = pos.z();
  
          this.cars[n].quat.x = quat.x();
          this.cars[n].quat.y = quat.y();
          this.cars[n].quat.z = quat.z();
          this.cars[n].quat.w = quat.w();
  
          //console.log(this.cars[n].pos);
  
          return true;
        }
      }
      return false;
    }
  
    setCarWheel(carid, which, pos, quat) {
      for (let n = 0; n < this.cars.length; n++) {
        if (this.cars[n].id === carid) {
  
          this.cars[n].wheels[which].pos.x = pos.x();
          this.cars[n].wheels[which].pos.y = pos.y();
          this.cars[n].wheels[which].pos.z = pos.z();
  
          this.cars[n].wheels[which].quat.x = quat.x();
          this.cars[n].wheels[which].quat.y = quat.y();
          this.cars[n].wheels[which].quat.z = quat.z();
          this.cars[n].wheels[which].quat.w = quat.w();
  
          return true;
        }
      }
      return false;
    }
  
  
  
    clearPhyTrack(track) {
      let car;

      while((car = this.cars.pop())) {
        this.removeCar(car);
        car.vehicle = null;
        car.physicsBody = null;
        car.ammoData = null;
      }
  
      if(track.groundObj){
        this.physicsUniverse.removeRigidBody(track.groundObj.physicsBody);
  
        Ammo.destroy(track.groundObj.ammoData.localInertia);
        Ammo.destroy(track.groundObj.ammoData.motionState);
        Ammo.destroy(track.groundObj.ammoData.transform);
        Ammo.destroy(track.groundObj.physicsBody);
  
        Ammo.destroy(track.groundObj.shape);
        Ammo.destroy(track.groundObj.mesh);
  
        track.groundObj = null;
      }
  
      if(this.wallObjPhy2 !== null) {
        this.physicsUniverse.removeRigidBody(this.wallObjPhy2.physicsBody);
  
        Ammo.destroy(this.wallObjPhy2.ammoData.localInertia);
        Ammo.destroy(this.wallObjPhy2.ammoData.motionState);
        Ammo.destroy(this.wallObjPhy2.ammoData.transform);
        Ammo.destroy(this.wallObjPhy2.physicsBody);
  
        Ammo.destroy(this.wallObjPhy2.shape);
        Ammo.destroy(this.wallObjPhy2.mesh);
  
        this.wallObjPhy2 = null;
      }
  
      if (this.roadObjPhy !== null) {
        this.physicsUniverse.removeRigidBody(this.roadObjPhy.physicsBody);
  
        Ammo.destroy(this.roadObjPhy.ammoData.localInertia);
        Ammo.destroy(this.roadObjPhy.ammoData.motionState);
        Ammo.destroy(this.roadObjPhy.ammoData.transform);
        Ammo.destroy(this.roadObjPhy.physicsBody);
  
        Ammo.destroy(this.roadObjPhy.shape);
        Ammo.destroy(this.roadObjPhy.mesh);
  
        this.roadObjPhy = null;
      }
      /*
      if (track.wallObj !== null) {
        this.physicsUniverse.removeRigidBody(track.wallObj.physicsBody);
  
        Ammo.destroy(track.wallObj.ammoData.localInertia);
        Ammo.destroy(track.wallObj.ammoData.motionState);
        Ammo.destroy(track.wallObj.ammoData.transform);
        Ammo.destroy(track.wallObj.shape);
        Ammo.destroy(track.wallObj.mesh);
  
        Ammo.destroy(track.wallObj.physicsBody);
  
        track.wallObj = null;
      }
      */
  
      /*
      for (let n = 0; n < track.walls.length; n++) {
        this.physicsUniverse.removeRigidBody(track.walls[n].physicsBody);
  
        Ammo.destroy(track.walls[n].ammoData.localInertia);
        Ammo.destroy(track.walls[n].ammoData.motionState);
        Ammo.destroy(track.walls[n].ammoData.transform);
        Ammo.destroy(track.walls[n].ammoData.geometry);
        Ammo.destroy(track.walls[n].physicsBody);
      }
      */
  
  
      if (track.woods) {
        for (let n = 0; n < track.woods.length; n++) {
          for (let i = 0; i < track.woods[n].woods.length; i++) {
            this.physicsUniverse.removeRigidBody(track.woods[n].woods[i].physicsBody);
  
            Ammo.destroy(track.woods[n].woods[i].ammoData.localInertia);
            Ammo.destroy(track.woods[n].woods[i].ammoData.motionState);
            Ammo.destroy(track.woods[n].woods[i].ammoData.transform);
            Ammo.destroy(track.woods[n].woods[i].physicsBody);
            Ammo.destroy(track.woods[n].woods[i].ammoData.geometry);
  
          }
        }
      }
  

      for (let n = 0; n < this.bonuses.length; n++) {
        this.physicsUniverse.removeRigidBody(this.bonuses[n].physicsBody);
        Ammo.destroy(this.bonuses[n].physicsBody);  
        Ammo.destroy(this.bonuses[n].ammoData.localInertia);
        Ammo.destroy(this.bonuses[n].ammoData.motionState);
        Ammo.destroy(this.bonuses[n].ammoData.transform);
        Ammo.destroy(this.bonuses[n].ammoData.geometry);
      }
  
      if (track.jumpers) {
        for (let n = 0; n < track.jumpers.length; n++) {

          this.physicsUniverse.removeRigidBody(track.jumpers[n].physicsBody);
  
          Ammo.destroy(track.jumpers[n].ammoData.localInertia);
          Ammo.destroy(track.jumpers[n].ammoData.motionState);
          Ammo.destroy(track.jumpers[n].ammoData.transform);
          Ammo.destroy(track.jumpers[n].physicsBody);
          Ammo.destroy(track.jumpers[n].ammoData.geometry);

          track.jumpers[n].ammoData = null;
          track.jumpers[n].physicsBody = null;

        }
      }

      if (this.mbgrids) {
        for (let n = 0; n < this.mbgrids.length; n++) {

          for(let i=0;i<this.mbgrids[n].balls.length;i++){
            this.physicsUniverse.removeRigidBody(this.mbgrids[n].balls[i].ammoData.physicsBody);

            Ammo.destroy(this.mbgrids[n].balls[i].ammoData.localInertia);
            Ammo.destroy(this.mbgrids[n].balls[i].ammoData.motionState);
            Ammo.destroy(this.mbgrids[n].balls[i].ammoData.transform);
            Ammo.destroy(this.mbgrids[n].balls[i].ammoData.physicsBody);
            Ammo.destroy(this.mbgrids[n].balls[i].ammoData.geometry);
          }

          for(let i=0;i<6;i++){

            this.physicsUniverse.removeRigidBody(this.mbgrids[n].borders[i].ammoData.physicsBody);

            Ammo.destroy(this.mbgrids[n].borders[i].ammoData.localInertia);
            Ammo.destroy(this.mbgrids[n].borders[i].ammoData.motionState);
            Ammo.destroy(this.mbgrids[n].borders[i].ammoData.transform);
            Ammo.destroy(this.mbgrids[n].borders[i].ammoData.physicsBody);
            Ammo.destroy(this.mbgrids[n].borders[i].ammoData.geometry);
          }
          
  
          if(this.mbgrids[n].hasBall){
            this.physicsUniverse.removeRigidBody(this.mbgrids[n].fallBallBody);
            Ammo.destroy(this.mbgrids[n].fallBallBody);
          }
        }
      }
  
      if(this.ropes){
        for (let n = 0; n < this.ropes.length; n++) {
  
          const anchor = this.ropes[n].anchor;
          const obj = this.ropes[n].obj;
  
          this.physicsUniverse.removeConstraint(this.ropes[n].const);
          this.physicsUniverse.removeRigidBody(anchor.physicsBody);
          this.physicsUniverse.removeRigidBody(obj.physicsBody);
  
          Ammo.destroy(anchor.ammoData.localInertia);
          Ammo.destroy(anchor.ammoData.motionState);
          Ammo.destroy(anchor.ammoData.transform);
          Ammo.destroy(anchor.physicsBody);
          Ammo.destroy(anchor.ammoData.geometry);
          
          Ammo.destroy(obj.ammoData.localInertia);
          Ammo.destroy(obj.ammoData.motionState);
          Ammo.destroy(obj.ammoData.transform);
          Ammo.destroy(obj.physicsBody);
          Ammo.destroy(obj.ammoData.geometry);
  
          Ammo.destroy(this.ropes[n].const);
        }
      }
  
      if(this.springs){
        for (let n = 0; n < this.springs.length; n++) {
  
          const obj = this.springs[n].obj;
  
          for(let constr of this.springs[n].const ){
            this.physicsUniverse.removeConstraint(constr);
          }

          this.physicsUniverse.removeRigidBody(obj.physicsBody);
  
          Ammo.destroy(obj.ammoData.localInertia);
          Ammo.destroy(obj.ammoData.motionState);
          Ammo.destroy(obj.ammoData.transform);
          Ammo.destroy(obj.physicsBody);
          Ammo.destroy(obj.ammoData.geometry);
  
          for(let anchor of this.springs[n].anchors ){
            this.physicsUniverse.removeRigidBody(anchor.physicsBody);
            Ammo.destroy(anchor.ammoData.localInertia);
            Ammo.destroy(anchor.ammoData.motionState);
            Ammo.destroy(anchor.ammoData.transform);
            Ammo.destroy(anchor.physicsBody);
            Ammo.destroy(anchor.ammoData.geometry);
          }
          for(let constr of this.springs[n].const ){
            Ammo.destroy(constr);
          }
        }
      }
  
      if(track.interestPoints){
  
        for (let n = 0; n < track.interestPoints.length; n++) {
          
          this.physicsUniverse.removeRigidBody(track.interestPoints[n].physicsBody);
          this.physicsUniverse.removeRigidBody(track.interestPoints[n].LphysicsBody);
  
          if(track.interestPoints[n].taken){
            this.physicsUniverse.removeConstraint(track.interestPoints[n].constraint);
            Ammo.destroy(track.interestPoints[n].constraint);
            track.interestPoints[n].constraint = null;
          }
    
          Ammo.destroy(track.interestPoints[n].ammoData.localInertia);
          Ammo.destroy(track.interestPoints[n].ammoData.motionState);
          Ammo.destroy(track.interestPoints[n].ammoData.transform);
          Ammo.destroy(track.interestPoints[n].physicsBody);
          Ammo.destroy(track.interestPoints[n].ammoData.geometry);
  
          Ammo.destroy(track.interestPoints[n].attachedObject.ammoData.localInertia);
          Ammo.destroy(track.interestPoints[n].attachedObject.ammoData.motionState);
          Ammo.destroy(track.interestPoints[n].attachedObject.ammoData.transform);
          Ammo.destroy(track.interestPoints[n].LphysicsBody);
          Ammo.destroy(track.interestPoints[n].attachedObject.ammoData.geometry);
          
        }
      }
  
      
      for (let n = 0; n < this.turrets.length; n++) {

        const shots = this.turrets[n].shots;

        for (let i = 0; i < shots.length; i++) {
          
          this.physicsUniverse.removeRigidBody(shots[i].physicsBody);

          try{
            Ammo.destroy(shots[i].ammoData.localInertia);
            Ammo.destroy(shots[i].ammoData.motionState);
            Ammo.destroy(shots[i].ammoData.transform);
            Ammo.destroy(shots[i].physicsBody);
            Ammo.destroy(shots[i].ammoData.shape);
          }
          catch(e) {
            console.log('cannot destroy shot '+e.message);
          }
        }

        this.turrets[n].shots = [];
  
        this.physicsUniverse.removeRigidBody(this.turrets[n].physicsBody);
        Ammo.destroy(this.turrets[n].ammoData.localInertia);
        Ammo.destroy(this.turrets[n].ammoData.motionState);
        Ammo.destroy(this.turrets[n].ammoData.transform);
        Ammo.destroy(this.turrets[n].physicsBody);
        Ammo.destroy(this.turrets[n].ammoData.geometry);
      }
      
  
      if (track.drones) {
        
        for (let n = 0; n < track.drones.length; n++) {

          const phydrone = this.drones[n];
          const shots = this.drones[n].shots;
  
          for (let i = 0; i < shots.length; i++) {
            
            this.physicsUniverse.removeRigidBody(shots[i].physicsBody);
  
            try{
              Ammo.destroy(shots[i].ammoData.localInertia);
              Ammo.destroy(shots[i].ammoData.motionState);
              Ammo.destroy(shots[i].ammoData.transform);
              Ammo.destroy(shots[i].physicsBody);
              Ammo.destroy(shots[i].ammoData.shape);
            }
            catch(e) {
              console.log('cannot destroy shot '+e.message);
            }
          }
  
          track.drones[n].shots = [];

          for(let pale of phydrone.pales ){

            if(pale.constraint != null){
              this.physicsUniverse.removeConstraint(pale.constraint);
              Ammo.destroy(pale.constraint);
              pale.constraint = null;
            }

            if(pale.physicsBody != null){
              this.physicsUniverse.removeRigidBody(pale.physicsBody);
              Ammo.destroy(pale.physicsBody);
              pale.physicsBody = null;
            }
          }
          phydrone.pales = [];
    
          this.physicsUniverse.removeRigidBody(track.drones[n].physicsBody);
          Ammo.destroy(track.drones[n].ammoData.localInertia);
          Ammo.destroy(track.drones[n].ammoData.motionState);
          Ammo.destroy(track.drones[n].ammoData.transform);
          Ammo.destroy(track.drones[n].physicsBody);
          Ammo.destroy(track.drones[n].ammoData.geometry);
        }
      }

      if (track.spiders) {

        for (let n = 0; n < track.spiders.length; n++) {

          const shots = track.spiders[n].pshots;
  
          for (let i = 0; i < shots.length; i++) {
            
            this.physicsUniverse.removeRigidBody(shots[i].physicsBody);

            try{
              Ammo.destroy(shots[i].ammoData.localInertia);
              Ammo.destroy(shots[i].ammoData.motionState);
              Ammo.destroy(shots[i].ammoData.transform);
              Ammo.destroy(shots[i].physicsBody);
              Ammo.destroy(shots[i].ammoData.shape);
            }
            catch(e) {
              console.log('cannot destroy shot '+e.message);
            }
          }

          track.spiders[n].pshots = [];
          this.removePhySpider(track.spiders[n].skel);
        }
      }
      
      for (let n = 0; n < this.xmeshes.length; n++) {
          this.destroyPhyXMesh(this.xmeshes[n]);
      }
        
      if (track.cubes) {
        for (let n = 0; n < track.cubes.length; n++) {

          if(track.cubes[n].ammoData){
            if(track.cubes[n].ammoData.springPhy){
              this.physicsUniverse.removeConstraint(track.cubes[n].ammoData.springPhy)
              Ammo.destroy(track.cubes[n].ammoData.springPhy);
              track.cubes[n].ammoData.springPhy = null;
            }
          }
              
    
          this.physicsUniverse.removeRigidBody(track.cubes[n].physicsBody);

          if(track.cubes[n].ammoData){
            Ammo.destroy(track.cubes[n].ammoData.localInertia);
            Ammo.destroy(track.cubes[n].ammoData.motionState);
            Ammo.destroy(track.cubes[n].ammoData.transform);
            Ammo.destroy(track.cubes[n].physicsBody);
            Ammo.destroy(track.cubes[n].ammoData.shape);
  
            track.cubes[n].physicsBody= null;
            track.cubes[n].ammoData = null;
          }


          if(track.cubes[n].anchor && track.cubes[n].anchor.physicsBody){
            this.physicsUniverse.removeRigidBody(track.cubes[n].anchor.physicsBody);
            Ammo.destroy(track.cubes[n].anchor.physicsBody);
            track.cubes[n].anchor.physicsBody = null;

            if(track.cubes[n].anchor.ammoData){
              Ammo.destroy(track.cubes[n].anchor.ammoData.localInertia);
              Ammo.destroy(track.cubes[n].anchor.ammoData.motionState);
              Ammo.destroy(track.cubes[n].anchor.ammoData.transform);
              Ammo.destroy(track.cubes[n].anchor.ammoData.shape);
            }
            track.cubes[n].anchor = null;
          }
        }
      }

      if (track.cyls) {
        for (let n = 0; n < track.cyls.length; n++) {

          if(track.cyls[n].ammoData){
            if(track.cyls[n].ammoData.springPhy){
              this.physicsUniverse.removeConstraint(track.cyls[n].ammoData.springPhy)
              Ammo.destroy(track.cyls[n].ammoData.springPhy);
              track.cyls[n].ammoData.springPhy = null;
            }
          }
              
          if(track.cyls[n].physicsBody){
            this.physicsUniverse.removeRigidBody(track.cyls[n].physicsBody);
            Ammo.destroy(track.cyls[n].physicsBody);
            track.cyls[n].physicsBody = null;
          }

          if(track.cyls[n].ammoData){
            Ammo.destroy(track.cyls[n].ammoData.localInertia);
            Ammo.destroy(track.cyls[n].ammoData.motionState);
            Ammo.destroy(track.cyls[n].ammoData.transform);
            Ammo.destroy(track.cyls[n].ammoData.shape);
            track.cyls[n].ammoData = null;
          }


          if(track.cyls[n].anchor && track.cyls[n].anchor.physicsBody){
            this.physicsUniverse.removeRigidBody(track.cyls[n].anchor.physicsBody);
            Ammo.destroy(track.cyls[n].anchor.physicsBody);
            track.cyls[n].anchor.physicsBody = null;

            if(track.cyls[n].anchor.ammoData){
              Ammo.destroy(track.cyls[n].anchor.ammoData.localInertia);
              Ammo.destroy(track.cyls[n].anchor.ammoData.motionState);
              Ammo.destroy(track.cyls[n].anchor.ammoData.transform);
              Ammo.destroy(track.cyls[n].anchor.ammoData.shape);
            }
            track.cyls[n].anchor = null;
          }
        }
      }

      if(track.colcubes){
        for (let n = 0; n < track.colcubes.length; n++) {

          this.physicsUniverse.removeRigidBody(track.colcubes[n].physicsBody);

          if(track.colcubes[n].ammoData){
            Ammo.destroy(track.colcubes[n].ammoData.localInertia);
            Ammo.destroy(track.colcubes[n].ammoData.motionState);
            Ammo.destroy(track.colcubes[n].ammoData.transform);
            Ammo.destroy(track.colcubes[n].physicsBody);
            Ammo.destroy(track.colcubes[n].ammoData.shape);
  
            track.colcubes[n].physicsBody= null;
            track.colcubes[n].ammoData = null;
          }
        }
      }

      if(track.colcyls){
        for (let n = 0; n < track.colcyls.length; n++) {

          this.physicsUniverse.removeRigidBody(track.colcyls[n].physicsBody);

          if(track.colcyls[n].ammoData){
            Ammo.destroy(track.colcyls[n].ammoData.localInertia);
            Ammo.destroy(track.colcyls[n].ammoData.motionState);
            Ammo.destroy(track.colcyls[n].ammoData.transform);
            Ammo.destroy(track.colcyls[n].physicsBody);
            Ammo.destroy(track.colcyls[n].ammoData.shape);
          }
          track.colcyls[n].physicsBody= null;
          track.colcyls[n].ammoData = null;
        }
      }
      
      if (this.npcs) {
        for (let n = 0; n < this.npcs.length; n++) {

          if(!this.npcs[n])
            continue;
    
          if(this.npcs[n].ragdoll){
            this.removePhyRagdoll(this.npcs[n].skeleton.root);
          }else{

            this.physicsUniverse.removeAction(this.npcs[n].ammoData.char);
            this.physicsUniverse.removeCollisionObject(this.npcs[n].physicsBody);

            if(this.npcs[n].physicsBody){
              Ammo.destroy(this.npcs[n].physicsBody);

              if(this.npcs[n].physicsBody.userData.obj){
                Ammo.destroy(this.npcs[n].physicsBody.userData.obj.p);
                Ammo.destroy(this.npcs[n].physicsBody.userData.obj.q);
              }
              this.npcs[n].physicsBody = null;
            }

            if(this.npcs[n].ammoData){
              Ammo.destroy(this.npcs[n].ammoData.transform);
              Ammo.destroy(this.npcs[n].ammoData.shape);
              //Ammo.destroy(this.npcs[n].ammoData.char);
              this.npcs[n].ammoData = null;
            }
          }
        }
      }

      if (this.characters) {
        for (let n = 0; n < this.characters.length; n++) {
    
          this.physicsUniverse.removeAction(this.characters[n].ammoData.char);
          this.physicsUniverse.removeCollisionObject(this.characters[n].physicsBody);
          
          
          Ammo.destroy(this.characters[n].physicsBody);
          Ammo.destroy(this.characters[n].ammoData.transform);
          Ammo.destroy(this.characters[n].ammoData.shape);
          //Ammo.destroy(this.characters[n].ammoData.char);
        }
      }
  
      this.rigidBody_List = [];
      this.syncList = [];
      this.character_List = [];
      this.ragdoll_List = [];
      this.walls = [];
      this.woods = [];
      this.bonuses = [];
      this.jumpers = [];
      this.turrets = [];
      this.springs = [];
      this.ropes = [];
      this.interestPoints = [];
      this.drones = [];
      this.npcs = [];
      this.characters = [];
      this.mbgrids = [];
      this.xmeshes = [];
      this.splits = [];
      
    }
  
  
    removeCar(Car ) {
      if(Car == null)
        return;

      this.removeAttachedObjects(Car);
      this.physicsUniverse.removeRigidBody(Car.physicsBody);
  
      if(Car.vehicle) {
        this.physicsUniverse.removeAction(Car.vehicle);
        Ammo.destroy(Car.vehicle);
        Car.vehicle = null;
      }
      
      if(Car.ammoData) {
        Ammo.destroy(Car.ammoData.localInertia);
        Ammo.destroy(Car.ammoData.motionState);
        Ammo.destroy(Car.ammoData.transform);
        Ammo.destroy(Car.ammoData.gtransform);
        Ammo.destroy(Car.ammoData.gtransform2);
        Ammo.destroy(Car.ammoData.gtransform3);
  
        Ammo.destroy(Car.ammoData.geometry[0]); 
        Ammo.destroy(Car.ammoData.geometry[1]);
        Ammo.destroy(Car.ammoData.shape);
        Ammo.destroy(Car.physicsBody);
        //Ammo.destroy(Car.ammoData.tuning);
        Ammo.destroy(Car.ammoData.rayCaster);

        Car.ammoData = null;
      }
      
      for(let n=0;n<this.syncList.length;n++) {
  
        if(this.syncList[n].id == Car.syncId) {
          this.syncList.splice(n,1);
          break;
        }
      }
  
      for(let n=0;n<Car.wheels.length;n++) {
        Ammo.destroy(Car.wheels[n].pos);
        Ammo.destroy(Car.wheels[n].quat);
      }
  
     
    }

    removeAttachedObjects(car){

        for(let ip of car.attachedObjects) {
            this.physicsUniverse.removeConstraint(ip.constraint);
            Ammo.destroy(ip.constraint);
            ip.constraint = null;
            ip.taken = 0;
      
            const ap = new Ammo.btVector3(ip.lpos.x, ip.lpos.y, ip.lpos.z);
            const aq = new Ammo.btQuaternion(ip.lrot.x, ip.lrot.y, ip.lrot.z, ip.lrot.w);
            
            ip.LphysicsBody.setLinearVelocity(this.zVec);
            ip.LphysicsBody.setAngularVelocity(this.zVec);
            ip.LphysicsBody.clearForces();
      
            this.tmpTransformation.setIdentity();
            this.tmpTransformation.setOrigin(ap);
            this.tmpTransformation.setRotation(aq);
      
            ip.LphysicsBody.setWorldTransform(this.tmpTransformation);
            const motionState = ip.LphysicsBody.getMotionState();
            motionState.setWorldTransform(this.tmpTransformation);
      
            Ammo.destroy(ap);
            Ammo.destroy(aq);
          }
          car.attachedObjects = [];
    }

    
    lapReset(car) {
      
      for(let drone of this.drones) {
        if((drone.active) && (drone.currentTarget != null) && (drone.currentTarget.id == car.id)) {
          drone.goHome = true;
          drone.currentTarget = null;
        }
      }

      if(this.reccord){

        console.log(JSON.stringify(this.reccord.samples));

        this.lastReccord = 0;
        this.reccord.startTime = 0;
        this.reccord.samples = [];
      }
        

      this.removeAttachedObjects(car);
  
      for (let bonus of this.bonuses) {
        bonus.taken = 0;
      }
    }
  
  
    getLapInfos(car, time, atts = true) {
  
        const dmg = parseInt(Math.floor(car.damage));
         
        let nBon = 0;
        for (let bonus of this.bonuses) {
          if(bonus.taken){
            if(bonus.type === 0)
              nBon++;
          }
        }
    
        if(atts){
          for(let ip of car.attachedObjects) {
              nBon+= ip.nBonuses;
        
              for(let b=0;b<ip.nBonuses;b++)
                car.bonuses.push(1);
            }
        }
    
        const lapTime =  ( car. nLaps  === 0 ) ? parseFloat(time) : parseFloat(time - car.lastLap);
        const remTime =  ( this.lapTime - lapTime) / lapTime;
        return { time : lapTime.toFixed(1), bonus : nBon, dmg : car.lapDmg, usedNitros : car.lapNitros, remTime: remTime, lapTime : lapTime };
      }

      getStats(cars, startTime){

        const time = this.clock.getElapsedTime() - (startTime + 3);
        const stats = [];

        for(let n = 0;n<cars.length; n++){

            const car = cars[n];
            const physcar = this.cars[n];

            car.active = false;

            const lapInfo = this.getLapInfos(physcar, time, false);
            physcar.laps.push(lapInfo);
            stats.push({    laps : physcar.laps, 
                            nlaps : physcar.nLaps, 
                            carid : n + 1,
                            totalD : car.totalDist,
                            color : car.color,
                            multiplier : parseFloat(physcar.multiplier).toFixed(1)});
        }

        stats.sort(function(a, b){

            if(a.totalD< b.totalD)
                return 1;

            if(a.totalD> b.totalD)
                return -1;

            return 0;
        });

        return stats
    }
  
    updateCarSegs(track, time) {

      if(track.trackPoints.length<2)
        return;

      for (let car of this.cars) {
        const cs = car.curSeg;
        car.curSeg = -1;

        const curSeg = this.findClosestSeg(track, car.pos, null, null);

        if (curSeg == 0) {
  
          if (cs >= (track.trackPoints.length - 1)) {
          
            const lapInfo = this.getLapInfos(car, time, true);

            car.laps.push(lapInfo);

            if((car.lapInfos === null) || ( lapInfo.lapTime < car.lapInfos.time)) {
              car.lapInfos = lapInfo;
            }
            
            car.lapDmg = 0;
            car.lapNitros = 0;
            car.multiplier *= (1.2 + lapInfo.remTime);
            
            this.lapReset(car);

            car.nLaps++;
            car.lastLap = time;

          }
          car.curSeg = 0;
        }
        else if (curSeg >= cs) {

          if(cs == 0) {
            if(curSeg == 1)
              car.curSeg = 1;
            else
              car.curSeg = 0;
          } else 
            car.curSeg = curSeg;  

        } else
          car.curSeg = cs;
      }


        /*
        for (let seg of this.trackSegments) {
          this.tmpPos.x = car.pos.x;
          this.tmpPos.y = car.pos.y;
          this.tmpPos.z = car.pos.z;
  
          const mv = this.tmpPos.sub(seg.center);
  
          if (Math.abs(mv.x) > seg.halfSize.x * 1.1)
            continue;
  
          if (Math.abs(mv.y) > seg.halfSize.y * 1.1)
            continue;
  
          if (Math.abs(mv.z) > seg.halfSize.z * 1.1)
            continue;
  
  
          if (seg.id == 0) {
  
            if (cs >= (this.trackSegments.length - 3)) {
            
              const lapInfo = this.getLapInfos(car, time, true);
  
              car.laps.push(lapInfo);
  
              if((car.lapInfos === null) || ( lapInfo.lapTime < car.lapInfos.time)) {
                car.lapInfos = lapInfo;
              }
              
              car.lapDmg = 0;
              car.lapNitros = 0;
              car.multiplier *= (1.2 + lapInfo.remTime);
              
              this.lapReset(car);
  
              car.nLaps++;
              car.lastLap = time;
  
            }
            car.curSeg = 0;
          }
          else if (seg.id >= cs) {
  
            if(cs == 0) {
              if(seg.id == 1)
                car.curSeg = 1;
              else
                car.curSeg = 0;
            } else 
              car.curSeg = seg.id;  

          } else
            car.curSeg = cs;

          return seg;
        }
      }
      */
  
      return null;
    }

    removeAnim(pchar, id){

      let n=0;
      while(n<pchar.anims.length) {
        if(pchar.anims[n].a === id)
          pchar.anims.splice(n, 1);
        else
          n++
      }      
    }

    
    findPath(pathFinder, startPos, forwardVec, targetNode, nodeType = 1){

      pathFinder.reset();
      let minDist = Infinity;
      let startNode = NaN;
      let found = false;
      let myTargetNode = NaN;
      let myss = null;


      if((targetNode & 0x00FF0000) != 0){
          const tagetType = (targetNode & 0x00FF0000) >> 16;
          if(tagetType == 1){
            const npcId = (targetNode & 0x0000FF00) >> 8;
            const targetId = (targetNode & 0x000000FF);

            const npc =this.npcs[npcId];
            
            for(let ss=0;ss<npc.subsystems.length;ss++){
              if(npc.subsystems[ss].target.id === targetId){
                myss=npc.subsystems[ss];
                break;
              }
            }

            if(!myss)
              return null;

            for(let i=0;i<this.navmeshs.length;i++){
              const dist = this.navmeshs[i].pos.distanceTo(myss.pos);

              if(dist<minDist){
                  minDist = dist;
                  myTargetNode = i;
              }
            }
          }
        }else{
          myTargetNode = targetNode;
        }

        if(isNaN(myTargetNode))
          return null;
       
        for(let i =0;i<this.navmeshs.length;i++){

            if(!(this.navmeshs[i].type  & nodeType))
              continue;

            this.tmpV3.subVectors(this.navmeshs[i].pos, startPos);
            const dot = forwardVec!= null ? forwardVec.dot(this.tmpV3) : 1.0;
            
            const d = this.tmpV3.length();
            if((dot >0)&&(d < minDist)){
                minDist = d;
                startNode = i;
            }
        }

        pathFinder.distance[startNode] = minDist;

        for(let i =0;i<this.navmeshs.length;i++){

            if(!(this.navmeshs[i].type  & nodeType))
              continue;

            let minDist = Infinity;
            let closest = -1;

            for(let l =0;l<this.navmeshs.length;l++){

                if(!(this.navmeshs[l].type  & nodeType))
                  continue;

                if((!pathFinder.visited[l])&&((pathFinder.distance[l]<minDist))){
                    minDist = pathFinder.distance[l];
                    closest = l;
                }
            }

            if(closest == myTargetNode){
                found = true;
                break;
            }

            if(closest<0){
                break;
            }
                

            pathFinder.visited[closest] = 1;

            for(let ll =0;ll<this.navmeshs[closest].links.length;ll++){

                const l = this.navmeshs[closest].links[ll];

                if(!(this.navmeshs[l].type  & nodeType))
                  continue;                

                if(pathFinder.visited[l])
                    continue;

                const alt = pathFinder.distance[closest] + this.navmeshs[closest].pos.distanceTo(this.navmeshs[l].pos);
  
                if(alt < pathFinder.distance[l]){
                    pathFinder.distance[l] = alt;
                    pathFinder.predecessors[l] = closest;
                }
            }
        }

        if(!found)
            return null;

        let path = [];
        let current = myTargetNode;
        while(pathFinder.predecessors[current] != undefined){

            const node = pathFinder.predecessors[current];
            path.unshift(node);
            current = node;
        }

        path.unshift(startNode);
        path.push(myTargetNode);

        return path;

    }

    applyRagDollAnimation(char, animation, t) {

        if (!animation)
            return;

        for (let n = 0; n < animation.bones.length; n++) {

            const bone = this.findSkelBone(char.skeleton, animation.bones[n].name);
            if (!bone) 
              continue;

            if(!bone.constraint)
              continue;
            const q = animation.bones[n].getValue(t);

            

            bone.trot.setValue(-q.x,-q.y, -q.z, q.w);
            bone.tpos.setValue(0, 0, 0);

            //const originB = new Ammo.btVector3(0, 0, 0);
            //const quat = new Ammo.btQuaternion(-q.x,-q.y, -q.z, q.w);

            const frameInB = bone.constraint.getFrameOffsetB();
            frameInB.setIdentity();
            frameInB.setOrigin(bone.tpos);
            frameInB.setRotation( bone.trot);

            //Ammo.destroy(quat);
            //Ammo.destroy(originB);
            //Ammo.destroy(frameInB);

            /*
            const originA = new Ammo.btVector3(0, bone.parent.length ? bone.parent.length * size: 0 , 0);
            const frameInA = new Ammo.btTransform();
            frameInA.setIdentity(); 
            frameInA.setOrigin(originA);
            //frameInA.setRotation(quat);

            const frameInB = new Ammo.btTransform();
            frameInB.setIdentity();
            frameInB.setOrigin(originB);
            frameInB.setRotation(quat);
            */

            //bone.constraint.setFrames (frameInA, frameInB);
        }
     }

  
    executeScript(script, scriptInstance, char, pchar, pos, time, context = null){

      let ret = false;
  
      if(scriptInstance.state === 'dialog'){
        return ret;
      }
        

      let n=0;
      while(n<pchar.anims.length) {
        if( (pchar.anims[n].e>0)&&(time > pchar.anims[n].e)){

          pchar.anims.splice(n, 1);
        }
        else
          n++
      }

      if((scriptInstance.state === 'wait')&&(time < scriptInstance.time))
        return true;
  
      if((((scriptInstance.state === 'move')/*&&((time -scriptInstance.lastStart)< 10)*/)||(scriptInstance.state === 'attack'))&&(pchar.targetPos!=null)) // &&((time -scriptInstance.lastStart)< 10)
        return true;
  
      const line = script[scriptInstance.line];

      if(line.length<1){
          scriptInstance.line++;
      }

      if(line.startsWith('resetscript')){
        scriptInstance.state = '';
        //char.script = char.script_o;
        scriptInstance.line = 0;
        return true;
      }

      if(line.startsWith('stop')){
        scriptInstance.line++;
        ret = true;
      }
      
      if(line.startsWith('unboard')){
        const passenger = context.car.passengers.pop();
        if(passenger.avatarDef.ragdoll){
          
            passenger.pos.x = context.car.pos.x + 6;
            passenger.pos.y = context.car.pos.y;
            passenger.pos.z = context.car.pos.z + 2;

            let idx = null;
            for(let ni=0;ni<this.npcs.length;ni++){
              if(this.npcs[ni].id == passenger.id){
                  idx = ni;
                  break;
              }
            }
            this.addPhyRagdoll(passenger, idx);
        }

        passenger.script = context.car.script;
        passenger.scriptInstance = context.car.scriptInstance;
        passenger.scriptInstance.state = '';
        passenger.scriptInstance.line++;
        
        context.car.script = null;
        context.car.scriptInstance = null;

        pchar.active = true;
        ret =true;
      }

      

      if(line.startsWith('remove')){

        if(char.avatarDef.ragdoll){
               
          this.removePhyRagdoll(char.skeleton.root);

          for(let ri = 0;ri<this.ragdoll_List.length;ri++){
            if(this.ragdoll_List[ri].netID == char.netID){
              this.ragdoll_List.splice(ri, 1);
              break;
            }
          }
        }
        scriptInstance.line++;
        pchar.active = false;
        ret = false;
      }

      if(line.startsWith('onboard')){

          if(context && context.car){

            context.car.passengers.push(char);

            if(char.avatarDef.ragdoll){

              pchar.active = false;
               
              this.removePhyRagdoll(char.skeleton.root);

              for(let ri = 0;ri<this.ragdoll_List.length;ri++){
                if(this.ragdoll_List[ri].netID == char.netID){
                  this.ragdoll_List.splice(ri, 1);
                  break;
                }
              }
            }
            scriptInstance.line++;
            
          }
          ret =false;
      }

       if(line.startsWith('retsettargetnode')){
          context.car.targetNode = -1;
          scriptInstance.state = '';
          scriptInstance.lastStart = time;
          scriptInstance.line++;
          ret =false;
      }
      
      if(line.startsWith('pay')){
        const arr=line.split(' ');
        const amount = parseInt(arr[1]);
          if(context && context.car){
            context.car.bonuses.push(amount);
            context.car.newbonuses.push(amount);
          }
        scriptInstance.line++;
        ret =false;
      }


      if(line.startsWith('setdialogline')){
        const arr=line.split(' ');
        pchar.currentDialogLine =  parseInt(arr[1]);
        scriptInstance.line++;
        ret =false;
      }

      if(line.startsWith('setnpctarget')){
          const arr=line.split(' ');

          const npcID = parseInt(arr[1]);
          const subName = arr[2];
          const npc_target = this.npcs[npcID].target;

          if(npc_target){

            const targetID = npc_target.id & 0xFF;
            for(let ssi = 0;ssi<npc_target.subsystems.length;ssi++){
              if(npc_target.subsystems[ssi].name === subName){
                const subtargetID = npc_target.subsystems[ssi].id & 0xFF;
                context.car.targetNode = (1<<16)|(targetID<<8)|(subtargetID);
                context.car.questNPC = char;
              }
            }
          }
          
          if(arr.length>3){
            const duration = parseInt(arr[3]);
            scriptInstance.finishTime = time+duration;    
          }
          scriptInstance.line++;
          ret =true;
      }

      if(line.startsWith('settargetnode')){
          const arr=line.split(' ');
          context.car.targetNode = parseInt(arr[1]);
          if(arr.length>2){
            const duration = parseInt(arr[2]);
            scriptInstance.finishTime = time+duration;    
          }
          scriptInstance.line++;
          ret =true;
      }

      if(line.startsWith('setpath')){
        pchar.pathName = line.substr(8);
        pchar.path = this.pathes[pchar.pathName];
        pchar.pathPts = 0;//pchar.pathStartPts;
        scriptInstance.state = 'move';
        scriptInstance.lastStart = time;
        scriptInstance.line++;
        ret =false;
      }

      if(line.startsWith('gotorandomnode')){

        let path = null;
        let targetNode = 0;

        while(path === null){

          do {
            targetNode = Math.floor(Math.random() * (this.navmeshs.length - 1));
          }while((this.navmeshs[targetNode].type & char.nodeType) == 0)

          if(isNaN(char.ppos.x)){
            char.ppos.set(this.navmeshs[targetNode].pos.x, this.navmeshs[targetNode].pos.y, this.navmeshs[targetNode].pos.z);
          }

          path = this.findPath(char.pathFinder, char.ppos, null , targetNode, char.nodeType);
        }

        const npath = [];
        const posArray = [];
        for(let p of path){
          npath.push({pos : this.navmeshs[p].pos});
          posArray.push(this.navmeshs[p].pos);
        }

        char.pathCurve = new THREE.CatmullRomCurve3(posArray);

        pchar.pathName = 'navmesh-'+targetNode;
        pchar.path = npath;
        pchar.pathPts = 0;
        //pchar.pathPts = pchar.pathStartPts;
        pchar.anims = [{a : char.avatarDef.walkAnim, t: time}];
        scriptInstance.state = 'move';
        scriptInstance.lastStart = time;
        scriptInstance.line++;
        ret =true;
      }
      

      if(line.startsWith('idle')){
        pchar.anims = [{a : char.avatarDef.idleAnim, t: time}];
        pchar.animStart = time;

        const itime = parseInt(line.substr(5));
        scriptInstance.state = 'wait';
        scriptInstance.lastStart = time;
        scriptInstance.time = time + itime;
        scriptInstance.line++;
        ret =true;
      }

      if(line.startsWith('nextmove')){
                
        if(pchar.pathPts>=pchar.path.length){
          scriptInstance.line++;
        }else{

          scriptInstance.lastStart = time;

          if(!pchar.targetPos){
            pchar.targetPos= new THREE.Vector3();
          }
          pchar.targetPos.x = pchar.path[pchar.pathPts].pos.x;
          pchar.targetPos.y = pchar.path[pchar.pathPts].pos.y;
          pchar.targetPos.z = pchar.path[pchar.pathPts].pos.z;
          pchar.pathPts++;
        }

        ret =true;
      }
  
  
      if(line.startsWith('move')){
        const coord = line.substr(5);
        const ca = coord.split(',');
        pchar.targetPos = new THREE.Vector3(parseFloat(ca[0]), parseFloat(ca[1]), parseFloat(ca[2])).add(pos);
        pchar.anims = [{a : char.avatarDef.walkAnim, t: time}];
        scriptInstance.state = 'move';
        scriptInstance.lastStart = time;
        scriptInstance.line++;
        ret =true;
        //console.log("move char ",char.name, pchar.targetPos, pos);
      }
  
      if(scriptInstance.line>= script.length){
          scriptInstance.line=0;
          scriptInstance.nLoops++;
      }

      return ret;
        
    }

    
    findClosestCar(pos, minRadius = Infinity){

      if(this.cars.length<=0)
        return null;
      
      let minDist = minRadius * minRadius;
      let closestCar = null;

      for(let i=0;i<this.cars.length;i++){

        const car = this.cars[i];

        const dx = pos.x - car.pos.x;
        const dy = pos.y - car.pos.y;
        const dz = pos.z - car.pos.z;
        const l = dx*dx+dy*dy+dz*dz;
        if(l < minDist) {
           minDist = l;
           closestCar = car;
        }
      }

      return closestCar;
    }

    updateNPC(char, deltaTime, time){
      //let Physics_Obj = this.character_List[i].object;
      //let controller = this.character_List[i].char;
      //let Physics_Obj = controller.getGhostObject();
      const Physics_Obj = char.physicsBody;

      /*
      if(!Physics_Obj.userData)
        return;

      let char = Physics_Obj.userData.obj;
      */

      //const tform = Physics_Obj.getWorldTransform();

      //this.tmpTransformationNPC.setIdentity();
      //const ms = Physics_Obj.getMotionState();
      //ms.getWorldTransform(this.tmpTransformationNPC);
      const tmpTransformationNPC = Physics_Obj.getWorldTransform();

      
      let new_pos = tmpTransformationNPC.getOrigin();

      //const tpos = new THREE.Vector3(new_pos.x(), new_pos.y(), new_pos.z());
      
      let phychar = this.getChar(char.netID);

      if(phychar){

        phychar.tpos.set(new_pos.x(), new_pos.y(), new_pos.z());
      
                  
        const nover = phychar.physicsBody.getNumOverlappingObjects();
        //const donw = new Ammo.btVector3(0,-1,0);

        for(let no =0; no<nover;no++){
          const over = phychar.physicsBody.getOverlappingObject (no);
          const rb0 = Ammo.castObject(over, Ammo.btRigidBody);

          if(!rb0.userData)
            continue;
          if (rb0.userData.name.startsWith('trigger-')){
            phychar.hasTrigger = this.charTriggerEnter(phychar, rb0.userData.obj.type, rb0.userData.idx);
          }

          /*
          if(rb0.userData.name.startsWith('cube-bonus')){

            const fac = phychar.charScale < 1.0 ? 2.0 : phychar.charScale < 5.0? 1.5 : 1.2;

            this.setCharScale(char, phychar.charScale * fac);
            this.physicsUniverse.removeRigidBody(rb0);

            const cname = rb0.userData.name.substr(5);
            this.removeCubes.push(cname);
          }
         
          if(rb0.userData.name.startsWith('cube-building')){

            const v = phychar.dir.clone().multiplyScalar(phychar.charScale * 15.0);
            const puls = new Ammo.btVector3(v.x, v.y, v.z);
            const pos = new Ammo.btVector3(0, char.ppos.y + (char.avatarDef.height * phychar.charScale * 3) / 4, 0.0);
            rb0.applyImpulse(puls, pos); 
            Ammo.destroy(puls);

          }
          */

          if(rb0.userData.name.startsWith('cyl-')){

                const v = phychar.dir.clone().multiplyScalar(10.0);
                const puls = new Ammo.btVector3(v.x, v.y, v.z);
                const pos = new Ammo.btVector3(0, 1.5, 0.0);
                rb0.applyImpulse(puls, pos); 
                Ammo.destroy(puls);
                //Ammo.destroy(v);
            }
        }


        let ydiff = 0.0;

        if(char.actions['goto'].state > 0) {
          phychar.anims = [{a : char.avatarDef.walkAnim, t: time}];
          phychar.targetPos = new THREE.Vector3(char.actions['goto'].pos.x, char.actions['goto'].pos.y, char.actions['goto'].pos.z);
          char.targetObj = null;
          char.actions['goto'].state = 0;
        }
        if(char.actions['use'].state > 0) {
          phychar.anims = [{a : char.avatarDef.walkAnim, t: time}];
          phychar.targetPos = new THREE.Vector3(char.actions['goto'].pos.x, char.actions['goto'].pos.y, char.actions['goto'].pos.z);
          char.targetObj = char.actions['use'].t_obj;
          char.actions['use'].state = 0;
        }

        if(char.actions['forward'].state > 0) {
          const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(char.prot).multiplyScalar(char.avatarDef.speed * phychar.charScale);
          forward.y = 0;
          this.movePhyChar(char.id, forward, char.ppos);
        }

        if(char.actions['backward'].state > 0) {
          const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(char.prot).multiplyScalar(char.avatarDef.speed * phychar.charScale);
          forward.y = 0;
          this.movePhyChar(char.id, forward, char.ppos);
        }
        if(char.actions['left'].state > 0) {
          const forward = new THREE.Vector3(-1, 0, 0).applyQuaternion(char.prot).multiplyScalar(char.avatarDef.speed * phychar.charScale);
          forward.y = 0;
          this.movePhyChar(char.id, forward, char.ppos);
        }
        if(char.actions['right'].state > 0) {
          const forward = new THREE.Vector3(1, 0, 0).applyQuaternion(char.prot).multiplyScalar(char.avatarDef.speed * phychar.charScale);
          forward.y = 0;
          this.movePhyChar(char.id, forward, char.ppos);
        }

        if((char.actions['forward'].state == 0) &&
           (char.actions['backward'].state == 0) &&
           (char.actions['left'].state == 0) &&
           (char.actions['right'].state == 0)) {
      
            const forward = new THREE.Vector3(0, 0, 0);
            this.movePhyChar(char.id, forward, char.pos);
        }
        
      }else {
          phychar = this.getNPC(char.netID);
          phychar.tpos.set(new_pos.x(), new_pos.y(), new_pos.z());

          if(!char.isRemoved){
            if((char.actions['dialog'].state > 0) &&(char.scriptInstance.state !== 'dialog')){

              phychar.prevAnims = phychar.anims;
              char.scriptInstance.prevState = char.scriptInstance.state;
              char.scriptInstance.pauseTime = time;
              
              const v1 = new THREE.Vector3(char.pos.x, 0 , char.pos.z);
              const v2 = new THREE.Vector3(char.actions['dialog'].targetCharPos.x, 0 , char.actions['dialog'].targetCharPos.z);
              const delta = v2.sub(v1);
              const angle = Math.atan2(delta.x, delta.z);
  
              this.setPhyNPCRotation(phychar.id, angle);
      
              phychar.anims = [{a : char.avatarDef.talkAnim, t: time}];
  
              char.scriptInstance.state = 'dialog';

              //const forward = new THREE.Vector3(0, 0, 0);
              this.stopPhyNPC(char.id);
  
              //char.p.setValue(0,0,0);
              //controller.setWalkDirection( char.p );
            }
  
            if((char.actions['dialog'].state == 0) &&(char.scriptInstance !== null) && (char.scriptInstance.state == 'dialog')){
  
              //this.removeAnim(phychar, char.avatarDef.talkAnim);
  
              phychar.anims = phychar.prevAnims;
              phychar.prevAnims = null;
  
              const pdt = time - char.scriptInstance.pauseTime;
              char.scriptInstance.time += pdt;
              char.scriptInstance.pauseTime = 0;
              char.scriptInstance.state = char.scriptInstance.prevState;
            }
  
            if(char.script){
              this.executeScript(char.script, char.scriptInstance, char, phychar, phychar.tpos, time);
            }
          }



          if((char.scriptInstance === null)||(char.scriptInstance.state !== 'dialog')){

            if((char.scriptInstance.state === 'attack')&&(char.avatarDef.attackOffset !== null)){

              if(char.currentTarget.isStuck === true)
                return;

              char.currentTarget.ammoData.motionState.getWorldTransform(this.tmpTransformation2);
              const p = this.tmpTransformation2.getOrigin();
              const q = this.tmpTransformation2.getRotation();

              const r = q.w();
              const i = q.x();
              const j = q.y();
              const k = q.z();

              const x = char.avatarDef.attackOffset.x * char.skelScale;
              const y = char.avatarDef.attackOffset.y * char.skelScale;
              const z = char.avatarDef.attackOffset.z * char.skelScale;
            
              const dx = (2*(r*z*j + i*z*k - r*y*k + i*y*j) + x*(r*r + i*i - j*j - k*k));
              const dy = (2*(r*x*k + i*x*j - r*z*i + j*z*k) + y*(r*r - i*i + j*j - k*k));
              const dz = (2*(r*y*i - r*x*j + i*x*k + j*y*k) + z*(r*r - i*i - j*j + k*k));
              const tpl = Math.sqrt(dx * dx + dz * dz);

              if(phychar.targetPos && phychar.targetPos.distanceTo(phychar.tpos) > char.avatarDef.deactivation) {
                char.scriptInstance.state = 'move';
                char.scriptInstance.prevState = '';
                phychar.targetPos = char.start_pos.clone();
              }else{
                if(!phychar.targetPos)
                  phychar.targetPos = new THREE.Vector3();

                phychar.targetPos.x = dx+  p.x();
                phychar.targetPos.y = dy + p.y();
                phychar.targetPos.z = dz + p.z();
              }

              Ammo.destroy(q);
              Ammo.destroy(p);

              //Ammo.destroy(tform);
            }

            if((char.scriptInstance.state !=='wait')&&(phychar.targetPos)&&(!char.isRemoved)) {
    
              const delta = new THREE.Vector3().subVectors(phychar.targetPos,  phychar.tpos);

              const flatLength = Math.sqrt(delta.x * delta.x + delta.z * delta.z);
      
              if(flatLength>= char.avatarDef.speed * deltaTime) {
    
                const dir = delta.clone().normalize().multiplyScalar(char.avatarDef.speed);
                const angle = Math.atan2(dir.x , dir.z);
                const eul = new THREE.Euler(0, angle, 0);
                const q = new THREE.Quaternion().setFromEuler(eul);
      
                //char.p.setValue(dir.x, dir.y , dir.z);
                //controller.setWalkDirection( char.p );
    
                char.q.setValue(q.x, q.y, q.z, q.w);
                tmpTransformationNPC.setRotation(char.q);
    
                //this.movePhyNPC(char.id, dir, char.ppos);

                phychar.dir.set(dir.x * deltaTime, dir.y * deltaTime, dir.z * deltaTime);
                
                const newPos = new THREE.Vector3().addVectors( phychar.tpos, phychar.dir);
                this.tmpVec3.setValue(newPos.x, newPos.y, newPos.z);
                tmpTransformationNPC.setOrigin(this.tmpVec3);

      
    
              } else {
      
                phychar.anims = [{a : char.avatarDef.idleAnim, t: time}];
      
                if(char.targetObj){
                  const infos = char.targetObj.split('-');
                  if(infos[0] === 'Car') {
                    char.active = false;
                  }
                }

                phychar.dir.set(0, 0, 0);
                //phychar.physicsBody.setLinearVelocity(this.tmpVec3);
      
                /*
                char.p.setValue(0, 0, 0);
                controller.setWalkDirection( char.p );
                */
                //this.stopPhyChar(char.id);
    
                phychar.targetPos = null;
              }
              
              //this.tmpVec3.setValue(phychar.dir.x, phychar.dir.y, phychar.dir.z);
              //phychar.physicsBody.setLinearVelocity(this.tmpVec3);
              
            }
          }

      }

      if(this.tGroundMesh){

            
        const o = tmpTransformationNPC.getOrigin();

        const rayCallBack = Ammo.castObject(  this.closestRayResultCallback, Ammo.RayResultCallback );
        rayCallBack.set_m_closestHitFraction( 1 );
        rayCallBack.set_m_collisionObject( null );
        rayCallBack.set_m_collisionFilterGroup(groundGroup);
        rayCallBack.set_m_collisionFilterMask(groundMask);

        this.btFrom.setValue(  o.x(),  o.y() + 5,  o.z() );
        this.btTo.setValue(  o.x(),  o.y() - 5,  o.z() );
        this.closestRayResultCallback.get_m_rayFromWorld().setValue(    o.x(),  o.y() + 5,  o.z() );
        this.closestRayResultCallback.get_m_rayToWorld().setValue(  o.x(),  o.y() - 5,  o.z() ); 

      // Perform ray test
        this.physicsUniverse.rayTest( this.btFrom, this.btTo,  this.closestRayResultCallback );

        const f = rayCallBack.get_m_closestHitFraction();
        if ( f<1 ) {

            const point = this.closestRayResultCallback.get_m_hitPointWorld();
            const npcHalfHeight = char.avatarDef.height * phychar.charScale /2;
            // Raycast also hits self capsule, ignore that case.
            //const a = pts[0].point;
            const minHeight = point.y() + npcHalfHeight;
            //if(char.ppos.y  < minHeight ) // capsule height is 1.5
            {
                //const xform = char.physicsBody.getWorldTransform();
                o.setValue(o.x(), minHeight, o.z());
                tmpTransformationNPC.setOrigin(o);
                //phychar.ammoData.char.preStep(this.physicsUniverse);
                //phychar.ammoData.char.setFallSpeed( 0.0 );
            }//else
                // phychar.ammoData.char.setFallSpeed( 40.0 * phychar.charScale );
        } // if ray hit foreign object.
        
        /*
        const from = new THREE.Vector3( char.ppos.x, char.ppos.y + 100, char.ppos.z );
        const to = new THREE.Vector3( 0, -1.0, 0 );
        this.raycaster.set(from, to);
      
        const pts = this.raycaster.intersectObject(this.tGroundMesh);
        if(pts.length >0) {

              const npcHalfHeight = char.avatarDef.height * phychar.charScale /2;
              // Raycast also hits self capsule, ignore that case.
              const a = pts[0].point;
              const minHeight = a.y + npcHalfHeight;
              //if(char.ppos.y  < minHeight ) // capsule height is 1.5
              {
                  //const xform = char.physicsBody.getWorldTransform();
                  
                  const o = tmpTransformationNPC.getOrigin();
                  o.setValue(o.x(), minHeight, o.z());
                  tmpTransformationNPC.setOrigin(o);
               


                  //phychar.ammoData.char.preStep(this.physicsUniverse);
                  //phychar.ammoData.char.setFallSpeed( 0.0 );
              }//else
                 // phychar.ammoData.char.setFallSpeed( 40.0 * phychar.charScale );
          } // if ray hit foreign object.
            */
      }
     

      let new_qua = tmpTransformationNPC.getRotation();
      new_pos = tmpTransformationNPC.getOrigin();
      
      if (Physics_Obj.userData.char) {


        Physics_Obj.userData.char.ppos.x = new_pos.x();
        Physics_Obj.userData.char.ppos.y = new_pos.y();
        Physics_Obj.userData.char.ppos.z = new_pos.z();

        Physics_Obj.userData.char.prot.x = new_qua.x();
        Physics_Obj.userData.char.prot.y = new_qua.y();
        Physics_Obj.userData.char.prot.z = new_qua.z();
        Physics_Obj.userData.char.prot.w = new_qua.w();
      }

         char.physicsBody.setWorldTransform(tmpTransformationNPC);
         //ms.setWorldTransform(this.tmpTransformationNPC);

    }

    stopCarAt(phycar, pos, q){
      this.tmpVec3.setValue(0.0, 0.0, 0.0);
      phycar.physicsBody.setLinearVelocity(this.tmpVec3);
      phycar.physicsBody.setAngularVelocity(this.tmpVec3);

      this.tmpVec3.setValue(pos.x, pos.y + 1.5, pos.z);
      this.tmpbtQ.setValue(q.x, q.y, q.z, q.w);

      this.tmpTransformation.setIdentity();
      this.tmpTransformation.setRotation(this.tmpbtQ);
      this.tmpTransformation.setOrigin(this.tmpVec3);

      phycar.physicsBody.setWorldTransform(this.tmpTransformation);
      const ms = phycar.physicsBody.getMotionState();
      ms.setWorldTransform(this.tmpTransformatio);
    }

    charTriggerEnter(phychar, trigger, triggerIDX){
      let ret = false;


      if(phychar.trigger === trigger )
          return true;

      if(trigger.startsWith('door'))
        ret = true;


      if(ret){
        phychar.trigger = trigger;
        phychar.triggerIDX = triggerIDX;
      }else{
        phychar.trigger = null;
        phychar.triggerIDX = -1;
      }

      return ret;
    }

    carTriggerEnter(phycar, trigger, triggerIDX){

      let ret = false;


      if(phycar.trigger === trigger )
          return true;

      if(trigger === "collector"){
        this.removeTakenObjects(phycar);
        ret = true;
      }

      if(trigger.startsWith('power')){
        ret = true;
      }

    /*
      if(trigger.startsWith('door1')){
          ret = true;
      }

      if( (trigger === 'platform1up')||(trigger === 'platform1down')){
        ret = true;
      }

      if(trigger === 'computer'){

        if(this.states['computer'] !== true){

          if(phycar.power > 50){
            this.states['computer'] = true;
            phycar.power -= 50;
            this.triggerEvents.push({trig : triggerIDX, state : 1});
          }else{
            this.triggerEvents.push({trig : triggerIDX, state : 0});
          }
        }
        ret = true;
      }

      if(trigger === 'radarlift'){
         
        if((this.states['computer'] === true)&&(phycar.speed < 5)&&(!this.radarlift.locked)) {
          this.stopCarAt(phycar, this.radarlift.pos, {x : 0, y: -0.7, z : 0.0, w : 0.7});

          this.movingObjects.push({obj : this.radarlift, dir : 'up'});
          this.radarlift.locked = true;
          ret = true;
        }else{
          this.triggerEvents.push({trig : triggerIDX, state : 0});
        }
      }
      */

      if(ret){
        phycar.trigger = trigger;
        phycar.triggerIDX = triggerIDX;
      }else{
        phycar.trigger = null;
        phycar.triggerIDX = -1;
      }

      return ret;

    }

    handleTriggeredObjects(phycar, track, dt) {

      if(!track)
        return;

      if(phycar.trigger == 'power'){
          
        const trig = track.triggers[phycar.triggerIDX];
        const target = trig.cubeTarget;
        const powerPerSec = trig.data.power / trig.data.time;
        
        const doorpower = powerPerSec * dt;

        if(phycar.power >= doorpower){

          const phyObj =target.physicsBody;
          const tform = phyObj.getWorldTransform();
          let new_pos = tform.getOrigin();

          if(trig.data.start < trig.data.stop){

            const doorspeed = (trig.data.stop - trig.data.start) / trig.data.time;

            if(new_pos.y() < trig.data.stop){

              const newY = Math.min(new_pos.y() + doorspeed * dt, trig.data.stop);

              new_pos.setValue(new_pos.x(), newY, new_pos.z());
              tform.setOrigin(new_pos);

              const ms = phyObj.getMotionState();
              ms.setWorldTransform(tform);
              phyObj.setWorldTransform(tform);

              phycar.power -= doorpower;
              target.moving = true;
            }

          }else if(new_pos.y() > trig.data.stop){

            const doorspeed = (trig.data.start - trig.data.stop) / trig.data.time;

            const newY = Math.max(new_pos.y() - doorspeed * dt, trig.data.stop);

            new_pos.setValue(new_pos.x(), newY, new_pos.z());
            tform.setOrigin(new_pos);

            const ms = phyObj.getMotionState();
            ms.setWorldTransform(tform);
            phyObj.setWorldTransform(tform);

            phycar.power -= doorpower;
            target.moving = true;
          }
        } 
      }
      
      
      const carTrigger = phycar.triggerIDX >= 0 ? track.triggers[phycar.triggerIDX] : null;
      
      for(let n=0;n<track.triggers.length;n++){

        if((carTrigger !== null) &&(carTrigger.id === track.triggers[n].id))
          continue;

        const trig = track.triggers[n];

        if(trig.type === 'power'){

          if((carTrigger !== null) &&((carTrigger.type === trig.type)&&(carTrigger.data.target === track.triggers[n].data.target)))
            continue;

          
          const target = trig.cubeTarget;
  
          if(target){
  
            if(target.moving === true){
              const phyObj =target.physicsBody;
              const tform = phyObj.getWorldTransform();
              let new_pos = tform.getOrigin();
      
              if(trig.data.start < trig.data.stop){

                const doorspeed = (trig.data.stop - trig.data.start) / (trig.data.time * 4.0);

                if(new_pos.y() > trig.data.start){
        
                  const newY = Math.max(new_pos.y() - doorspeed * dt, trig.data.start);
        
                  new_pos.setValue(new_pos.x(), newY, new_pos.z());
                  tform.setOrigin(new_pos);
                  const ms = phyObj.getMotionState();
                  ms.setWorldTransform(tform);
                  phyObj.setWorldTransform(tform);
                  target.moving = true;
                }else
                  target.moving = false;

              }else{

                const doorspeed = (trig.data.start - trig.data.stop) / (trig.data.time * 4.0);

                if(new_pos.y() < trig.data.start){
                  const newY = Math.min(new_pos.y() + doorspeed * dt, trig.data.start);
        
                  new_pos.setValue(new_pos.x(), newY, new_pos.z());
                  tform.setOrigin(new_pos);
                  const ms = phyObj.getMotionState();
                  ms.setWorldTransform(tform);
                  phyObj.setWorldTransform(tform);
                  target.moving = true;
                }else
                  target.moving = false;
              }
            }
          }
        }
      }
   

      /*
      if(this.radarlift) {
        if((phycar.trigger !== 'radarlift')&&(this.radarlift.moving === false))  {
          this.radarlift.locked = false;
        }
      }

      if(this.door1Cube){

        const doorpower = 10 * dt;
        const doorspeed = 4;

        if(((phycar.trigger !== null)&&phycar.trigger.startsWith('door1'))&&(phycar.power > doorpower)){

          const phyObj =this.door1Cube.physicsBody;
          const tform = phyObj.getWorldTransform();
          let new_pos = tform.getOrigin();

          if(new_pos.y() < 13){
            new_pos.setValue(new_pos.x(), new_pos.y() + doorspeed * dt, new_pos.z());
            tform.setOrigin(new_pos);

            const ms = phyObj.getMotionState();
            ms.setWorldTransform(tform);
            phyObj.setWorldTransform(tform);

            phycar.power -= doorpower *dt;

            this.door1Cube.moving = true;
            
          }//else
            //this.door1Cube.moving = false;
            

        } else if(this.door1Cube.moving === true){
          const phyObj = this.door1Cube.physicsBody;
          const tform = phyObj.getWorldTransform();
          let new_pos = tform.getOrigin();

          if(new_pos.y() > -4.5){
            new_pos.setValue(new_pos.x(), new_pos.y() - doorspeed * dt, new_pos.z());
            tform.setOrigin(new_pos);
            const ms = phyObj.getMotionState();
            ms.setWorldTransform(tform);
            phyObj.setWorldTransform(tform);
            this.door1Cube.moving = true;
          }else
          this.door1Cube.moving = false;
        }
      }

      if(this.platform1){

        if( (phycar.trigger === 'platform1up')||
            (phycar.trigger === 'platform1down')){

          const phyObj = this.platform1.physicsBody;

          const tform = phyObj.getWorldTransform();
          let new_pos = tform.getOrigin();

          if(phycar.trigger === 'platform1up'){
            if(new_pos.y() < 85){
              new_pos.setValue(new_pos.x(), new_pos.y() + 0.1, new_pos.z());
              tform.setOrigin(new_pos);

              const ms = phyObj.getMotionState();
              ms.setWorldTransform(tform);
              phyObj.setWorldTransform(tform);

              this.platform1.moving = true;
            }else
              this.platform1.moving = false;
          }

          if(phycar.trigger === 'platform1down'){
            if(new_pos.y() > 40){
              new_pos.setValue(new_pos.x(), new_pos.y() - 0.1, new_pos.z());
              tform.setOrigin(new_pos);

              const ms = phyObj.getMotionState();
              ms.setWorldTransform(tform);
              phyObj.setWorldTransform(tform);

              this.platform1.moving = true;
            }else
              this.platform1.moving = false;
          }
        }
      }
        */
    }


    
    findSpeed(path, d){

      for(let p of path){

        if(p.dist > d)
            return Math.abs(p.speed);
      }

    }

    handleNPCActivation(c){
      if(this.world.myCar.pos.distanceTo(c.ppos) > 400){

          if(!c.isRemoved){
            c.isRemoved = true;
            c.physicsBody.setActivationState(WANTS_DEACTIVATION);
          }

          this.stopPhyChar(c.id);
          
          //this.tmpVec3.setValue(0,0,0);
          //this.character_List[i].char.setWalkDirection(this.tmpVec3);
          

          //this.physicsUniverse.removeCollisionObject(c.physicsBody);
        } else {

          if(c.isRemoved === true){
            c.physicsBody.setActivationState(DISABLE_DEACTIVATION);
            c.isRemoved = false;
          }
          
          //this.physicsUniverse.addCollisionObject(c.physicsBody, worldGroup, worldMask);
        }
    }

    findContact(contacts ,name, pos){
      for(let n=0;n<contacts.length;n++){
        if((contacts[n].name == name)&&(contacts[n].lpos.distanceTo(pos)<0.5))
          return true;
      }
      return false;
    }
  
    updatePhysicsUniverse(world, deltaTime, time) {

      const track = world.track;
  
      /*console.time('sync');*/

      /*console.timeEnd('sync')*/
      /*console.time('sim');*/  


      var fixedTimeStep = frameTime / 1000;
  
      this.physicsUniverse.stepSimulation(deltaTime, 2, fixedTimeStep  * 2);

     



        for(let n=0;n<this.mbgrids.length;n++){

          const spd = 4000;
          const spd2 = spd /2;
          if(this.mbgrids[n].hasBall) {

            const grid = track.grids[n];

            if((time - this.mbgrids[n].time)> 10){

              this.tmpV1.set( Math.random() * grid.width * grid.cell_size.x * 0.8, 
                              4 ,
                              Math.random() * grid.depth * grid.cell_size.z * 0.8 ).applyQuaternion(grid.rot).add(grid.pos);

              this.tmpVec3.setValue(this.tmpV1.x, this.tmpV1.y, this.tmpV1.z);

              this.tmpTransformation.setIdentity();
              this.tmpTransformation.setOrigin(this.tmpVec3);
              this.mbgrids[n].fallBallBody.setWorldTransform( this.tmpTransformation );
              this.mbgrids[n].fallBallBody.activate();


              this.tmpVec3.setValue(Math.random() * spd -spd2, -20, Math.random() * spd-spd2);

              this.mbgrids[n].fallBallBody.applyCentralImpulse(this.tmpVec3);
              this.mbgrids[n].time = time;
            }
          }
      }
      if(track){
        for(let i =0; i <track.spiders.length;i++){
          track.spiders[i].inContact = false;
          this.resetskelContact(track.spiders[i].skel.root);
        }
      }

      
      for(let car of this.cars){
        car.hasTrigger = false;
      }
      for(let char of this.characters){
        char.hasTrigger = false;
      }
      for(let char of this.npcs){
        if(!char)
          continue;

        char.hasTrigger = false;
      }
  
      

      for (var i = 0; i < this.syncList.length; i++)
      {

          this.syncList[i].sync(track, deltaTime, time);
      }

     
      this.detectCollision(time, deltaTime);

      let n = 0;
      while ( n < this.ragdoll_List.length) {
          if(this.updatePhyRagdoll(this.ragdoll_List[n].char, deltaTime, time))
            n++;
      }

      for (let i = 0; i < this.character_List.length; i++) {

        const c = this.character_List[i].object;
        this.updateNPC(c, deltaTime, time);
        //this.handleNPCActivation(c);
      }


      for(let car of this.cars){

        if(!car.hasTrigger){
          car.trigger = null;
          car.triggerIDX = -1;
        }
      }

      for(let char of this.npcs){
        if(!char)
          continue;

        if(!char.hasTrigger){
          char.trigger = null;
          char.triggerIDX = -1;
        }
      }
      for(let char of this.characters){

        if(!char.hasTrigger){
          char.trigger = null;
          char.triggerIDX = -1;
        }
      }


      if(this.cars.length>0){
        const phycar = this.cars[0];
        if(phycar.active){
          if(this.reccord){

            if(time - this.lastReccord>=0.1){

              if(this.reccord.startTime == 0){
                this.reccord.startTime = time;
              }

              this.lastReccord = time;
              const rt = time - this.reccord.startTime;
              const pd = this.getDistanceOnCurve(track, phycar);
              const speed = phycar.vehicle.getCurrentSpeedKmHour();
              
              if(pd>=0)
                this.reccord.samples.push({time: rt, dist: pd, speed : speed})
            }
          }
        }
      }

      for (let i = 0; i < this.cars.length; i++) {

        const phycar = this.cars[i];
        const car = world.cars[i];
        const avel =  phycar.physicsBody.getAngularVelocity();
        let x = avel.x();
        let y = avel.y();
        let z = avel.z();

        const radarRad = car.carDef.radar ? car.carDef.radar.range * 0.25: 2.0;

        if(track){
          for(let s = 0;s<track.spiders.length;s++){

            const bone = this.findSpiderBoneDistance(track.spiders[s].skel.root, phycar.pos, radarRad);
            if(bone != null){
                this.takeFallenLeg(phycar.physicsBody, bone.physicsBody, time);
            }
          }

          for(let s = 0;s<track.npcs.length;s++){

            if(!track.npcs[s].skeleton)
              continue;

            const bone = this.findSpiderBoneDistance(track.npcs[s].skeleton.root, phycar.pos, radarRad);
            if(bone != null){
                this.takeFallenLeg(phycar.physicsBody, bone.physicsBody, time);
            }
          }

          if(phycar.targetNode>=0){

              if((phycar.targetNode & 0x00FF0000) == 0){
                if((phycar.pos.distanceTo(this.navmeshs[phycar.targetNode].pos)<10)&&(Math.abs(phycar.speed<15))){

                  if(phycar.passengers.length>0){
                    const passenger = phycar.passengers[0];
                   
                    
                    let ret=false;
                    while(ret === false){
                      const phychar = this.getNPC(passenger.netID);
                      ret = this.executeScript(phycar.script, phycar.scriptInstance, passenger, phychar, phychar.pos, time, {car : phycar});
                    }

                  }else{
                    phycar.targetNode = -1;
                  }
                }
              }

             
          }

          if ((car.actions.activate)&&(phycar.targetNode<0)) {

            let minDist = 1000000000;
            let selectedNPC = -1;
            for(let npci = 0;npci<this.npcs.length;npci++){

              const d = world.track.npcs[npci].ppos.distanceTo(phycar.pos);

              if((d<minDist)&&(d<world.track.npcs[npci].avatarDef.activation)&&this.npcs[npci].active){
                  selectedNPC = npci;
                  minDist = d;
              }
            }

            if(selectedNPC>=0){

              if((world.track.npcs[selectedNPC].dialog)&&(phycar.selectedSubsystemId>=0)){

                const line = world.track.npcs[selectedNPC].dialog.lines[this.npcs[selectedNPC].currentDialogLine];

                if((typeof line.txt === 'string')||((this.npcs[selectedNPC].currentDialogSubLine+1)>=line.txt.length)){

                  const answer = line.answers[ phycar.selectedSubsystemId];

                  if(answer.action){

                      phycar.scriptInstance={state : '', prevState : '', time : 0, line : 0, nLoops: 0}
                      phycar.script = answer.action.replace('\r','');
                      phycar.script = phycar.script.split('\n');

                      let ret= false;
                      while(ret === false){
                        ret = this.executeScript(phycar.script, phycar.scriptInstance, world.track.npcs[selectedNPC], this.npcs[selectedNPC], world.track.npcs[selectedNPC].ppos, time, {car: phycar});
                      }
                  }
                  if(answer.dialogID<0){
                    phycar.selectedSubsystemId = -1;
                    this.npcs[selectedNPC].currentDialogSubLine = 0;
                  }else{
                    phycar.selectedSubsystemId = 0;
                    this.npcs[selectedNPC].currentDialogSubLine = 0;
                    this.npcs[selectedNPC].currentDialogLine = answer.dialogID;
                  }
                }else if((this.npcs[selectedNPC].currentDialogSubLine+1)<line.txt.length){
                  this.npcs[selectedNPC].currentDialogSubLine++;
                }
              }
            }
            car.actions.activate = false;
          }
        }

        const ta = (x*x)+(y*y)+(z*z);

        if(phycar.isAI){
            const offset = (world.track.trackSegments[phycar.curSeg].looping) ? 0.001 : 0.001;
            //const delta = new THREE.Vector3();

            const d = this.getDistanceOnCurve(track, phycar, this.tmpV3); 

            if(d<0){

              if(phycar.isStuck === false){
                phycar.startStuck = time;
              }
              phycar.isStuck = true;
              continue;
            }

            const dd = (d + offset>1) ? d + offset - 1: d + offset;

            const t = track.curveC.getTangentAt(dd);
            /*const carvelB = phycar.physicsBody.getLinearVelocity();*/
            /* const carVel = new THREE.Vector3(carvelB.x(), 0, carvelB.z()).normalize(); */

            this.tmpV3.normalize().multiplyScalar(phycar.AIProfile.centerForce);


            t.y = 0;
            t.normalize();
            
            let angleFactor= 1;

            if((track.trackSegments[phycar.curSeg].looping)){
              angleFactor  = 1.5;
            }
            else
              angleFactor  = 1.0;

            //const sforward = this.forwardVec.clone().applyQuaternion(phycar.quat);
            const sforward = phycar.vehicle.getForwardVector();
            phycar.nForward.set(sforward.x(), 0, sforward.z()).normalize();
            
            phycar.dirAngle.setFromUnitVectors( phycar.nForward, t );
            const eul = phycar.dirAngleEul.setFromQuaternion(phycar.dirAngle);

            this.tmpVec3.setValue(this.tmpV3.x, this.tmpV3.y, this.tmpV3.z);
            phycar.physicsBody.applyCentralImpulse(this.tmpVec3);

            if(Math.abs(eul.y)> phycar.AIProfile.angleThresold){
              this.tmpVec3.setValue(0, eul.y * phycar.AIProfile.angleForce * angleFactor , 0);
              phycar.physicsBody.applyTorqueImpulse(this.tmpVec3);
            }

            if((phycar.dirAngle.y < -0.1))
                car.actions["right"] = true;
            else
                car.actions["right"] = false;

            if((phycar.dirAngle.y > 0.1))
                car.actions["left"] = true;
            else
                car.actions["left"] = false;

            
            let tspeed = this.findSpeed(track.AITable, dd);

            if((world.track.trackSegments[phycar.curSeg].looping)){
                tspeed *= 1.2;
                //car.EngineForceFactor = 1.3;
            }
              

            const speed = phycar.vehicle.getCurrentSpeedKmHour();
            
            if(speed<tspeed * phycar.AIProfile.speedFactor){

              //console.log('ai car accelerating ');

              if(Math.abs(eul.y)< phycar.AIProfile.angleThresold*2){
                car.actions["acceleration"] = true;
              }

              car.actions["braking"] = false;
            }else{

              //console.log('ai car braking ');
              //car.actions["braking"] = true;
              car.actions["acceleration"] = false;
            }

          if(phycar.pos.y < this.minGroundY - 1.0){
            if(phycar.isStuck === false){
                phycar.startStuck = time;
            }
            phycar.isStuck = true;
          }

              
          if((phycar.started)&&(!phycar.fullGround)&&(Math.abs(speed) < 5)&&(ta<5)){

            if(phycar.isStuck === false){
                phycar.startStuck = time;
            }
            phycar.isStuck = true;
          }

          if((phycar.isStuck)&&((time-phycar.startStuck)>1)){
   
            this.removeAttachedObjects(phycar);

            let pts, q;

            if(this.trackMode === 1){

              phycar.dirAngleEul.set(0, Math.PI/2, 0, "XYZ");

              const qc = phycar.dirAngle.setFromEuler(phycar.dirAngleEul);
              q = new THREE.Quaternion(track.trackPoints[phycar.curSeg].rot.x,track.trackPoints[phycar.curSeg].rot.y, track.trackPoints[phycar.curSeg].rot.z, track.trackPoints[phycar.curSeg].rot.w).multiply(qc)
              pts = track.trackPoints[phycar.curSeg].pos;

              phycar.RPM = 0;
              phycar.gear = 1;

            }else{
              pts = {x : phycar.pos.x, y : phycar.pos.y +1.0, z : phycar.pos.z  };
              q = phycar.dirAngle.set(0,0,0,1);
            }

            let fnd=false;

            for(let c=0;c<this.cars.length;c++){
              if(this.cars[c].pos.distanceTo(pts)<5)
                 fnd=true;
            }

            if(!fnd){
              this.tmpVec3.setValue(pts.x, pts.y +1.0, pts.z);
              this.tmpbtQ.setValue(q.x, q.y, q.z, q.w);
              
              this.tmpTransformation.setIdentity();
              this.tmpTransformation.setOrigin( this.tmpVec3);
              this.tmpTransformation.setRotation(this.tmpbtQ);

              this.setCarTrans(phycar.id, this.tmpVec3, this.tmpbtQ, 0.0);

              //this.cars[i].p.setValue(pts.x, pts.y, pts.z);
              //this.cars[i].q.setValue(q.x, q.y, q.z, q.w);

              const ms = phycar.physicsBody.getMotionState();
              ms.setWorldTransform(this.tmpTransformation);
              phycar.physicsBody.setWorldTransform(this.tmpTransformation);

              phycar.isStuck = false;
            }
          
            //Ammo.destroy(bv);
            //Ammo.destroy(bq);
          }
          continue;
        }
        this.handleTriggeredObjects(phycar, world.track, deltaTime);
        
         if(world.track.trackPoints.length > 1){

          for(let a=0;a<phycar.attachedObjects.length;a++){

            const ip=phycar.attachedObjects[a];
            const xform = ip.LphysicsBody.getWorldTransform();
             const o = xform.getOrigin();

            const from = new THREE.Vector3( o.x(), o.y() + 100, o.z() );
            const to = new THREE.Vector3( 0, -1.0, 0 );
            this.raycaster.set(from, to);
            const pts = this.raycaster.intersectObjects([track.tRoadMesh,track.tWallMesh]);

            if(pts.length >0) {
              
              const a = pts[0].point;
              const minHeight = a.y + 0.5;
              if(o.y()  < minHeight ) {

                o.setValue(o.x(), minHeight, o.z());
                xform.setOrigin(o);

                const ms = ip.LphysicsBody.getMotionState();
                ms.setWorldTransform(xform);
                ip.LphysicsBody.setWorldTransform(xform);
              }
            } 
          }
        }
        
        if((phycar.started)&&(!phycar.fullGround)&&(Math.abs(phycar.speed) < 1)&&(ta<0.5)){

            if(phycar.isStuck === false){
                phycar.startStuck = time;
            }
            phycar.isStuck = true;
        }else
            phycar.isStuck = false;

        if((phycar.isStuck)&&((time-phycar.startStuck)>2)){
   
            this.removeAttachedObjects(phycar);

            let pts, q;

            if(this.trackMode === 1){
              const qc = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.PI/2, 0, "XYZ"));
              q = new THREE.Quaternion(track.trackPoints[phycar.curSeg].rot.x, track.trackPoints[phycar.curSeg].rot.y, track.trackPoints[phycar.curSeg].rot.z, track.trackPoints[phycar.curSeg].rot.w).multiply(qc)
              pts = track.trackPoints[phycar.curSeg].pos;
            }else{
              pts = {x : phycar.pos.x, y : phycar.pos.y +1.0, z : phycar.pos.z  };
              q = new THREE.Quaternion();
            }

            const bv = new Ammo.btVector3(pts.x, pts.y +1.0, pts.z);
            const bq = new Ammo.btQuaternion(q.x, q.y, q.z, q.w);
            
            this.tmpTransformation.setIdentity();
            this.tmpTransformation.setOrigin(bv);
            this.tmpTransformation.setRotation(bq);

            this.setCarTrans(phycar.id, bv, bq, 0.0);

            //this.cars[i].p.setValue(pts.x, pts.y, pts.z);
            //this.cars[i].q.setValue(q.x, q.y, q.z, q.w);

            const ms = phycar.physicsBody.getMotionState();
            ms.setWorldTransform(this.tmpTransformation);
            phycar.physicsBody.setWorldTransform(this.tmpTransformation);

            phycar.isStuck = false;

            Ammo.destroy(bv);
            Ammo.destroy(bq);
        }

      }
     


      for (let i = 0; i < this.rigidBody_List.length; i++) {
        let Physics_Obj = this.rigidBody_List[i];

        if(Physics_Obj.disabled === true)
          continue;


        if(Physics_Obj.userData.contacts){
          for(let n=0;n<Physics_Obj.userData.contacts.length;n++){
              if(!this.findContact(Physics_Obj.userData.lastContacts, Physics_Obj.userData.contacts[n].name, Physics_Obj.userData.contacts[n].lpos)){
                  this.addImpact(Physics_Obj.userData.contacts[n].pos, 8);
              }
          }

          Physics_Obj.userData.lastContacts = Physics_Obj.userData.contacts;
          Physics_Obj.userData.contacts = [];
        }
        
  
        let motionState = Physics_Obj.getMotionState();
  
        if (motionState) {
          const tform = Physics_Obj.getWorldTransform();
  
          let new_pos = tform.getOrigin();
          let new_qua = tform.getRotation();
  
          if (Physics_Obj.userData.obj) {
            
            Physics_Obj.userData.obj.pos.x = new_pos.x();
            Physics_Obj.userData.obj.pos.y = new_pos.y();
            Physics_Obj.userData.obj.pos.z = new_pos.z();
  
            /*
            if(Physics_Obj.userData.name.startsWith("cube-topspring")){
              console.log('rb : '+Physics_Obj.userData.name +' ' +Physics_Obj.userData.obj.pos.x + ' ' + Physics_Obj.userData.obj.pos.y +' ' + Physics_Obj.userData.obj.pos.z)
            }
            */
  
            if(Physics_Obj.userData.name.startsWith("shot")){

              if(Physics_Obj.userData.weaponid >= 0){
                if(Physics_Obj.userData.turret.weapons){
                  const weapon = Physics_Obj.userData.turret.weapons[Physics_Obj.userData.weaponid];
                  if((weapon.projHoming)&&((time - Physics_Obj.userData.obj.time)>1)){
                    
                    const v = new THREE.Vector3().subVectors(Physics_Obj.userData.turret.currentTarget.pos, Physics_Obj.userData.obj.pos).normalize();
                    this.tmpVec3.setValue(v.x * weapon.projSpeed, 0, v.z* weapon.projSpeed);
                    Physics_Obj.applyCentralImpulse(this.tmpVec3);
                  }
                }
              }

            }else{
              Physics_Obj.userData.obj.rot.x = new_qua.x();
              Physics_Obj.userData.obj.rot.y = new_qua.y();
              Physics_Obj.userData.obj.rot.z = new_qua.z();
              Physics_Obj.userData.obj.rot.w = new_qua.w();

            }
            /*
            if(Physics_Obj.userData.name.startsWith('drone-')){
  
              let p = Physics_Obj.userData.obj.pos;
              let q = Physics_Obj.userData.obj.rot;
              if(Physics_Obj.userData.obj.active)
              {
                console.log(p);
                console.log(q);
              }
            }
            */
          }
        }
      }


     
    
      if(track){
        for (let n = 0; n < track.cubes.length; n++) {   

          if(track.cubes[n].name === "platform1")  {

            let mon =0;
            while(mon < this.movingObjects.length){
              const mo = this.movingObjects[mon];

              const o = mo.obj;
              const phyObj = o.physicsBody;
              const tform = phyObj.getWorldTransform();
              let new_pos = tform.getOrigin();

              if(mo.dir === 'up'){
                if(new_pos.y() < 55){
                  new_pos.setValue(new_pos.x(), new_pos.y() + 0.1, new_pos.z());
                  o.moving = true;
                }else{
                  mo.dir = 'up-idle';
                  mo.idleTime = time + 2;
                }
              }else if(mo.dir === 'up-idle'){

                if(time >= mo.idleTime)
                    mo.dir = 'down';

              }else if(mo.dir === 'down'){
                if(new_pos.y() > -14.0){
                  new_pos.setValue(new_pos.x(), new_pos.y() - 0.1, new_pos.z());
                  o.moving = true;
                }else{
                  mo.dir = 'stop';
                  o.moving = false;
                }
              }

              tform.setOrigin(new_pos);
              const ms = phyObj.getMotionState();
              ms.setWorldTransform(tform);
              phyObj.setWorldTransform(tform);

              if(!o.moving){
                this.movingObjects.splice(mon, 1);
              }else
                mon++;
            }
          }
        }

        
        this.updateSpiders(track, time, deltaTime);

        this.updateDrones(track, time, deltaTime);
  
        this.updateTurrets(track, time, deltaTime);
    
        this.updateRopes(track, time, deltaTime);
      }
    }

    setSpiderSkelPos(skel, pos){

      if(!skel.root.physicsBody)
        return;

      const origin = new Ammo.btVector3(pos.x, pos.y, pos.z);

      this.tmpTransformation.setIdentity();
      this.tmpTransformation.setOrigin(origin);
      
      skel.root.physicsBody.setWorldTransform( this.tmpTransformation );
      const motionState = skel.root.physicsBody.getMotionState();
      motionState.setWorldTransform(this.tmpTransformation);
      Ammo.destroy(origin);
    }



    animateSpider(skel, dt, dir){
      const spd = 2;
      const st = dt * spd;

      const ampZ = 0.4;

      const root = skel.root;
      for(let child of root.children) {

        if(child.name.startsWith('joint-leg')){

          const joint = child;
          const leg = joint.children[0];

          if(!leg.constraint)
            continue;

          const arr = leg.name.split('-');
          const id = parseInt(arr[2]) - 1;
          const side = arr[1];

          if(id === 0){
            const phase = (side === 'l') ? 0 : Math.PI / 2;
            leg.ae.set( 0, 0, Math.sin(st+phase) * ampZ);
            leg.aq.setFromEuler(leg.ae);
            leg.aaq.setValue(leg.aq.x, leg.aq.y, leg.aq.z, leg.aq.w);
            leg.constraint.getFrameOffsetB().setRotation(leg.aaq);

            const leg2 = leg.children[0];
            if(leg2.constraint !== null)
              leg2.physicsBody.applyCentralImpulse(dir);
            
          }

          if(id === 1){
            const phase = (side === 'l') ? 0 : Math.PI / 2;
            leg.ae.set(  0, Math.cos(st+phase) * 0.4, Math.sin(st+phase) * ampZ);
            leg.aq.setFromEuler(leg.ae);
            leg.aaq.setValue(leg.aq.x, leg.aq.y, leg.aq.z, leg.aq.w);
            leg.constraint.getFrameOffsetB().setRotation(leg.aaq);

            const leg2 = leg.children[0];
            if(leg2.constraint !== null)
              leg2.physicsBody.applyCentralImpulse(dir);

          }
          if(id === 2){
            const phase = (side === 'r') ? 0 : Math.PI / 2;
            leg.ae.set(  0, Math.cos(st+phase) * 0.4, Math.sin(st+phase) * ampZ);
            leg.aq.setFromEuler(leg.ae);
            leg.aaq.setValue(leg.aq.x, leg.aq.y, leg.aq.z, leg.aq.w);
            leg.constraint.getFrameOffsetB().setRotation(leg.aaq);

            const leg2 = leg.children[0];
            if(leg2.constraint !== null)
              leg2.physicsBody.applyCentralImpulse(dir);
          }

          if(id === 3){
            const phase = (side === 'l') ? 0 : Math.PI / 2;
            leg.ae.set( 0, 0, Math.sin(st+phase) * ampZ);
            leg.aq.setFromEuler(leg.ae);
            leg.aaq.setValue(leg.aq.x, leg.aq.y, leg.aq.z, leg.aq.w);
            leg.constraint.getFrameOffsetB().setRotation(leg.aaq);

            const leg2 = leg.children[0];
            if(leg2.constraint !== null)
              leg2.physicsBody.applyCentralImpulse(dir);

          }
        }
      }

    }
    removePhySpider(skel){

      const root = skel.root;




      for(let child of root.children){
        if(child.name.startsWith('joint')){
          const joint = child;
          const leg = joint.children[0];
          const leg2 = leg.children[0];

          if(leg.constraint){
            this.physicsUniverse.removeConstraint(leg.constraint);
            Ammo.destroy(leg.constraint);
            leg.constraint=null;
          }

          if(leg.physicsBody){
            this.physicsUniverse.removeRigidBody(leg.physicsBody);
            Ammo.destroy(leg.physicsBody);
            leg.physicsBody = null;
          }

          if(leg.ammoData){
            Ammo.destroy(leg.ammoData.localInertia);
            Ammo.destroy(leg.ammoData.motionState);
            Ammo.destroy(leg.ammoData.transform);
            leg.ammoData = null;
          }

          if(leg2){

            if(leg2.constraint){
              this.physicsUniverse.removeConstraint(leg2.constraint);
              Ammo.destroy(leg2.constraint);
              leg2.constraint = null;
            }

            if(leg2.physicsBody){
              this.physicsUniverse.removeRigidBody(leg2.physicsBody);
              Ammo.destroy(leg2.physicsBody);
              leg2.physicsBody = null;
            }

            if(leg2.ammoData){
              Ammo.destroy(leg2.ammoData.localInertia);
              Ammo.destroy(leg2.ammoData.motionState);
              Ammo.destroy(leg2.ammoData.transform);
              leg2.ammoData = null;
            }
          }
        }
      }

      if(root.physicsBody){
        this.physicsUniverse.removeRigidBody(root.physicsBody);
        Ammo.destroy(root.physicsBody);
        root.physicsBody = null;
      }

      if(root.ammoData){
        Ammo.destroy(root.ammoData.localInertia);
        Ammo.destroy(root.ammoData.motionState);
        Ammo.destroy(root.ammoData.transform);
        root.ammoData = null;
      }

    }

    getSpiderLegs(bone, nConst = 0){

      if(bone.name.startsWith('leg')){

          if(bone.constraint != null)
            nConst++;
          else
            return nConst;
      }

      if((bone.name.startsWith('fleg'))&&(bone.constraint != null))
        nConst++;
      
      for(let c of bone.children){
        nConst = this.getSpiderLegs(c, nConst);
      }

      return nConst;
    }

    killSpider(skel){

      const root = skel.root;

      const upVec = new Ammo.btVector3(0,10,0);

      for(let child of root.children){

        if(child.name.startsWith('joint')){
          const joint = child;
          const leg = joint.children[0];
          const leg2 = leg.children[0];

          if(leg.constraint){
            this.physicsUniverse.removeConstraint(leg.constraint);
            Ammo.destroy(leg.constraint);
            leg.constraint=null;

            leg.physicsBody.applyCentralImpulse(upVec);

          }
          
          if((leg2)&&(leg2.constraint)){
            this.physicsUniverse.removeConstraint(leg2.constraint);
            Ammo.destroy(leg2.constraint);
            leg2.constraint=null;
            leg2.physicsBody.applyCentralImpulse(upVec);
          }
        }
      }

      Ammo.destroy(upVec);
    }

 

    

    removePhyRagdoll(bone){

      for(let c of bone.children){
        this.removePhyRagdoll(c);
      }

      if(bone.constraint){
        this.physicsUniverse.removeConstraint(bone.constraint);
        Ammo.destroy(bone.constraint);
        bone.constraint=null;
      }

      if(bone.physicsBody){
        this.physicsUniverse.removeRigidBody(bone.physicsBody);
        Ammo.destroy(bone.physicsBody);
        bone.physicsBody = null;
      }

      if(bone.ammoData){
        Ammo.destroy(bone.ammoData.localInertia);
        Ammo.destroy(bone.ammoData.motionState);
        Ammo.destroy(bone.ammoData.transform);
        Ammo.destroy(bone.ammoData.shape);

        /*
        if(bone.ammoData.pshape)
          Ammo.destroy(bone.ammoData.pshape);
        */
        bone.ammoData = null;
      }
    }


    getRagDollLimit(axis, name){


      const ar = name.split('-');
      const boneName = ar[0];
      const side = ar[1];
      
      if((boneName == 'arm')||(boneName == 'leg')){

        switch(axis){
          case 2:return [-Math.PI/8, Math.PI/8]; break;
          case 1:return [0, 0]; break;
          case 0:return [-Math.PI/4, Math.PI/4]; break;
        }
      }else if(boneName == 'shoulder'){

        switch(axis){
          case 2:return [-Math.PI/6, Math.PI/6]; break;
          case 1:return [0, 0]; break;
          case 0:return [0, 0]; break;
        }
      }else if(boneName == 'hip'){
        switch(axis){
          case 2:return [0, 0]; break;
          case 1:return [0, 0]; break;
          case 0:return [0, 0]; break;
        }
      }else if(boneName == 'fleg'){

        switch(axis){
          case 2:return [ -0.1,  0.1]; break;
          case 1:return [0, 0]; break;
          case 0:return [-0.2, 0.2]; break;
        }

      }else if(boneName == 'farm'){

        switch(axis){
          case 2:return [ -0.1,  0.1]; break;
          case 1:return [0, 0]; break;
          case 0:return [-0.2, 0.2]; break;
        }

      }else if(boneName == 'spine'){
        switch(axis){
          case 2:return [-0.1, 0.1]; break;
          case 1:return [0, 0]; break;
          case 0:return [-Math.PI/16, Math.PI/16]; break;
        }
      }else if(boneName == 'spine2'){
        switch(axis){
          case 2:return [-0.4, 0.4]; break;
          case 1:return [0, 0]; break;
          case 0:return [-0.4, 0.4]; break;
        }
      }else if(boneName == 'spine3'){
        switch(axis){
          case 2:return [-0.4, 0.4]; break;
          case 1:return [0, 0]; break;
          case 0:return [-0.4, 0.4]; break;
        }
      }else if(boneName== 'head'){

        switch(axis){
          case 2:return [-Math.PI/8, Math.PI/8]; break;
          case 1:return [0, 0]; break;
          case 0:return [0, 0]; break;
        }

      }else if((boneName == 'hand')||(boneName == 'foot')){

        if(side == 'left'){

          switch(axis){
            case 2:return [0, Math.PI/4]; break;
            case 1:return [0, 0]; break;
            case 0:return [0, 0]; break;
          }
        }else{

          switch(axis){
            case 2:return [-Math.PI/4, 0]; break;
            case 1:return [0, 0]; break;
            case 0:return [0, 0]; break;
          }
        }
      }

      return [-Math.PI/64, Math.PI/64];
    }


    
    
    addPhyRagdollBone(char, parent, bone, mass, size = 1) {
    
          const origin = new Ammo.btVector3(char.pos.x , char.pos.y + bone.length * size, char.pos.z);
    
          //console.log('bone '+bone.name+' '+char.pos.x+' '+char.pos.y+' '+char.pos.z);
          //console.log('bone '+bone.name+' '+bone.quat.x+' '+bone.quat.y+' '+bone.quat.z+' '+bone.quat.w);
          
          
          let shape;
          let length = bone.length;
          let tlength;
          let radius;
          let shapeType = -1;
    
          char.lastAttack = 0;
    
          bone.mass = mass;
          bone.iq = new THREE.Quaternion(0,0,0,1);
          bone.rot = new THREE.Quaternion(0,0,0,1);
    
          bone.trot = new Ammo.btQuaternion(0,0,0,1);
          bone.tpos = new Ammo.btVector3(0,0,0);

          char.currentTarget = null;
          
    
          if((bone.name.startsWith('head'))||(bone.name.startsWith('hand'))){
            shape = new Ammo.btSphereShape(bone.radius * size);
            tlength = bone.radius;
            radius = bone.radius;
            shapeType = 0;
          }else {
    
            const maxRad = bone.name.startsWith('hip') || bone.name.startsWith('shoulder') ? 0.1 : bone.name === 'spine' ? 0.5 : 0.2;
    
            radius = Math.min(maxRad, bone.radius);
    
            /*
            if(bone.name.startsWith('hip'))
                length *= 0.5
            */
    
            let l = Math.max(0, length - radius * 2) * 0.90;
    
            /*
            if(bone.name === 'spine'){
                l *= 0.5;
                radius *= 0.5
            }
            */
              
    
            shape = new Ammo.btCapsuleShape (radius * size, l * size);
            tlength = bone.name.startsWith('foot') ? 0 : length / 2 ;
            shapeType = 1;
          }
    
          //console.log('bone '+bone.name+' '+tlength+' '+radius + ' '+size+ ' '+mass);
          
          const boneOffset = new Ammo.btVector3(0, tlength * size , 0);
          const stransform = new Ammo.btTransform();
          stransform.setIdentity();
          stransform.setOrigin(boneOffset);
          const myShape = new Ammo.btCompoundShape (true, 1);
          myShape.addChildShape (stransform, shape);
          
          //const myShape = shape;
    
          const transform = new Ammo.btTransform();
          transform.setIdentity();
          transform.setOrigin(origin);
          const defaultMotionState = new Ammo.btDefaultMotionState(transform);
      
          const localInertia = new Ammo.btVector3(0, 0, 0);
          shape.calculateLocalInertia(mass, localInertia);
          //myShape.calculateLocalInertia(mass, localInertia);
      
          const RBody_Info = new Ammo.btRigidBodyConstructionInfo(mass, defaultMotionState, myShape, localInertia);
          const RBody = new Ammo.btRigidBody(RBody_Info);
          RBody.userData = { name: 'npc-'+bone.name, obj: bone, char: char, taken : false };
    
          RBody.setFriction(0.8);
          RBody.setDamping(0.2, 0.1);
          RBody.setCcdMotionThreshold(0.01);
          RBody.setCcdSweptSphereRadius(1.0);
    
          bone.physicsBody = RBody;
          this.physicsUniverse.addRigidBody(RBody, worldGroup, worldMask);
    
          bone.ammoData = {localInertia: localInertia, shapeType  : shapeType, motionState :defaultMotionState, shape: shape, pshape : myShape, transform: transform/*, stransform: stransform*/};
          
       
          //console.log('added ragdoll bone x'+size.toFixed(2)+ ' l:'+bone.length.toFixed(2)+' '+bone.quat.x.toFixed(2) +' '+ bone.quat.y.toFixed(2)  +' '+ bone.quat.z.toFixed(2) +' '+ bone.quat.w.toFixed(2)+' '+bone.name);
    
          const originA = new Ammo.btVector3(0, parent.length ? parent.length * size: 0 , 0);
          const originB = new Ammo.btVector3(0, 0, 0);
          const quat = new Ammo.btQuaternion(-bone.quat.x, -bone.quat.y, -bone.quat.z, bone.quat.w);
    
          //bone.irestQ = new THREE.Quaternion(bone.quat.x, bone.quat.y, bone.quat.z, -bone.quat.w).invert();
    
          const frameInA = new Ammo.btTransform();
          frameInA.setIdentity(); 
          frameInA.setOrigin(originA);
          //frameInA.setRotation(quat);
    
          const frameInB = new Ammo.btTransform();
          frameInB.setIdentity();
          frameInB.setOrigin(originB);
          frameInB.setRotation(quat);
    
          
         
          const quat2 = new Ammo.btQuaternion(0,0,0,1);
    
          bone.constraint = new Ammo.btGeneric6DofConstraint(parent.physicsBody, bone.physicsBody,  frameInA,  frameInB, true);
          const rotationalMotor= new Ammo.btRotationalLimitMotor();
    
          const minL = 0.0;
          const limitX =  this.getRagDollLimit( 0, bone.name);
          const limitZ =  this.getRagDollLimit( 2, bone.name);
    
          const loL = new Ammo.btVector3( -minL , -minL , -minL );
          const hiL = new Ammo.btVector3(  minL ,  minL , minL );
          const loA = new Ammo.btVector3( limitX[0] * 0.1, 0, limitZ[0] * 0.1);
          const hiA = new Ammo.btVector3( limitX[1] * 0.1, 0, limitZ[1] * 0.1);
    
          bone.constraint.setLinearLowerLimit( loL );
          bone.constraint.setLinearUpperLimit( hiL );
          bone.constraint.setAngularLowerLimit( loA );
          bone.constraint.setAngularUpperLimit( hiA );
    
          Ammo.destroy(loA);
          Ammo.destroy(hiA);
    
          Ammo.destroy(loL);
          Ammo.destroy(hiL);
    
          const erp = 0.8;
          const stop_erp = 0.8;
    
          const cfm = 0.1;
          const stop_cfm = 0.01;
    
          for(let i=0;i<6;i++) {
            
            bone.constraint.setParam(BT_CONSTRAINT_STOP_CFM, stop_cfm, i);
            bone.constraint.setParam(BT_CONSTRAINT_CFM, cfm, i);
    
            bone.constraint.setParam(BT_CONSTRAINT_STOP_ERP, stop_erp, i);
            bone.constraint.setParam(BT_CONSTRAINT_ERP, erp, i);
           
            if(i>=3){
    
              const limits = this.getRagDollLimit( i -3, bone.name);
    
             
              bone.constraint.getRotationalLimitMotor2(i, rotationalMotor);
              
              rotationalMotor.set_m_enableMotor(true);
              
              rotationalMotor.set_m_maxMotorForce(20000 * mass / 1000);
              rotationalMotor.set_m_maxLimitForce(20000 * mass / 1000);
              rotationalMotor.set_m_targetVelocity(20000 * mass / 1000);
              rotationalMotor.set_m_normalCFM(cfm);
              rotationalMotor.set_m_stopCFM(stop_cfm);
              rotationalMotor.set_m_stopERP(stop_erp);
              
              rotationalMotor.set_m_damping(0.2);
              rotationalMotor.set_m_limitSoftness(0.02);
              rotationalMotor.set_m_bounce(0.0);
    
              rotationalMotor.set_m_loLimit(limits[0]);
              rotationalMotor.set_m_hiLimit(limits[1]);
            
            }
          }
    
    
          /*
          bone.constraint = new Ammo.btConeTwistConstraint(parent.physicsBody, bone.physicsBody,  frameInA,  frameInB, true);
          bone.constraint.setLimit (0.01, 0.01, 0.01, 0.0, 0.1, 0.1);
          bone.constraint.setAngularOnly(true);
          bone.constraint.enableMotor(true);
          //bone.constraint.setMotorTarget (quat);
          bone.constraint.setMotorTargetInConstraintSpace (quat2);
          bone.constraint.setMaxMotorImpulse(200000);
          //bone.constraint.setMaxMotorImpulseNormalized(100.0);
          bone.constraint.setDamping  (0.2); //0.0008
          for(let i=0;i<6;i++) {
            
            bone.constraint.setParam(BT_CONSTRAINT_STOP_CFM, 0.0, i);
            bone.constraint.setParam(BT_CONSTRAINT_STOP_ERP, 1.0, i);
            
            //bone.constraint.enableSpring(i,  true);
            //bone.constraint.enableFeedback(i,  false);
    
            bone.constraint.setParam(BT_CONSTRAINT_CFM, 0.0, i);
            bone.constraint.setParam(BT_CONSTRAINT_ERP, 1.0, i);
    
            
          }
            */
    
          /*
          for(let i=0;i<3;i++) {
            
            bone.constraint.setParam(BT_CONSTRAINT_STOP_CFM, 0.0, i);
            bone.constraint.setParam(BT_CONSTRAINT_STOP_ERP, 1.0, i);
            
            bone.constraint.enableSpring(i,  true);
            bone.constraint.enableFeedback(i,  false);
    
            bone.constraint.setParam(BT_CONSTRAINT_CFM, 0.0, i);
            bone.constraint.setParam(BT_CONSTRAINT_ERP, 1.0, i);
    
            bone.constraint.setStiffness(i, 1000 ); //10000
            bone.constraint.setDamping  (i, 0.2); //0.0008
          }
    
          for(let i=3;i<6;i++) {
            
              bone.constraint.enableSpring(i,  true);
              bone.constraint.enableFeedback(i,  false);
    
              
              //if(bone.name.startsWith('leg')){
                //bone.constraint.setStiffness(i, 5000 ); //10000
                //bone.constraint.setDamping  (i, 1.0); //0.0008
              //}else
               if((bone.name.startsWith('arm'))||(bone.name.startsWith('farm'))){
                bone.constraint.setStiffness(i, 500 ); //10000
                bone.constraint.setDamping  (i, 0.01); //0.0008
    
                bone.constraint.setParam(BT_CONSTRAINT_STOP_CFM, 0.5, i);
                bone.constraint.setParam(BT_CONSTRAINT_STOP_ERP, 0.4, i);
      
                bone.constraint.setParam(BT_CONSTRAINT_CFM, 0.5, i);
                bone.constraint.setParam(BT_CONSTRAINT_ERP, 0.4, i);
    
              }else if((bone.name.startsWith('foot'))||(bone.name.startsWith('hand'))){
                bone.constraint.setStiffness(i, 2000 ); //10000
                bone.constraint.setDamping  (i, 0.01); //0.0008
    
                bone.constraint.setParam(BT_CONSTRAINT_STOP_CFM, 0.4, i);
                bone.constraint.setParam(BT_CONSTRAINT_STOP_ERP, 1.4, i);
      
                bone.constraint.setParam(BT_CONSTRAINT_CFM, 0.4, i);
                bone.constraint.setParam(BT_CONSTRAINT_ERP, 0.4, i);
              }else{
                bone.constraint.setStiffness(i, 18000 ); //10000
                bone.constraint.setDamping  (i, 0.02); //0.0008
    
                bone.constraint.setParam(BT_CONSTRAINT_STOP_CFM, 0.0, i);
                bone.constraint.setParam(BT_CONSTRAINT_STOP_ERP, 1.0, i);
      
                bone.constraint.setParam(BT_CONSTRAINT_CFM, 0.0, i);
                bone.constraint.setParam(BT_CONSTRAINT_ERP, 1.0, i);
              }
          }
          */
    
         
    
    
         
          this.physicsUniverse.addConstraint(bone.constraint, true);
          //bone.constraint.setEquilibriumPoint();
          //this.rigidBody_List.push(RBody);
    
          for(let child of bone.children){
            this.addPhyRagdollBone(char, bone, child, mass, size);
          }
    
          //Ammo.destroy(boneOffset);
          Ammo.destroy(frameInA);
          Ammo.destroy(frameInB);
          Ammo.destroy(originA);
          Ammo.destroy(originB);
        }
    
    findBone(bone, name) {

        if(bone.name === name)
            return bone;
        
        for(let c of bone.children) {
            const nc = this.findBone(c, name);
            if(nc !== null)
                return nc;
        }
        return null;
    }
 

    findSkelBone(skel, name){

        for(let c of skel.root.children){
            const nc = this.findBone(c, name);
            if(nc !== null)
                return nc;
        }

        return null;
    }

    addPhyRagdoll(character, id = null) {

      const root = character.skeleton.root;

      character.rootOffset = new THREE.Vector3(root.pos.x, root.pos.y, root.pos.z).multiplyScalar(character.skelScale);
      character.ro = new THREE.Vector3();
      character.iq = new THREE.Quaternion();
      character.rq = new THREE.Quaternion();

      character.to  = new THREE.Vector3();
      character.from  = new THREE.Vector3();

      this.buildSkelIds(root);

      character.p = new Ammo.btVector3(0, 0, 0);  
      character.q = new Ammo.btQuaternion(0, 0, 0, 1);

      character.netID = character.id;

      for(let a in character.anims){
        const anim = new SkelAnimation();
        anim.load(character.anims[a].bones);
        character.anims[a] = anim;
      }

      

      const rq = new THREE.Quaternion(root.quat.x, root.quat.y, root.quat.z, root.quat.w);
      const q = new THREE.Quaternion(character.quat.x, character.quat.y, character.quat.z, character.quat.w).multiply(rq);

      character.tmpP = new THREE.Vector3();
      character.avatarDef.attackOffset = character.attackOffset ? new THREE.Vector3(character.attackOffset.x, character.attackOffset.y, character.attackOffset.z) : null;
      character.pathFinder = new PathFinder(this.navmeshs.length);
      character.stopStand = 0;

      character.ppos = new THREE.Vector3(character.pos.x, character.pos.y, character.pos.z) 
      
      const origin = new Ammo.btVector3(character.pos.x + character.rootOffset.x, character.pos.y + character.rootOffset.y + character.avatarDef.height / 2, character.pos.z + character.rootOffset.z);
      const quat = new Ammo.btQuaternion(q.x, q.y, q.z, q.w);
      
      let shape= new Ammo.btSphereShape(0.5);

      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      const defaultMotionState = new Ammo.btDefaultMotionState(transform);
  
      const localInertia = new Ammo.btVector3(0, 0, 0);
      shape.calculateLocalInertia(0, localInertia);
      const RBody_Info = new Ammo.btRigidBodyConstructionInfo(character.staticroot === true ? 0 :character.mass, defaultMotionState, shape, localInertia);
      root.physicsBody = new Ammo.btRigidBody(RBody_Info);
      root.physicsBody.userData = { name: "npc-root", obj: root, char:character };

      root.ammoData = {localInertia: localInertia, motionState :defaultMotionState, shape: shape, transform: transform};
        
      root.physicsBody.setDamping(0.2, 1.0);

      const ori = new Ammo.btVector3(1.0, 1.0, 1.0);
      root.physicsBody.setAngularFactor(ori);

      this.physicsUniverse.addRigidBody(root.physicsBody, worldGroup, worldMask);
      
      for(let child of root.children){
        this.addPhyRagdollBone(character, root, child, character.mass / 8, character.skelScale);
      }

      this.ragdoll_List.push({  netID: character.netID, object: root, char : character});
      
      const phychar ={ id: character.id, tpos: new THREE.Vector3(), netID: character.netID, target:character.target, currentDialogLine: 0, currentDialogSubLine : 0,active : true, targetPos : null, ragdoll: true, skeleton: character.skeleton,  pos: new THREE.Vector3(character.pos.x, character.pos.y, character.pos.z ), rot: new THREE.Quaternion (character.rot.x, character.rot.y, character.rot.z, character.rot.w ), anims :[], ammoData: character.ammoData};

      if(id === null)
        this.npcs.push(phychar);
      else
        this.npcs[id] = phychar;
      

      Ammo.destroy(ori);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
    }

    catwalkRagDoll(bone, time, angle, speed){

      if(bone.name){
        if(bone.name.startsWith('farm')){
           const ar=bone.name.split('-');

          const a = Math.max(0, ar[1] === 'left' ? Math.cos(time * Math.PI * 2 / 2 * 0.5) * speed : -Math.cos(time * Math.PI * 2 / 2* 0.5 + Math.PI) * speed );
          const x = Math.sin(angle) * a;
          const z = Math.cos(angle) * a;
          this.tmpVec3.setValue(z , 0, x)

          bone.physicsBody.applyTorqueImpulse(this.tmpVec3);
        }

        if(bone.name.startsWith('fleg')){

          const ar=bone.name.split('-');

          const a = Math.max(0, ar[1] === 'left' ? Math.cos(time * Math.PI * 2 / 2 * 0.5) * speed : -Math.cos(time * Math.PI * 2 / 2* 0.5 + Math.PI) * speed );
          const x = Math.sin(angle) * a;
          const z = Math.cos(angle) * a;
          this.tmpVec3.setValue(z , 0, x)
          bone.physicsBody.applyTorqueImpulse(this.tmpVec3);
        }
      }



      /*
      if((bone.name === 'fleg-left')){

        const a = Math.max(0, Math.sin(time * Math.PI)) * speed ;

        const x = Math.sin(angle) * a;
        const z = Math.cos(angle) * a;
        const torque = new Ammo.btVector3( z, 0, x);
        bone.physicsBody.applyTorqueImpulse(torque);
        Ammo.destroy(torque);
      }

      if((bone.name === 'fleg-right')){

        const a = Math.max(0, Math.cos(time * Math.PI)) * speed ;

        const x = Math.sin(angle) * a;
        const z = Math.cos(angle) * a;
        const torque = new Ammo.btVector3( z, 0, x);
        bone.physicsBody.applyTorqueImpulse(torque);
        Ammo.destroy(torque);
      }
      */
      /*
      if((bone.name === 'fleg-right')||(bone.name === 'farm-right')){

        const a = Math.abs(Math.cos(time * Math.PI)) * speed ;

        const x = Math.sin(angle) * a;
        const z = Math.cos(angle) * a;
        const torque = new Ammo.btVector3( 0, z , x);
        bone.physicsBody.applyTorqueImpulse(torque);
        Ammo.destroy(torque);
      }
        */

      /*
      if(bone.name === 'fleg-right'){

        const x = Math.sin(angle) * speed * Math.abs(Math.cos(time * Math.PI));
        const z = Math.cos(angle) * speed * Math.abs(Math.cos(time * Math.PI));
        const torque = new Ammo.btVector3( z, 0, x);
        bone.physicsBody.applyTorqueImpulse(torque);
        Ammo.destroy(torque);
      }
      */

      for(let c of bone.children){
        this.catwalkRagDoll(c, time, angle, speed);
      }

    }

    walkRagDoll(character, root, time, angle, speed){

      /*
      root.ammoData.motionState.getWorldTransform(this.tmpTransformation);
      const new_pos = this.tmpTransformation.getOrigin();
      */

      const deltax = Math.sin(angle);
      const deltaz = Math.cos(angle);

      const deltaL = Math.sqrt(deltax * deltax + deltaz * deltaz);

      const dirx = deltax * speed / deltaL;
      const dirz = deltaz * speed / deltaL;
      character.p.setValue(dirx, 0 , dirz);
      //root.physicsBody.applyCentralImpulse( character.p);

      if((time % 4)>2)
      {
        const fl = this.findRagDollChild(root, 'foot-left');
        if((fl)&&(!fl.isBroken))
          fl.physicsBody.applyCentralImpulse( character.p);
      } else{
        const fr = this.findRagDollChild(root, 'foot-right');
        if((fr)&&(!fr.isBroken))
          fr.physicsBody.applyCentralImpulse( character.p);
      }
        

      /*
      if(bone.name === 'fleg-left'){

        const x = Math.sin(angle) * speed * Math.abs(Math.sin(time * Math.PI * 2 / 2));
        const z = Math.cos(angle) * speed * Math.abs(Math.sin(time * Math.PI * 2 / 2));
        const torque = new Ammo.btVector3( z, 0, x);
        bone.physicsBody.applyTorqueImpulse(torque);
        Ammo.destroy(torque);
      }

      if(bone.name === 'fleg-right'){

        const x = Math.sin(angle) * speed * Math.abs(Math.cos(time * Math.PI * 2 / 2));
        const z = Math.cos(angle) * speed * Math.abs(Math.cos(time * Math.PI * 2 / 2));
        const torque = new Ammo.btVector3( z, 0, x);
        bone.physicsBody.applyTorqueImpulse(torque);
        Ammo.destroy(torque);
      }

      for(let c of bone.children){
        this.walkRagDoll(c, time, angle, speed);
      }
      */
    }

    updatePhyRagdollBone(bone, ipq) {

      if(bone.physicsBody){
        const ms = bone.physicsBody.getMotionState();
        ms.getWorldTransform(this.tmpTransformation);

        const q = this.tmpTransformation.getRotation();
        const p = this.tmpTransformation.getOrigin();

        this.tmpQ.set(q.x(), q.y(), q.z(), q.w());
        this.tmpV1.set(p.x(), p.y(), p.z());
        bone.iq.set(this.tmpQ.x, this.tmpQ.y, this.tmpQ.z, this.tmpQ.w).invert();

        if(bone.life !== 0){
          this.tmpQ.premultiply(ipq);
        }

        bone.rot.x = this.tmpQ.x;
        bone.rot.y = this.tmpQ.y;
        bone.rot.z = this.tmpQ.z;
        bone.rot.w = this.tmpQ.w;

        if(bone.life === 0){
          bone.pos.x = this.tmpV1.x;
          bone.pos.y = this.tmpV1.y;
          bone.pos.z = this.tmpV1.z;
        }

        Ammo.destroy(q);
        Ammo.destroy(p);
      }
      
      for(let child of bone.children){
        this.updatePhyRagdollBone(child, bone.iq);
      }
    }

    freezeRagDoll(bone){

      this.tmpVec3.setValue(0.0, 0.0, 0.0);
      bone.physicsBody.setLinearVelocity(this.tmpVec3);
      bone.physicsBody.setAngularVelocity(this.tmpVec3);
      bone.physicsBody.clearForces();

      bone.ammoData.localInertia.setValue(0, 0, 0);
      bone.ammoData.shape.calculateLocalInertia(bone.mass, bone.ammoData.localInertia);
      bone.physicsBody.setMassProps(bone.mass,  bone.ammoData.localInertia);


      for(let c of bone.children){
        this.freezeRagDoll(c);
      }
    }

    pAngle(a){
      if(a < 0) 
        return PI2 - ((-a) % PI2);
      else 
        return a % PI2;

    }
    getAngleDirectionTo(angle, deltax, deltaz, angleThresold = 0.1){

      let angle2 = Math.atan2(deltax , deltaz);
      //const diff= angleDelta(angle, angle2);

      

      //const diff = smallestSignedAngleBetween(angle, angle2);
      angle = this.pAngle(angle);
      angle2 = this.pAngle(angle2);

      /*
      let d = angle2 - angle;
      if(d<0)
        d+=PI2;

      if(d>(Math.PI - 0.1))
        return 1;

      if(d<(Math.PI + 0.1))
        return -1;

      return 0;

      const sd = angle - angle2;
      const d = Math.abs(sd) % PI2; 
      
      const r = d > Math.PI ? PI2 - d : d;
      const sign = (sd >= 0 && sd <= Math.PI) || (sd <=-Math.PI && sd>= -PI2) ? 1 : -1; 
      const diff = r * sign;
      */

      //const diff = Math.atan2(Math.sin(angle2-angle), Math.cos(angle-angle2))
      //const diff = Math.abs(angle2 - angle);
      //const diff = ((angle2 - (angle + ((3 * Math.PI) / 2))) % (Math.PI * 2)) - Math.PI;

      /*
      let diff = (angle2 % Math.PI) - (angle % Math.PI)
      diff += (diff>Math.PI) ? -Math.PI*2 : (diff<-Math.PI) ? Math.PI * 2 : 0
      */

      /*    
      let diff = angle2 - angle;
      diff = this.amod(a + Math.PI, Math.PI*2) - Math.PI;

      let diff = Math.abs(angle2 - angle);
      if (diff > Math.PI) diff = Math.PI*2 - diff;
      diff = Math.sign(angle2 - angle) * diff;
      

      if(diff < -0.1)
        return -1;

      if(diff > 0.1)
        return 1;

      return 0;
      */

      const sforwardX = Math.sin(angle);
      const sforwardZ = Math.cos(angle);

      this.tmpV1.set(sforwardX, 0, sforwardZ).normalize();
      this.tmpV2.set(deltax, 0, deltaz).normalize();

      this.tmpQ.setFromUnitVectors( this.tmpV1,  this.tmpV2 );

      //console.log('getAngle ',this.tmpV1, this.tmpV2, this.tmpQ );
      /*
      if(Math.abs(this.tmpQ.w) < 0.1)
        return 0;
      */

      //console.log('angles '+angle+' '+angle2+' '+this.tmpQ.y);

      if(this.tmpQ.y<-0.1)
        return -1;

      if(this.tmpQ.y>0.1)
        return 1;

      return 0;
    }

    addAnim(phychar , anim){

      for(let a of phychar.anims){
        if(a.a === anim.a)
          return false;
      }
      phychar.anims.push(anim);
       return true;
    }

    
    doRagDollScript(character, phychar, dt, time){

      const root = character.skeleton.root;

      if(!root.ammoData)
        return true;

      root.ammoData.motionState.getWorldTransform(this.tmpTransformation);
      //const rootTForm = root.physicsBody.getWorldTransform();

      const new_pos = this.tmpTransformation.getOrigin();
      phychar.tpos.set(new_pos.x(), new_pos.y(), new_pos.z());

      if((character.actions['dialog'].state > 0) &&(character.scriptInstance.state !== 'dialog')){

        phychar.prevAnims = phychar.anims;
        character.scriptInstance.prevState = character.scriptInstance.state;
        character.scriptInstance.pauseTime = time;
        
        const deltax = character.actions['dialog'].targetCharPos.x - character.pos.x;
        const deltaz = character.actions['dialog'].targetCharPos.z - character.pos.z;
        character.angle = Math.atan2(deltax, deltaz);
        this.tmpEUL(0, character.angle , 0);
        this.tmpQ.setFromEuler(this.tmpEUL);

        character.q.setValue(this.tmpQ.x, this.tmpQ.y, this.tmpQ.z, this.tmpQ.w);
        this.tmpTransformation.setRotation(character.q);
        root.physicsBody.setWorldTransform(this.tmpTransformation);
        phychar.anims = [{a : character.avatarDef.talkAnim, t: time}];
        character.scriptInstance.state = 'dialog';
      }

      if((character.actions['dialog'].state == 0) && (character.scriptInstance.state == 'dialog')){

        phychar.anims = phychar.prevAnims;
        phychar.prevAnims = null;

        const pdt = time - character.scriptInstance.pauseTime;
        character.scriptInstance.time += pdt;
        character.scriptInstance.pauseTime = 0;
        character.scriptInstance.state = character.scriptInstance.prevState;
      }
   
      if(character.script){
        this.executeScript(character.script, character.scriptInstance, character, phychar, phychar.tpos, time);
      }

      if(character.avatarDef.speed ===0 ){

        const closestCar = this.findClosestCar(character.ppos, character.avatarDef.activation );
        
        if(closestCar != null){

          const deltax = closestCar.pos.x - character.ppos.x;
          const deltaz = closestCar.pos.z - character.ppos.z;

          if(((time - character.lastAttack)>character.avatarDef.attackTime)){
            const cdirx = Math.cos(character.angle);
            const cdirz = Math.sin(character.angle);
            const dot = cdirx * deltax + cdirz * deltaz;


            closestCar.ammoData.motionState.getWorldTransform(this.tmpTransformation2);
            const p = this.tmpTransformation2.getOrigin();
  
            const side = dot < 0.0 ? 0 : 1;
            this.ragdollAttack(character, side, p, character.attackForce);
            character.lastAttack = time;
            
          }
        }
        
      } else {

        if(character.currentTarget === null){
          const closestCar = this.findClosestCar(root.pos);
          if(closestCar !== null){
              
              const deltax = closestCar.pos.x - new_pos.x();
              const deltaz = closestCar.pos.z - new_pos.z();
              const deltaL = Math.sqrt(deltax * deltax + deltaz * deltaz);

              if(character.avatarDef.waveAnim){
                if((deltaL< character.avatarDef.activation * character.skelScale))
                  this.addAnim(phychar, {a : character.avatarDef.waveAnim, t : time, e : time + 2})
                else
                  this.removeAnim(phychar, character.avatarDef.waveAnim)
              }
          }
        }
    
        if((character.scriptInstance.state === 'move')||(character.scriptInstance.state === 'attack')){

          let deltax =0, deltaz = 0, angleDir = 0, deltaL = 0, minDist = 0;
          
          if((character.avatarDef.attackOffset !== null) &&(character.scriptInstance.state === 'attack')){

          

            if(character.currentTarget.ammoData){
              character.currentTarget.ammoData.motionState.getWorldTransform(this.tmpTransformation2);
              const p = this.tmpTransformation2.getOrigin();
              const q = this.tmpTransformation2.getRotation();

              const r = q.w();
              const i = q.x();
              const j = q.y();
              const k = q.z();

              const x = character.avatarDef.attackOffset.x * character.skelScale;
              const y = character.avatarDef.attackOffset.y * character.skelScale;
              const z = character.avatarDef.attackOffset.z * character.skelScale;
            
              const dx = (2*(r*z*j + i*z*k - r*y*k + i*y*j) + x*(r*r + i*i - j*j - k*k));
              const dy = (2*(r*x*k + i*x*j - r*z*i + j*z*k) + y*(r*r - i*i + j*j - k*k));
              const dz = (2*(r*y*i - r*x*j + i*x*k + j*y*k) + z*(r*r - i*i - j*j + k*k));
              const tpl = Math.sqrt(dx * dx + dz * dz);

              if(phychar.targetPos === null)
                phychar.targetPos = new THREE.Vector3();

              if(character.currentTarget.isStuck === true){
                phychar.targetPos.x = dx * 4+  p.x();
                phychar.targetPos.y = dy * 4 + p.y();
                phychar.targetPos.z = dz * 4 + p.z();

              }else{
                phychar.targetPos.x = dx+  p.x();
                phychar.targetPos.y = dy + p.y();
                phychar.targetPos.z = dz + p.z();
              }

              deltax = phychar.targetPos.x - new_pos.x();
              deltaz = phychar.targetPos.z - new_pos.z();
              deltaL = Math.sqrt(deltax * deltax + deltaz * deltaz);

              angleDir = this.getAngleDirectionTo(character.angle, deltax /deltaL, deltaz / deltaL,  0.3 );
        
              if(character.currentTarget.isStuck === false){
                if(((time - character.lastAttack)>character.avatarDef.attackTime)){
                  const cdirx = Math.cos(character.angle);
                  const cdirz = Math.sin(character.angle);
                  const dot = cdirx * dz + cdirz * dx;
        
                  if((deltaL< character.avatarDef.attackOffset.length() * 1.5 * character.skelScale)) {
                      const side = dot < 0.0 ? 0 : 1;
                      this.ragdollAttack(character, side, p, character.attackForce);
                      character.lastAttack = time;
                  }else{
        
                    if(phychar.prevAnims){
                      phychar.anims = phychar.prevAnims;
                      phychar.prevAnims = null;
                    }
              
                    const pdt = time - character.scriptInstance.pauseTime;
                    character.scriptInstance.time += pdt;
                    character.scriptInstance.pauseTime = 0;
                    character.scriptInstance.state = character.scriptInstance.prevState && character.scriptInstance.prevState.length>0?character.scriptInstance.prevState:"move";
                    character.scriptInstance.prevState="attack";
                  }
                }
              }
              
              if(deltaL> character.avatarDef.deactivation) {
                character.scriptInstance.state = '';
                character.scriptInstance.prevState = '';
                character.currentTarget = null;
                character.moving = false;
              }
              
              Ammo.destroy(q);
              Ammo.destroy(p);
            }

            minDist = 4;
            //Ammo.destroy(tform);
          }else if(phychar.targetPos !== null){

            deltax = phychar.targetPos.x - new_pos.x();
            deltaz = phychar.targetPos.z - new_pos.z();

            deltaL = Math.sqrt(deltax * deltax + deltaz * deltaz);

            angleDir = this.getAngleDirectionTo(character.angle, deltax /deltaL, deltaz / deltaL,  0.3 );
            minDist = 8;
          }

          if(angleDir !== 0){
            //console.log('turn '+character.name+' '+angleDir);
            character.angle += (angleDir * 0.8 * dt);
            character.moving = false;
          } else {

            if(deltaL> minDist) {

              character.angle = Math.atan2(deltax, deltaz);
              character.moving = true;
              phychar.anims = [{a : character.avatarDef.walkAnim, t: time}];;

            } else if(character.scriptInstance.state === 'move'){

              if(character.moving === true){
                character.p.setValue(0.0, 0.0, 0.0);
                root.physicsBody.setLinearVelocity(character.p);
                root.physicsBody.setAngularVelocity(character.p);
                root.physicsBody.clearForces();
              }

              if(character.scriptInstance.prevState === 'attack'){
                phychar.anims =[];
                character.scriptInstance.state = character.scriptInstance.prevState;
                character.scriptInstance.prevState = 'move';
              }else{
                character.moving = false;
                this.addAnim(phychar,{a : character.avatarDef.idleAnim, t: time});
                phychar.targetPos = null;

              }
            }
            
            /*
            if(character.turning !== 0)
              this.freezeRagDoll(root);
            */
            
          }
            
        
          /*
          if(character.name === 'cat')
            console.log('angle '+character.name, this.tmpQ);
          */
          if(!root.physicsBody){
            return false;
          }

          if((character.moving === true)&&(phychar.targetPos !== null)){

            deltax = phychar.targetPos.x - new_pos.x();
            deltaz = phychar.targetPos.z - new_pos.z();

            deltaL = Math.sqrt(deltax * deltax + deltaz * deltaz);
            this.tmpVec3.setValue(deltax* character.avatarDef.speed / deltaL, 0, deltaz * character.avatarDef.speed / deltaL);

            this.moveRagdollBone(character.skeleton.root, this.tmpVec3, ["foot","hand"]);
          }
  
          character.turning = angleDir;
          this.tmpEUL.set(0, character.angle, 0);
          this.tmpQ.setFromEuler(this.tmpEUL);
          character.q.setValue(this.tmpQ.x, this.tmpQ.y, this.tmpQ.z, this.tmpQ.w);
          this.tmpTransformation.setRotation(character.q);
          root.physicsBody.setWorldTransform(this.tmpTransformation);
          root.ammoData.motionState.setWorldTransform(this.tmpTransformation);

        }
      }

      return true;



      //Ammo.destroy(rootTForm);
      //Ammo.destroy(new_pos);
    }

    unstuckBones(bone){

      if(bone.physicsBody){
        const t = bone.physicsBody.getWorldTransform();
        const p =t.getOrigin();

        const rayCallBack = Ammo.castObject(  this.closestRayResultCallback, Ammo.RayResultCallback );
        rayCallBack.set_m_closestHitFraction( 1 );
        rayCallBack.set_m_collisionObject( null );
        rayCallBack.set_m_collisionFilterGroup(groundGroup);
        rayCallBack.set_m_collisionFilterMask(groundMask);


        this.btFrom.setValue( p.x(), p.y() + 5, p.z() );
        this.btTo.setValue( p.x(), p.y() - 5, p.z() );
        this.closestRayResultCallback.get_m_rayFromWorld().setValue(  p.x(), p.y() + 5, p.z() );
        this.closestRayResultCallback.get_m_rayToWorld().setValue( p.x(), p.y() - 5, p.z() );

      // Perform ray test
        this.physicsUniverse.rayTest( this.btFrom, this.btTo,  this.closestRayResultCallback );

        const f = rayCallBack.get_m_closestHitFraction();
        if ( f<1 ) {
          const point = this.closestRayResultCallback.get_m_hitPointWorld();
          if(p.y()-bone.size.y < point.y()){
            p.setValue(p.x(), point.y() + bone.size.y, p.z());
            t.setOrigin(p);
            bone.physicsBody.setWorldTransform(t);
          }
        }

                //console.log(i+' '+pixel);

        /*
        this.from.set( p.x(), p.y() + 50, p.z() );
        this.to.set( 0, -1.0, 0 );
        this.raycaster.set(this.from, this.to);
  
        this.pts = [];
  
        if(this.tGroundMesh)
          this.tGroundMesh.raycast(this.raycaster, this.pts);
        if(this.pts.length >0) {
  
          const a = this.pts[0].point;
  
          if(p.y()-bone.size.y < a.y){
            p.setValue(p.x(), a.y + bone.size.y, p.z());
            t.setOrigin(p);
            bone.physicsBody.setWorldTransform(t);
          }
        }
        */
  
        Ammo.destroy(p);
        Ammo.destroy(t);
      }


      for(let c =0;c<bone.children.length;c++){
        this.unstuckBones(bone.children[c]);
      }

    }


    updatePhyRagdoll(character, dt,  time) {
      const root = character.skeleton.root;
      const phychar = this.getNPC(character.netID);
      if(!phychar.active)
        return true;

      if(!this.doRagDollScript(character, phychar, dt, time))
        return false;



      for(let a of phychar.anims){

        if(character.anims[a.a]){
          
            this.applyRagDollAnimation(character, character.anims[a.a], time- a.t);

          
        }else if(a.a === "ragdollwalk"){
          this.walkRagDoll(character, root, (time - a.t) * 3, character.angle, character.avatarDef.speed);
        }else if((a.a === "catwalk")&&(character.moving === true)){
          this.catwalkRagDoll(root, time - a.t, character.angle, 10000);
        }else if(a.a === "turnl"){
          this.turnLRagDoll(root, time - a.t, character.angle, 50);
        }else if(a.a === "turnr"){
          this.turnRRagDoll(root, time - a.t, character.angle, 50);
        }
      }

      const rootTForm = root.physicsBody.getWorldTransform();
      const new_quat = rootTForm.getRotation();
      const new_pos = rootTForm.getOrigin();

      character.rq.set(new_quat.x(), new_quat.y(), new_quat.z(), new_quat.w());
      character.ro.set(character.rootOffset.x, character.rootOffset.y, character.rootOffset.z).applyQuaternion(character.rq);
   
      character.ppos.x = new_pos.x() - character.ro.x;
      character.ppos.y = new_pos.y() - character.ro.y;
      character.ppos.z = new_pos.z() - character.ro.z;
 
      character.prot.x = new_quat.x();
      character.prot.y = new_quat.y();
      character.prot.z = new_quat.z();
      character.prot.w = new_quat.w();

      character.iq.set(character.prot.x, character.prot.y, character.prot.z, character.prot.w).invert();

      for(let child of root.children){
        this.updatePhyRagdollBone(child, character.iq);
      }

      if(this.tGroundMesh){

        const foots = ['foot-left','foot-right', 'leg-left', 'hip-right', 'hip-left', 'leg-right', 'fleg-left', 'fleg-right', 'hand-left','hand-right', 'head'];
        const pts = [];

        for(let foot of foots){
          const footbone = this.findRagDollChild(character.skeleton.root, foot);
          if((footbone == null)||(footbone.physicsBody === null))
            continue;
          
          const t = footbone.physicsBody.getWorldTransform();
          const p =t.getOrigin();

          const rayCallBack = Ammo.castObject(  this.closestRayResultCallback, Ammo.RayResultCallback );
          rayCallBack.set_m_closestHitFraction( 1 );
          rayCallBack.set_m_collisionObject( null );
          rayCallBack.set_m_collisionFilterGroup(groundGroup);
          rayCallBack.set_m_collisionFilterMask(groundMask);


          this.btFrom.setValue( p.x(), p.y() + 5, p.z() );
          this.btTo.setValue( p.x(), p.y() - 5, p.z() );
          this.closestRayResultCallback.get_m_rayFromWorld().setValue(  p.x(), p.y() + 5, p.z() );
          this.closestRayResultCallback.get_m_rayToWorld().setValue( p.x(), p.y() - 5, p.z() );

        // Perform ray test
          this.physicsUniverse.rayTest( this.btFrom, this.btTo,  this.closestRayResultCallback );

          const f = rayCallBack.get_m_closestHitFraction();
          if ( f<1 ) {
              const point = this.closestRayResultCallback.get_m_hitPointWorld();
              if(p.y()< point.y() + footbone.radius){
                p.setValue(p.x(),  point.y() + footbone.radius, p.z());
                t.setOrigin(p);
                footbone.physicsBody.setWorldTransform(t);
              }
          }

          /*
          character.from .set( p.x(), p.y() + 50, p.z() );
          character.to.set( 0, -1.0, 0 );
          this.raycaster.set(character.from, character.to);
          this.tGroundMesh.raycast(this.raycaster, pts);
        


          if(pts.length >0) {

            const a = pts[0].point;

            if(p.y()-footbone.radius< a.y){
              p.setValue(p.x(), a.y + footbone.radius, p.z());
              t.setOrigin(p);
              footbone.physicsBody.setWorldTransform(t);
            }
          }
          */
          Ammo.destroy(p);
          Ammo.destroy(t);
          
        }
        
        if(time > character.stopStand ){


          if(character.stopStand>0){
            root.ammoData.localInertia.setValue(0,0,0);
            root.ammoData.shape.calculateLocalInertia(0, root.ammoData.localInertia);
            root.physicsBody.setMassProps(character.mass, root.ammoData.localInertia);
            
            root.physicsBody.setAngularVelocity(this.zVec);
            root.physicsBody.setDamping(0.1, 0.8);

            character.stopStand = 0;
          }

          const t = root.physicsBody.getWorldTransform();
          const q = t.getRotation();
          q.setValue(0, q.y(), 0, q.w());
          t.setRotation(q);
          root.physicsBody.setWorldTransform(t);

          Ammo.destroy(t);
          Ammo.destroy(q);
        }


        const rayCallBack = Ammo.castObject(  this.closestRayResultCallback, Ammo.RayResultCallback );
        rayCallBack.set_m_closestHitFraction( 1 );
        rayCallBack.set_m_collisionObject( null );
        rayCallBack.set_m_collisionFilterGroup(groundGroup);
        rayCallBack.set_m_collisionFilterMask(groundMask);

        this.btFrom.setValue( character.ppos.x, character.ppos.y + 5, character.ppos.z );
        this.btTo.setValue( character.ppos.x, character.ppos.y - 5, character.ppos.z );
        this.closestRayResultCallback.get_m_rayFromWorld().setValue(  character.ppos.x, character.ppos.y + 5, character.ppos.z );
        this.closestRayResultCallback.get_m_rayToWorld().setValue( character.ppos.x, character.ppos.y - 5, character.ppos.z );

      // Perform ray test
        this.physicsUniverse.rayTest( this.btFrom, this.btTo,  this.closestRayResultCallback );

        const f = rayCallBack.get_m_closestHitFraction();
        if ( f<1 ) {
            const point = this.closestRayResultCallback.get_m_hitPointWorld();        

            const rootPosY = new_pos.y();
            const minRootPosY = (point.y() + character.rootOffset.y);
            const vminRootPosY = (point.y() + character.rootOffset.y * 0.15);
            if(rootPosY < minRootPosY) {
                const dd = Math.abs((minRootPosY - rootPosY) / character.rootOffset.y);
                character.pushUp = 600 * dd;
            }else
              character.pushUp = 0;
            
            character.tmpP.set(0 , character.pushUp , 0)
            this.addRagdollImp(character, character.tmpP);
        }
        /*
        character.from.set( character.ppos.x, character.ppos.y + 50, character.ppos.z );
        character.to.set( 0, -100.0, 0 );
        this.raycaster.set(character.from, character.to);
        this.tGroundMesh.raycast(this.raycaster, pts);
        //const pts = this.raycaster.intersectObjects([this.tGroundMesh]);
        if(pts.length >0) {
          const a = pts[0].point;
          const rootPosY = new_pos.y();
          const minRootPosY = (a.y + character.rootOffset.y);
          const vminRootPosY = (a.y + character.rootOffset.y * 0.15);
          if(rootPosY < minRootPosY) {
              const dd = Math.abs((minRootPosY - rootPosY) / character.rootOffset.y);
              character.pushUp = 600 * dd;
          }else
            character.pushUp = 0;
          
          character.tmpP.set(0 , character.pushUp , 0)
          this.addRagdollImp(character, character.tmpP);
        } 
          */
      }else{
        const cposY = new_pos.y();
        const minY = (character.avatarDef.height * character.skelScale / 2.0) + 1.0;

        if( cposY <minY){

          const diff = ( minY - cposY)   /  (minY );
          
          if(character.pushUp< 40)
            character.pushUp += diff;
          else
            character.pushUp = 40;

            character.tmpP.set(0 , character.pushUp , 0)
            this.addRagdollImp(character, character.tmpP);

        }else{

          if(character.pushUp>0)
            character.pushUp -= 0.02;
          else
            character.pushUp = 0;
        }
      }


      Ammo.destroy(new_quat);
      Ammo.destroy(new_pos);
      Ammo.destroy(rootTForm);

       return true;
    }


    findRagDollChild(node, name){
      if(node.name === name)
        return node;

      for(let child of node.children){
        const ret = this.findRagDollChild(child, name);
        if(ret !== null)
            return ret;
      }
      return null;
    }

    moveRagdollBone(bone, dir, boneNames){

      if(bone.name){
        for(let i=0;i<boneNames.length;i++){
          if(bone.name.startsWith(boneNames[i])){
            bone.physicsBody.applyCentralImpulse(dir);
          }
        }
      }

      for(let i=0;i<bone.children.length;i++){
        this.moveRagdollBone(bone.children[i], dir,boneNames);
      }
    }

    addRagdollImp(char, pos){

      const root = char.skeleton.root;
      if(!root.physicsBody)
        return;

      /*
      const ori = new Ammo.btQuaternion(0,0,0,1);
      const tform = root.physicsBody.getWorldTransform();
      tform.setRotation(ori);
      
      root.physicsBody.setWorldTransform(tform);
      const motionState = root.physicsBody.getMotionState();
      motionState.setWorldTransform(tform);
      Ammo.destroy(ori);
      */
 
      
      this.tmpVec3.setValue(pos.x, pos.y * char.mass / 200, pos.z);

      const node = this.findRagDollChild(root, 'head');

      const ms = root.physicsBody.getMotionState();
      ms.getWorldTransform(this.tmpTransformation);
      const q = this.tmpTransformation.getRotation();
      q.setValue(q.x() * 0.9, q.y(), q.z() * 0.9, q.w());
      this.tmpTransformation.setRotation(q);
      ms.setWorldTransform(this.tmpTransformation);

      const v = root.physicsBody.getAngularVelocity();
      v.setValue(v.x() * 0.9, v.y(), v.z() * 0.9);
      root.physicsBody.setAngularVelocity(v)


      root.physicsBody.applyCentralImpulse(this.tmpVec3);

      if((node)&&(!node.isBroken))
        node.physicsBody.applyCentralImpulse(this.tmpVec3);

      /*
      const fl = this.findRagDollChild(root, 'fleg-left');
      if((fl)&&(!fl.isBroken))
        fl.physicsBody.applyCentralImpulse(this.tmpVec3);

      const fr = this.findRagDollChild(root, 'fleg-right');
      if((fr)&&(!fr.isBroken))
        fr.physicsBody.applyCentralImpulse(this.tmpVec3);
      */

    }


    setRagdollPos(char, pos){

      const root = char.skeleton.root;
      if(!root.physicsBody)
        return;

      const origin = new Ammo.btVector3(char.rootOffset.x + pos.x, char.rootOffset.y +pos.y, char.rootOffset.z +pos.z);

      this.tmpTransformation.setIdentity();
      this.tmpTransformation.setOrigin(origin);
      
      root.physicsBody.setWorldTransform( this.tmpTransformation );
      const motionState = root.physicsBody.getMotionState();
      motionState.setWorldTransform(this.tmpTransformation);
      Ammo.destroy(origin);
    }

    addPhySpiderBone(spider, bone, mass, type, pos = null){

      const bpos = pos === null ? bone.pos : {x:  bone.pos.x+ pos.x, y: bone.pos.y+ pos.y, z: bone.pos.z+ pos.z};

      const sZ = (type === 0) ? bone.size :bone.size.x.toFixed(2)+','+bone.size.y.toFixed(2)+','+bone.size.z.toFixed(2);
      //console.log('add spider bone ['+ bone.pos.x.toFixed(2)+','+bone.pos.y.toFixed(2)+','+bone.pos.z.toFixed(2)+'] |  ['+ sZ+'] | ['+ bone.rot.x.toFixed(2)+','+bone.rot.y.toFixed(2)+','+bone.rot.z.toFixed(2)+','+bone.rot.w.toFixed(2)+'] | ['+ pos.x.toFixed(2)+','+pos.y.toFixed(2)+','+pos.z.toFixed(2)+ '] '+bone.name);


      const origin = new Ammo.btVector3(bpos.x, bpos.y, bpos.z);
      const quat = new Ammo.btQuaternion(bone.rot.x, bone.rot.y, bone.rot.z, bone.rot.w);

      let shape;

      if(type === 0)
        shape = new Ammo.btSphereShape(bone.size * 1.4);
      else {

        const size = new Ammo.btVector3(bone.size.x, bone.size.y, bone.size.z);
        shape = new Ammo.btBoxShape(size);
        Ammo.destroy(size);
      }
        
  
      bone.ae = new THREE.Euler( 0, 0, 0);
      bone.aq = new THREE.Quaternion();
      bone.aaq = new Ammo.btQuaternion(0, 0, 0, 1);
      bone.life = 100;
      bone.inContact = false;
      bone.newContact = false;
      bone.isInContact = false;
      bone.lastSnd = 0;

      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.setOrigin(origin);
      //transform.setRotation(quat);
      const defaultMotionState = new Ammo.btDefaultMotionState(transform);
  
      const localInertia = new Ammo.btVector3(0, 0, 0);
      shape.calculateLocalInertia(mass, localInertia);
  
      const RBody_Info = new Ammo.btRigidBodyConstructionInfo(mass, defaultMotionState, shape, localInertia);
      const RBody = new Ammo.btRigidBody(RBody_Info);
      RBody.userData = { name: bone.name, obj: bone, spider : spider, taken : false };
  
      bone.physicsBody = RBody;
      this.physicsUniverse.addRigidBody(RBody, worldGroup, worldMask | carMask);
      this.rigidBody_List.push(RBody);
  
      bone.ammoData = { localInertia: localInertia, motionState: defaultMotionState, transform: transform, geometry : shape };
  
      Ammo.destroy(RBody_Info);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
    }

    
    addSndBit(sndBits, snd){
      let smask = 0;
      for(let i=0;i<32;i+=4){
        const mask = 0xF << i;
        if((sndBits & mask) == 0){
          smask |= ((snd & 0xF)<<i);
          break;
        }
      }
      return sndBits | smask;
    }



    
    addPhySpider(spider, pos) {

      spider.shots = [];
      spider.pshots = [];
      spider.proj = [];
      spider.lastShotId = 1;
      spider.lastShot = 0;
      spider.curProj = 0;
      spider.active = false;
      spider.sndBits = 0;

      spider.sforward = new THREE.Vector3();
      spider.toCar = new THREE.Vector3();
      spider.nForward = new THREE.Vector3();
      spider.dirAngle = new THREE.Quaternion();
      

      spider.torque = new Ammo.btVector3(0, 0, 0);
      spider.move = new Ammo.btVector3(0, 0, 0);

      spider.pt = new THREE.Vector3();
      spider.stv = new THREE.Vector3();
      spider.sv = new Ammo.btVector3(0, 0, 0);

      const skel  = spider.skel;
      const root = skel.root;
      
      this.buildSkelIds(root);
     
      this.addPhySpiderBone(spider, root, 1000, 0, pos);

      //root.physicsBody.setDamping(0.00, 1.0);

      root.physicsBody.setDamping(0.2, 0.8);

      const fac = new Ammo.btVector3(0.1 , 1 , 0.1);
      root.physicsBody.setAngularFactor(fac);

      for(let child of root.children){

        
        if(child.name.startsWith('joint-cannon')){

            const joint = child;
            const leg = joint.children[0];

            this.addPhySpiderBone(spider, leg, 1, 1, pos);

            leg.physicsBody.setDamping(0.1, 0.1);

            const originA = new Ammo.btVector3(joint.pos.x * 1.1, joint.pos.y * 1.1, joint.pos.z * 1.1);
            const originB = new Ammo.btVector3(0, 0, -leg.size.z * 1.1);

            const newCanon = { pt : {x : joint.pos.x, y : joint.pos.y, z : joint.pos.z + leg.size.z * 2 }};
            spider.proj.push(newCanon);
            
            const frameInA = new Ammo.btTransform();
            frameInA.setIdentity(); 
            frameInA.setOrigin(originA);
  
            const frameInB = new Ammo.btTransform();
            frameInB.setIdentity();
            frameInB.setOrigin(originB);
              
            leg.constraint = new Ammo.btGeneric6DofConstraint(root.physicsBody, leg.physicsBody,  frameInA,  frameInB, true);

            for(let i=3;i<6;i++) {
              leg.constraint.setParam(BT_CONSTRAINT_STOP_CFM, 0.0, i);
              leg.constraint.setParam(BT_CONSTRAINT_CFM, 0.0, i);
              leg.constraint.setParam(BT_CONSTRAINT_STOP_ERP, 1.0, i);
              leg.constraint.setParam(BT_CONSTRAINT_ERP, 1.0, i);

            }
      
            for(let i=0;i<3;i++) {
              leg.constraint.setParam(BT_CONSTRAINT_STOP_CFM, 0.0, i);
              leg.constraint.setParam(BT_CONSTRAINT_CFM, 0.0, i);
              leg.constraint.setParam(BT_CONSTRAINT_STOP_ERP, 1.0, i);
              leg.constraint.setParam(BT_CONSTRAINT_ERP, 1.0, i);
            }

            const loL = new Ammo.btVector3( 0 , 0 , 0 );
            const hiL = new Ammo.btVector3( 0 , 0 , 0 );
            leg.constraint.setLinearLowerLimit( loL );
            leg.constraint.setLinearUpperLimit( hiL );

            const loA = new Ammo.btVector3( 0, 0, 0 );
            const hiA = new Ammo.btVector3( 0, 0, 0 );
            leg.constraint.setAngularLowerLimit( loA );
            leg.constraint.setAngularUpperLimit( hiA );

            this.physicsUniverse.addConstraint(leg.constraint, true);
          }

          if(child.name.startsWith('joint-leg')){
            const joint = child;
            const leg = joint.children[0];
            const leg2 = leg.children[0];

            this.addPhySpiderBone(spider, leg, 20, 1, pos);
            
            const originA = new Ammo.btVector3(joint.pos.x, joint.pos.y, joint.pos.z);
            const originB = (leg.name.startsWith('leg-l')) ? new Ammo.btVector3(-leg.size.x, 0, 0) : new Ammo.btVector3( leg.size.x, 0, 0) ;

            const frameInA = new Ammo.btTransform();
            frameInA.setIdentity(); 
            frameInA.setOrigin(originA);
  
            const frameInB = new Ammo.btTransform();
            frameInB.setIdentity();
            frameInB.setOrigin(originB);
              
            leg.constraint = new Ammo.btGeneric6DofSpringConstraint(root.physicsBody, leg.physicsBody,  frameInA,  frameInB, true);

            for(let i=3;i<6;i++) {
              //leg.constraint.setParam(BT_CONSTRAINT_STOP_CFM, 0.00001, i);
              leg.constraint.setParam(BT_CONSTRAINT_CFM, 0.2, i);
              //leg.constraint.setParam(BT_CONSTRAINT_STOP_ERP, 0.6, i);
              leg.constraint.setParam(BT_CONSTRAINT_ERP, 0.2, i);

              leg.constraint.setStiffness(i, 14000 ); //10000
              leg.constraint.setDamping  (i, 2.0); //0.0008
            }
      
            for(let i=0;i<3;i++) {
              //leg.constraint.setParam(BT_CONSTRAINT_STOP_CFM, 0.2, i);
              leg.constraint.setParam(BT_CONSTRAINT_CFM, 0.0, i);
              //leg.constraint.setParam(BT_CONSTRAINT_STOP_ERP, 0.5, i);
              leg.constraint.setParam(BT_CONSTRAINT_ERP, 1.0, i);
            }

            const loL = new Ammo.btVector3( 0 , 0 , 0 );
            const hiL = new Ammo.btVector3( 0 , 0 , 0 );
            leg.constraint.setLinearLowerLimit( loL );
            leg.constraint.setLinearUpperLimit( hiL );

            const eul = new THREE.Euler(0,0,0, "ZYX").setFromQuaternion(leg.rot);
            const loA = new Ammo.btVector3( eul.x, -eul.y, -eul.z);
            const hiA = new Ammo.btVector3( eul.x, -eul.y, -eul.z);
            leg.constraint.setAngularLowerLimit( loA );
            leg.constraint.setAngularUpperLimit( hiA );

            //console.log('add spider constraint ['+ originA.x().toFixed(2)+','+originA.y().toFixed(2)+','+originA.z().toFixed(2)+'] | ['+ originB.x().toFixed(2)+','+originB.y().toFixed(2)+','+originB.z().toFixed(2)+'] | ['+ eul.x.toFixed(2)+','+eul.y.toFixed(2)+','+eul.z.toFixed(2)+ '] '+leg.name);

            
            this.physicsUniverse.addConstraint(leg.constraint, true);
            leg.constraint.setEquilibriumPoint();
            
            if(leg2){

              const arr = leg2.name.split('-');
              const id = parseInt(arr[2]) - 1;
              const side = arr[1];

              this.addPhySpiderBone(spider, leg2, 30, 1, pos);

              const originA = new Ammo.btVector3(leg2.pos.x * 1.1, leg2.pos.y* 1.1, leg2.pos.z* 1.1);

              let originB; 
              if(leg2.name.startsWith('fleg-l'))
                 originB = new Ammo.btVector3( -leg2.size.x* 1.1, 0, 0) ;
              else
                 originB = new Ammo.btVector3( leg2.size.x* 1.1, 0, 0) ;
  
              const frameInA = new Ammo.btTransform();
              frameInA.setIdentity(); 
              frameInA.setOrigin(originA);
    
              const frameInB = new Ammo.btTransform();
              frameInB.setIdentity();
              frameInB.setOrigin(originB);

              leg2.physicsBody.setFriction(1.0);
            
              leg2.constraint = new Ammo.btGeneric6DofSpringConstraint(leg.physicsBody, leg2.physicsBody,  frameInA,  frameInB, true);


              for(let i=3;i<6;i++) {
                //leg2.constraint.setParam(BT_CONSTRAINT_STOP_CFM, 0.00001, i);
                leg2.constraint.setParam(BT_CONSTRAINT_CFM, 0.2, i);
                //leg2.constraint.setParam(BT_CONSTRAINT_STOP_ERP, 0.6, i);
                leg2.constraint.setParam(BT_CONSTRAINT_ERP, 0.2, i);
  
                leg.constraint.setStiffness(i, 8000 ); //10000
                leg.constraint.setDamping  (i, 1.0); //0.0008
              }
        
              for(let i=0;i<3;i++) {
                //leg2.constraint.setParam(BT_CONSTRAINT_STOP_CFM, 0.2, i);
                leg2.constraint.setParam(BT_CONSTRAINT_CFM, 0.0, i);
                //leg2.constraint.setParam(BT_CONSTRAINT_STOP_ERP, 0.5, i);
                leg2.constraint.setParam(BT_CONSTRAINT_ERP, 1.0, i);
              }

              const loL = new Ammo.btVector3( 0 , 0 , 0 );
              const hiL = new Ammo.btVector3( 0 , 0 , 0 );
              leg2.constraint.setLinearLowerLimit( loL );
              leg2.constraint.setLinearUpperLimit( hiL );


              const eul = new THREE.Euler(0,0,0, "XYZ").setFromQuaternion(leg2.rot);

              const min = side == 'r' ? -1.6 : 1.0;
              const max = side == 'r' ? -1.0 : 1.6;
              

              if(((id == 0)||(id == 3))){
                const loA = new Ammo.btVector3( 0, 0, min);
                const hiA = new Ammo.btVector3( 0, 0, max);
                leg2.constraint.setAngularLowerLimit( loA );
                leg2.constraint.setAngularUpperLimit( hiA );
              }else{
                const loA = new Ammo.btVector3( 0, min, 0);
                const hiA = new Ammo.btVector3( 0, max, 0);
                leg2.constraint.setAngularLowerLimit( loA );
                leg2.constraint.setAngularUpperLimit( hiA );
              }

              //console.log('add spider constraint 2 ['+ originA.x().toFixed(2)+','+originA.y().toFixed(2)+','+originA.z().toFixed(2)+'] | ['+ originB.x().toFixed(2)+','+originB.y().toFixed(2)+','+originB.z().toFixed(2)+'] | ['+ loA.x().toFixed(2)+','+loA.y().toFixed(2)+','+loA.z().toFixed(2)+'] | ['+ hiA.x().toFixed(2)+','+hiA.y().toFixed(2)+','+hiA.z().toFixed(2)+ '] '+leg2.name);

              this.physicsUniverse.addConstraint(leg2.constraint, true);

              leg2.constraint.setEquilibriumPoint();
            }
         }
      }
    }

    ragdollAttack(char, side, targetPos, speed){

      if(!char.avatarDef.attackLimb)
        return;

      for(let l of char.avatarDef.attackLimb){
        const boneName = l === 'head' ? 'head' : side === 0 ? l+'-left' : l+'-right';
        const bone = this.findRagDollChild(char.skeleton.root, boneName);
        if((!bone)||(bone.isBroken))
            return;
        
        bone.ammoData.motionState.getWorldTransform(this.tmpTransformation3);
        const p = this.tmpTransformation3.getOrigin();
  
        const dx = targetPos.x() - p.x();
        const dy = targetPos.y() - p.y();
        const dz = targetPos.z() - p.z();
  
        const d = Math.sqrt(dx * dx + dy * dy + dz* dz);
        
        const vec = new Ammo.btVector3(dx * speed / d, dy * speed / d, dz * speed / d);
        bone.physicsBody.applyCentralImpulse(vec);
  
        Ammo.destroy(p);
      }


    }

    createGroundCube(minY, height = 1){
        const h2 = height / 2;
        const size = new Ammo.btVector3(1000, h2 , 1000 );
        const objGeometry = new Ammo.btBoxShape(size);
        objGeometry.setMargin(0.01);
    
        const origin = new Ammo.btVector3(0, minY - h2, 0);
        const quat = new Ammo.btQuaternion(0, 0, 0, 1);
    
        const transform = new Ammo.btTransform();
        transform.setIdentity();
        transform.setOrigin(origin);
        transform.setRotation(quat);
        const defaultMotionState = new Ammo.btDefaultMotionState(transform);
        const localInertia = new Ammo.btVector3(0, 0, 0);
        objGeometry.calculateLocalInertia(0, localInertia);
        const RBody_Info = new Ammo.btRigidBodyConstructionInfo(0, defaultMotionState, objGeometry, localInertia);
        
        const RBody = new Ammo.btRigidBody(RBody_Info);
        RBody.setFriction(0.0);
        RBody.setRestitution(0.0);
        RBody.setDamping(0,0);
        RBody.userData = { name: "groundcube", obj: null };
    
        this.groundCube = {physicsBody : RBody, ammoData: { localInertia: localInertia, shape : objGeometry, motionState: defaultMotionState, transform: transform}};
        this.physicsUniverse.addRigidBody( this.groundCube.physicsBody, worldGroup, worldMask);

        Ammo.destroy(origin);
        Ammo.destroy(quat);
        Ammo.destroy(size);
    }
    
    addPhyTrack(track) {
      const self = this;

      this.lapTime = track.lapTime;
      this.navmeshs = [];
      
      for(let n=0;n<track.navmeshs.length;n++){

        this.navmeshs.push({
          pos : new THREE.Vector3(track.navmeshs[n].pos.x, track.navmeshs[n].pos.y, track.navmeshs[n].pos.z),
          links : track.navmeshs[n].links,
          type: track.navmeshs[n].type,
        })

      }

      this.lastReccord = 0;

      if(track.trackPoints.length>1)
        this.trackMode = 1;
      else
        this.trackMode = 2;

        for(let pathName in track.pathes){

          const path = track.pathes[pathName];

          this.pathes[pathName] = [];

          for(let pt of track.pathes[pathName]) {

            this.pathes[pathName].push({pos : pt.pos, rot : pt.rot});

          }

        }

      
      if(this.roadObjPhy){
        self.addPhyObj("track", this.roadObjPhy, 0, groundGroup, worldMask); 
        this.roadObjPhy.physicsBody.setFriction(track.friction); 
        this.roadObjPhy.physicsBody.setRestitution(track.restitution);
      }
  
      if(track.wallObjPhy){
        self.addPhyObj("track-wall", track.wallObjPhy, 0, groundGroup, worldMask); 
        track.wallObjPhy.physicsBody.setFriction(4.0); 
        track.wallObjPhy.physicsBody.setRestitution(0.2);
      }
      
      if(track.groundObj){
        self.addPhyObj("ground", track.groundObj, 0, groundGroup, worldMask);  
        track.groundObj.physicsBody.setFriction(0.8); 
        track.groundObj.physicsBody.setRestitution(0.2);
      }
        
      if(this.wallObjPhy2){
        self.addPhyObj("track-wall2", this.wallObjPhy2, 0, carGroup, carMask | worldMask); 
        this.wallObjPhy2.physicsBody.setFriction(0.8); 
        this.wallObjPhy2.physicsBody.setRestitution(0.0);

        if(track.trackPoints.length < 2)
          this.createGroundCube(track.minGroundY, 10);

      }
      
      self.gravity = new Ammo.btVector3(0, -track.gravity, 0);
      self.physicsUniverse.setGravity(this.gravity);

      
      this.trackSegments = [];
      for (let seg of track.trackSegments) {
        this.trackSegments.push(seg);
      }
  
      for (let n = 0; n < track.woods.length; n++) {  

        for (let w = 0; w < track.woods[n].woods.length; w++) {
            this.addWoodsPhy(track.woods[n].woods[w], track.trackPoints[track.woods[n].ptsIdx]);
          }
      }

    
      for (let n = 0; n < track.bonuses.length; n++) {
        this.addBonusPhy(track.bonuses[n]);
      }

      for (let n = 0; n < track.jumpers.length; n++) {
        this.addJumperPhy(track.jumpers[n]);
      }
      
      for (let n = 0; n < track.turrets.length; n++) {
        this.addTurretPhy(track.turrets[n]);
      }
        
      for (let n = 0; n < track.springs.length; n++) {
        this.addSpringPhy(track.springs[n]);
      }
  

      for (let n = 0; n < track.grids.length; n++) {
        this.addPhyMetaGrid(track.grids[n]);
      }

      for (let n = 0; n < track.drones.length; n++) {
        this.addDronePhy(track.drones[n]);
      }


  
      for (let n = 0; n < track.interestPoints.length; n++) {
        this.addInterestPointPhy(track.interestPoints[n]);
      }

      //let colShapeId = 1;
  
      for (let n = 0; n < track.cyls.length; n++) {
        track.cyls[n].id = track.lastId++; //colShapeId++;
        this.addPhyCyl(track.cyls[n]); 
      }

      for (let n = 0; n < track.cubes.length; n++) {
        track.cubes[n].id = track.lastId++; //colShapeId++;
        this.addPhyCube(track.cubes[n]); 
      }

      for (let n = 0; n < track.colcubes.length; n++) {
        track.colcubes[n].id =  track.lastId++; //colShapeId++;
        this.addPhyCube(track.colcubes[n]); 
      }
      
      for (let n = 0; n < track.colcyls.length; n++) {
        track.colcyls[n].id =  track.lastId++; //colShapeId++;
        track.colcyls[n].deltaPhys = new THREE.Vector3();
        track.colcyls[n].heightOffset = 0;

        this.addPhyCyl(track.colcyls[n]); 
      }

      for (let n = 0; n < track.triggers.length; n++) {
        track.triggers[n].id =  track.lastId++; //colShapeId++;
        this.addTriggertPhy(track.triggers[n], n); 
        
        if(track.triggers[n].action) {
          
          for (let c = 0; c < track.cubes.length; c++) {
            if(track.cubes[c].name == track.triggers[n].data.target){
              track.triggers[n].cubeTarget = track.cubes[c];
            }
          }
        }
      }

      for (let n = 0; n < track.npcs.length; n++) {

        if(!track.npcs[n].avatarDef){
          this.npcs.push(null);
          continue;
        }
          

        if(track.npcs[n].avatarDef.ragdoll === true)
          this.addPhyRagdoll(track.npcs[n]);
        else
          this.addPhyNPC(track.npcs[n]);
      }

      for (let n = 0; n < track.ropes.length; n++) {
        this.addRopePhy(track.ropes[n]);
      }

      for (let n = 0; n < track.spiders.length; n++) {
        this.addPhySpider(track.spiders[n], track.spiders[n].pos);
      }

      for (let n = 0; n < track.xmeshes.length; n++) {
        this.addPhyXMesh(track.xmeshes[n]);
      }


      this.doorCube = null;
      this.door1Cube = null;
      this.radarlift = null;
      this.platform1 = null;
      for (let n = 0; n < track.cubes.length; n++) {   
        if(track.cubes[n].name === "doorbase") {
          this.doorCube = track.cubes[n];
        }

        if(track.cubes[n].name === "door1") {
          this.door1Cube = track.cubes[n];
        }
        if(track.cubes[n].name === "radarlift") {
          this.radarlift = track.cubes[n];
        }
        if(track.cubes[n].name === "platform1") {
          this.platform1 = track.cubes[n];
        }
      }
    }
  
    removePhyShot(shot) {
      
      for(let n=0;n<this.rigidBody_List.length;n++) {
  
        if(this.rigidBody_List[n].userData.name.startsWith('shot')) {
  
          if(this.rigidBody_List[n].userData.obj.id == shot.id) {
  
            const rb = this.rigidBody_List[n];
            this.rigidBody_List.splice(n,1);
            
            this.physicsUniverse.removeRigidBody(rb);
            Ammo.destroy(rb);
            /*
            Ammo.destroy(shot.ammoData.localInertia);
            Ammo.destroy(shot.ammoData.motionState);
            Ammo.destroy(shot.ammoData.transform);
            Ammo.destroy(shot.ammoData.shape);
            */
      
            return;
          }
        }
      }
    }
  
    addPhyShot(turret, shot, size, mass, weaponid = -1, name = 'shot', dmg = 0, impact) {
  
      const origin = new Ammo.btVector3(shot.pos.x, shot.pos.y, shot.pos.z);
      const quat = new Ammo.btQuaternion(0, 0, 0, 1.0);
  
      const geometry = new Ammo.btSphereShape(size);
      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      const defaultMotionState = new Ammo.btDefaultMotionState(transform);
  
      const localInertia = new Ammo.btVector3(0, 0, 0);
      geometry .calculateLocalInertia(mass, localInertia);
  
      const RBody_Info = new Ammo.btRigidBodyConstructionInfo(mass, defaultMotionState, geometry, localInertia);
      const RBody = new Ammo.btRigidBody(RBody_Info);
      RBody.userData = { name: name, obj: shot, turret: turret , weaponid: weaponid, dmg : dmg };
  
      RBody.setRestitution(0.1);
      RBody.setRollingFriction(0.01);
      RBody.setFriction(0.04);

      
      RBody.setCcdMotionThreshold(1);
      RBody.setCcdSweptSphereRadius(size);

      shot.physicsBody = RBody;

      let colMask = worldMask;

      if(turret.moveAngle==0)
        colMask |= carMask;
      

      this.physicsUniverse.addRigidBody(RBody, worldGroup, colMask);
      this.rigidBody_List.push(RBody);
  
      shot.ammoData = { localInertia: localInertia, shape : geometry, motionState: defaultMotionState, transform: transform };
      
      Ammo.destroy(RBody_Info);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
  
    }
  
    addPhyObj(name, pShape, mass, group, mask) {
  
      const origin = new Ammo.btVector3(0, 0, 0);
      const quat = new Ammo.btQuaternion(0, 0, 0, 1.0);
  
      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      const defaultMotionState = new Ammo.btDefaultMotionState(transform);
  
      const localInertia = new Ammo.btVector3(0, 0, 0);
      pShape.shape.calculateLocalInertia(mass, localInertia);
  
      const RBody_Info = new Ammo.btRigidBodyConstructionInfo(mass, defaultMotionState, pShape.shape, localInertia);
      const RBody = new Ammo.btRigidBody(RBody_Info);
      RBody.userData = { name: name, obj: null };
  
      pShape.physicsBody = RBody;
      this.physicsUniverse.addRigidBody(RBody, group, mask);
      this.rigidBody_List.push(RBody);
  
      pShape.ammoData = { localInertia: localInertia, motionState: defaultMotionState, transform: transform };
  
      Ammo.destroy(RBody_Info);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
    }
  

    destroyPhyXMesh(xmesh){


      if(xmesh.physicsBody){
        this.physicsUniverse.removeRigidBody(xmesh.physicsBody);
        Ammo.destroy(xmesh.physicsBody);
        xmesh.physicsBody = null;
      }

      if(xmesh.ammoData){
        Ammo.destroy(xmesh.ammoData.localInertia);
        Ammo.destroy(xmesh.ammoData.motionState);
        Ammo.destroy(xmesh.ammoData.transform);
        Ammo.destroy(xmesh.ammoData.shape);
        xmesh.ammoData.localInertia = null;
        xmesh.ammoData.motionState = null;
        xmesh.ammoData.transform = null;
        xmesh.ammoData.shape = null;
      }
    }

    removePhyXMesh(xmesh){
      for(let r=0; r< this.rigidBody_List.length; r++){
        if((this.rigidBody_List[r].userData.obj)&&(this.rigidBody_List[r].userData.obj.id == xmesh.id)){
          this.rigidBody_List.splice(r, 1);
          break;
        }
      }

      for(let n=0;n<this.xmeshes.length;n++){
        if(this.xmeshes[n].id === xmesh.id){
          this.xmeshes.slice(n , 1);
          break;
        }
      }

    }



    createXMesh(xmesh){
      if(!xmesh.meshArray)
        return;

      const vertices = xmesh.meshArray;

    /*
      const size = new Ammo.btVector3(xmesh.size.x /2, xmesh.size.y/2, xmesh.size.z/2 );
      const tmpshape = new Ammo.btBoxShape(size);
    */
    /*
       
      const trimesh = new Ammo.btTriangleMesh();
      for (let i = 0; i < vertices.length; i+=9) {

        const vertex0 = new Ammo.btVector3 (vertices[i + 0], vertices[i + 1], vertices[i + 2]);
        const vertex1 = new Ammo.btVector3 (vertices[i + 3], vertices[i + 4], vertices[i + 5]);
        const vertex2 = new Ammo.btVector3 (vertices[i + 6], vertices[i + 7], vertices[i + 8]);

        trimesh.addTriangle(vertex0, vertex1, vertex2, true);
      }
      trimesh.computeVertexNormals();
      const scale = new Ammo.btVector3(1.0, 1.0, 1.0);

      const tmpshape = new Ammo.btBvhTriangleMeshShape(trimesh, true, true); 
      tmpshape.setMargin(0.1);
      tmpshape.setLocalScaling(scale);
      */



      
     
      /*
      const ConvexHullShape = new Ammo.btConvexHullShape( vertices, vertices.length / 3, 12);
      ConvexHullShape.setMargin(0); 
      */

      const tmpshape = new Ammo.btConvexHullShape();
      for (let i = 0; i < xmesh.nVtx; i+=3) {
          this.tmpVec3.setValue(vertices[i + 0], vertices[i + 1], vertices[i + 2]);
          tmpshape.addPoint(this.tmpVec3, false);
      }
      tmpshape.setMargin(0.1);
      tmpshape.recalcLocalAabb();

      /*
      const hull = new Ammo.btShapeHull(tmpshape);
      hull.buildHull(0);

      const vtx = hull.getVertexPointer();
      const nvtx = hull.numVertices();

      const pConvexHullShape = new Ammo.btConvexHullShape();
      
      for(let v=0;v<nvtx;v++){
        const x = vtx.x();
        const y = vtx.y();
        const z = vtx.z();
        vtx.$B +=4;

        console.log('hull '+x+' '+y+ ' '+z);

        this.tmpVec3.setValue(x, y, z);
        pConvexHullShape.addPoint(this.tmpVec3, false);
      }
      pConvexHullShape.recalcLocalAabb();
      xmesh.ammoData.shape = pConvexHullShape;
      */
      /*
      const pConvexHullShape = new Ammo.btConvexHullShape(hull.getVertexPointer(), hull.numVertices(), 12);    
      */

      
      
      xmesh.ammoData.shape = tmpshape;
      /*
      const hull = new Ammo.btShapeHull(tmpshape);
      hull.buildHull(0.1, true);
      const pConvexHullShape = new Ammo.btConvexHullShape(hull.getVertexPointer(), hull.numVertices(), 12);    
      Ammo.destroy(tmpshape);
      */
      xmesh.ammoData.transform = new Ammo.btTransform();
       
      const origin = new Ammo.btVector3(xmesh.pos.x + xmesh.boxOffset.x, xmesh.pos.y + xmesh.boxOffset.y, xmesh.pos.z + xmesh.boxOffset.z);
      const quat = new Ammo.btQuaternion(xmesh.rot.x, xmesh.rot.y, xmesh.rot.z, xmesh.rot.w);
      
      xmesh.ammoData.transform.setIdentity();
      xmesh.ammoData.transform.setOrigin(origin);
      xmesh.ammoData.transform.setRotation(quat);
      xmesh.ammoData.motionState = new Ammo.btDefaultMotionState(xmesh.ammoData.transform);
      xmesh.ammoData.localInertia = new Ammo.btVector3(0, 0, 0);
      xmesh.ammoData.shape.calculateLocalInertia(xmesh.mass, xmesh.ammoData.localInertia);
      const RBody_Info = new Ammo.btRigidBodyConstructionInfo(xmesh.mass, xmesh.ammoData.motionState, xmesh.ammoData.shape, xmesh.ammoData.localInertia);

      const oldUserData = xmesh.physicsBody ? xmesh.physicsBody.userData : null;
      
      xmesh.physicsBody = new Ammo.btRigidBody(RBody_Info);
      xmesh.physicsBody.setFriction(0.1);
      xmesh.physicsBody.setRestitution(0.0);
      xmesh.physicsBody.setDamping(0,0);

      xmesh.physicsBody.userData = oldUserData ? oldUserData : { name: "xmesh-"+xmesh.idx, obj: xmesh };

      Ammo.destroy(RBody_Info);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
      this.physicsUniverse.addRigidBody(xmesh.physicsBody, worldGroup, worldMask);

      this.rigidBody_List.push(xmesh.physicsBody);

      return xmesh;

    }

    addPhyXMesh(xmesh){

      const newIdx = this.xmeshes.length;

  
      const newXmesh =  { id : xmesh.id, 
                          idx : newIdx,
                          ammoData : { localInertia: null, motionState: null, transform: null, shape: null },
                          name : "xmesh-"+newIdx, 
                          physicsBody : null,
                          original : true,
                          boxOffset:  new THREE.Vector3(xmesh.boxOffset.x, xmesh.boxOffset.y, xmesh.boxOffset.z),
                          nVtx : xmesh.meshArray.length,
                          meshArray : xmesh.meshArray,  
                          mass: xmesh.mass, 
                          nVtxAlloc : (xmesh.meshArray.length /3 ) * 2,
                          vertices : null,
                          size : new THREE.Vector3(xmesh.size.x, xmesh.size.y, xmesh.size.z),
                          pos: new THREE.Vector3(xmesh.pos.x, xmesh.pos.y, xmesh.pos.z), 
                          rot: new THREE.Quaternion(xmesh.rot.x, xmesh.rot.y, xmesh.rot.z, xmesh.rot.w), 
                          splits: []
                        };

      this.createXMesh(newXmesh);
      this.xmeshes.push(newXmesh);
    }
    encodeSplitCase(a, b, c) {
      return ((a << 4) |(b << 2) | (c));
  }

  fraction(a, b) {
      return Math.abs(a) / (Math.abs(a) + Math.abs(b));
  }

  dist(a, b, c, d, x, y, z) {
      return (a * x + b * y + c * z + d);
  }

  signd(a, b, c, d, x,y, z) {
      return Math.sign(a * x + b * y + c * z + d);
  }

  splitt(vertices , index, v0, v1, v2){

      vertices[index++] = v0.x;
      vertices[index++] = v0.y;
      vertices[index++] = v0.z;

      vertices[index++] = v1.x;
      vertices[index++] = v1.y;
      vertices[index++] = v1.z;

      vertices[index++] = v2.x;
      vertices[index++] = v2.y;
      vertices[index++] = v2.z;

      return index;
  }

  splittn(normals , index, n0, n1, n2){

      normals[index++] = n0.x;
      normals[index++] = n0.y;
      normals[index++] = n0.z;

      normals[index++] = n1.x;
      normals[index++] = n1.y;
      normals[index++] = n1.z;

      normals[index++] = n2.x;
      normals[index++] = n2.y;
      normals[index++] = n2.z;

      return index;
  }

  splittuv(uvs , index, uv0, uv1, uv2){

      uvs[index++] = uv0.x;
      uvs[index++] = uv0.y;

      uvs[index++] = uv1.x;
      uvs[index++] = uv1.y;

      uvs[index++] = uv2.x;
      uvs[index++] = uv2.y;

      return index;
  }

  centerVertices(vertices, num){


    const min = new THREE.Vector3(Infinity,Infinity,Infinity);
    const max = new THREE.Vector3(-Infinity,-Infinity,-Infinity);

    for(let n=0;n<num;n+=3){
      min.x= Math.min(min.x, vertices[n +0])
      min.y= Math.min(min.y, vertices[n +1])
      min.z= Math.min(min.z, vertices[n +2])

      max.x= Math.max(max.x, vertices[n +0])
      max.y= Math.max(max.y, vertices[n +1])
      max.z= Math.max(max.z, vertices[n +2])
    }

    const center = new THREE.Vector3().subVectors(max, min).multiplyScalar(0.5).add(min);

    for(let n=0;n<num;n+=3){
      vertices[n +0] = vertices[n  +0] - center.x;
      vertices[n +1] = vertices[n  +1] - center.y;
      vertices[n +2] = vertices[n  +2] - center.z;
    }
  }

    splitGeo(a, b, c, d, xmesh, vel = null) {

        const v = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
        const aa = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
        
        xmesh.vertices = new Float32Array(xmesh.nVtxAlloc);

        const newXmesh =  { id :this.lastId++,
                            idx : this.xmeshes.length,
                            ammoData : { localInertia: null, motionState: null, transform: null, shape: null },
                            name : 'xmesh-'+this.xmeshes.length, 
                            physicsBody : null,
                            boxOffset : new THREE.Vector3(),
                            nVtx : 0,
                            meshArray : null,  
                            mass: 100500, 
                            nVtxAlloc : xmesh.nVtxAlloc,
                            vertices : null,
                            size : xmesh.size.clone(), 
                            pos: xmesh.pos.clone(), 
                            rot: xmesh.rot.clone(),
                            splits: []
                          };


        newXmesh.vertices = new Float32Array(newXmesh.nVtxAlloc);

        const sarr = new Int8Array(4);
        const uarr = new Uint8Array(4);

        let nVtx1 = 0;

        for (let i = 0 ; i< xmesh.nVtx; i+=9) {

            v[0].set(xmesh.meshArray[i + 0], xmesh.meshArray[i + 1], xmesh.meshArray[i + 2]);
            v[1].set(xmesh.meshArray[i + 3], xmesh.meshArray[i + 4], xmesh.meshArray[i + 5]);
            v[2].set(xmesh.meshArray[i + 6], xmesh.meshArray[i + 7], xmesh.meshArray[i + 8]);

            const dA = this.dist(a, b, c, d, v[0].x, v[0].y, v[0].z);
            const dB = this.dist(a, b, c, d, v[1].x, v[1].y, v[1].z);
            const dC = this.dist(a, b, c, d, v[2].x, v[2].y, v[2].z);
            sarr[0] = Math.sign(dA);
            sarr[1] = Math.sign(dB);
            sarr[2] = Math.sign(dC);

            if (sarr[0] * sarr[1] == -1)
                aa[0].lerpVectors(v[0], v[1], this.fraction(dA, dB));

            if (sarr[1] * sarr[2] == -1)
                aa[1].lerpVectors(v[1], v[2], this.fraction(dB, dC));
                
            if (sarr[2] * sarr[0] == -1)
                aa[2].lerpVectors(v[2], v[0], this.fraction(dC, dA));

            uarr[0] = sarr[0] +1;
            uarr[1] = sarr[1] +1;
            uarr[2] = sarr[2] +1;

            const code = this.encodeSplitCase(uarr[0], uarr[1], uarr[2]);

            const triCase = SplitArray[code];
            const behinds = triCase[0];
            const fronts = triCase[1];

            for(let behind = 0;behind<behinds.length;behind++){

                const b0 = behinds[behind][0];
                const b1 = behinds[behind][1];
                const b2 = behinds[behind][2];

                const v0 = b0 < 3 ? v[b0] : aa[b0 - 3];
                const v1 = b1 < 3 ? v[b1] : aa[b1 - 3];
                const v2 = b2 < 3 ? v[b2] : aa[b2 - 3];


                if((nVtx1+9)>=xmesh.nVtxAlloc) {
                  xmesh.nVtxAlloc *= 2;
                  const newArray = new Float32Array(xmesh.nVtxAlloc);
                  newArray.set(xmesh.vertices);
                  xmesh.vertices = newArray;
                }

                nVtx1 = this.splitt(xmesh.vertices, nVtx1,   v0, v1, v2);
            }

            for(let front = 0;front<fronts.length;front++){

                const f0 = fronts[front][0];
                const f1 = fronts[front][1];
                const f2 = fronts[front][2];

                const v0 = f0 < 3 ? v[f0] : aa[f0 - 3];
                const v1 = f1 < 3 ? v[f1] : aa[f1 - 3];
                const v2 = f2 < 3 ? v[f2] : aa[f2 - 3];

                if((newXmesh.nVtx+9)>=newXmesh.nVtxAlloc) {
                  newXmesh.nVtxAlloc *= 2;
                  const newArray = new Float32Array(newXmesh.nVtxAlloc);
                  newArray.set(newXmesh.vertices);
                  newXmesh.vertices = newArray;
                }
                newXmesh.nVtx = this.splitt(newXmesh.vertices, newXmesh.nVtx, v0, v1, v2);
            }
        }

        if(newXmesh.nVtx<=0)
          return;

        xmesh.nVtx = nVtx1;
        xmesh.meshArray = xmesh.vertices;
        newXmesh.meshArray = newXmesh.vertices;


        this.centerVertices(xmesh.meshArray,  xmesh.nVtx );
        this.centerVertices(newXmesh.meshArray, newXmesh.nVtx );

        const xm = this.createXMesh(newXmesh);

        if(vel)
          xm.physicsBody.applyCentralImpulse(vel);


        this.xmeshes.push(newXmesh);

        this.splits.push({id: xmesh.idx, a:a,b:b,c:c, d: d});
        //console.log('phys new split '+' a:'+a.toFixed(1)+' b:'+b.toFixed(1)+' c:'+c.toFixed(1)+' d:'+d.toFixed(1)+ ' '+ xmesh.nVtx / 9 +'+'+newXmesh.nVtx / 9 + ' triangles');
     
        return newXmesh;
       
    }

    
    doBlast(xmesh, r, ipos, irot, vel = null){

        this.destroyPhyXMesh(xmesh);
        for(let n=0;n<=9;n++){

            const alpha = n * Math.PI / 8 - Math.PI/2;
            const norm = new THREE.Vector3( Math.sin(alpha)     , Math.cos(alpha)     , 0 ).normalize().applyQuaternion(irot);
            const pos  = new THREE.Vector3( Math.sin(alpha) * r , Math.cos(alpha) * r , 0 ).applyQuaternion(irot);

            pos.x += ipos.x();
            pos.y += ipos.y();
            pos.z += ipos.z();

            const d = -norm.dot(pos);
            this.splitGeo(norm.x, norm.y, norm.z, d, xmesh, vel);
            
        }

        xmesh.boxOffset.set(0,0,0);
        const xm = this.createXMesh(xmesh, vel);

        if(vel)
          xm.physicsBody.applyCentralImpulse(vel);
        
        
    }
    
    xmeshHit(xmesh, obj, wp){
      const myXmesh = xmesh.userData.obj;

      if(myXmesh.original !== true)
        return;

      if(myXmesh.blasted === true)
          return;


      let myObj = null;
      let quat = null;
      
      

      if(obj.userData.name.startsWith('car')){
        let carid = obj.userData.name.substr(4);

        /*
        if(carid.indexOf('-')<0)
          carid = parseInt(carid);
        */
  
        myObj = this.getNetCar(carid);
        if(myObj)
          quat= myObj.quat;
  
   
      } else if(obj.userData.name.startsWith('shot-car-')) {
        myObj = obj.userData.turret;
        quat = myObj.quat;
      } else if(obj.userData.name.startsWith('lip')) {

        const idstr = obj.userData.name.substr(4);
        const id = parseInt(idstr);

        for(let n=0;n<this.interestPoints.length;n++){

          if(this.interestPoints[n].id === id){
            myObj = this.interestPoints[n];
            quat = myObj.LphysicsBody.userData.obj.rot;
            break;
          }
        }
      }

      if(!myObj)
        return;

      if(!myObj.physicsBody)
        return;

      const carvel = myObj.physicsBody.getLinearVelocity();
      this.tmpV1.set(carvel.x(), carvel.y(), carvel.z());

      if(this.tmpV1.length< 20)
         return;

      this.doBlast(myXmesh, 4,  wp, quat , carvel);;
      myXmesh.blasted = true;
    }

    addPhyCube(cube) {
  
      const size = new Ammo.btVector3(cube.size.x /2, cube.size.y/2, cube.size.z/2 );
      const objGeometry = new Ammo.btBoxShape(size);
      objGeometry.setMargin(cube.margin ? cube.margin : 0.01);
  
      const origin = new Ammo.btVector3(cube.pos.x, cube.pos.y, cube.pos.z);
      const quat = new Ammo.btQuaternion(cube.rot.x, cube.rot.y, cube.rot.z, cube.rot.w);
  
      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      const defaultMotionState = new Ammo.btDefaultMotionState(transform);
      const localInertia = new Ammo.btVector3(0, 0, 0);
      objGeometry.calculateLocalInertia(cube.mass, localInertia);
      const RBody_Info = new Ammo.btRigidBodyConstructionInfo(cube.mass, defaultMotionState, objGeometry, localInertia);
      
      const RBody = new Ammo.btRigidBody(RBody_Info);
      RBody.setFriction(1.0);
      RBody.setRestitution(0.0);
      RBody.setDamping(0.1, 0.1);
      
      RBody.userData = { name: "cube-"+cube.name, obj: cube, lastContacts: [], contacts: [] };

      cube.physicsBody = RBody;

      this.physicsUniverse.addRigidBody(cube.physicsBody, worldGroup, worldMask);
      this.rigidBody_List.push(cube.physicsBody);

      cube.ammoData = { localInertia: localInertia, motionState: defaultMotionState, transform: transform, shape: objGeometry };
  
      if(cube.stiffness>0){

        cube.anchor = {};

        const offset = cube.anchorPos === "top" ? cube.size.y / 2.0 : -cube.size.y/ 2.0;

        let maxBreakimpulse = cube.anchorPos === "top" ? 25000 : 0;

        cube.anchor.pos = new THREE.Vector3(cube.pos.x, cube.pos.y + offset, cube.pos.z );
        const eq = new Ammo.btVector3(0, offset * 1.1, 0);
        const Aorigin = new Ammo.btVector3(cube.anchor.pos.x, cube.anchor.pos.y, cube.anchor.pos.z ) ;
        const Aquat = new Ammo.btQuaternion(cube.rot.x, cube.rot.y, cube.rot.z, cube.rot.w);
        
        const anchorGeometry = new Ammo.btSphereShape(0.1);
        const Atransform = new Ammo.btTransform();
        Atransform.setIdentity();
        Atransform.setOrigin(Aorigin);
        Atransform.setRotation(Aquat);
        const AmotionState = new Ammo.btDefaultMotionState(Atransform);
        const AlocalInertia = new Ammo.btVector3(0, 0, 0);
        anchorGeometry.calculateLocalInertia(0, AlocalInertia);
        const Ainfos = new Ammo.btRigidBodyConstructionInfo(0, AmotionState, anchorGeometry, AlocalInertia);
    
        cube.anchor.physicsBody = new Ammo.btRigidBody(Ainfos);
        cube.anchor.physicsBody.userData = { name: 'cube-anchor-' + cube.id, obj: cube };
        cube.anchor.ammoData = { localInertia: AlocalInertia, motionState: AmotionState, transform: Atransform, shape: anchorGeometry };
    
        const frameInA = new Ammo.btTransform();
        frameInA.setIdentity();
              
        const frameInB = new Ammo.btTransform();
        frameInB.setIdentity(); 
        frameInB.setOrigin(eq);
        
        const springPhy = new Ammo.btGeneric6DofSpringConstraint(cube.anchor.physicsBody, cube.physicsBody, frameInA, frameInB, true);
        
        this.physicsUniverse.addConstraint(springPhy, true);

        const lL = new Ammo.btVector3( -0.5, -0.5, -0.5);
        const lH = new Ammo.btVector3(  0.5,  0.5, 0.5);
    
        const aL = new Ammo.btVector3(-1.2,  0.0,  -1.2);
        const aH = new Ammo.btVector3( 1.2,  0.0,   1.2);
    
        springPhy.setLinearLowerLimit(lL);
        springPhy.setLinearUpperLimit(lH);
    
        springPhy.setAngularLowerLimit(aL);
        springPhy.setAngularUpperLimit(aH);
        /*
        springPhy.setAxis(this.zAxis, this.yAxis);
        springPhy.getFrameOffsetA().setOrigin(this.zVec);
        springPhy.getFrameOffsetB().setOrigin(eq);
        */
    
        const stiffness = cube.stiffness;
        const damping = cube.damping;
    
        const cfm = 0.1;
        const scfm = 0.005;
        const erp = 0.8;
        const serp= 0.001;
    
        for(let n=0;n<6;n++) {

            if(n>=3){
              springPhy.enableSpring(n,  true);
              springPhy.setStiffness(n, stiffness ); //10000
              springPhy.setDamping  (n, damping); //0.0008
            }else{
              springPhy.enableSpring(n,  true);
              springPhy.setStiffness(n, stiffness ); //10000
              springPhy.setDamping  (n, damping); //0.0008
            }
            
            springPhy.setParam(BT_CONSTRAINT_CFM      , cfm, n);
            springPhy.setParam(BT_CONSTRAINT_ERP      , erp, n);


            //springPhy.setParam(BT_CONSTRAINT_STOP_CFM , scfm, n);
            //springPhy.setParam(BT_CONSTRAINT_STOP_ERP , serp, n);

            /*
            if(maxBreakimpulse>0)
              springPhy.setBreakingImpulseThreshold(maxBreakimpulse);
            */
        }
    
        springPhy.setEquilibriumPoint(0, 0);
        springPhy.setEquilibriumPoint(1, 0);
        springPhy.setEquilibriumPoint(2, 0);
    
        springPhy.setEquilibriumPoint(3, 0);
        springPhy.setEquilibriumPoint(4, 0);
        springPhy.setEquilibriumPoint(5, 0);
  
        cube.ammoData.springPhy = springPhy;

     

        Ammo.destroy(eq);
        Ammo.destroy(Ainfos);
        Ammo.destroy(Aorigin);
        Ammo.destroy(Aquat);

        Ammo.destroy(aL);
        Ammo.destroy(aH);
        Ammo.destroy(lL);
        Ammo.destroy(lH);
        Ammo.destroy(frameInB);
        Ammo.destroy(frameInA);
      }
     
      Ammo.destroy(size);
      Ammo.destroy(RBody_Info);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
    }
  
  
    addPhyCyl(cyl) {
  
  
      const size = new Ammo.btVector3(1,1,1);
      const objGeometry = new Ammo.btCylinderShape(size);

      const centerPos = new THREE.Vector3(cyl.pos.x, cyl.pos.y, cyl.pos.z);

      const origin =new Ammo.btVector3( centerPos.x, 
                                        centerPos.y + cyl.heightOffset, 
                                        centerPos.z ) ;
      const quat = new Ammo.btQuaternion(cyl.rot.x, cyl.rot.y, cyl.rot.z, cyl.rot.w);
      const scale = new Ammo.btVector3(cyl.radiusX , cyl.height / 2 +cyl.heightOffset, cyl.radiusZ );  
 
      objGeometry.setLocalScaling(scale);
  
      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      const defaultMotionState = new Ammo.btDefaultMotionState(transform);
      const localInertia = new Ammo.btVector3(0, 0, 0);
      objGeometry.calculateLocalInertia(cyl.mass, localInertia);
      const RBody_Info = new Ammo.btRigidBodyConstructionInfo(cyl.mass, defaultMotionState, objGeometry, localInertia);
      
      RBody_Info.set_m_angularSleepingThreshold(0.1);
      RBody_Info.set_m_linearSleepingThreshold(0.1);
  
      const RBody = new Ammo.btRigidBody(RBody_Info);

      //this.physicsUniverse.updateSingleAABB(RBody);

      if(cyl.friction !== undefined){
        RBody.setFriction(cyl.friction);
        RBody.setRollingFriction(0.5);
      }

      RBody.setRestitution(0.1);
        

      RBody.userData = { name: "cyl-"+cyl.id, obj: cyl, lastContacts: [], contacts: [] };
      cyl.physicsBody = RBody;
      /*
      cyl.physicsBody.setActivationState( DISABLE_DEACTIVATION );
      cyl.physicsBody.activate();
      */
      this.physicsUniverse.addRigidBody(RBody, worldGroup, worldMask);
      this.rigidBody_List.push(RBody);
  
  
      cyl.ammoData = { localInertia: localInertia, motionState: defaultMotionState, transform: transform, shape: objGeometry };
  
      if(cyl.stiffness>0){

        cyl.anchor = {};
        let eq;

        

        if(cyl.anchorPos === 'top'){
          cyl.anchor.pos = new THREE.Vector3( centerPos.x, 
                                              centerPos.y +  cyl.height / 2 + cyl.heightOffset, 
                                              centerPos.z );
          eq = new Ammo.btVector3(0, +cyl.height / 2 + cyl.heightOffset, 0);
        }else{          
          cyl.anchor.pos = new THREE.Vector3( centerPos.x, 
                                              centerPos.y, 
                                              centerPos.z );
          eq = new Ammo.btVector3(0, -cyl.height / 2 - cyl.heightOffset, 0);
        }

        const Aorigin = new Ammo.btVector3(cyl.anchor.pos.x, cyl.anchor.pos.y, cyl.anchor.pos.z);
        const Aquat = new Ammo.btQuaternion(cyl.rot.x, cyl.rot.y, cyl.rot.z, cyl.rot.w);
        
        const anchorGeometry = new Ammo.btSphereShape(1);
        const Atransform = new Ammo.btTransform();
        Atransform.setIdentity();
        Atransform.setOrigin(Aorigin);
        Atransform.setRotation(Aquat);
        const AmotionState = new Ammo.btDefaultMotionState(Atransform);
        const AlocalInertia = new Ammo.btVector3(0.0, 0, 0);
        anchorGeometry.calculateLocalInertia(0.0, AlocalInertia);
        const Ainfos = new Ammo.btRigidBodyConstructionInfo(0.0, AmotionState, anchorGeometry, AlocalInertia);
        //Ainfos.set_m_angularSleepingThreshold(10.0);
    
        
        cyl.anchor.physicsBody = new Ammo.btRigidBody(Ainfos);
        cyl.anchor.physicsBody.userData = { name: 'cyl-anchor-' + cyl.id, obj: cyl };
        cyl.anchor.ammoData = { localInertia: AlocalInertia, motionState: AmotionState, transform: Atransform, shape: anchorGeometry };

        cyl.anchor.physicsBody.setFriction(0);
        cyl.anchor.physicsBody.setRollingFriction(0);

        //cyl.anchor.physicsBody.setActivationState( DISABLE_DEACTIVATION );
        //cyl.anchor.physicsBody.activate();

        const eq2 = new Ammo.btVector3(0, 0.2, 0);

        const frameInA = new Ammo.btTransform();
        frameInA.setIdentity();
        frameInA.setOrigin(eq2);
              
        const frameInB = new Ammo.btTransform();
        frameInB.setIdentity(); 
        frameInB.setOrigin(eq);
        
        const springPhy = new Ammo.btGeneric6DofSpringConstraint(cyl.anchor.physicsBody, cyl.physicsBody, frameInA, frameInB, true);
    
        const lL = new Ammo.btVector3( -1.5, -1.5, -1.5);
        const lH = new Ammo.btVector3(  1.5,  1.5,  1.5);
    
        const aL = new Ammo.btVector3(-0.4,  0,  -0.4);
        const aH = new Ammo.btVector3( 0.4,  0,   0.4);
    
        springPhy.setLinearLowerLimit(lL);
        springPhy.setLinearUpperLimit(lH);
    
        springPhy.setAngularLowerLimit(aL);
        springPhy.setAngularUpperLimit(aH);
        /*
        springPhy.setAxis(this.zAxis, this.yAxis);
        springPhy.getFrameOffsetA().setOrigin(this.zVec);
        springPhy.getFrameOffsetB().setOrigin(eq);
        */
    
        Ammo.destroy(aL);
        Ammo.destroy(aH);
        Ammo.destroy(lL);
        Ammo.destroy(lL);
        Ammo.destroy(frameInB);
        Ammo.destroy(frameInA);
    
        this.physicsUniverse.addConstraint(springPhy, true);
    
        const stiffness = cyl.stiffness;
        const damping = cyl.damping;
    
        const cfm = 0.1;
        const scfm = 0.05;
        const erp = 0.6;
        const serp= 0.001;
    
        for(let n=0;n<6;n++) {
            
            if ((n==3)||(n==4)||(n==5)) {
              springPhy.enableSpring(n,  true);
              springPhy.enableFeedback(n,  true);
              springPhy.setStiffness(n, stiffness ); //10000
              springPhy.setDamping  (n, damping); //0.0008
            }else
              springPhy.enableSpring(n,  false);
            
            springPhy.setParam(BT_CONSTRAINT_CFM      , cfm, n);
            springPhy.setParam(BT_CONSTRAINT_ERP      , erp, n);

            //springPhy.setParam(BT_CONSTRAINT_STOP_CFM , scfm, n);
            //springPhy.setParam(BT_CONSTRAINT_STOP_ERP , serp, n);

            
            //springPhy.setBreakingImpulseThreshold()
        }
    
        springPhy.setEquilibriumPoint(0, 0);
        springPhy.setEquilibriumPoint(1, 0);
        springPhy.setEquilibriumPoint(2, 0);
    
        springPhy.setEquilibriumPoint(3, 0);
        springPhy.setEquilibriumPoint(4, 0);
        springPhy.setEquilibriumPoint(5, 0);
  
        cyl.ammoData.springPhy = springPhy;
  
        Ammo.destroy(eq);
        Ammo.destroy(Ainfos);
        Ammo.destroy(Aorigin);
        Ammo.destroy(Aquat);
      }
  
     
  
      Ammo.destroy(size);
      Ammo.destroy(RBody_Info);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
    }
  
  
    setMassRatio(phycar, ratio, massVehicle) {
      const masses = [massVehicle * ratio, massVehicle  * (1-ratio)];

      phycar.mt.setIdentity();
      phycar.ammoData.localInertia.setValue(0, 0, 0);
      phycar.ammoData.shape.calculatePrincipalAxisTransform2(masses[0],masses[1], phycar.mt, phycar.ammoData.localInertia);
      phycar.physicsBody.setMassProps(massVehicle, phycar.ammoData.localInertia);
    
      //Ammo.destroy(t);
      
  
    }
  
    enableBraking(phycar, car) {
        if(!phycar.isBraking){
          
          this.setMassRatio(phycar, 0.05, car.massVehicle);
  
          for (let n = 0; n < 4; n++) {
            const infos=phycar.vehicle.getWheelInfo(n);  
            if(!infos.get_m_bIsFrontWheel()){

              phycar.wheels[n].frictionFac = 0.85;

              //infos.set_m_frictionSlip(car.carDef.suspension.friction * 1.25);
            }
          }
          phycar.isBraking=true;
        }
    }
  
    disableBraking(phycar, car)
    {
        if(phycar.isBraking){
          this.setMassRatio(phycar, 0.95, car.massVehicle);
          for (let n = 0; n < 4; n++) {
            const infos=phycar.vehicle.getWheelInfo(n);  
            if(!infos.get_m_bIsFrontWheel()){
              //infos.set_m_frictionSlip(car.carDef.suspension.friction);
              phycar.wheels[n].frictionFac = 1.0;
            }
          }
          phycar.isBraking=false;
        }
    }
    
    removePhyCharacter(player) {
  
      const character = player.character;
  
      if(character.physicsBody){
        this.physicsUniverse.removeAction(character.ammoData.char);
        this.physicsUniverse.removeCollisionObject(character.physicsBody);
        
        if(character.ammoData){
          Ammo.destroy(character.ammoData.transform);
          Ammo.destroy(character.ammoData.shape);
          Ammo.destroy(character.ammoData.char);
        }
  
        Ammo.destroy(character.physicsBody);
        character.ammoData = null;
        character.physicsBody = null;
      }
      
      for(let n=0;n<this.character_List.length;n++){
        if(this.character_List[n].netID == character.netID){
          this.character_List.splice(n, 1);
          break;
        }
      }
  
      for(let n=0;n<this.characters.length;n++){
  
        if(this.characters[n].netID == character.netID){
          this.characters.splice(n, 1);
          return;
        }
      }
    }


    updatePhyGridRot(Grid){
      const origin = new Ammo.btVector3();
      const rot = new Ammo.btQuaternion();
      const pos = new THREE.Vector3();

      const sw = Grid.width * Grid.cell_size.x* 0.9;
      const sh = Grid.height * Grid.cell_size.y;
      const sd = Grid.depth * Grid.cell_size.z* 0.9;

      const bSz = 0.1;

      const w2 = sw/2;
      const h2 = sh/2;
      const d2 = sd/2;

      const poses = [ [Grid.pos.x-bSz      , Grid.pos.y+h2        , Grid.pos.z + d2],
                      [Grid.pos.x+sw +bSz  , Grid.pos.y+h2        , Grid.pos.z + d2], 
                      [Grid.pos.x+w2       , Grid.pos.y-bSz       , Grid.pos.z + d2],
                      [Grid.pos.x+w2       , Grid.pos.y+sh+bSz    , Grid.pos.z + d2], 
                      [Grid.pos.x+w2       , Grid.pos.y+h2        , Grid.pos.z -bSz],
                      [Grid.pos.x+w2       , Grid.pos.y+h2        , Grid.pos.z + sd + bSz], 
                    
                    ]


      for(let n=0;n<6;n++){

        pos.set(poses[n][0], poses[n][1], poses[n][2]).sub(Grid.pos).applyQuaternion(Grid.rot).add(Grid.pos);

        rot.setValue(Grid.rot.x, Grid.rot.y, Grid.rot.z, Grid.rot.w);
        origin.setValue(pos.x, pos.y, pos.z);
        
        this.tmpTransformation.setIdentity();
        this.tmpTransformation.setOrigin(origin);
        this.tmpTransformation.setRotation(rot);

        this.mbgrids[0].borders[n].ammoData.physicsBody.setWorldTransform( this.tmpTransformation );

      }

      Ammo.destroy(origin)
      Ammo.destroy(rot)
      
    }

    addPhyMetaGrid(Grid){

      const origin = new Ammo.btVector3();
      const rot = new Ammo.btQuaternion();
      const size = new Ammo.btVector3();
      const pos = new THREE.Vector3();

      const sw = Grid.width * Grid.cell_size.x* 0.9;
      const sh = Grid.height * Grid.cell_size.y * 0.9;
      const sd = Grid.depth * Grid.cell_size.z* 0.9;

      const bSz = 0.1;

      const sizes = [ [bSz , sh , sd],
                      [bSz , sh , sd], 
                      [sw  , bSz, sd],
                      [sw  , bSz, sd], 
                      [sw  , sh , bSz],
                      [sw  , sh , bSz]];

      const w2 = sw/2;
      const h2 = sh/2;
      const d2 = sd/2;

      const poses = [ [Grid.pos.x-bSz      , Grid.pos.y+h2        , Grid.pos.z + d2],
                      [Grid.pos.x+sw +bSz  , Grid.pos.y+h2        , Grid.pos.z + d2], 
                      [Grid.pos.x+w2       , Grid.pos.y-bSz       , Grid.pos.z + d2],
                      [Grid.pos.x+w2       , Grid.pos.y+sh+bSz    , Grid.pos.z + d2], 
                      [Grid.pos.x+w2       , Grid.pos.y+h2        , Grid.pos.z -bSz],
                      [Grid.pos.x+w2       , Grid.pos.y+h2        , Grid.pos.z + sd + bSz], 
                    
                    ]

      const gridPhy = {time : 0, borders : [], balls : [], hasBall : Grid.hasBall}


      for(let n=0;n<6;n++){

        pos.set(poses[n][0], poses[n][1], poses[n][2]).sub(Grid.pos).applyQuaternion(Grid.rot).add(Grid.pos);

        size.setValue(sizes[n][0] * 0.5, sizes[n][1] * 0.5, sizes[n][2] * 0.5);
        rot.setValue(Grid.rot.x, Grid.rot.y, Grid.rot.z, Grid.rot.w);
        origin.setValue(pos.x, pos.y, pos.z);
          
        const geometry = new Ammo.btBoxShape(size);
        const transform = new Ammo.btTransform();
        transform.setIdentity();
        transform.setOrigin(origin);
        transform.setRotation(rot);

        const motionState = new Ammo.btDefaultMotionState(transform); //, localTransform
        const localInertia = new Ammo.btVector3(0, 0.0, 0);
        geometry.calculateLocalInertia(0, localInertia);
    
        const infos = new Ammo.btRigidBodyConstructionInfo(0, motionState, geometry, localInertia);
        const objbody = new Ammo.btRigidBody(infos);
        gridPhy.borders[n] = {ammoData : {geometry: geometry, transform: transform, motionState: motionState, localInertia:localInertia, physicsBody: objbody }};

        objbody.setFriction(1.0);
        objbody.setRestitution(0.5);
        this.physicsUniverse.addRigidBody(objbody, ballGroup,  worldMask);
      }


      for(let n=0;n<Grid.balls.length;n++){
        
        const geometry = new Ammo.btSphereShape(Grid.balls[n].radius * 0.5);
        geometry.setMargin(0.1);
        pos.set(Grid.balls[n].center.x, Grid.balls[n].center.y, Grid.balls[n].center.z).applyQuaternion(Grid.rot).add(Grid.pos);
        origin.setValue(pos.x,  pos.y,  pos.z);

        const transform = new Ammo.btTransform();
        transform.setIdentity();
        transform.setOrigin(origin);

        const motionState = new Ammo.btDefaultMotionState(transform); //, localTransform
        const localInertia = new Ammo.btVector3(0, 0.0, 0);
        geometry.calculateLocalInertia(Grid.balls[n].weight, localInertia);
    
        const infos = new Ammo.btRigidBodyConstructionInfo(Grid.balls[n].weight, motionState, geometry, localInertia);

        infos.set_m_angularSleepingThreshold(0.1);
        infos.set_m_linearSleepingThreshold(0.1);

        
        const objbody = new Ammo.btRigidBody(infos);
        gridPhy.balls[n] = {pos : new THREE.Vector3(), rot : new THREE.Quaternion(), ammoData : {geometry: geometry, transform: transform, motionState: motionState, localInertia:localInertia, physicsBody: objbody }};

        objbody.setRollingFriction(0.4);
        objbody.setFriction(0.4);
        objbody.setRestitution(0.1);
        objbody.setDamping(0.8, 0.8);

        objbody.userData = { name: 'metaball-' + n, obj: gridPhy.balls[n], grid : Grid};

        this.physicsUniverse.addRigidBody(objbody, worldGroup,  worldMask | ballMask);
        this.rigidBody_List.push(objbody);
      }

      if(gridPhy.hasBall) {

        const geometry = new Ammo.btSphereShape(5 * Grid.cell_size.x);
        origin.setValue(  Grid.pos.x + w2,
                          Grid.pos.y + sh, 
                          Grid.pos.z + d2);

        const transform = new Ammo.btTransform();
        transform.setIdentity();
        transform.setOrigin(origin);

        const motionState = new Ammo.btDefaultMotionState(transform); //, localTransform
        const localInertia = new Ammo.btVector3(0, 0.0, 0);
        geometry.calculateLocalInertia(50, localInertia);
    
        const infos = new Ammo.btRigidBodyConstructionInfo(50, motionState, geometry, localInertia);
        gridPhy.fallBallBody = new Ammo.btRigidBody(infos);
        gridPhy.fallBallBody.userData = { name: 'fallball', obj: Grid.fallBall, grid : Grid};

        this.physicsUniverse.addRigidBody(gridPhy.fallBallBody, worldGroup,  worldMask | ballMask | carMask);
        
        this.rigidBody_List.push(gridPhy.fallBallBody);
      }
     
      this.mbgrids.push(gridPhy);
      Ammo.destroy(origin)
      Ammo.destroy(rot)
      Ammo.destroy(size)
    }
  
    addPhyNPC(character) {
  
      if(!character)
        return;
  
      character.netID = character.id;
      

      character.p = new Ammo.btVector3(0, 0, 0);  
      character.q = new Ammo.btQuaternion(0, 0, 0, 1);



      character.start_pos = new THREE.Vector3(character.pos.x, character.pos.y, character.pos.z);
      character.pathFinder = new PathFinder(this.navmeshs.length);

      character.ppos = new THREE.Vector3(character.pos.x, character.pos.y, character.pos.z) 

      const origin = new Ammo.btVector3(character.pos.x, character.pos.y + character.avatarDef.height * character.skelScale * 0.5, character.pos.z);  
      const quat = new Ammo.btQuaternion(character.rot.x, character.rot.y, character.rot.z, character.rot.w);
      const transform = new Ammo.btTransform();   
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);


      let myShape = null;

      if(character.avatarDef.collisionType === 1){
        const size = new Ammo.btVector3(character.avatarDef.radius * character.skelScale, Math.abs(character.avatarDef.height) * .5 * character.skelScale, character.avatarDef.radius * character.skelScale);
        myShape = new Ammo.btBoxShape (size);
        Ammo.destroy(size);
      }else{
        const h = Math.max(0, Math.abs(character.avatarDef.height) - character.avatarDef.radius * 2);
        myShape =   new Ammo.btCapsuleShape (character.avatarDef.radius * character.skelScale, h * character.skelScale);
      }

      
      

      //const myShape = new Ammo.btCylinderShape (character.avatarDef.radius * character.skelScale, character.avatarDef.height * character.skelScale);
      
      /*
      const ghostObject = new Ammo.btPairCachingGhostObject();
      ghostObject.setWorldTransform( transform );
      ghostObject.setCollisionShape( myShape );
      ghostObject.setCollisionFlags ( CF_CHARACTER_OBJECT );
      //ghostObject.setActivationState( DISABLE_DEACTIVATION );
      ghostObject.activate();
      ghostObject.userData = { name: "npc-" + character.netID , obj : character};
  
      ghostObject.setFriction( 0.8);
      ghostObject.setRestitution( 0.0);
  
      const z = new Ammo.btVector3(0,0,0);
  
      const characterObj = new Ammo.btKinematicCharacterController( ghostObject, myShape, 0.25);
      characterObj.setJumpSpeed( 1 );
      characterObj.setMaxSlope( 1 );
      characterObj.setFallSpeed( 40.0 );
      //characterObj.setGravity( - this.physicsUniverse.getGravity().y() );
      characterObj.setUseGhostSweepTest( true );
      characterObj.setVelocityForTimeInterval(z , 1);
      
      this.physicsUniverse.addCollisionObject(ghostObject, worldGroup, worldMask);
      this.physicsUniverse.addAction(characterObj);
  
      characterObj.warp(origin);
      ghostObject.setWorldTransform( transform );
      character.physicsBody = ghostObject;
      */

    
      const defaultMotionState = new Ammo.btDefaultMotionState(transform);
      const localInertia = new Ammo.btVector3(0, 0, 0);
      myShape.calculateLocalInertia(0, localInertia);
      const RBody_Info = new Ammo.btRigidBodyConstructionInfo(0, defaultMotionState, myShape, localInertia);
      
      RBody_Info.set_m_angularSleepingThreshold(0.1);
      RBody_Info.set_m_linearSleepingThreshold(0.1);
  
      const RBody = new Ammo.btRigidBody(RBody_Info);

      //this.physicsUniverse.updateSingleAABB(RBody);

     
      RBody.setFriction(0.2);
      RBody.setRollingFriction(0.5);
      RBody.setRestitution(0.1);
      RBody.setWorldTransform( transform );



      character.physicsBody = RBody;

      character.physicsBody.userData = { name: "npc-" + character.netID , obj : null, char : character};
        
      this.physicsUniverse.addRigidBody(RBody, worldGroup, worldMask);
  
      character.ammoData = {transform: transform, shape: myShape, char : character};
 
     
      this.character_List.push({  netID: character.netID, object: character});
      
      const phychar ={  id: character.id, 
                        actions : character.actions, 
                        dir:new THREE.Vector3(),
                        triggerIDX : -1, 
                        active : true,
                        trigger : null, 
                        tpos: new THREE.Vector3(),
                        pathStartPts : character.pathStartPts,
                        ragdoll: false, 
                        currentDialogLine : 0,
                        currentDialogSubLine : 0,
                        netID: character.netID, 
                        charScale : character.charScale,
                        pathPts : character.pathStartPts,
                        physicsBody: RBody, 
                        pos: new THREE.Vector3(character.pos.x, character.pos.y, character.pos.z ), 
                        rot: new THREE.Quaternion (character.rot.x, character.rot.y, character.rot.z, character.rot.w ), 
                

                        anims :[], ammoData: character.ammoData};
      this.npcs.push(phychar);
  
      //Ammo.destroy(z);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
    }

    stopPhyNPC(id){
      for(let n = 0; n <this.npcs.length;n++){
        if(this.npcs[n].id == id){
          this.npcs[n].dir.set(0,0,0);
          return;
        }
      }
    }


    
    stopPhyChar(id){
      for(let n = 0; n <this.characters.length;n++){
        if(this.characters[n].id == id){
          
          const char = this.characters[n];
          char.dir.set(0,0,0);
          this.tmpVec3.set(char.dir.x, char.dir.y, char.dir.z);  

          /*
          this.characters[n].ammoData.char.preStep(this.physicsUniverse);
          this.characters[n].ammoData.char.preStep(this.physicsUniverse);
          this.characters[n].ammoData.char.preStep(this.physicsUniverse);
          this.characters[n].ammoData.char.preStep(this.physicsUniverse);
          */

          this.characters[n].ammoData.char.setWalkDirection(this.tmpVec3);
        
          //Ammo.destroy(btDir);
        }
      }
    }

    movePhyChar(id, dir, pos = null){
      for(let n = 0; n <this.characters.length;n++){
        if(this.characters[n].id == id){
          
          this.characters[n].dir.set(dir.x, dir.y, dir.z);
          this.tmpVec3.setValue(dir.x, dir.y, dir.z);  

          /*
          this.characters[n].ammoData.char.preStep(this.physicsUniverse);
          this.characters[n].ammoData.char.preStep(this.physicsUniverse);
          this.characters[n].ammoData.char.preStep(this.physicsUniverse);
          this.characters[n].ammoData.char.preStep(this.physicsUniverse);
          */

          this.characters[n].ammoData.char.setWalkDirection(this.tmpVec3);
          return;
        
          //Ammo.destroy(btDir);
        }
      }
    }

    rotatePhyChar(id, angle){

        for(let n = 0; n <this.characters.length;n++){
            if(this.characters[n].id == id){

                const eul = new THREE.Euler(0,angle,0,"XYZ");
                this.characters[n].rot.multiply(new THREE.Quaternion().setFromEuler(eul));
                const quat = new Ammo.btQuaternion(this.characters[n].rot.x, this.characters[n].rot.y, this.characters[n].rot.z, this.characters[n].rot.w);

                const tform = this.characters[n].physicsBody.getWorldTransform();
                const origin = tform.getOrigin();

                //const transform = new Ammo.btTransform();   
                this.tmpTransformation.setIdentity();
                this.tmpTransformation.setOrigin(origin);
                this.tmpTransformation.setRotation(quat);

                this.characters[n].physicsBody.setWorldTransform( this.tmpTransformation );
                //this.characters[n].ammoData.char.set_m_currentOrientation(quat);

                Ammo.destroy(tform);
                //Ammo.destroy(transform);
                Ammo.destroy(origin);
                Ammo.destroy(quat);
                return;
            }
        }
    }

    teleportPhyChar(id , pos, angle){

      for(let n = 0; n <this.characters.length;n++){
        if(this.characters[n].id == id){
          
            const eul = new THREE.Euler(0,angle,0,"XYZ");
            this.characters[n].rot.setFromEuler(eul);
            const quat = new Ammo.btQuaternion(this.characters[n].rot.x, this.characters[n].rot.y, this.characters[n].rot.z, this.characters[n].rot.w);


            const origin = new Ammo.btVector3(pos.x, pos.y, pos.z);  
            const btquat = new Ammo.btQuaternion(quat.x, quat.y, quat.z, quat.w);
            this.characters[n].ammoData.char.warp(origin);

            const transform = new Ammo.btTransform();   
            transform.setIdentity();
            transform.setOrigin(origin);
            transform.setRotation(quat);

            this.characters[n].physicsBody.setWorldTransform( transform );

            /*
            const transform = this.characters[n].physicsBody.getWorldTransform();
            transform.setRotation(btquat);
            this.characters[n].physicsBody.setWorldTransform( transform );
            */

            Ammo.destroy(transform);
            Ammo.destroy(origin);
            Ammo.destroy(btquat);
        }
    }


    }

    setPhyCharRotation(id, angle){

      for(let n = 0; n <this.characters.length;n++){
          if(this.characters[n].id == id){

              const eul = new THREE.Euler(0,angle,0,"XYZ");
              this.characters[n].rot.setFromEuler(eul);
              const quat = new Ammo.btQuaternion(this.characters[n].rot.x, this.characters[n].rot.y, this.characters[n].rot.z, this.characters[n].rot.w);

              
              const tform = this.characters[n].physicsBody.getWorldTransform();
              const origin = tform.getOrigin();

              const transform = new Ammo.btTransform();   
              transform.setIdentity();
              transform.setOrigin(origin);
              transform.setRotation(quat);

              this.characters[n].physicsBody.setWorldTransform( transform );
              //this.characters[n].ammoData.char.set_m_currentOrientation(quat);

              Ammo.destroy(transform);
              Ammo.destroy(origin);
              Ammo.destroy(quat);
          }
      }
    }

    setPhyNPCRotation(id, angle){

      for(let n = 0; n <this.npcs.length;n++){
          if(this.npcs[n].id == id){

              const eul = new THREE.Euler(0,angle,0,"XYZ");
              this.npcs[n].rot.setFromEuler(eul);
              const quat = new Ammo.btQuaternion(this.npcs[n].rot.x, this.npcs[n].rot.y, this.npcs[n].rot.z, this.npcs[n].rot.w);

              
              const tform = this.npcs[n].physicsBody.getWorldTransform();
              const origin = tform.getOrigin();

              const transform = new Ammo.btTransform();   
              transform.setIdentity();
              transform.setOrigin(origin);
              transform.setRotation(quat);

              this.npcs[n].physicsBody.setWorldTransform( transform );
              //this.characters[n].ammoData.char.set_m_currentOrientation(quat);

              Ammo.destroy(transform);
              Ammo.destroy(origin);
              Ammo.destroy(quat);
          }
      }
    }

    CustomMaterialCombinerCallback(cp,colObj0, partId0, index0, colObj1, partId1, index1)
    {
       Ammo.btAdjustInternalEdgeContacts(cp,colObj1,colObj0, partId1,index1);
       return true;
    }

    addPhyCharacterController(character) {

        const netID = character.id;

        character.netID = netID;
        character.ppos = new THREE.Vector3(character.pos.x, character.pos.y, character.pos.z) 
  
        const origin = new Ammo.btVector3(character.pos.x, character.pos.y, character.pos.z);  
        const quat = new Ammo.btQuaternion(character.rot.x, character.rot.y, character.rot.z, character.rot.w);
        const transform = new Ammo.btTransform();   
        transform.setIdentity();
        transform.setOrigin(origin);
        transform.setRotation(quat);
    
        const h = Math.max(0, character.avatarDef.height - character.avatarDef.radius*2.0);
        const myShape = new Ammo.btCapsuleShape (character.avatarDef.radius, h);
        
        const ghostObject = new Ammo.btPairCachingGhostObject();
        ghostObject.setWorldTransform( transform );
        ghostObject.setCollisionShape( myShape );
        ghostObject.setCollisionFlags ( 16 );
        
        ghostObject.setActivationState( DISABLE_DEACTIVATION );
        ghostObject.activate();
        ghostObject.userData = { name: "character-" + netID  , obj : null, char : character};
        

        ghostObject.setFriction( 0.0);
        ghostObject.setRestitution( 0.0);
    
        const z = new Ammo.btVector3(0,0,0);

        const up = new Ammo.btVector3(0,1,0);
    
        const characterObj = new Ammo.btKinematicCharacterController( ghostObject, myShape, 0.1);
        characterObj.setJumpSpeed( 1 );
        characterObj.setMaxSlope( 0.7 );
        
        characterObj.setFallSpeed( 4.0 );
        characterObj.setGravity( 9 );
        
        characterObj.setUseGhostSweepTest( true );
        characterObj.setVelocityForTimeInterval(z , 1);
        
        this.physicsUniverse.addCollisionObject(ghostObject, worldGroup, worldMask);
        this.physicsUniverse.addAction(characterObj);


        this.physicsUniverse.getBroadphase().getOverlappingPairCache().setInternalGhostPairCallback(new Ammo.btGhostPairCallback());	

        characterObj.warp(origin);
    
        character.ammoData = {transform: transform, shape: myShape, char : characterObj, physicsBody: ghostObject };
        
        ghostObject.setWorldTransform( transform );
    
        character.physicsBody = ghostObject;
        
        //this.character_List.push({  netID: netID, object: ghostObject, char : characterObj});
        this.character_List.push({  netID: netID, object: character, char : characterObj});
        
        const phychar ={ id: character.id,  tpos: new THREE.Vector3(), charScale : character.charScale ? character.charScale : 1.0 , triggerIDX : -1, trigger : null, tmpQ : new THREE.Quaternion(), tmpEUL : new THREE.Euler(), actions : character.actions, netID: netID, physicsBody: ghostObject, dir : new THREE.Vector3(), pos: new THREE.Vector3(character.pos.x, character.pos.y, character.pos.z), rot: new THREE.Quaternion(character.rot.x, character.rot.y, character.rot.z,character.rot.w), ammoData: character.ammoData};
        this.characters.push(phychar);

    
        Ammo.destroy(z);
        Ammo.destroy(origin);
        Ammo.destroy(quat);
        Ammo.destroy(transform);

        return phychar;
    }

    setCharScale(character, scale){


      const phychar = this.getChar(character.id);
      if(!phychar)
          return;

      phychar.charScale = scale;

      let transform = null;

      if(character.physicsBody){
        this.physicsUniverse.removeAction(character.ammoData.char);
        this.physicsUniverse.removeCollisionObject(character.physicsBody);
        transform = character.physicsBody.getWorldTransform();
      }

    

      character.ammoData.shape = new Ammo.btCapsuleShape (character.avatarDef.radius * phychar.charScale, character.avatarDef.height  * phychar.charScale);
     
      const ghostObject = new Ammo.btPairCachingGhostObject();
      ghostObject.setWorldTransform( transform );
      ghostObject.setCollisionShape( character.ammoData.shape );
      ghostObject.setCollisionFlags ( 16 );
      ghostObject.setActivationState( DISABLE_DEACTIVATION );
      ghostObject.activate();
      ghostObject.userData = { name: "character-" + character.netID , obj : character};
  
      ghostObject.setFriction( 0.8);
      ghostObject.setRestitution( 0.0);

      phychar.physicsBody = ghostObject;

      const characterObj = new Ammo.btKinematicCharacterController( phychar.physicsBody, character.ammoData.shape, 0.25);
      characterObj.setJumpSpeed( 1 );
      characterObj.setMaxSlope( 1 );
      characterObj.setFallSpeed( 400.0 * phychar.charScale );
      characterObj.setGravity( - this.physicsUniverse.getGravity().y() );
      characterObj.setUseGhostSweepTest( true );

      character.ammoData.char = characterObj;
      character.physicsBody = ghostObject;
      
      this.physicsUniverse.addCollisionObject(phychar.physicsBody,  worldGroup, worldMask);
      this.physicsUniverse.addAction(characterObj);

      for(let c of this.character_List){
        if(c.netID === character.netID){
          c.object = ghostObject;
          c.char = characterObj;
          break;
        }
      }

      //this.physicsUniverse.removeCollisionObject(oldShape);

    }
  
    addPhyCharacter(player) {

      
  
     
      const character = player.character;
      const car = player.car;

      if(car){
        let netID = 0;
        const phycar = this.getCar(car.carDef.id);
    
        if(car.carDef.type === 3){
    
          const a = car.carDef.id.substr(0, 44);
          const b = new Uint8Array(base58_to_binary(a));
          var view = new DataView(b.buffer, 0);
          netID  = view.getUint32(0, true); 
        }else if (car.carDef.type === -2){
  
          if((typeof car.carDef.type == 0)||(typeof car.carDef.type == 1)){
            netID  = car.carDef.id;
          }else{
            const a = car.carDef.id.substr(0, 44);
            const b = new Uint8Array(base58_to_binary(a));
            var view = new DataView(b.buffer, 0);
            netID  = view.getUint32(0, true); 
          }
  
        }else
          netID  = car.carDef.id;    

        character.rot.set(phycar.quat.x, phycar.quat.y, phycar.quat.z, phycar.quat.w);
        const pos_char = new THREE.Vector3(-2.0, 1.0, 0).add(phycar.pos);
        character.pos.set(pos_char.x, pos_char.y, pos_char.z);
        character.netID = netID;
          
      }else{
        character.netID = character.id;
      }
        
      
      const origin = new Ammo.btVector3(character.pos.x, character.pos.y, character.pos.z);  
      const quat = new Ammo.btQuaternion(character.rot.x, character.rot.y, character.rot.z, character.rot.w);
      const transform = new Ammo.btTransform();   
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
  
      const myShape = new Ammo.btCapsuleShape (character.avatarDef.radius, character.avatarDef.height);
      
      const ghostObject = new Ammo.btPairCachingGhostObject();
      ghostObject.setWorldTransform( transform );
      ghostObject.setCollisionShape( myShape );
      ghostObject.setCollisionFlags ( 16 );
      ghostObject.setActivationState( DISABLE_DEACTIVATION );
      ghostObject.activate();
      ghostObject.userData = { name: "character-" + character.netID , obj : character};
  
      ghostObject.setFriction( 0.8);
      ghostObject.setRestitution( 0.0);
  
      const z = new Ammo.btVector3(0,0,0);
  
      const characterObj = new Ammo.btKinematicCharacterController( ghostObject, myShape, 0.25);
      characterObj.setJumpSpeed( 1 );
      characterObj.setMaxSlope( 1 );
      characterObj.setFallSpeed( 40.0 );
      //characterObj.setGravity( - this.physicsUniverse.getGravity().y() );
      characterObj.setUseGhostSweepTest( true );
      characterObj.setVelocityForTimeInterval(z , 1);
      
      this.physicsUniverse.addCollisionObject(ghostObject,  worldGroup, worldMask);

      
      this.physicsUniverse.addAction(characterObj);
      this.physicsUniverse.getBroadphase().getOverlappingPairCache().setInternalGhostPairCallback(new Ammo.btGhostPairCallback());	
  
      characterObj.warp(origin);
  
      character.ammoData = {transform: transform, shape: myShape, char : characterObj };
  
      ghostObject.setWorldTransform( transform );
  
      character.physicsBody = ghostObject;
      this.character_List.push({  netID: character.netID, object: ghostObject, char : characterObj});
      
      const phychar ={  id: car ? car.carDef.id : character.netID, 
                        actions: character.actions, 
                        netID: character.netID, 
                        charScale : character.charScale,
                        physicsBody: ghostObject, 
                        tpos: new THREE.Vector3(),
                        speed: character.avatarDef.speed, 
                        pos: { x: character.pos.x, y: character.pos.y, z: character.pos.z }, 
                        rot: new THREE.Quaternion (character.rot.x, character.rot.y, character.rot.z, character.rot.w), 
                        ammoData: character.ammoData};
      this.characters.push(phychar);

 
      Ammo.destroy(z);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
    }
      getEngineForce(car, rpm, gear) {

            
        const x = rpm;
        //const Torque = Math.max(0, -0.0000043*x*x+.033*x+125);
        /*
        this.carDef.motor.a = -0.0000048;
        this.carDef.motor.b = 0.04;
        this.carDef.motor.c = 125;
        */

        /*
        car.carDef.motor.a = -0.00000310;
        car.carDef.motor.b = 0.0125;
        car.carDef.motor.c = 110;
        */
        
        const Torque = Math.max(0, car.carDef.motor.a*x*x+car.carDef.motor.b*x+car.carDef.motor.c);
        const Force = car.carDef.motor.maxEngineForce * car.EngineForceFactor * Torque / (55);
        return Force;
    }
    addPhyCar(car, dmg, startPts, newsp = null, AIProfile = null) {
  
      let netID = 0;
      

      /*
      if(car.isAI){
        netID = 0x12340000 | AIProfile.id;
      }else*/
      if(car.carDef.type === 3){
  
        const a = car.carDef.id.substr(0, 44);
        const b = new Uint8Array(base58_to_binary(a));
        var view = new DataView(b.buffer, 0);
        netID  = view.getUint32(0, true); 
      }else if (car.carDef.type === -2){

          if((car.carDef.type == 0)||(car.carDef.type == 1)||(car.carDef.type == -2)){
            netID  = car.carDef.id;
          }else{
            const a = car.carDef.id.substr(0, 44);
            const b = new Uint8Array(base58_to_binary(a));
            var view = new DataView(b.buffer, 0);
            netID  = view.getUint32(0, true); 
          }

      }else
        netID  = car.carDef.id.toString();    
  
      // Chassis

      car.tdown = new THREE.Vector3();
      car.down = new Ammo.btVector3();

      car.p = new Ammo.btVector3();
      car.q = new Ammo.btQuaternion();
      car.t = new Ammo.btTransform();
      

      
      
      const ratio = 0.54; 
  
      const masses = [car.massVehicle * ratio, car.massVehicle  * (1-ratio)];
  
      const origin = new Ammo.btVector3(car.center.x, car.center.y, car.center.z);  
      const quat = new Ammo.btQuaternion(car.quat.x, car.quat.y, car.quat.z, car.quat.w);
      
      const gorigin = new Ammo.btVector3(0, meshsInfos[car .carDef.chassis].chassisOffset, +car.chassisLength * .25 );
      const gorigin2 = new Ammo.btVector3(0, meshsInfos[car .carDef.chassis].chassisOffset , -car.chassisLength * .25);
  
      const com = new Ammo.btVector3(meshsInfos[car .carDef.chassis].extents.center.x, meshsInfos[car.carDef.chassis].extents.center.y, meshsInfos[car.carDef.chassis].extents.center.z);
      const gtransform = new Ammo.btTransform();
      gtransform.setIdentity();
      gtransform.setOrigin(com);
  
      /*
      const geometry = new Ammo. btCapsuleShapeZ (car.chassisWidth * 1.2 /2, car.chassisLength);
      const gscale2 = new Ammo.btVector3(1, car.chassisHeight * 0.5/ car.chassisWidth, 1);
      geometry.setLocalScaling(gscale2);
      */
  
      const myShape = new Ammo.btCompoundShape (true, 1);
      const size = new Ammo.btVector3(car.chassisWidth * .5, car.chassisHeight * .5, car.chassisLength * .25);
      
      const geometry = new Ammo. btBoxShape (size);
      const gtransform2 = new Ammo.btTransform();
      
      gtransform2.setIdentity();
      gtransform2.setOrigin(gorigin);
      myShape.addChildShape (gtransform2, geometry);
  
      const geometry2 = new Ammo. btBoxShape (size);
      const gtransform3 = new Ammo.btTransform();
      gtransform3.setIdentity();
      gtransform3.setOrigin(gorigin2);
      myShape.addChildShape (gtransform3, geometry2);

      //geometry.setMargin(0.4);
      //geometry2.setMargin(0.4);
      
      const localInertia = new Ammo.btVector3(0, 0, 0);
      //myShape.calculateLocalInertia(car.massVehicle, localInertia);
      myShape.calculatePrincipalAxisTransform2(masses[0],masses[1], gtransform2, localInertia);
  
      /*
      const localInertia = new Ammo.btVector3(0, 0, 0);
      geometry.calculateLocalInertia(car.massVehicle, localInertia);
      */
      
      const transform = new Ammo.btTransform();   
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      
  
      const motionState = new Ammo.btDefaultMotionState(transform, gtransform);
      const infos = new Ammo.btRigidBodyConstructionInfo(car.massVehicle, motionState, myShape, localInertia);
      const body = new Ammo.btRigidBody(infos);
  
      body.setCcdMotionThreshold(100.0);
      body.setCcdSweptSphereRadius(8);

      body.setRollingFriction(0.8);
      body.setFriction(1.0);
      body.setRestitution(0.0);
  
      body.setActivationState(DISABLE_DEACTIVATION);
  
      body.userData = { name: "car-" + netID, obj : car };
  
      this.physicsUniverse.addRigidBody(body, worldGroup, carMask | worldMask);
  
      Ammo.destroy(infos);
      Ammo.destroy(origin);
      Ammo.destroy(size);
      Ammo.destroy(quat);
      Ammo.destroy(gorigin);
      Ammo.destroy(gorigin2);
      Ammo.destroy(com);
      
  
      car.physicsBody = body;
      // Raycast Vehicle
      const tuning = new Ammo.btVehicleTuning();
      const rayCaster = new Ammo.btDefaultVehicleRaycaster(this.physicsUniverse);
  
      if(this.trackMode == 1)
        rayCaster.set_m_interpolateNormals(2);
      else
        rayCaster.set_m_interpolateNormals(2);

      rayCaster.setCollisionFilter(worldGroup, carMask | worldMask, true);

      //car.carDef.suspensionRestLength = 0.8;
  
      tuning.set_m_maxSuspensionTravelCm(car.carDef.suspensionRestLength * 110 );
  
      const vehicle = new Ammo.btRaycastVehicle(tuning, body, rayCaster);
      vehicle.setCoordinateSystem(0, 1, 2);
      
      this.physicsUniverse.addAction(vehicle);
  
      const wheelDirectionCS0 = new Ammo.btVector3(0, -1, 0);
      const wheelAxleCS = new Ammo.btVector3(-1, 0, 0);
      const self = this;
  
      const ammoData = {  localInertia: localInertia, 
                          motionState: motionState, 
                          transform: transform,
                          gtransform: gtransform,
                          gtransform2: gtransform2,
                          gtransform3: gtransform3,
                          geometry: [geometry, geometry2], 
                          shape: myShape, 
                          tuning: tuning, 
                          rayCaster: rayCaster 
                       };
  
      const phycar ={ id: car.carDef.id, mt : new Ammo.btTransform(), from: new THREE.Vector3(), to: new THREE.Vector3(), passengers: [], targetNode : -1, massVehicle: car.massVehicle, gear : 0, RPM : 0, netID: netID, isAI: car.isAI, triggerIDX : -1, trigger : null, isStuck : false, started : false, laps : [], chassisLength: car.chassisLength, chassisHeight: car.chassisHeight, chassisWidth: car.chassisWidth, lapInfos : null , multiplier:1.0, lapNitros : 0, lapDmg : 0, nitros : car.nitros, usedNitros : 0, curSeg: startPts, nLaps: 0, lastLap: 0, bonuses: [], contacts: [], physicsBody: body, initialdmg: dmg, damage: 0, pos: new THREE.Vector3( car.center.x, car.center.y, car.center.z), quat: new THREE.Quaternion(car.quat.x, car.quat.y, car.quat.z, car.quat.w), wheels: [], vehicle : vehicle, ammoData: ammoData, newAttachedObjects : 0, attachedObjects: [], newTakenObjects : 0, TakenObjects: [], shots : [], weapons : [], newbonuses: [], techs : [], newtechs: [], blueprints : 0, lastShotId : 1};
      phycar.target = car.target;

      phycar.from = new THREE.Vector3();
      phycar.to = new THREE.Vector3();
      phycar.contactNorm = new THREE.Vector3();
      phycar.norm = new THREE.Vector3();

      phycar.curve2 = new THREE.CatmullRomCurve3();

      phycar.nForward = new THREE.Vector3();
      phycar.dirAngle = new THREE.Quaternion();
      phycar.dirAngleEul = new THREE.Euler();
      phycar.shotEul =  new THREE.Euler();

      if(phycar.isAI){
        phycar.AIProfile = AIProfile ? AIProfile : profiles[0];
      }

      phycar.pt = new THREE.Vector3();
      phycar.stv = new THREE.Vector3();
      phycar.sv = new Ammo.btVector3(0, 0, 0);

      if(car.carDef.weapon)
        phycar.weapons[0] = {name: car.carDef.weapon.name, model : car.carDef.weapon.model , projSpeed : car.carDef.weapon.projSpeed, projMass : car.carDef.weapon.projMass, projDelay : car.carDef.weapon.projDelay, projSize : car.carDef.weapon.projSize, durability : car.carDef.weapon.durability, power : car.carDef.weapon.power, blastForce: car.carDef.weapon.blastForce, blastRadius : car.carDef.weapon.blastRadius, dmg : car.carDef.weapon.dmg, pt : {x: 0, y: car.chassisHeight / 4 + 1.5, z: 1.0}, lastShot : 0 };

      if(car.carDef.battery){
        phycar.battery = {name: car.carDef.battery.name, model : car.carDef.battery.model , power: car.carDef.battery.power , charge: car.carDef.battery.charge };
        phycar.power =  phycar.battery.power;
      }
      else
        phycar.power = 0;

      //phycar.weapons[0] = {name: car.carDef.weapon.name, model : 'car_cannon',projSpeed : 18800, projMass : 170, projDelay : 100, projSize : 0.025, pt : {x: 0, y: car.chassisHeight / 4 + 1.2, z: 1.0}, dmg : 10, lastShot : 0 };

  
      phycar.RPM = 0;
      phycar.nextNitro = 0;
  
      this.cars.push(phycar);
  
      function addCarWheel(isFront, pos, radius) {
  
        if (!car.carDef.suspension)
          return;
  

        /* racing
        var friction = 1.9;
        var suspensionStiffness = 27;
        var suspensionDamping = 1.8;
        var suspensionCompression = 1.1;
        var suspensionRestLength = meshsInfos[car.carDef.chassis].suspensionRestLength;
        var rollInfluence = meshsInfos[car.carDef.chassis].rollInfluence;
        var suspensionForce = 1800 ? car.carDef.suspension.force : 6000;
        */

        const sus = newsp !== null ? newsp:car.carDef.suspension;


        let friction;
        let suspensionStiffness;
        let suspensionDamping;
        let suspensionCompression;
        let suspensionRestLength;
        let rollInfluence;
        let suspensionForce;


        car.transmission = 0.8;

        friction = sus.friction; //1.4;
        suspensionStiffness = sus.stiffness;
        suspensionDamping = sus.damping;// * 1.5;
        suspensionCompression = sus.compression;// * 2.0;
        suspensionRestLength = car.carDef.suspensionRestLength;
        rollInfluence = meshsInfos[car.carDef.chassis].rollInfluence;
        suspensionForce = sus.force;// * 1.5;

        /*
        if(!isFront)
          suspensionRestLength += 0.1;
        */

        if(car.carDef.chassis == 'car3'){

          if(car.isAI){
            car.physicsBody.setDamping(0.075, 0.4);
            car.EngineForceFactor = 1.2;
          }
        }

        if(car.carDef.suspension.name == 'tire drift'){
            friction = 0.4;
        }
        
        /*
        if(car.carDef.suspension.name == 'tire buggy'){

        }else if(car.carDef.suspension.name == 'tire dirt'){
          friction = 0.4;
          suspensionStiffness = 34;
          suspensionForce = 24000.0;
        }else if(car.carDef.suspension.name == 'tire racing'){
          friction = 0.7;
        }else
        {
          friction = sus.friction;
          suspensionStiffness = sus.stiffness;
          suspensionDamping = sus.damping;
          suspensionCompression = sus.compression;
          suspensionRestLength = car.carDef.suspensionRestLength;
          rollInfluence = meshsInfos[car.carDef.chassis].rollInfluence;
          suspensionForce = sus.force ? sus.force : 6000;
        }
        
        
        // buggy 
        
        if(car.carDef.suspension.name == 'tire buggy'){
          friction = 4.0;//car.carDef.suspension.friction;
          suspensionStiffness = 11.0; //car.carDef.suspension.stiffness* 1.4;
          suspensionDamping = 0.4; //car.carDef.suspension.damping;
          suspensionCompression = 1.43; //car.carDef.suspension.compression;
          suspensionRestLength = car.carDef.suspensionRestLength;
          rollInfluence = meshsInfos[car.carDef.chassis].rollInfluence;
          suspensionForce = 4500.0; //car.carDef.suspension.force * 1.4 ? car.carDef.suspension.force : 6000;
        }
        
        */

        /*
        if(car.carDef.suspension.name == 'tire rally C'){
          suspensionStiffness = 28;
          suspensionCompression = 0.6;
          suspensionForce = 4500;
          
        }
        
        if(car.carDef.chassis == 'car4'){
          suspensionCompression *= 1.5;
          suspensionStiffness *= 1.68;
          suspensionDamping *= 0.2;
          //friction *= 0.5;

        }

        if(car.carDef.chassis == 'davinci'){
          suspensionForce *= 1.4;
          suspensionStiffness *= 1.2;
          suspensionCompression *= 1.5;
          //friction *= 0.5;
       }

       if(car.carDef.suspension.name == 'tire racing C'){
           suspensionCompression *= 1.5;
           suspensionStiffness *= 1.68;
           suspensionDamping *= 0.2;
           //friction *= 0.5;
        }
        */

        //"force": , "stiffness": 10.0, "compression": 1.4, "relaxation" : 1.4, "friction": 2.4
          
        /*
        if(pos.x<0)
          wheelAxleCS.x = -1;
        else
          wheelAxleCS.x = 1;
        */
  
        var wheelInfo =phycar.vehicle.addWheel(
          pos,
          wheelDirectionCS0,
          wheelAxleCS,
          suspensionRestLength,
          radius,
          tuning,
          isFront);
  
  
        wheelInfo.set_m_suspensionStiffness(suspensionStiffness);
        wheelInfo.set_m_wheelsDampingRelaxation(suspensionDamping); 
        wheelInfo.set_m_wheelsDampingCompression(suspensionCompression);
        wheelInfo.set_m_frictionSlip(friction); 
        wheelInfo.set_m_rollInfluence(rollInfluence);
        wheelInfo.set_m_maxSuspensionForce(suspensionForce);
        
      
  
  
        /* class B racing */
        
        /*
        wheelInfo.set_m_suspensionStiffness(28);  //105
        wheelInfo.set_m_wheelsDampingCompression(1.0); //0.6
        wheelInfo.set_m_wheelsDampingRelaxation(0.1); // 0.8
        wheelInfo.set_m_frictionSlip(3.9);
        wheelInfo.set_m_rollInfluence(0.1);
        wheelInfo.set_m_maxSuspensionForce(8000);
        */
        
        /* class B rally */   
        /*
        wheelInfo.set_m_suspensionStiffness(20);  //105
        wheelInfo.set_m_wheelsDampingCompression(0.4); //0.6
        wheelInfo.set_m_wheelsDampingRelaxation(1.4); // 0.8
        wheelInfo.set_m_frictionSlip(10.5);
        wheelInfo.set_m_rollInfluence(0.1);
        wheelInfo.set_m_maxSuspensionForce(14000);
        */
        /* class B stunt */   
          
        /*
        wheelInfo.set_m_suspensionStiffness(105);  //105
        wheelInfo.set_m_wheelsDampingCompression(0.1); //0.6
        wheelInfo.set_m_wheelsDampingRelaxation(1.0); // 0.8
        wheelInfo.set_m_frictionSlip(10.5);
        wheelInfo.set_m_rollInfluence(0.1);
        wheelInfo.set_m_maxSuspensionForce(35000);
        */
          
        /*
        wheelInfo.set_m_suspensionStiffness(22.23);  //105
        wheelInfo.set_m_wheelsDampingRelaxation(0.4); // 0.8
        wheelInfo.set_m_wheelsDampingCompression(0.6); //0.6
        wheelInfo.set_m_frictionSlip(4.9);
        wheelInfo.set_m_maxSuspensionForce(4350.0);
        */
  
        
        //buggy
        /*
        wheelInfo.set_m_wheelsDampingCompression(1.0);
        wheelInfo.set_m_wheelsDampingRelaxation(0.6); // 0.8
        wheelInfo.set_m_maxSuspensionForce(5000);
        wheelInfo.set_m_suspensionStiffness(16.4);  //105
        wheelInfo.set_m_frictionSlip(9.9);
        */
        
        
        
        /*
        mustang
        wheelInfo.set_m_frictionSlip(9.9);
        wheelInfo.set_m_suspensionStiffness(18.23);  //105
        wheelInfo.set_m_wheelsDampingCompression(0.4);
        wheelInfo.set_m_wheelsDampingRelaxation(0.8); // 0.8
        wheelInfo.set_m_maxSuspensionForce(3200);
        */
        
  
        /* class B buggy */
        
        /*
        wheelInfo.set_m_suspensionStiffness(12);
        wheelInfo.set_m_wheelsDampingCompression(0.1);
        wheelInfo.set_m_wheelsDampingRelaxation(0.8);
        wheelInfo.set_m_frictionSlip(3.9);
        wheelInfo.set_m_rollInfluence(0.2);
        wheelInfo.set_m_maxSuspensionForce(6500);
        */
        
        
  
        /*
         racing 
         */
        /*
              wheelInfo.set_m_suspensionStiffness(40);
              wheelInfo.set_m_wheelsDampingRelaxation(1.0);
              wheelInfo.set_m_wheelsDampingCompression(0.6);
              wheelInfo.set_m_frictionSlip(3.9);
              wheelInfo.set_m_rollInfluence(0.1);
              wheelInfo.set_m_maxSuspensionForce(3500);
        */
  
        /* -------------------------------------------------------*/
        /*
        rally
        wheelInfo.set_m_suspensionStiffness(15);
        wheelInfo.set_m_wheelsDampingRelaxation(1.0);
        wheelInfo.set_m_wheelsDampingCompression(0.2  );
        wheelInfo.set_m_frictionSlip(0.9);
        wheelInfo.set_m_rollInfluence(0.1);
        wheelInfo.set_m_maxSuspensionForce(2400);
        */
  
        /* looping A  */
  /*
              wheelInfo.set_m_suspensionStiffness(60);
              wheelInfo.set_m_wheelsDampingRelaxation(1.0);
              wheelInfo.set_m_wheelsDampingCompression(0.2);
              wheelInfo.set_m_frictionSlip(10.0);
              wheelInfo.set_m_rollInfluence(0.01);
              wheelInfo.set_m_maxSuspensionForce(60000);
              */
          wheelInfo.set_m_rollInfluence(0.1);
          return wheelInfo;
      }
  
      for (let n = 0; n < 4; n++) {
        const p = new Ammo.btVector3(-car.wheels[n].opos.x, car.wheels[n].opos.y, car.wheels[n].opos.z);
        const q = new Ammo.btQuaternion(0, 0, 0, 1);
  
        const infos = addCarWheel(car.wheels[n].isFront, p, car.wheels[n].radius);
  
        phycar.wheels.push({ pos: p, quat: q, radius: car.wheels[n].radius, contactR : 1.0, frictionFac: 1.0, infos: infos, contactNormal: new THREE.Vector3(), contactPoint: new THREE.Vector3(), skidding: 1.0 });
      }
  
  
  
      Ammo.destroy(wheelDirectionCS0);
      Ammo.destroy(wheelAxleCS);
  
      var vehicleSteering = 0;
  
      sync(null, 0, 0);
  
  
      // Sync keybord actions and physics and graphics
      function sync(track, dt , time) {
  
        if (!car.carDef.motor)
          return;

        if((car.carDef.battery)&&(phycar.power < car.carDef.battery.power)){
          phycar.power += car.carDef.battery.charge * dt;
        }
  
        var engineForce = 0;
        var breakingForce = 0;
        
        var steeringIncrement = 0.028;
        var steeringClamp = 0.35;  // buggy 0.14 //0.12;//

        var steeringIncrement = car.carDef.steeringIncrement* 1.5;
        var steeringClamp = car.carDef.steeringClamp;  // buggy 0.14 //0.12;//

        if(car.speed < 20)
            steeringClamp *= 4;
        
        var maxEngineForce = car.carDef.motor.maxEngineForce;
        var maxBreakingForce = car.carDef.motor.maxBreakingForce;
        var speed = parseFloat(phycar.vehicle.getCurrentSpeedKmHour());

        
        if (car.actions['next-target'] > 0) {
          self.nextCarTargets(track, phycar);
          car.actions['next-target'] = false;
        }

        if (car.actions['next-subsystem'] > 0) {
          self.nextCarTargetsSubSystem(track, phycar);
          car.actions['next-subsystem'] = false;
        }

        if (car.actions.acceleration) {
          if (speed < -1)
          {
            //self.enableBraking(phycar, car);
            breakingForce = maxBreakingForce * 10;
          } else {
  
            self.disableBraking(phycar, car);
            //engineForce = maxEngineForce;
            phycar.RPM += dt * (4000 / (phycar.gear + 1));
            phycar.RPM = Math.min(phycar.RPM, 8000);
          }
        } else {
          phycar.RPM -= dt * 2000;
          phycar.RPM = Math.max(phycar.RPM, 0);
        }
    
        if (phycar.RPM < 100) {

          if(phycar.gear>0){
            phycar.gear--;
            phycar.RPM = 1500;
          }
        }else if ((phycar.RPM > 7500)&&(phycar.gear<6)) {
          phycar.gear++;
          phycar.RPM = 3000;
        }


        const rayCallBack = Ammo.castObject(  self.closestRayResultCallback, Ammo.RayResultCallback );
        rayCallBack.set_m_closestHitFraction( 1 );
        rayCallBack.set_m_collisionObject( null );
        rayCallBack.set_m_collisionFilterGroup(groundGroup);
        rayCallBack.set_m_collisionFilterMask(groundMask);


        self.btFrom.setValue(  phycar.pos.x,  phycar.pos.y - 5,  phycar.pos.z );
        self.btTo.setValue( phycar.pos.x,  phycar.pos.y + 5,  phycar.pos.z );
        self.closestRayResultCallback.get_m_rayFromWorld().setValue(  phycar.pos.x,  phycar.pos.y - 5,  phycar.pos.z );
        self.closestRayResultCallback.get_m_rayToWorld().setValue( phycar.pos.x,  phycar.pos.y + 5,  phycar.pos.z );

      // Perform ray test
        self.physicsUniverse.rayTest( self.btFrom, self.btTo,  self.closestRayResultCallback );

        const f = rayCallBack.get_m_closestHitFraction();
        if ( f<1 ) {
          const point = self.closestRayResultCallback.get_m_hitPointWorld();
          const normal = self.closestRayResultCallback.get_m_hitNormalWorld();
          if(phycar.pos.y<point.y()){

              self.tmpV3.set(normal.x(), -normal.y(), normal.z())               //const n = new THREE.Vector3(pts[0].face.normal.x, -pts[0].face.normal.y, pts[0].face.normal.z);
              self.tmpQ.setFromUnitVectors(self.tmpV3, self.upVec );

              car.p.setValue(phycar.pos.x, point.y() + 4.0, phycar.pos.z);
              car.q.setValue(self.tmpQ.x, self.tmpQ.y, self.tmpQ.z, self.tmpQ.w); 

              const t = phycar.physicsBody.getWorldTransform();
              t.setOrigin(car.p);
              t.setRotation(car.q);
              phycar.physicsBody.setWorldTransform(t);

              const tmpVel = phycar.physicsBody.getLinearVelocity();
              self.tmpVec3.setValue(tmpVel.x(),0,tmpVel.z());
              phycar.physicsBody.setLinearVelocity(self.tmpVec3);

              self.tmpVec3.setValue(0,0,0);
              phycar.physicsBody.setAngularVelocity(self.tmpVec3);                  
            }
        }

        /*
        phycar.from.set( phycar.pos.x, phycar.pos.y - 50, phycar.pos.z );
        phycar.to.set( 0, +1.0, 0 );
        self.raycaster.set(phycar.from, phycar.to);
         const objs = self.tGroundMesh ? [self.tGroundMesh]: [self.tRoadMesh, self.tWallMesh];

        if(objs){
          const pts = self.raycaster.intersectObjects(objs);

          if((pts.length >0)&&(pts[0].face.normal.y > 0)) {
            // Raycast also hits self capsule, ignore that case.
            const a = pts[0].point;

            if(phycar.pos.y<pts[0].point.y){

              //const n = new THREE.Vector3(pts[0].face.normal.x, -pts[0].face.normal.y, pts[0].face.normal.z);
              this.tmpQ.setFromUnitVectors(pts[0].face.normal, self.upVec );

              car.p.setValue(phycar.pos.x, pts[0].point.y + 4.0, phycar.pos.z);
              car.q.setValue(this.tmpQ.x, this.tmpQ.y, this.tmpQ.z, this.tmpQ.w); 

              const t = phycar.physicsBody.getWorldTransform();
              t.setOrigin(car.p);
              t.setRotation(car.q);
              phycar.physicsBody.setWorldTransform(t);

              this.tmpVec3 = phycar.physicsBody.getLinearVelocity();
              this.tmpVec3.setValue(this.tmpVec3.x(),0,this.tmpVec3.z());
              phycar.physicsBody.setLinearVelocity(this.tmpVec3);

              this.tmpVec3.setValue(0,0,0);
              phycar.physicsBody.setAngularVelocity(this.tmpVec3);                  
            }
          }
        }
        */
        
        
        
        for(let w = 0; w < phycar.weapons.length; w++){

          const weapon = phycar.weapons[w];
          const aid = 'shoot'+w;

          if (car.actions[aid]) {

            if((time - weapon.lastShot) > weapon.projDelay / 1000.0) {

              if(phycar.power >= weapon.power){
                self.addCarShot(phycar, car.carDef, w, time);
                weapon.lastShot = time;
                phycar.lastShotId++;
                phycar.power -= weapon.power;
              }
           }
        }

        let i=0;
        while(i<phycar.shots.length) {
           if((time - phycar.shots[i].time) >  3 ) {
             self.removePhyShot(phycar.shots[i]);
             phycar.shots.splice(i , 1);
           }
           else{i++;}
         }


        }
  
        if (car.active) {  
                
          engineForce = self.getEngineForce(car, phycar.RPM, phycar.gear);

          if((phycar.gear>0)&&(phycar.RPM> 6000)&&(engineForce<120)){
              phycar.gear--;
              phycar.RPM=2000;
          }


          //console.log('engineForce '+engineForce);
    
          //steeringIncrement=   0.8 * dt ;
    
          if (car.actions.braking) {
            if (speed > 70) {
              breakingForce = maxBreakingForce * 8;
              self.enableBraking(phycar, car);
            } else if(speed >1) {
              breakingForce = maxBreakingForce * 8;
            }else if(!car.actions.acceleration)
              engineForce = -maxEngineForce;
              
          }else{
            self.disableBraking(phycar, car);
          }


          for (let n = 0; n < 4; n++) {
            const infos=phycar.vehicle.getWheelInfo(n);  
            infos.set_m_frictionSlip(car.carDef.suspension.friction / (phycar.wheels[n].frictionFac * phycar.wheels[n].contactR));
          }
  
          if (car.actions.left) {
            if (vehicleSteering < steeringClamp)
              vehicleSteering += steeringIncrement * car.actions.left * 0.1;
          }
          else if (car.actions.right) {   
            if (vehicleSteering > -steeringClamp)
             vehicleSteering -= steeringIncrement * car.actions.right* 0.1;
          } else if (vehicleSteering < -steeringIncrement)
            vehicleSteering += steeringIncrement;
          else if (vehicleSteering > steeringIncrement)
            vehicleSteering -= steeringIncrement;
          else 
            vehicleSteering = 0;
          
          if(car.actions.acceleration) {
            const front = engineForce * (1.0 - car.transmission)* 2.0;
            const back = engineForce * car.transmission * 2.0;

            phycar.vehicle.applyEngineForce(front, FRONT_LEFT);
            phycar.vehicle.applyEngineForce(front, FRONT_RIGHT);

            phycar.vehicle.applyEngineForce(back, BACK_LEFT);
            phycar.vehicle.applyEngineForce(back, BACK_RIGHT);

          } 
          
          if (car.actions.braking) {
  
            if (engineForce < 0) {
              phycar.vehicle.applyEngineForce(engineForce * 0.8, BACK_LEFT);
              phycar.vehicle.applyEngineForce(engineForce * 0.8, BACK_RIGHT);
  
              phycar.vehicle.applyEngineForce(engineForce *1.2, FRONT_LEFT);
              phycar.vehicle.applyEngineForce(engineForce *1.2, FRONT_RIGHT);
            }else{

              phycar.vehicle.setBrake(maxBreakingForce * 8.2, BACK_LEFT);
              phycar.vehicle.setBrake(maxBreakingForce * 8.2, BACK_RIGHT);
        
              phycar.vehicle.setBrake(maxBreakingForce * 1.2, FRONT_LEFT);
              phycar.vehicle.setBrake(maxBreakingForce * 1.2, FRONT_RIGHT);

            }
          } else if(car.actions.acceleration < 0.01) {
          
              phycar.vehicle.setBrake(maxBreakingForce * 6.2, BACK_LEFT);
              phycar.vehicle.setBrake(maxBreakingForce * 6.2, BACK_RIGHT);
        
              phycar.vehicle.setBrake(maxBreakingForce * 2.2, FRONT_LEFT);
              phycar.vehicle.setBrake(maxBreakingForce * 2.2, FRONT_RIGHT);
              

              phycar.vehicle.applyEngineForce(0, BACK_LEFT);
              phycar.vehicle.applyEngineForce(0, BACK_RIGHT);
    
              phycar.vehicle.applyEngineForce(0, FRONT_LEFT);
              phycar.vehicle.applyEngineForce(0, FRONT_RIGHT);
          }
          
          if (!phycar.nextNitro)
            phycar.nextNitro = 0;
          else
            phycar.nextNitro -= dt * 1000;
        
          if ((car.actions.nitro)&&(phycar.usedNitros<phycar.nitros)) {
    
            if (phycar.nextNitro <= 0) {
              phycar.nextNitro = 5000;
              phycar.usedNitros ++;
              phycar.lapNitros++;  
            }
    
            if (phycar.nextNitro > 3000) {
              const d = (Math.sin((5000.0 - phycar.nextNitro) * Math.PI / 2000.0) + 1.0) * 3;
    
              self.tmpV3.set(0, 0, maxEngineForce / 15.0).applyQuaternion(phycar.quat);
              self.tmpVec3.setValue(self.tmpV3.x * d, self.tmpV3.y * d, self.tmpV3.z * d)
              car.physicsBody.applyCentralImpulse(self.tmpVec3);
            }
          }
       }
      
       let avg = 0;

       for(let i=0;i<4;i++){
          avg += phycar.wheels[i].contactR;
       }
       avg/=4;
       

       if(phycar.isBraking){

          if(car.carDef.chassis == 'car3')
            vehicle.setSideFrictionStiffness2(0.2, 0.8 / avg);
          else if(car.carDef.chassis == 'viper')
            vehicle.setSideFrictionStiffness2(0.2, 0.5);
          else if(car.carDef.chassis == 'buggy')
            vehicle.setSideFrictionStiffness2(0.08, 0.1 / avg);
          else if(car.carDef.chassis == 'cocc')
            vehicle.setSideFrictionStiffness2(0.05, 0.7 / avg);
          else
            vehicle.setSideFrictionStiffness2(0.2, 0.8 / avg); 

        }else{
          if(car.carDef.chassis == 'car3')
            vehicle.setSideFrictionStiffness2(0.1, 0.9 / avg);
          else if(car.carDef.chassis == 'viper')
            vehicle.setSideFrictionStiffness2(0.2, 0.4 / avg);
          else if(car.carDef.chassis == 'buggy')
            vehicle.setSideFrictionStiffness2(0.1, 0.2 / avg);
          else if(car.carDef.chassis == 'cocc')
            vehicle.setSideFrictionStiffness2(0.2, 0.6 / avg);
          else
            vehicle.setSideFrictionStiffness2(0.6, 1.0 / avg);
        }
          
        phycar.flying = true;
        phycar.fullGround = true;

        {
          phycar.norm.set(0, 0, 0);
  
          for (let n = 0; n < 4; n++) {
            const infos=vehicle.getWheelInfo(n);  
            /*infos.set_m_frictionSlip(car.carDef.suspension.friction);*/
              phycar.wheels[n].skidding = infos.get_m_skidInfo();

            const rc = infos.get_m_raycastInfo();
            if(rc.get_m_isInContact()){
              const cn = rc.get_m_contactNormalWS();
              const cp = rc.get_m_contactPointWS();
              phycar.wheels[n].inContact = true;
              phycar.wheels[n].contactNormal.set(cn.x(), cn.y(), cn.z());
              phycar.wheels[n].contactPoint.set(cp.x(), cp.y(), cp.z());
              phycar.norm.add(phycar.wheels[n].contactNormal);
              phycar.flying = false;
            }else{
              phycar.wheels[n].inContact = false;
              phycar.fullGround = false;
            }
          }
  
          phycar.norm.normalize();
  
          if(phycar.flying)
            phycar.contactNorm.set(0,1, 0);
          else
            phycar.contactNorm.set(phycar.norm.x,phycar.norm.y,phycar.norm.z);
            
        } 
  
        if(phycar.fullGround)
            phycar.started = true;

        if(phycar.flying){
          self.angularDamping(phycar, dt);
          car.physicsBody.setDamping(0.00, 0.4);
        }
        else
          car.physicsBody.setDamping(0.075, 0.1);
  
        car.tdown.x = 0;
        car.tdown.y = - speed * speed * 0.0005;
        car.tdown.z = 0;
        car.tdown.applyQuaternion(car.quat);
        car.down.setValue(car.tdown.x, car.tdown.y , car.tdown.z );
        car.physicsBody.applyCentralImpulse(car.down);
    
        
        phycar.vehicle.setBrake(breakingForce * 0.1, BACK_LEFT);
        phycar.vehicle.setBrake(breakingForce * 0.1, BACK_RIGHT);
  
        phycar.vehicle.setBrake(breakingForce * 0.05, FRONT_LEFT);
        phycar.vehicle.setBrake(breakingForce * 0.05, FRONT_RIGHT);
        
        //phycar.vehicle.setBrake(breakingForce /18, FRONT_LEFT);
        //phycar.vehicle.setBrake(breakingForce /18, FRONT_RIGHT);
        
        //phycar.vehicle.setBrake(breakingForce / 4 , BACK_LEFT);
        //phycar.vehicle.setBrake(breakingForce / 4, BACK_RIGHT);
  
        phycar.vehicle.setSteeringValue(vehicleSteering, FRONT_LEFT);
        phycar.vehicle.setSteeringValue(vehicleSteering, FRONT_RIGHT);
  
  
        if (car.active !== true) {
  
          car.p.setValue(car.center.x, car.center.y, car.center.z);
          car.q.setValue(car.quat.x, car.quat.y, car.quat.z, car.quat.w);
  
          car.t.setIdentity();
          car.t.setRotation(car.q);
          car.t.setOrigin(car.p);
  
          phycar.physicsBody.setWorldTransform(car.t);
  
          car.p.setValue(0.0, 0.0, 0.0);
          phycar.physicsBody.setLinearVelocity(car.p);
          phycar.physicsBody.setAngularVelocity(car.p);
  
          //const v = new Ammo.btVector3(0.0, 0.0, 0.0);
  
          /*
          phycar.physicsBody.setInterpolationWorldTransform(car.physicsBody.getWorldTransform());
          phycar.physicsBody.setInterpolationLinearVelocity(car.physicsBody.getLinearVelocity());
          phycar.physicsBody.setInterpolationAngularVelocity(car.physicsBody.getAngularVelocity());
          */
  
  
          phycar.physicsBody.clearForces();
          phycar.physicsBody.activate();
        }
  
        const rb = phycar.vehicle.getRigidBody();
        const ms = rb.getMotionState();
        ms.getWorldTransform(self.tmpTransformation);
  
        p = self.tmpTransformation.getOrigin();
        q = self.tmpTransformation.getRotation();
  
        self.setCarTrans(car.carDef.id, p, q, speed);
  
        var tm, p, q, i;
        var n = phycar.vehicle.getNumWheels();
        for (i = 0; i < n; i++) {
          phycar.vehicle.updateWheelTransform(i, true);
          tm = phycar.vehicle.getWheelTransformWS(i);
          p = tm.getOrigin();
          q = tm.getRotation();
          self.setCarWheel(car.carDef.id, i, p, q);

          if((track)&&(track.fxMapData)){
            phycar.from.set( p.x(),p.y() - 50, p.z() );
            phycar.to.set( 0, +1.0, 0 );
            self.raycaster.set(phycar.from, phycar.to);
            const objs = self.tGroundMesh ? [self.tGroundMesh]: [self.tRoadMesh, self.tWallMesh];
            
            if(objs){
              const pts = self.raycaster.intersectObjects(objs);

              if ((pts.length>0)&&(pts[0].uv3)){
                const ix = Math.floor(pts[0].uv3.x * track.fxMapDataWidth);
                const iy = Math.floor(pts[0].uv3.y * track.fxMapDataHeight);
                const pixel = 1.0 - track.fxMapData[(track.fxMapDataWidth * iy + ix)* 4] / 255.0;
                phycar.wheels[i].contactR = (pixel + 1.0);
                //console.log(i+' '+pixel);
              }
            }
          }
        }

        self.tmpTransformation.setIdentity();

        if(track){
         
          if(phycar.weapons[0]){
            const targets = self.getCarTargets(track, phycar);
            
            phycar.weapons[0].headAngle = 0;
            phycar.weapons[0].cannonAngle = -0.04;

            for(let n=0;n<targets.length;n++) {
              if(targets[n].id === phycar.selectedTargetId){
                if(phycar.selectedSubsystemId == 0){
                  phycar.weapons[0].headAngle = targets[n].angleY;
                  phycar.weapons[0].cannonAngle = targets[n].angleX;
                  break;
                }else{
                  for(let s=0;s<targets[n].subsystems.length;s++){
                    const ts = targets[n].subsystems[s];

                    if(ts.id == phycar.selectedSubsystemId){
                        phycar.weapons[0].headAngle = ts.angleY;
                        phycar.weapons[0].cannonAngle = ts.angleX;
                        break;
                    }
                  }
                }
              }
            }
          }
        }
      };
      
      phycar.syncId = this.nextSyncId++;
      this.syncList.push({id : phycar.syncId, sync: sync});
    }
  
    copyTransform(t) {
      const o = t.getOrigin();
      const r = t.getRotation();
  
      const ret = new Ammo.btTransform();
      ret.setIdentity();
      ret.setOrigin(new Ammo.btVector3(o.x(),o.y(),o.z()));
  
      for(let i=0;i<3;i++) {
        const or = t.getBasis().getRow(i);
        ret.getBasis().getRow(i).setValue(new Ammo.btVector3(or.x(),or.y(),r.z()));
      }
      return ret;
    }

    attachObject(car, LphysicsBody, ox = 0,  oy = 0, oz = 0){
      
      const cart = car.physicsBody.getWorldTransform();
      let carpos = cart.getOrigin();
      let carq = cart.getRotation();

      const pA = new Ammo.btVector3(  0,  0, -car.chassisLength / 2 - 4.0)
      const pB = new Ammo.btVector3(  ox,  oy, oz);
      
      const lL = new Ammo.btVector3(  -1,  0, -3 ) ;
      const lH = new Ammo.btVector3(   1,  2,  0 );

      const vec = new THREE.Vector3(pA.x(), pA.y(), pA.z()).applyQuaternion(new THREE.Quaternion(carq.x(), carq.y(),carq.z(),carq.w())).add(new THREE.Vector3(carpos.x(),carpos.y(),carpos.z()));
      const iPos = new Ammo.btVector3(vec.x, vec.y, vec.z);

      const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, -Math.PI/2, 0));
      const bq = new Ammo.btQuaternion(q.x, q.y, q.z, q.w);
     


      this.tmpTransformation.setIdentity();
      this.tmpTransformation.setOrigin(iPos);
      //this.tmpTransformation.setRotation(carq);


      LphysicsBody.setDamping(0.01, 0.01);
      LphysicsBody.setFriction(0);


      LphysicsBody.setWorldTransform(this.tmpTransformation);
      const motionState = LphysicsBody.getMotionState();
      motionState.setWorldTransform(this.tmpTransformation);

      const aL = new Ammo.btVector3(  -Math.PI /8  , -Math.PI/4 ,  -Math.PI /8 );
      const aH = new Ammo.btVector3(   Math.PI /8  ,  Math.PI/4 ,   Math.PI /8 );

      const frameInA = new Ammo.btTransform();
      frameInA.setIdentity();
      frameInA.setOrigin(pA);

      const frameInB = new Ammo.btTransform();
      frameInB.setIdentity(); 
      frameInB.setOrigin(pB);
      frameInB.setRotation(bq);

      const carObjectPhy = new Ammo.btGeneric6DofSpringConstraint(car.physicsBody, LphysicsBody, frameInA, frameInB, true);

      carObjectPhy.setLinearLowerLimit( lL);
      carObjectPhy.setLinearUpperLimit( lH );

      carObjectPhy.setAngularLowerLimit(aL);
      carObjectPhy.setAngularUpperLimit(aH);
      
      for(let n=0;n<6;n++) {

        carObjectPhy.setParam(BT_CONSTRAINT_CFM      , 0.2, n);
        carObjectPhy.setParam(BT_CONSTRAINT_ERP      , 0.2, n);
        
        carObjectPhy.setParam(BT_CONSTRAINT_STOP_CFM , 0.2, n);
        carObjectPhy.setParam(BT_CONSTRAINT_STOP_ERP , 0.2, n);
        
        //springPhy.setBreakingImpulseThreshold()
      }
      this.physicsUniverse.addConstraint(carObjectPhy, true);

      
      Ammo.destroy(iPos);
      Ammo.destroy(pA);
      Ammo.destroy(lL);
      Ammo.destroy(lH);

      Ammo.destroy(aL);
      Ammo.destroy(aH);

      Ammo.destroy(frameInA);
      Ammo.destroy(frameInB);

      /*
      const carvel = car.physicsBody.getLinearVelocity();
      LphysicsBody.setLinearVelocity(carvel);
      */

      return carObjectPhy;
    }
  
    addCarObject(carid, ipid) {
        let car = null;
        let ip = null;
  
        for (let n = 0; n < this.interestPoints.length; n++) {
          if (this.interestPoints[n].id === ipid) {
            ip = this.interestPoints[n];
          }
        }
    
        if(ip === null)
          return;
  
        if(ip.taken)
          return;
  
        for (let n = 0; n < this.cars.length; n++) {
          if (this.cars[n].netID === carid) {
            car = this.cars[n];
          }
        }
  
        if(car === null)
          return;
  
        ip.constraint =  this.attachObject(car, ip.LphysicsBody);
        ip.taken = 1;

        car.targetNode = ip.targetNode;
  
        car.newAttachedObjects++;
        car.attachedObjects.push(ip);
    }


    makeSpringAnchor(springAnchor, ofs) {
  
      const origin = new Ammo.btVector3(springAnchor.pos.x+ ofs.x, springAnchor.pos.y, springAnchor.pos.z+ ofs.z );
      const quat = new Ammo.btQuaternion(0, 0, 0, 1);
      
      const anchorGeometry = new Ammo.btSphereShape(0.1);
      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      const motionState = new Ammo.btDefaultMotionState(transform);
      const localInertia = new Ammo.btVector3(0, 0, 0);
      anchorGeometry.calculateLocalInertia(0, localInertia);
      const infos = new Ammo.btRigidBodyConstructionInfo(0, motionState, anchorGeometry, localInertia);
      const body = new Ammo.btRigidBody(infos);
  
      body.setActivationState(DISABLE_DEACTIVATION);
  
      
      Ammo.destroy(infos);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
  
      return {physicsBody:body, 
              pos: {x:springAnchor.pos.x, y: springAnchor.pos.y, z: springAnchor.pos.z}, 
              rot: {x:springAnchor.rot.x, y: springAnchor.rot.y, z: springAnchor.rot.z, w: springAnchor.rot.w}, 
              ammoData : { localInertia: localInertia, motionState: motionState, transform: transform, geometry: anchorGeometry } }
  
      
    }
  
    makeSpringObject(springObj, isize) {
  
      const origin = new Ammo.btVector3(springObj.pos.x , springObj.pos.y, springObj.pos.z);
      const quat = new Ammo.btQuaternion(springObj.rot.x, springObj.rot.y, springObj.rot.z , springObj.rot.w);
      /*
      const origin = new Ammo.btVector3(0 , 0, 0);
      const quat = new Ammo.btQuaternion(0, 0, 0 , 1);
      */
      let objGeometry;
      
      if(springObj.shape == 0)
      {
        const size = new Ammo.btVector3(isize.x /2 , isize.y / 2, isize.z /2 );
        objGeometry = new Ammo.btBoxShape(size);
        Ammo.destroy(size);
      }
      else{
        const size = new Ammo.btVector3(isize.x , isize.y / 2, isize.x );
        objGeometry = new Ammo.btCylinderShape(size);
        Ammo.destroy(size);
      }
      
      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      const motionState = new Ammo.btDefaultMotionState(transform);
      const localInertia = new Ammo.btVector3(0, 0, 0);
      objGeometry.calculateLocalInertia(springObj.mass, localInertia);
  
      const infos = new Ammo.btRigidBodyConstructionInfo(springObj.mass, motionState, objGeometry, localInertia);
      infos.set_m_angularSleepingThreshold(0.0);
      infos.set_m_linearSleepingThreshold(0.0);
  
      const objbody = new Ammo.btRigidBody(infos);
      objbody.setDamping(0.01, 0.02);
          
      objbody.setActivationState(DISABLE_DEACTIVATION);
  
      objbody.setFriction(0.3); 
      objbody.setRestitution(0.0);
     
      Ammo.destroy(infos);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
  
      return {physicsBody: objbody, 
              pos:{ x:springObj.pos.x, y:springObj.pos.y, z:springObj.pos.z}, 
              rot: { x:springObj.rot.x, y:springObj.rot.y, z:springObj.rot.z, w:springObj.rot.w} , 
              ammoData : { localInertia: localInertia, motionState: motionState, transform: transform, geometry: objGeometry } }
    }
    
    makeRopeObject(springObj) {
  
      const origin = new Ammo.btVector3(springObj.pos.x, springObj.pos.y , springObj.pos.z);
      const quat = new Ammo.btQuaternion(springObj.rot.x, springObj.rot.y, springObj.rot.z , springObj.rot.w);
  
      const size = new Ammo.btVector3(springObj.size.x / 2, springObj.size.y / 2, springObj.size.z / 2);
      let objGeometry;
      
      if(springObj.meshData){
        
        const scale = new Ammo.btVector3(1.0, 1.0, 1.0);
        let ammoMesh = new Ammo.btTriangleMesh();
        const v1 = new Ammo.btVector3();
        const v2 = new Ammo.btVector3();
        const v3 = new Ammo.btVector3();
        
        for (let n = 0; n < springObj.  meshData.length; n+=9) {
          
          //v1.setValue((springObj.meshData[n+0] - track.terrain.offset.x) * springObj.size.x, (springObj.meshData[n+1] - track.terrain.offset.y)* springObj.size.y, (springObj.meshData[n+2] - track.terrain.offset.z) * springObj.size.z);
          //v2.setValue((springObj.meshData[n+3] - track.terrain.offset.x) * springObj.size.x, (springObj.meshData[n+4] - track.terrain.offset.y)* springObj.size.y, (springObj.meshData[n+5] - track.terrain.offset.z) * springObj.size.z);
          //v3.setValue((springObj.meshData[n+6] - track.terrain.offset.x) * springObj.size.x, (springObj.meshData[n+7] - track.terrain.offset.y)* springObj.size.y, (springObj.meshData[n+8] - track.terrain.offset.z) * springObj.size.z);
          
          v1.setValue(springObj.meshData[n+0] * springObj.size.x, springObj.meshData[n+1] * springObj.size.y, springObj.meshData[n+2] * springObj.size.z);
          v2.setValue(springObj.meshData[n+3] * springObj.size.x, springObj.meshData[n+4] * springObj.size.y, springObj.meshData[n+5] * springObj.size.z);
          v3.setValue(springObj.meshData[n+6] * springObj.size.x, springObj.meshData[n+7] * springObj.size.y, springObj.meshData[n+8] * springObj.size.z);
  
          ammoMesh.addTriangle(v1, v2, v3, true);
        }
    
        Ammo.destroy(v1);
        Ammo.destroy(v2);
        Ammo.destroy(v3);
    
        ammoMesh.computeVertexNormals();
        objGeometry = new Ammo.btBvhTriangleMeshShape(ammoMesh, true,true);
        objGeometry.setMargin(0.2);
        //objGeometry.setLocalScaling(scale);
        Ammo.destroy(scale);
  
      }else if(springObj.shape === 0)
        objGeometry = new Ammo.btBoxShape(size)
      else
        objGeometry = new Ammo.btSphereShape(springObj.size.x / 2)
  
      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
  
      /*
      const localGeo = new Ammo.btVector3(0, 0, 0);
      const localTransform = new Ammo.btTransform();
      localTransform.setIdentity();
      localTransform.setOrigin(localGeo);
      */
  
      const motionState = new Ammo.btDefaultMotionState(transform); //, localTransform
      const localInertia = new Ammo.btVector3(0, 0.0, 0);
      objGeometry.calculateLocalInertia(springObj.mass, localInertia);
  
      const infos = new Ammo.btRigidBodyConstructionInfo(springObj.mass, motionState, objGeometry, localInertia);
  
      infos.set_m_angularSleepingThreshold(4.0);
      infos.set_m_linearSleepingThreshold(4.0);
  
      const objbody = new Ammo.btRigidBody(infos);
  
      objbody.setDamping(0.1, 0.2);
      objbody.setFriction(0.6);
      objbody.setRestitution(0.0);
      
      objbody.setActivationState(DISABLE_DEACTIVATION);
  
      Ammo.destroy(size);
      Ammo.destroy(infos);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
  
      return {physicsBody: objbody, 
              pos:{ x:springObj.pos.x, y:springObj.pos.y, z:springObj.pos.z}, 
              rot: { x:springObj.rot.x, y:springObj.rot.y, z:springObj.rot.z, w:springObj.rot.w} , 
              ammoData : { localInertia: localInertia, motionState: motionState, transform: transform, geometry: objGeometry } }
    }
    addSpringPhy(spring) {
  
        const obj = this.makeSpringObject(spring.obj, spring.size);
  
        /*
        const q = new Ammo.btQuaternion(spring.obj.rot.x, spring.obj.rot.y, spring.obj.rot.z , spring.obj.rot.w);
        const v = new Ammo.btVector3(spring.obj.pos.x , spring.obj.pos.y, spring.obj.pos.z);
   
        this.tmpTransformation.setIdentity();
        this.tmpTransformation.setOrigin(v);
        this.tmpTransformation.setRotation(q);
        const t = new Ammo.btTransform(q, v); 
        obj.physicsBody.setWorldTransform(t );
  
        Ammo.destroy(q);
        Ammo.destroy(v);
        */
  
        
        obj.physicsBody.userData = { name: 'spring-obj-' + spring.id, obj: obj };
        this.physicsUniverse.addRigidBody(obj.physicsBody, worldGroup, worldMask);
  
        const sphy = [];
        const anchors = [];
        spring.anchors = [];
  
        const ofs = [{x: 0.0, z: 0.0},{x: 10, z: -10.0},{x: -10.0, z: 10.0}, {x: 10.0, z: 10.0}]
        const sfac = [ 1.0, .8, 0.8, 0.8];  
  
        let i =0;
        //for(let i=0;i<3;i++) 
          { 
  
          const anchor = this.makeSpringAnchor(spring.anchor, ofs[i]);
          anchor.physicsBody.userData = { name: 'spring-anchor-' + spring.id, obj: anchor };
    
          const frameInA = new Ammo.btTransform();
          frameInA.setIdentity();
          
          const frameInB = new Ammo.btTransform();
          frameInB.setIdentity(); 

          if(spring.mode === 1)
            frameInB.setOrigin(new Ammo.btVector3(  -spring.eqPts.x,  -spring.eqPts.y, -spring.eqPts.z));
          else
            frameInB.setOrigin(new Ammo.btVector3(  ofs[i].x,  0, ofs[i].z ));
  
          const springPhy = new Ammo.btGeneric6DofSpringConstraint(anchor.physicsBody, obj.physicsBody, frameInA, frameInB, true);
  
          const cfm = 0.8;
          const scfm = 0.8;
          const serp = 0.8;
          const erp = 0.8;

         
          const aL = new Ammo.btVector3( spring.eqA.x - spring.limitA.x  ,  spring.eqA.y - spring.limitA.y, spring.eqA.z - spring.limitA.z);
          const aH = new Ammo.btVector3( spring.eqA.x + spring.limitA.x  ,  spring.eqA.y + spring.limitA.y, spring.eqA.z + spring.limitA.z);
          if(spring.mode === 1){
            const loL = new Ammo.btVector3(-spring.limit.x,  -spring.limit.y, -spring.limit.z);
            const hiL = new Ammo.btVector3( spring.limit.x,   spring.limit.y,  spring.limit.z);

            springPhy.setLinearLowerLimit(loL);
            springPhy.setLinearUpperLimit(hiL);
            Ammo.destroy(loL);
            Ammo.destroy(hiL);
          }else{
            const loL = new Ammo.btVector3(spring.eqPts.x - spring.limit.x,  spring.eqPts.y - spring.limit.y, spring.eqPts.z - spring.limit.z);
            const hiL = new Ammo.btVector3(spring.eqPts.x + spring.limit.x,  spring.eqPts.y + spring.limit.y, spring.eqPts.z + spring.limit.z);

            springPhy.setLinearLowerLimit(loL);
            springPhy.setLinearUpperLimit(hiL);
            Ammo.destroy(loL);
            Ammo.destroy(hiL);
          }
  
          springPhy.setAngularLowerLimit(aL);
          springPhy.setAngularUpperLimit(aH);
  
          Ammo.destroy(aL);
          Ammo.destroy(aH);
       
          Ammo.destroy(frameInB);
          Ammo.destroy(frameInA);
  
          this.physicsUniverse.addConstraint(springPhy, true);
  
          for(let n=0;n<6;n++) {
  
            springPhy.enableSpring(n,  true);
            springPhy.enableFeedback(n,  false);
  
            if (n<3) {
              springPhy.setStiffness(n, spring.stiffness * sfac[i] ); //10000
              springPhy.setDamping  (n, spring.damping); //0.0008
              springPhy.setTranslationalLimitMotor	(n ,true, spring.stiffness);
            } else {
              springPhy.setStiffness(n, spring.stiffness * sfac[i] ); //10000
              springPhy.setDamping  (n, 0.1); //0.0008
            }
            
            springPhy.setParam(BT_CONSTRAINT_CFM      , cfm, n);
            springPhy.setParam(BT_CONSTRAINT_ERP      , erp, n);
            
            springPhy.setParam(BT_CONSTRAINT_STOP_CFM , scfm, n);
            springPhy.setParam(BT_CONSTRAINT_STOP_ERP , serp, n);
            
            //springPhy.setBreakingImpulseThreshold()
          }
  
          if(spring.mode === 0){
            springPhy.setEquilibriumPoint(0, spring.eqPts.x);
            springPhy.setEquilibriumPoint(1, spring.eqPts.y);
            springPhy.setEquilibriumPoint(2, spring.eqPts.z);
          }else{
            springPhy.setEquilibriumPoint(0, 0);
            springPhy.setEquilibriumPoint(1, 0);
            springPhy.setEquilibriumPoint(2, 0);
          }
    
          springPhy.setEquilibriumPoint(3, spring.eqA.x);
          springPhy.setEquilibriumPoint(4, spring.eqA.y);
          springPhy.setEquilibriumPoint(5, spring.eqA.z);
    
          sphy.push(springPhy);
          anchors.push(anchor);
        }
  
        this.rigidBody_List.push(obj.physicsBody);
        this.springs.push({ id: spring.id, obj: obj , anchors: anchors, const: sphy});
    }
  
  
  
    addRopePhy(rope) {
        const obj = this.makeRopeObject(rope.obj);
        obj.physicsBody.userData = { name: 'rope-obj-' + rope.id, obj: obj };
        
        const anchor = this.makeSpringAnchor(rope.anchor, {x: 0, y:0, z:0});
        anchor.physicsBody.userData = { name: 'rope-anchor-' + rope.id, obj: anchor };      
  
        this.physicsUniverse.addRigidBody(obj.physicsBody, worldGroup, worldMask);
        
        const frameInA = new Ammo.btTransform();
        frameInA.setIdentity(); 
  
        const frameInB = new Ammo.btTransform();
        frameInB.setIdentity(); 
  
        const ropePhy = new Ammo.btGeneric6DofConstraint(anchor.physicsBody, obj.physicsBody,  frameInA,  frameInB, true);
        const eq = new Ammo.btVector3(  rope.eqPts.x,   rope.eqPts.y , rope.eqPts.z);
  
        ropePhy.setAxis(this.zAxis, this.yAxis);
        ropePhy.getFrameOffsetA().setOrigin(this.zVec);
        ropePhy.getFrameOffsetB().setOrigin(eq);
  
        for(let i=0;i<3;i++) {
          /*
          let rotationalMotor= new Ammo.btRotationalLimitMotor();
          ropePhy.getRotationalLimitMotor2(i, rotationalMotor);
  
          
          rotationalMotor.set_m_enableMotor(true);
          
          rotationalMotor.set_m_maxMotorForce(rope.motorforce);
          rotationalMotor.set_m_maxLimitForce(rope.motorforce);
          rotationalMotor.set_m_targetVelocity(0.001);
          
          rotationalMotor.set_m_damping(rope.damping);
          rotationalMotor.set_m_limitSoftness(rope.softness);
          rotationalMotor.set_m_bounce(rope.bounce);
  
          switch(i)
          {
            case 0:{
              rotationalMotor.set_m_loLimit(rope.eqA.x-rope.limitA.x);
              rotationalMotor.set_m_hiLimit(rope.eqA.x+rope.limitA.x);
            }
            case 1:{
              rotationalMotor.set_m_loLimit(rope.eqA.y-rope.limitA.y);
              rotationalMotor.set_m_hiLimit(rope.eqA.y+rope.limitA.y);
            }
            case 2:{
              rotationalMotor.set_m_loLimit(rope.eqA.z-rope.limitA.z);
              rotationalMotor.set_m_hiLimit(rope.eqA.z+rope.limitA.z);
            }
          }
                  ropePhy.setRotationalLimitMotor(i, rotationalMotor);
  
          */
                  
  
          ropePhy.setParam(BT_CONSTRAINT_STOP_CFM, 0.00001, i+3);
          ropePhy.setParam(BT_CONSTRAINT_CFM, 0.4, i+3);
          ropePhy.setParam(BT_CONSTRAINT_STOP_ERP, 0.1, i+3);
          ropePhy.setParam(BT_CONSTRAINT_ERP, 0.2, i+3);
        }
  
        const loL = new Ammo.btVector3( -rope.limit.x , -rope.limit.y , -rope.limit.z );
        const loA = new Ammo.btVector3( -rope.limitA.x, -rope.limitA.y, -rope.limitA.z );
        const hiL = new Ammo.btVector3(  rope.limit.x ,  rope.limit.y ,  rope.limit.z );
        const hiA = new Ammo.btVector3(  rope.limitA.x,  rope.limitA.y,  rope.limitA.z );
  
        ropePhy.setLinearLowerLimit( loL );
        ropePhy.setLinearUpperLimit( hiL );
  
        ropePhy.setAngularLowerLimit( loA );
        ropePhy.setAngularUpperLimit( hiA );
        
        this.physicsUniverse.addConstraint(ropePhy, false);
        this.rigidBody_List.push(obj.physicsBody);
  
        Ammo.destroy(loL);
        Ammo.destroy(hiL);
  
        Ammo.destroy(loA);
        Ammo.destroy(hiA);
  
        Ammo.destroy(eq);
  
        Ammo.destroy(frameInA);
        Ammo.destroy(frameInB);
  
        this.ropes.push({id: rope.id, anchor: anchor, obj: obj, const: ropePhy});
    }

    
    findSpiderBone(bone , name){

      if(bone.name === name)
          return bone;

      for(let child of bone.children){
          let ret = this.findSpiderBone(child, name);
          if(ret != null)
              return ret;
      }

      return null;
    }

    findSpiderBoneDistance(bone , pos, dist){

      
      if((bone.constraint === null)&&(bone.physicsBody !== null)&&(bone.physicsBody.userData.taken === false)&&(pos.distanceTo(bone.pos)<dist))
          return bone;

      for(let child of bone.children){
          let ret = this.findSpiderBoneDistance(child, pos, dist);
          if(ret != null)
              return ret;
      }

      return null;
    }


    getCarTargets(track, car ){

      const carPos = car.pos;
      const carQuat =  car.quat;

      const targets = [];
      const fv = new THREE.Vector3(0,0,1).applyQuaternion(carQuat);
      const angleX = Math.asin(fv.y);
      const angleY = Math.atan2(fv.x, fv.z);
      const c  = Math.cos(angleX);
      const s  = Math.sin(angleX);

      const maxDist = 40;
      const maxAngle = 0.2;
      const sp = car.weapons.length >= 1 ? new THREE.Vector3(carPos.x, carPos.y + car.weapons[0].pt.y, carPos.z) : new THREE.Vector3(carPos.x, carPos.y, carPos.z);
      const dd = new THREE.Vector3();
      const d = new THREE.Vector3();

      const minYAngle = -0.1;

      for(let n=0;n<track.npcs.length;n++){

        if(!track.npcs[n].target)
          continue;

        if((track.npcs[n].skeleton)&&(track.npcs[n].skeleton.root.physicsBody)){
          const motionState = track.npcs[n].skeleton.root.physicsBody.getMotionState();
          motionState.getWorldTransform(this.tmpTransformation);
          const tpos = this.tmpTransformation.getOrigin();
          this.tmpV1.set(tpos.x(), tpos.y(), tpos.z());
        }else{
          this.tmpV1.set(track.npcs[n].ppos.x, track.npcs[n].ppos.y, track.npcs[n].ppos.z);
        }
          
        d.subVectors(this.tmpV1, carPos);

        const l = d.length();
        if(l < maxDist) 
        {
              d.normalize();
              if(d.dot(fv)> maxAngle) {

                dd.subVectors(this.tmpV1, sp).normalize();
                
                const sangleX = Math.asin(dd.y);
                const sangleY = Math.atan2(dd.x, dd.z);
                const target = {  id : track.npcs[n].target.id,
                                  type : track.npcs[n].target.type,
                                  eid : track.npcs[n].id,
                                  dist : l, 
                                  subsystems : [],
                                  angleX : -(sangleX - angleX),
                                  angleY : Math.max(minYAngle, (sangleY - angleY)),
                                  pos : this.tmpV1.clone(),
                                };
              
                for(let s_subsystem of track.npcs[n].target.subsystems){
                
                  const bone = this.findRagDollChild(track.npcs[n].skeleton.root, s_subsystem.name);
                  if(!bone)
                    continue;

                  if(bone.isBroken)
                    continue;

                  const motionState = bone.physicsBody.getMotionState();
                  motionState.getWorldTransform(this.tmpTransformation);
                  const stpos = this.tmpTransformation.getOrigin();

                  this.tmpV1.set(stpos.x(), stpos.y(), stpos.z());

                  d.subVectors(this.tmpV1, carPos);
                  const sl = d.length();
                  d.normalize();

                  dd.subVectors(this.tmpV1, sp).normalize();

                  const ssangleX = Math.asin(dd.y);
                  const ssangleY = Math.atan2(dd.x, dd.z);
                  const subsystem = { 
                                      id : s_subsystem.id,
                                      dist : sl, 
                                      angleX : -(ssangleX - angleX),
                                      angleY : Math.max(minYAngle, ssangleY - angleY),
                                      pos : this.tmpV1.clone()
                                    };

                  target.subsystems.push( subsystem);
                }
                targets.push(target);
              }
          }
      }



      for(let n=0;n<track.drones.length;n++){
          const drone = track.drones[n];
          const dronePhy = this.drones[n];

          d.subVectors(dronePhy.pos, carPos);
          const l = d.length();
          if(l < maxDist){
              d.normalize();
              if(d.dot(fv)> maxAngle){

                 
                  dd.subVectors(dronePhy.pos, sp).normalize();

                  const dangleX = Math.asin(dd.y);
                  const dangleY =  Math.atan2(dd.x, dd.z);
                  const target = {id : drone.target.id,
                                  dist : l, 
                                  subsystems : [],
                                  angleX : -(dangleX - angleX),
                                  angleY : Math.max(minYAngle, dangleY - angleY),
                                  pos : {x : drone.pos.x, y : drone.pos.y, z : drone.pos.z}
                                };
      
                  for(let p = 0; p< dronePhy.pales.length; p++){

                      const pale = dronePhy.pales[p];
                      if(pale.life<=0)
                        continue;

                      d.subVectors({x : pale.pos.x, y : pale.pos.y - 0.5, z: pale.pos.z}, carPos);
                      const sl = d.length();

                      d.normalize();

                      const sdangleX = Math.asin(d.y);
                      const sdangleY = Math.atan2(d.x, d.z);
                      const subsystem = { id : drone.target.subsystems[p].id,
                                          dist : sl, 
                                          angleX : -(sdangleX - angleX),
                                          angleY : Math.max(minYAngle,sdangleY - angleY),
                                          pos : pale.pos
                                        };


                      target.subsystems.push(subsystem);
                  }

                  targets.push(target);
                }
            }
        }


        for(let n=0;n<track.spiders.length;n++){

          if(!track.spiders[n].target)
              continue;

          d.subVectors(track.spiders[n].skel.root.pos, carPos);
          const l = d.length();
          if(l < maxDist) 
          {
              d.normalize();
              if(d.dot(fv)> maxAngle) {

                dd.subVectors(track.spiders[n].skel.root.pos, sp).normalize();
                
                const sangleX = Math.asin(dd.y);
                const sangleY = Math.atan2(dd.x, dd.z);
                const target = {  id : track.spiders[n].target.id,
                                  dist : l, 
                                  subsystems : [],
                                  angleX : -(sangleX - angleX),
                                  angleY : Math.max(minYAngle, (sangleY - angleY)),
                                  pos : track.spiders[n].skel.root.pos,
                                };
              
                for(let s_subsystem of track.spiders[n].target.subsystems){

                  const bone = this.findSpiderBone(track.spiders[n].skel.root, s_subsystem.name);

                   d.subVectors(bone.pos, carPos);
                   const sl = d.length();
                   d.normalize();

                   dd.subVectors(bone.pos, sp).normalize();

                   const ssangleX = Math.asin(dd.y);
                   const ssangleY = Math.atan2(dd.x, dd.z);
                   const subsystem = { 
                                      id : s_subsystem.id,
                                      dist : sl, 
                                      angleX : -(ssangleX - angleX),
                                      angleY : Math.max(minYAngle, ssangleY - angleY),
                                      pos : bone.pos
                                     };

                  target.subsystems.push( subsystem);
                }
                targets.push(target);
              }
          }
        }


        for(let n=0;n<this.cars.length;n++){

          if(this.cars[n] .id == car.id)
            continue;

          if(!this.cars[n].target)
            continue;

          d.subVectors(this.cars[n].pos, carPos);
          const l = d.length();
          if(l < maxDist) 
          {
            d.normalize();
            if(d.dot(fv)> maxAngle) {

              dd.subVectors(this.cars[n].pos, sp).normalize();
              
              const sangleX = Math.asin(dd.y);
              const sangleY = Math.atan2(dd.x, dd.z);
              const target = {  id : this.cars[n].target.id,
                                dist : l, 
                                subsystems : [],
                                angleX : -(sangleX - angleX),
                                angleY :Math.max(minYAngle, (sangleY - angleY)),
                                pos : this.cars[n].pos,
                                
                              };

              targets.push(target);

            }
          }
        }
        return targets;
    }
  
    nextCarTargets(track, car){

        const targets = this.getCarTargets(track, car);

        if(targets === null)
          return;

        for(let i = 0; i<targets.length;i++) {
            const target = targets[i];

            if(car.selectedTargetId === 0){
              car.selectedTargetId = target.id;
              return;
            }

            if(car.selectedTargetId === target.id) {

                if( i +1 < targets.length)
                    car.selectedTargetId = targets[i+1].id;
                else
                    car.selectedTargetId = 0;

                return;
            }
        }

        car.selectedSubsystemId = 0;
        car.selectedTargetId = 0;
    }

    nextCarTargetsSubSystem(track, car){

        if(car.selectedTargetId === 0){
          car.selectedSubsystemId = 0;
          return;
        }

        const targets = this.getCarTargets(track, car);
        if(targets === null)
          return;

        for(let i = 0; i<targets.length;i++){

            const target = targets[i];

            if(car.selectedTargetId  === target.id){

                if(( target)&&(target.type =='npc')){

                    for(let npc of track.npcs) {

                        if(npc.id == target.eid) {

                          if(npc.dialog){

                            const phyNPC = this.getNPC(npc.netID);
                            const line = npc.dialog.lines[phyNPC.currentDialogLine];

                            if((typeof line.txt === 'string')||((phyNPC.currentDialogSubLine+1)>=line.txt.length)){

                              car.selectedSubsystemId++;
                              if(car.selectedSubsystemId>=line.answers.length){
                                  car.selectedSubsystemId=0;
                              }
                              return;
                            }
                          }
                        }
                    }
                }

                for(let s = 0; s<target.subsystems.length;s++){

                    const subsystem = target.subsystems[s];

                    if(car.selectedSubsystemId === 0){
                      car.selectedSubsystemId = subsystem.id;
                      return;
                    }

                    if(car.selectedSubsystemId === subsystem.id) {

                        if( s + 1 < target.subsystems.length)
                          car.selectedSubsystemId = target.subsystems[ s + 1].id;
                        else
                          car.selectedSubsystemId = 0;

                        return;
                    }
                }
                car.selectedSubsystemId = 0;
            }
        }
        this.currentSelectedTargetSubSystem = 0;
    }
    
    addDronePhy(drone) {
      
      const origin = new Ammo.btVector3(drone.pos.x, drone.pos.y, drone.pos.z);
      const quat = new Ammo.btQuaternion(drone.rot.x, drone.rot.y, drone.rot.z, drone.rot.w);
      const size = new Ammo.btVector3(drone.size.x * 0.5, drone.size.y * 0.5, drone.size.z * 0.5);
      const geometry = new Ammo.btBoxShape(size);
      const transform = new Ammo.btTransform();
      
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      
      const motionState = new Ammo.btDefaultMotionState(transform);
      const localInertia = new Ammo.btVector3(0, 0, 0);
      geometry.calculateLocalInertia(drone.weight, localInertia);
      const infos = new Ammo.btRigidBodyConstructionInfo(drone.weight, motionState, geometry, localInertia);
      const body = new Ammo.btRigidBody(infos);


      const z  = new Ammo.btVector3(0.0, 1.0, 0.4);
      body.setAngularFactor(z);
      Ammo.destroy(z);


      body.setDamping(0.3, 0.6);
  
      drone.physicsBody = body;
      drone.ammoData = { localInertia: localInertia, motionState: motionState, transform: transform, geometry: geometry };
  
      const phydrone = { id: drone.id, tp : new THREE.Vector3(), physicsBody: body, currentTarget : null, goHome: false, active: false, deactivation: drone.deactivation, pos : new THREE.Vector3(drone.pos.x, drone.pos.y, drone.pos.z), size :  new THREE.Vector3(drone.size.x, drone.size.y, drone.size.z) , rot :  new THREE.Quaternion(drone.rot.x, drone.rot.y, drone.rot.z, drone.rot.w), lastShotId: 1, pales: [],shots : [], weapons : []};

      for(let w of drone.weapons){

        phydrone.weapons.push(
        { 
            projPts: new THREE.Vector3(w.projPts.x,  w.projPts.y, w.projPts.z),
            projSize: w.projSize, 
            projMass : w.projMass, 
            projSpeed : w.projSpeed,  
            projDelay : w.projDelay,  
            projTTL : w.projTTL, 
            blastForce: w.projBlastForce, 
            projHoming: w.projHoming, 
            blastRadius: w.projBlastRadius, 
            blastImpact: w.projBlastImpact, 
            lastShot : 0
        });
      }

      body.userData = { name: 'drone-' + drone.id, obj: phydrone, drone: drone};

      phydrone.shotPt = new THREE.Vector3();
      phydrone.shotVel = new THREE.Vector3();

      phydrone.targetAngleQ = new THREE.Quaternion();
      phydrone.qtoCar= new THREE.Quaternion();
      phydrone.targetPos = new THREE.Vector3();
      phydrone.dronePos = new THREE.Vector3();
      phydrone.biNormal = new THREE.Vector3();
      phydrone.from = new THREE.Vector3();
      phydrone.to = new THREE.Vector3();
      
  
      this.physicsUniverse.addRigidBody(body, worldGroup, worldMask);
      this.rigidBody_List.push(body);
      
      
      for(let pale of drone.pales) {

        const origin = new Ammo.btVector3(drone.pos.x + pale.rpos.x, drone.pos.y + pale.rpos.y, drone.pos.z + pale.rpos.z);
        const quat = new Ammo.btQuaternion(pale.rot.x, pale.rot. y, pale.rot.z, pale.rot.w);
        const size = new Ammo.btVector3(pale.radiusX, pale.height, pale.radiusZ);
        const geometry = new Ammo.btCylinderShape(size);
        const transform = new Ammo.btTransform();
        
        transform.setIdentity();
        transform.setOrigin(origin);
        transform.setRotation(quat);
        
        const motionState = new Ammo.btDefaultMotionState(transform);
        const localInertia = new Ammo.btVector3(0, 0, 0);
        geometry.calculateLocalInertia(50000, localInertia);
        const infos = new Ammo.btRigidBodyConstructionInfo(50, motionState, geometry, localInertia);
        const pbody = new Ammo.btRigidBody(infos);

        pbody.setDamping(0.5, 0.5);

        const ori = new Ammo.btVector3(0.1,1,0.1);
        pbody.setAngularFactor(ori);
        Ammo.destroy(ori);
          
        const phypale = {physicsBody : pbody, life: pale.life, radius : pale.radiusX, pos : new THREE.Vector3(pale.rpos.x,pale.rpos.y,pale.rpos.z), rot: new THREE.Quaternion(pale.rot.x, pale.rot.y, pale.rot.z, pale.rot.w), ammoData : { localInertia: localInertia, motionState: motionState, transform: transform, geometry: geometry }}
        pbody.userData = { name: 'pale-' + drone.id, obj: phypale, drone : drone };
     
        phypale.ae = new THREE.Euler( 0, 0, 0);
        phypale.aq = new THREE.Quaternion();
        phypale.aaq = new Ammo.btQuaternion(0, 0, 0, 1);

        this.physicsUniverse.addRigidBody(pbody, worldGroup, worldMask);
        this.rigidBody_List.push(pbody);

        
        const originA = new Ammo.btVector3(pale.rpos.x, pale.rpos.y, pale.rpos.z);
               
        const frameInA = new Ammo.btTransform();
        frameInA.setIdentity(); 
        frameInA.setOrigin(originA);
  
        const frameInB = new Ammo.btTransform();
        frameInB.setIdentity();
                              
        phypale.constraint = new Ammo.btGeneric6DofConstraint(phydrone.physicsBody, phypale.physicsBody,  frameInA,  frameInB, true);

       
            for(let i=3;i<6;i++) {


              phypale.constraint.setParam(BT_CONSTRAINT_CFM, 0.0, i);
              phypale.constraint.setParam(BT_CONSTRAINT_ERP, 1.0, i);

            }
      
            for(let i=0;i<3;i++) {
              phypale.constraint.setParam(BT_CONSTRAINT_CFM, 0.0, i);
              phypale.constraint.setParam(BT_CONSTRAINT_ERP, 1.0, i);


            }

            const loL = new Ammo.btVector3( 0 , 0 , 0 );
            const hiL = new Ammo.btVector3( 0 , 0 , 0 );
            phypale.constraint.setLinearLowerLimit( loL );
            phypale.constraint.setLinearUpperLimit( hiL );

            const loA = new Ammo.btVector3( -0.1, -0.1, -0.1 );
            const hiA = new Ammo.btVector3(  0.1,  0.1,  0.1 );
            phypale.constraint.setAngularLowerLimit( loA );
            phypale.constraint.setAngularUpperLimit( hiA );
      
            this.physicsUniverse.addConstraint(phypale.constraint, true);

        
            Ammo.destroy(loL)
            Ammo.destroy(hiL)
            Ammo.destroy(loA)
            Ammo.destroy(hiA)

            Ammo.destroy(frameInB)
            Ammo.destroy(frameInA)
            Ammo.destroy(originA)
        

            phydrone.pales.push(phypale);
      }
      
      phydrone.curve2 = new THREE.CatmullRomCurve3();
      phydrone.factor = new Array(drone.pales.length);


      this.drones.push(phydrone);
      
      Ammo.destroy(size);
      Ammo.destroy(infos);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
    }
  
    addTurretPhy(turret) {
  
      const origin = new Ammo.btVector3(turret.pos.x, turret.pos.y +  turret.projPts.y / 2, turret.pos.z);
      const quat = new Ammo.btQuaternion(turret.rot.x, turret.rot. y, turret.rot.z, turret.rot.w);
      const size = new Ammo.btVector3(turret.size, turret.size, turret.size);
  
      const geometry = new Ammo.btCylinderShape(new Ammo.btVector3(1, turret.projPts.y / 2, 1));
      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      const motionState = new Ammo.btDefaultMotionState(transform);
      const localInertia = new Ammo.btVector3(0, 0, 0);
      geometry.calculateLocalInertia(0, localInertia);
      const infos = new Ammo.btRigidBodyConstructionInfo(0, motionState, geometry, localInertia);
      const body = new Ammo.btRigidBody(infos);

      const phyturret = { id: turret.id, ap: new THREE.Vector3(), tdelta : new THREE.Vector3(), ammoData : { localInertia: localInertia, motionState: motionState, transform: transform, geometry: geometry }, physicsBody : body, baseHeadAngle : turret.oHeadAngle, headAngle: turret.oHeadAngle, canonAngle: turret.canonAngle, activation: turret.activation, pos : new THREE.Vector3(turret.pos.x,turret.pos.y, turret.pos.z), size : turret.size, rot : new THREE.Quaternion(turret.rot.x, turret.rot.y, turret.rot.z, turret.rot.w), projPts: new THREE.Vector3(turret.projPts.x, turret.projPts.y, turret.projPts.z), projSize: turret.projSize, projMass : turret.projMass, projSpeed : turret.projSpeed,  projDelay : turret.projDelay,  projTTL : turret.projTTL, moveAngle : turret.moveAngle, projLL : turret.projLL, lastShotId: 1, shots : [], lastShot : 0}
      
  
      this.physicsUniverse.addRigidBody(body, worldGroup, worldMask);
      //this.rigidBody_List.push(body);
      //body.userData = { name: 'turret-' + turret.id, obj: turret };
  
      this.turrets.push(phyturret);
      
      Ammo.destroy(size);
      Ammo.destroy(infos);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
  
    }
  
    addInterestPointPhy(ip) {
  
      /* pod */
      const origin = new Ammo.btVector3(ip.pos.x, ip.pos.y, ip.pos.z);
      const quat = new Ammo.btQuaternion(ip.rot2.x, ip.rot2.y, ip.rot2.z, ip.rot2.w);
      const size = new Ammo.btVector3(ip.size.x / 2.0, 5, ip.size.z/ 2.0);
  
      const geometry = new Ammo.btBoxShape(size);
      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      const motionState = new Ammo.btDefaultMotionState(transform);
      const localInertia = new Ammo.btVector3(0, 0, 0);
      geometry.calculateLocalInertia(0, localInertia);
      const infos = new Ammo.btRigidBodyConstructionInfo(0, motionState, geometry, localInertia);
      const body = new Ammo.btRigidBody(infos);
      body.setCollisionFlags(CF_NO_CONTACT_RESPONSE);
      body.userData = { name: 'ip-' + ip.id, obj: ip };
      ip.physicsBody = body;
      ip.ammoData = { localInertia: localInertia  , motionState: motionState  , transform: transform  , geometry: geometry};
  
      /* object */
      const lorigin = new Ammo.btVector3(ip.lpos.x, ip.lpos.y + 0.5, ip.lpos.z);
      const lquat = new Ammo.btQuaternion(ip.rot2.x, ip.rot2.y, ip.rot2.z, ip.rot2.w);
      const lsize = new Ammo.btVector3(ip.lsize / 2.0, ip.lsize / 2.0, ip.lsize / 2.0);
  
      const lgeometry = new Ammo.btBoxShape(lsize);
      const ltransform = new Ammo.btTransform();
      ltransform.setIdentity();
      ltransform.setOrigin(lorigin);
      ltransform.setRotation(lquat);
      const lmotionState = new Ammo.btDefaultMotionState(ltransform);
      const llocalInertia = new Ammo.btVector3(0, 0, 0);
      lgeometry.calculateLocalInertia(ip.lweight, llocalInertia);
      const linfos = new Ammo.btRigidBodyConstructionInfo(ip.lweight, lmotionState, lgeometry, llocalInertia);
      const lbody = new Ammo.btRigidBody(linfos);
      lbody.setActivationState(DISABLE_DEACTIVATION);
      
      
      ip.LphysicsBody = lbody;
      ip.attachedObject = {pos: {x:0, y:0, z:0}, rot: {x:0, y:0, z:0, w:1.0}, ammoData: {localInertia: llocalInertia, motionState: lmotionState, transform: ltransform, geometry: lgeometry}};
      lbody.userData = { name: 'lip-' + ip.id, obj: ip.attachedObject };
  
      ip.taken = 0;
  
      this.physicsUniverse.addRigidBody(body, worldGroup, carMask | worldMask);
      this.physicsUniverse.addRigidBody(lbody, worldGroup, carMask | worldMask);
  
      this.rigidBody_List.push(body);
      this.rigidBody_List.push(lbody);
      
      this.interestPoints.push({ id: ip.id, targetNode : ip.targetNode, taken:0, physicsBody: body, nBonuses : ip.nBonuses, lpos: new THREE.Vector3(ip.lpos.x, ip.lpos.y, ip.lpos.z), lrot: new THREE.Quaternion(ip.rot2.x, ip.rot2.y, ip.rot2.z, ip.rot2.w), LphysicsBody: lbody });
  
      Ammo.destroy(size);
      Ammo.destroy(infos);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
  
      Ammo.destroy(lsize);
      Ammo.destroy(linfos);
      Ammo.destroy(lorigin);
      Ammo.destroy(lquat);
    }
  

    addTriggertPhy(trigger, idx) {
  
      /* pod */
      const origin = new Ammo.btVector3(trigger.pos.x, trigger.pos.y, trigger.pos.z);
      const quat = new Ammo.btQuaternion(trigger.rot.x, trigger.rot.y, trigger.rot.z, trigger.rot.w);
      const size = new Ammo.btVector3(trigger.size.x /  2.0, trigger.size.y / 2.0, trigger.size.z/ 2.0);
  
      const geometry = new Ammo.btBoxShape(size);
      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      const motionState = new Ammo.btDefaultMotionState(transform);
      const localInertia = new Ammo.btVector3(0, 0, 0);
      geometry.calculateLocalInertia(0, localInertia);
      const infos = new Ammo.btRigidBodyConstructionInfo(0, motionState, geometry, localInertia);
      const body = new Ammo.btRigidBody(infos);
      body.setCollisionFlags(CF_NO_CONTACT_RESPONSE);
      body.userData = { name: 'trigger-' + trigger.type, idx : idx, obj: trigger };
      trigger.physicsBody = body;
      trigger.ammoData = { localInertia: localInertia  , motionState: motionState  , transform: transform  , geometry: geometry};
  
      this.physicsUniverse.addRigidBody(body, worldGroup, carMask | worldMask);
    
  
      Ammo.destroy(size);
      Ammo.destroy(infos);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
    }

    addJumperPhy(jumper) {


      const p1 = new THREE.Vector3( 0.5 * jumper.size.x,  0.25 * jumper.size.y, 0);
      const p2 = new THREE.Vector3(-0.5 * jumper.size.x, -0.25 * jumper.size.y, 0);
      const d= new THREE.Vector3().subVectors(p1, p2);
      const a = Math.atan2(d.y , d.x);
      const aa = new THREE.Euler(0, 0, a, "XYZ");
      const q = new THREE.Quaternion().setFromEuler(aa);
      const up = new THREE.Vector3(0, -jumper.size.y * 0.25, 0).applyQuaternion(q);
      const pos = new THREE.Vector3(jumper.pos.x, jumper.pos.y, jumper.pos.z).add(up);
      
      const jq = new THREE.Quaternion(jumper.rot.x, jumper.rot.y, jumper.rot.z, jumper.rot.w);
      const rot = new THREE.Quaternion().multiplyQuaternions(jq, q);

      const origin = new Ammo.btVector3(pos.x, pos.y, pos.z);
      const quat = new Ammo.btQuaternion(rot.x, rot.y, rot.z, rot.w);
      const size = new Ammo.btVector3(jumper.size.x / 2, jumper.size.y / 4, jumper.size.z / 2);
  
      const geometry = new Ammo.btBoxShape(size);
      const transform = new Ammo.btTransform(); 
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      const motionState = new Ammo.btDefaultMotionState(transform);
      const localInertia = new Ammo.btVector3(0, 0, 0);
      geometry.calculateLocalInertia(0, localInertia);
      const infos = new Ammo.btRigidBodyConstructionInfo(0, motionState, geometry, localInertia);
      const body = new Ammo.btRigidBody(infos);
  
      jumper.physicsBody = body;
      jumper.ammoData = { localInertia: localInertia, motionState: motionState, transform: transform, geometry: geometry };
      this.physicsUniverse.addRigidBody(body, worldGroup, worldMask);

      //body.userData = { name: 'jumper-' + jumper.id, obj: jumper };
      //this.rigidBody_List.push(body);
  
      this.jumpers.push({ id: jumper.id, physicsBody: body });
  
      Ammo.destroy(size);
      Ammo.destroy(infos);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
    }
  
    addBonusPhy(bonus) {
      const origin = new Ammo.btVector3(bonus.pos.x, bonus.pos.y, bonus.pos.z);
      const quat = new Ammo.btQuaternion(0, 0, 0, 1);
      const size = new Ammo.btVector3(0.5 * bonus.size, 0.5 * bonus.size, 0.5 * bonus.size);

      const geometry = new Ammo.btBoxShape(size);
      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      const motionState = new Ammo.btDefaultMotionState(transform);
      const localInertia = new Ammo.btVector3(0, 0, 0);
      geometry.calculateLocalInertia(0, localInertia);
      const infos = new Ammo.btRigidBodyConstructionInfo(0, motionState, geometry, localInertia);
      const body = new Ammo.btRigidBody(infos);
      body.setCollisionFlags(CF_NO_CONTACT_RESPONSE);
  
      body.userData = { name: 'bonus-' + bonus.id, obj: bonus };
  
      this.physicsUniverse.addRigidBody(body, worldGroup, worldMask);
      this.rigidBody_List.push(body);
  
      const phybon = { id: bonus.id, physicsBody: body, ammoData: { localInertia: localInertia, motionState: motionState, transform: transform, geometry: geometry }, scale: bonus.scale, type: bonus.type, amount: bonus.amount, size: bonus.size, bpMask : 0, taken: 0 }
      if(phybon.type === 1){
        phybon.bpMask= (1 << phybon.amount);
      }

      this.bonuses.push(phybon);
  
      Ammo.destroy(size);
      Ammo.destroy(infos);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
    }
  
    addWoodsPhy(wood, tpts) {
  
      const origin = wood.mass > 0 ? new Ammo.btVector3(wood.pos.x, wood.pos.y + 1.0, wood.pos.z): new Ammo.btVector3(wood.pos.x, wood.pos.y, wood.pos.z);


      const eul = new THREE.Euler().setFromQuaternion(wood.rot);
      eul.z += Math.PI/2;
      const q = new THREE.Quaternion().setFromEuler(eul);

      const quat = new Ammo.btQuaternion(q.x, q.y, q.z, q.w);
  
      const geometry = new Ammo.btCylinderShape(new Ammo.btVector3( wood.radius, wood.width / 2, wood.radius));
      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.setOrigin(origin);
      transform.setRotation(quat);
      const motionState = new Ammo.btDefaultMotionState(transform);
      const localInertia = new Ammo.btVector3(0, 0, 0);
      geometry.calculateLocalInertia(wood.mass, localInertia);
  
      const infos = new Ammo.btRigidBodyConstructionInfo(wood.mass, motionState, geometry, localInertia);
      const body = new Ammo.btRigidBody(infos);
      body.userData = { name: 'wood-' + wood.id, obj: wood };
      wood.physicsBody = body;
      wood.ammoData = { localInertia: localInertia, motionState: motionState, transform: transform, geometry: geometry };
  
      /*
      body.setCcdMotionThreshold(2);
      body.setCcdSweptSphereRadius(wood.radius);
      */
  
      body.setFriction(0.2); 
      body.setRestitution(0.2);
      body.setRollingFriction(0.2);
  
      body.setActivationState(DISABLE_DEACTIVATION)
      this.physicsUniverse.addRigidBody(body, worldGroup, worldMask);
      this.rigidBody_List.push(body);
      this.woods.push({ id: wood.id, physicsBody: body });
  
      Ammo.destroy(infos);
      Ammo.destroy(origin);
      Ammo.destroy(quat);
    }
  
  
    addTurretShot(turret, tpos, time) {
        this.tmpV1.set(turret.projPts.x, 0, turret.projPts.z).applyQuaternion(turret.rot).applyAxisAngle ( this.forwardVec , -turret.canonAngle ).applyAxisAngle ( this.upVec, turret.headAngle );
        this.tmpV1.y += turret.projPts.y;
        this.tmpV1.multiply(turret.size);
        this.tmpV1.add(tpos);
        
        const newShot = {pos: {x: this.tmpV1.x, y: this.tmpV1.y, z: this.tmpV1.z}, time : time, id: turret.lastShotId};

        turret.shots.push(newShot);
    
        this.addPhyShot(turret, newShot, turret.projSize, turret.projMass);

        this.tmpV1.set(this.forwardVec.x,this.forwardVec.y, this.forwardVec.z).applyQuaternion ( turret.rot );

        this.tmpEUL.set(turret.canonAngle + (Math.random() - 0.5 ) / 10, 0, 0,  "XYZ");
        this.tmpQ.setFromEuler(this.tmpEUL);
        this.tmpV1.applyQuaternion ( this.tmpQ );

        this.tmpEUL.set(0, turret.headAngle + (Math.random() - 0.5) / 10, 0,  "XYZ");
        this.tmpQ.setFromEuler(this.tmpEUL);
        this.tmpV1.applyQuaternion ( this.tmpQ );
        
        this.tmpV1.multiplyScalar(turret.projSpeed);

        this.tmpVec3.setValue(this.tmpV1.x,-this.tmpV1.y, this.tmpV1.z);
        newShot.physicsBody.applyCentralImpulse(this.tmpVec3);
    }
    
    addDroneShot(drone, weaponid, tpos, time) {
        const weapon = drone.weapons[weaponid];
        drone.shotPt.set(weapon.projPts.x, weapon.projPts.y, weapon.projPts.z).applyQuaternion(drone.rot).add(drone.pos);
        const newShot = {weaponid: weaponid, pos: drone.shotPt.clone(), time : time, id: drone.lastShotId};
        drone.shots.push(newShot);
    
        this.addPhyShot(drone, newShot, weapon.projSize, weapon.projMass, weaponid, "shot", weapon.dmg);

        if(!weapon.projHoming){
          drone.shotVel.set(tpos.x - drone.shotPt.x, tpos.y - drone.shotPt.y, tpos.z - drone.shotPt.z).normalize().multiplyScalar(weapon.projSpeed);
        }else{
          drone.shotVel.set(0, 0, 1).applyQuaternion(drone.rot).multiplyScalar(weapon.projSpeed);
        }

        this.tmpVec3.setValue(drone.shotVel.x, drone.shotVel.y, drone.shotVel.z);
        newShot.physicsBody.applyCentralImpulse(this.tmpVec3);

     }

    addSpiderShot(spider, id, time) {
      const root = spider.skel.root;
      spider.pt.set(spider.proj[id].pt.x, spider.proj[id].pt.y, spider.proj[id].pt.z).applyQuaternion(root.rot).add(root.pos);
      
      const newShot = {pos: {x: spider.pt.x, y: spider.pt.y, z: spider.pt.z}, time : time, id: spider.lastShotId};
      spider.pshots.push(newShot);
      this.addPhyShot(spider, newShot, spider.projSize, spider.projMass, 0, 'shot', 1);

      spider.stv.set(0, 0, 1).applyQuaternion ( root.rot ).applyQuaternion(spider.dirAngle).multiplyScalar(spider.projSpeed);
      spider.sv.setValue(spider.stv.x, -spider.stv.y, spider.stv.z);
      newShot.physicsBody.applyCentralImpulse(spider.sv);
  }
  
  addCarShot(car, carDef, id, time) {

    car.shotEul.set(car.weapons[id].cannonAngle, car.weapons[id].headAngle, 0, "YXZ");
    
    car.pt.set(car.weapons[id].pt.x, car.weapons[id].pt.y, car.weapons[id].pt.z).applyEuler(car.shotEul).applyQuaternion(car.quat).add(car.pos).add(meshsInfos[carDef.chassis].extents.center);
    const newShot = {pos: {x: car.pt.x, y: car.pt.y, z: car.pt.z}, time : time, id: car.lastShotId};
    car.shots.push(newShot);
    this.addPhyShot(car, newShot, car.weapons[id].projSize, car.weapons[id].projMass, id, 'shot-car-'+car.lastShotId, car.weapons[id].dmg);

    const vel = car.physicsBody.getLinearVelocity();
    this.tmpV1.set(vel.x(), vel.y(), vel.z());
    newShot.physicsBody.setLinearVelocity(vel);
    
    car.stv.set(0, 0, 1).applyEuler(car.shotEul).applyQuaternion ( car.quat ).multiplyScalar(car.weapons[id].projSpeed);
    car.sv.setValue(car.stv.x, car.stv.y, car.stv.z);
    newShot.physicsBody.applyCentralImpulse(car.sv);
   }

    getAdvancePos(track, car, time, out) {
        let cs = this.findClosestSeg(track, car.pos, this.tmpV2);
        
        if(cs < 0)
          return null;
  
        const vel = car.physicsBody.getLinearVelocity();
        let dv = this.tmpV3.set(vel.x(), vel.y(), vel.z()).multiplyScalar(time).length();  
        let dcp = this.tmpV3.subVectors(this.tmpV2 , track.trackPoints[cs].pos) . length();
        
        let cd = track.trackPoints[cs].line.distance() - dcp;
  
        while(dv > cd) {
            dv -= cd;
            cs++;
            if(cs>=track.trackPoints.length)
              cs=0;
  
            cd = track.trackPoints[cs].line.distance();
            this.tmpV2.set(track.trackPoints[cs].pos.x, track.trackPoints[cs].pos.y, track.trackPoints[cs].pos.z);
        }
  
  
        track.trackPoints[cs].line.delta(this.tmpV1);
        this.tmpV1.multiplyScalar( dv / cd);
  
        out.set(this.tmpV2.x + this.tmpV1.x, this.tmpV2.y + this.tmpV1.y, this.tmpV2.z + this.tmpV1.z);
    }
    updateTurrets(track, time, delta){

        for(let n=0;n<this.turrets.length;n++){

            const turret = this.turrets[n];
            const mv = delta * 1.2;
            let sd = 100000;
      
            turret.active = false;
      
            for(let i=0;i<this.cars.length;i++){
             
              turret.tdelta.subVectors ( turret.pos, this.cars[i].pos);
              const dist = turret.tdelta.length();
              
              if((dist < turret.activation)&&(dist < sd)) {
                sd = dist;
                turret.active = true;
                if(turret.projTTL > 0) 
                  this.getAdvancePos(track, this.cars[i], turret.projTTL, turret.ap) 
                else
                  turret.ap.set(this.cars[i].pos.x, this.cars[i].pos.y, this.cars[i].pos.z);

                turret.ap.sub(turret.pos).normalize();
                turret.headTargetAngle = Math.atan2(turret.ap.x , turret.ap.z);
              }
            }
            
            if(!turret.active)
              turret.headTargetAngle = turret.baseHeadAngle;
            

            if(turret.moveAngle>0){
              const da = turret.headTargetAngle-turret.headAngle;
              let angleDelta = da + Math.PI;
        
              if(angleDelta > Math.PI *2)
                angleDelta -= Math.PI *2;
        
              angleDelta -= Math.PI;
                
              if(angleDelta < 0) {
                  
                if((turret.headAngle + mv) < turret.headTargetAngle)
                  turret.headAngle += mv;
                else
                  turret.headAngle = turret.headTargetAngle;
        
              } else if((turret.headAngle - mv) > turret.headTargetAngle) 
                turret.headAngle -= mv;
              else
                turret.headAngle = turret.headTargetAngle;
          }else
            turret.headAngle = turret.baseHeadAngle;
           
   
          if((turret.active)&&((time - turret.lastShot)> turret.projDelay / 1000.0)) {
            this.addTurretShot(turret, turret.pos, time);
            turret.lastShotId++;
            turret.lastShot = time;
          }
                  
          let i=0;
          while(i<turret.shots.length) {
              if((time - turret.shots[i].time) >  turret.projLL + (turret.projTTL / 1000.0) ) {
                this.removePhyShot(turret.shots[i]);
                turret.shots.splice(i , 1);
              }
              else{i++;}
            }
          }
    }
    
    updateRopes(track, time, delta){
      

        for(let n=0;n<this.ropes.length;n++) {

            if(track.movingObjects.hasOwnProperty(track.ropes[n].anchorObj)) {
      
              const mo = track.movingObjects[track.ropes[n].anchorObj];
              const s = track.ropes[n].anchorObj.split('-');
              const dir = s[1];
      
              if(dir=='z')
                mo.angle.set(0, 0, Math.sin(time * 0.8) * 0.15 - 0.1, "XYZ");
              else if(dir=='y')
                mo.angle.set(0, Math.sin(time * 0.4) * 0.6, 0, "XYZ");
              else if(dir=='yf')
                mo.angle.set(0, time * 2.5, 0, "XYZ");        
              else if(dir=='x')
                mo.angle.set(Math.sin(time * 0.8) * 0.25, 0, 0, "XYZ");
      
              mo.rot.setFromEuler(mo.angle);
      
              this.tmpV1.set(mo.deltaAnchor.x, mo.deltaAnchor.y, mo.deltaAnchor.z).applyQuaternion(mo.rot).add(mo.axis).add(track.terrain.offset).add(track.ropes[n].offset);
      
              this.tmpVec3.setValue(this.tmpV1.x, this.tmpV1.y, this.tmpV1.z);
      
              this.tmpTransformation.setIdentity();
              this.tmpTransformation.setOrigin(this.tmpVec3);
      
              this.ropes[n].anchor.physicsBody.setWorldTransform(this.tmpTransformation);
              const motionState = this.ropes[n].anchor.physicsBody.getMotionState();
              motionState.setWorldTransform(this.tmpTransformation);
            }
        }
    }

    updateSpiders(track, time, delta){

      for(let si =0;si<track.spiders.length;si++){
        const s = track.spiders[si];
        const root = s.skel.root;
        this.unstuckBones(root);

        let i=0;
        while(i<s.pshots.length) {
          if((time - s.pshots[i].time) >  3 ) {
            this.removePhyShot(s.pshots[i]);
            s.pshots.splice(i , 1);
          }
          else{i++;}
        }

        this.findSkelnewContact(root);

        if(s.dead === true)
          continue;

        const closestCar = this.findClosestCar(root.pos);
        if(closestCar === null)
          continue;
       
        /*
        if(!s.active){

          if(root.pos.distanceTo(closestCar.pos)< s.activation)
            s.active = true;
        }
        */

        if((s.active === true)&&(closestCar !== null))
        {
          const nLegs = this.getSpiderLegs(root);
          const speed =  40;

          if(nLegs == 0){
            s.dead = true;
            s.sndBits = this.addSndBit(s.sndBits, 4);
            continue;
          }

          s.sforward.set(0,0,1).applyQuaternion(root.rot);
          s.toCar.set(closestCar.pos.x - root.pos.x, 0, closestCar.pos.z - root.pos.z).normalize();
          s.nForward.set(s.sforward.x, 0, s.sforward.z).normalize();
          s.dirAngle.setFromUnitVectors( s.nForward, s.toCar );

          if(s.lastPos){

            const dist = s.lastPos.distanceTo(root.pos);

            if(Math.abs(dist - s.lastDist)< 0.1){
  
              if(s.stuckMode == false){
                s.stuckTime = time;
                s.stuckMode = true;
              }else if((time - s.stuckTime)>5){
                s.backMode = true;
                s.backTime = time;
              }
  
            }else
              s.stuckMode = false;
  
            if(s.backMode === true){
              if((time - s.backTime)>2){
                s.backMode = false;
              }
            }

            s.lastDist = dist;
          }

          s.dir = s.backMode === true ? -1 : 1;
          s.lastPos =  root.pos.clone();
          

          if((s.dirAngle.y < 0)){
            s.torqueValue = Math.min(s.torqueValue, 0);
            s.torqueValue -= 100 * delta * nLegs / 16;
            s.torqueValue = Math.max(s.torqueValue, -440);
          } else {
            s.torqueValue = Math.max(s.torqueValue, 0);
            s.torqueValue += 100 * delta * nLegs / 16;
            s.torqueValue = Math.min(s.torqueValue, 440);
          }


          //s.torqueValue *= 0.99;

          s.torque.setValue(0, s.torqueValue, 0);
          
          root.physicsBody.applyTorqueImpulse(s.torque);

          
          s.move .setValue(s.sforward.x * speed * s.dir, 10, s.sforward.z * speed* s.dir);
          /*
          const pos = new Ammo.btVector3(0, 0.2, 0.1);
          root.physicsBody.applyImpulse(s.move, pos);
          Ammo.destroy(pos);
          */

          //s.toCar

          const dot = s.toCar.dot(s.nForward);
          
          if(((time - s.lastShot) > s.projDelay / 1000.0)&&(dot > 0.8 )) {
              this.addSpiderShot(s, s.curProj, time);
              s.lastShotId++;
              s.lastShot = time;
              s.curProj++;
              if(s.curProj>=s.proj.length)
                s.curProj = 0;
           }

           this.animateSpider(s.skel, time, s.move);

           
        }
      }
    }
    updateDrones(track, time, delta){
        

    for(let n=0;n<track.drones.length;n++){

        const drone = track.drones[n];
        const dronePhy = this.drones[n];

        let i=0;
        while(i<dronePhy.shots.length) {

          const w = dronePhy.weapons[dronePhy.shots[i].weaponid];

          if((time - dronePhy.shots[i].time) >  3 + (w.projTTL / 1000.0) ) {
            this.removePhyShot(dronePhy.shots[i]);
            dronePhy.shots.splice(i , 1);
          }
          else{i++;}
        }
        
        if(dronePhy.dead)
            continue;
  
        let minDist = 1000000;
        let closestCar = null;
  
        for(let i=0;i<this.cars.length;i++){
  
          const car = this.cars[i];

          if(car.isStuck)
            continue;
  
          this.tmpV1.subVectors(drone.pos, car.pos);
          const l = this.tmpV1.length();
          if(l < minDist) {
             minDist = l;
             closestCar = car;
          }
        }
  
        if(!closestCar)
          continue;
  
        if(!dronePhy.active) {
          if((minDist > drone.activation)/*|| (closestCar.curSeg< 4)*/)
            continue;

 
          dronePhy.active = true;
          dronePhy.physicsBody.activate();
          dronePhy.currentTarget = closestCar;



        }
        else if(minDist > drone.deactivation) {
          dronePhy.goHome = true;
          dronePhy.currentTarget = null;
        }else
          dronePhy.currentTarget = closestCar;
       
        //const carPos = new THREE.Vector3(closestCar.pos.x, closestCar.pos.y, closestCar.pos.z);
        dronePhy.dronePos.set(drone.pos.x, drone.pos.y, drone.pos.z);
        let targetPos = null;
        let destPoint = null;
        let speed = drone.speed;
  
        if(dronePhy.goHome) {
            const pt = track.trackPoints[drone.ptIdx].pos;
            destPoint = new THREE.Vector3(pt.x, pt.y, pt.z);
            targetPos = new THREE.Vector3(drone.offset.x, drone.offset.y, drone.offset.z).add(destPoint);
            speed = 20.0;
  
            if(dronePhy.dronePos.distanceTo(targetPos) < 1) {
              dronePhy.active = false;
              dronePhy.goHome = false;
              continue;
            }

            const ddelta = new THREE.Vector3().subVectors(targetPos, dronePhy.dronePos);
            const spd = Math.min(delta * speed, ddelta.length());
            const deltaPos = ddelta.normalize().multiplyScalar(spd);
            drone.pos = dronePhy.dronePos.add(deltaPos);
      
            const m = new THREE.Matrix4().lookAt( drone.pos, destPoint, new THREE.Vector3(0,1,0));
      
            const p = new THREE.Vector3();
            const s = new THREE.Vector3();
            m.decompose ( p, drone.rot, s );
      
            const ap = new Ammo.btVector3(drone.pos.x, drone.pos.y, drone.pos.z);
            const aq = new Ammo.btQuaternion(drone.rot.x, drone.rot.y, drone.rot.z, drone.rot.w);
      
            //const transform = new Ammo.btTransform();
            this.tmpTransformation.setIdentity();
            this.tmpTransformation.setOrigin(ap);
            this.tmpTransformation.setRotation(aq);
      
            drone.physicsBody.setWorldTransform(this.tmpTransformation);
            const motionState = drone.physicsBody.getMotionState();
            motionState.setWorldTransform(this.tmpTransformation);
  
        }else {
  
          /*
          const fvector =  dronePhy.currentTarget.vehicle.getForwardVector();
  
          const forward = new THREE.Vector3(fvector.x(), 0, fvector.z());
  
          const angle = Math.atan2(forward.x, forward.z);
          const eul = new THREE.Euler(0, angle, 0, "XYZ");
          targetPos = new THREE.Vector3(drone.caroffset.x, drone.caroffset.y, drone.caroffset.z).applyEuler(eul).add(carPos);
          targetPos.y += 4; 
          */
  
          /*
            targetPos = new THREE.Vector3(drone.offset.x, 0, drone.offset.z).applyEuler(eul).add(carPos);
            targetPos.y = drone.offset.y + carPos.y;
            const dir = dronePos.clone().sub(carPos).normalize();
          */

          let nPales = 0;
          
          for(let p of dronePhy.pales){
            if(p.constraint !== null)
              nPales++;
          }

          if((nPales == 0)&&(dronePhy.pales.length>0)){
            dronePhy.dead = true;
            dronePhy.physicsBody.setDamping(0.0, 0.0);
            dronePhy.sndBits = this.addSndBit(dronePhy.sndBits, 4);
            continue;
          }

          const fvector =  dronePhy.currentTarget.vehicle.getForwardVector();
          //const fvector = new THREE.Vector3().subVectors(dronePhy.dronePos, closestCar.pos).normalize();
          const angle = Math.atan2(fvector.x(), fvector.z());
          


          this.tmpEUL.set(0, angle, 0, "XYZ");
          dronePhy.targetAngleQ.setFromEuler(this.tmpEUL);
          const upLift = drone.upLift * delta;

          dronePhy.targetPos.set(drone.caroffset.x, 0, drone.caroffset.z).applyQuaternion(dronePhy.targetAngleQ).add(closestCar.pos);
          dronePhy.targetPos.y = drone.caroffset.y + closestCar.pos.y; 

          const deltaY = dronePhy.targetPos.y - dronePhy.pos.y;
        
          const upDist = Math.min(Math.max(0, deltaY) , 2);
          const up = (upDist * upLift) / 2 ;

          
          
          
          const toCarDeltaX = drone.pos.x  - closestCar.pos.x;
          const toCarDeltaZ = drone.pos.z  - closestCar.pos.z;
          this.tmpV1.set(toCarDeltaX, 0, toCarDeltaZ);
          
          /*
          //const fvector = new THREE.Vector3().subVectors(dronePhy.dronePos, closestCar.pos).normalize();
          const angleToCar = Math.atan2(toCarDeltaX, toCarDeltaY);
          //const speedPales = [600 * dronePhy.pales[0].life / 100, 600 * dronePhy.pales[1].life / 100];
          this.tmpEUL.set(0, angleToCar, 0, "XYZ");
          dronePhy.qtoCar.setFromEuler(this.tmpEUL);
          */

          dronePhy.qtoCar.setFromUnitVectors(this.forwardVec, this.tmpV1);

          this.nFrames++;
          let isUnderRoad = false;

          if(track.trackPoints.length > 1){
            dronePhy.from.set(dronePhy.dronePos.x, dronePhy.dronePos.y - 50, dronePhy.dronePos.z);
            dronePhy.to.set(0, 1, 0);
            this.raycaster.set(dronePhy.from, dronePhy.to);
    
            const pts = this.raycaster.intersectObject(track.tRoadMesh);

            if(pts.length >0) {
              // Raycast also hits self capsule, ignore that case.
              if((dronePhy.dronePos.y < pts[0].point.y)&&(pts[0].distance < 100)){


                const pd = this.getDistanceOnCurve(track, dronePhy);
                const t = track.curveC.getTangentAt(pd);
                dronePhy.biNormal.crossVectors(t, this.upVector);
                isUnderRoad = true;
              }
            }
          }

          /*
          const motionState = dronePhy.physicsBody.getMotionState();
          motionState.getWorldTransform(this.tmpTransformation);
          let new_qua = this.tmpTransformation.getRotation();
          const mq = new THREE.Quaternion(new_qua.x(), new_qua.y(), new_qua.z(), new_qua.w());
          const me = new THREE.Euler().setFromQuaternion(mq);
          me.z *= 0.9;
          mq.setFromEuler(me);
          new_qua.setValue(mq.x, mq.y, mq.z, mq.w);

          this.tmpTransformation.setRotation(new_qua);
          dronePhy.physicsBody.setWorldTransform(this.tmpTransformation);
          motionState.setWorldTransform(this.tmpTransformation);
          */
    
          //console.log('drone up '+up.toFixed(2) + ' '+ deltaY.toFixed(2));

          if(dronePhy.pales.length==0){

            /*
            this.tmpV1.set(drone.pales[p].rpos.x, drone.pales[p].rpos.y , drone.pales[p].rpos.z).applyQuaternion(dronePhy.qtoCar).add(dronePhy.dronePos);
            this.tmpV2.subVectors(this.tmpV1, dronePhy.pales[p].pos);
            const rl1 = Math.min(3.0, this.tmpV2.length()) / 3.0;
            const nrddelta = this.tmpV2.normalize();
            const rvel = new Ammo.btVector3(nrddelta.x * speedPale1 * rl1, 0 , nrddelta.z * speedPale1* rl1);
            dronePhy.pales[p].physicsBody.applyCentralImpulse(rvel);
            Ammo.destroy(rvel);
            */

            this.tmpV1.set(drone.caroffset.x, drone.caroffset.y ,drone.caroffset.z).applyQuaternion(dronePhy.targetAngleQ).add(closestCar.pos);
            this.tmpV2.subVectors(this.tmpV1, dronePhy.pos);
            const l1 = Math.min(3.0, this.tmpV2.length()) / 3.0;
            const npddelta =  this.tmpV2.normalize();
            this.tmpVec3.setValue(npddelta.x * drone.speedPale2 * l1 * 2, up  , npddelta.z * drone.speedPale2* l1 * 2);
            dronePhy.physicsBody.applyCentralImpulse(this.tmpVec3);
          }else{

            let minY = Infinity, maxY = -Infinity;
           

            for(let p = 0;p < dronePhy.pales.length; p++){
                minY = Math.min(dronePhy.pales[p].pos.y, minY);
                maxY = Math.max(dronePhy.pales[p].pos.y, minY);
            }

            for(let p = 0;p < dronePhy.pales.length; p++){
              dronePhy.factor[p] = 1.0 -(dronePhy.pales[p].pos.y - minY) / dronePhy.size.x;
            }

            

            for(let p = 0;p < dronePhy.pales.length; p++){

              const speedPale1 = drone.speedPale1 * dronePhy.pales[p].life / drone.pales[p].life * delta;
              const speedPale2 = drone.speedPale2 * dronePhy.pales[p].life / drone.pales[p].life * delta;

              if(dronePhy.pales[p].constraint !== null){


                if(isUnderRoad === false){
                  
                  this.tmpV1.set(drone.pales[p].rpos.x, drone.pales[p].rpos.y , drone.pales[p].rpos.z).applyQuaternion(dronePhy.qtoCar).add(dronePhy.dronePos);
                  this.tmpV2.subVectors(this.tmpV1, dronePhy.pales[p].pos);
                  const rl1 = Math.min(3.0, this.tmpV2.length()) / 3.0;
                  const nrddelta = this.tmpV2.normalize();
                  const rvel = new Ammo.btVector3(nrddelta.x * speedPale1 * rl1, 0 , nrddelta.z * speedPale1* rl1);
                  dronePhy.pales[p].physicsBody.applyCentralImpulse(rvel);
                  Ammo.destroy(rvel);

                  this.tmpV1.set(drone.caroffset.x + drone.pales[p].rpos.x, drone.caroffset.y + drone.pales[p].rpos.y , drone.caroffset.z + drone.pales[p].rpos.z).applyQuaternion(dronePhy.targetAngleQ).add(closestCar.pos);
                  this.tmpV2.subVectors(this.tmpV1, dronePhy.pales[p].pos);
                  const l1 = Math.min(3.0, this.tmpV2.length()) / 3.0;
                  const npddelta =  this.tmpV2.normalize();
                  this.tmpVec3.setValue(npddelta.x * speedPale2 * l1 * 2, up * dronePhy.factor[p]  , npddelta.z * speedPale2* l1 * 2);
                  dronePhy.pales[p].physicsBody.applyCentralImpulse(this.tmpVec3);
                  
                  const avel =  dronePhy.physicsBody.getAngularVelocity();
                  avel.setValue(avel.x() * 0.90, avel.y() , avel.z() * 0.90);
                  dronePhy.physicsBody.setAngularVelocity(avel);

                }else{
                  const rvel = new Ammo.btVector3(dronePhy.biNormal.x * speedPale1, 0 , dronePhy.biNormal.z * speedPale1);
                  dronePhy.pales[p].physicsBody.applyCentralImpulse(rvel);
                  Ammo.destroy(rvel);
                }
              }
            }
          }
        }

        if(!dronePhy.goHome) {

          for(let wi =0;wi< dronePhy.weapons.length;wi++){
            const w = dronePhy.weapons[wi];
            

            if((time - w.lastShot + wi * w.projDelay / 2000) > w.projDelay / 1000.0) {
              if(w.projTTL > 0)
                this.getAdvancePos(track, closestCar, w.projTTL,  dronePhy.tp) 
              else
                 dronePhy.tp.set(closestCar.pos.x, closestCar.pos.y, closestCar.pos.z);
    
              this.addDroneShot(dronePhy, wi,  dronePhy.tp, time );
              dronePhy.lastShotId++;
              w.lastShot = time;
            }
          }
          
        }
  
        /*
        Ammo.destroy(ap);
        Ammo.destroy(aq);
        */
  
        
      }
    }

    updatePlayers(track, players, time, delta){
        for(let p of players){

            if((p.character.active === false)&&(p.controlMode == 1)){
              this.removePhyCharacter(p);
              p.car.active = true;
              p.controlMode = 0;
            }
            if(p.character.active === true) {

              for(let npc of track.characters) {
                const cd  = npc.pos.distanceTo(p.character.pos);

                if(npc.active) {
      
                  if(cd>=npc.avatarDef.activation)
                    npc.active = false;
      
                } else if((cd<npc.avatarDef.activation)&&(npc.currentTarget === null)) {
                  const phychar = this.getNPC(npc.netID);
                  phychar.anims.push({a : npc.avatarDef.waveAnim, t : time, e : time + 2});
                  npc.active = true;
                }
              }
            }
        }
    }

    buildSkelIds(bone, id = 0) {
      bone.netID = id++;

      for(let c of bone.children){
        id = this.buildSkelIds(c, id);
      }
      return id;
    }

    resetskelContact(bone) {
      bone.inContact = false;
      for(let c of bone.children){
        this.resetskelContact(c);
      }
    }


    findSkelnewContact(bone) {

      if(bone.inContact){
        if(!bone.isInContact) {
          bone.newContact = true;
          bone.isInContact = true;
        }
      } else if( bone.isInContact ) {
        bone.isInContact = false;
      }
        
      for(let c of bone.children){
        this.findSkelnewContact(c);
      }
    }
    
  getActiveBones(bone, obj){

    if((bone.physicsBody != null)||(obj.firstFrame)){
      obj.cnt++;
    }
    for(let child of bone.children){
      obj = this.getActiveBones(child, obj);
    }

    return obj;
  }

  getBoneData(bone, array, obj){

    const offset = obj.ofs;

    if((bone.physicsBody != null)||(obj.firstFrame)){
      array[offset + 0] = bone.netID;
      array[offset + 1] = bone.pos.x;
      array[offset + 2] = bone.pos.y;
      array[offset + 3] = bone.pos.z;
  
      array[offset + 4] = bone.rot.x;
      array[offset + 5] = bone.rot.y;
      array[offset + 6] = bone.rot.z;
      array[offset + 7] = bone.rot.w;
  
      obj.ofs += 8;
      obj.cnt++;
    }

    if(bone.newContact) {
      obj.spider.sndBits = this.addSndBit(obj.spider.sndBits, 6);
      bone.newContact = false;
    }
    
   for(let child of bone.children){
      obj = this.getBoneData(child, array, obj);
    }

    return obj;
  }
  
  getCarBonus(car){

    let bonus = 0;
    for(let b of car.bonuses){
      bonus += b;
    }

    return bonus;

  }

  getCarTechs(car){

    let techs = 0;
    for(let b of car.techs){
      techs += b;
    }

    return techs;

  }

  getRagdollBoneData(bone, array, obj){

    const offset = obj.ofs;

    array[offset + 0] = bone.netID;
    array[offset + 1] = bone.rot.x;
    array[offset + 2] = bone.rot.y;
    array[offset + 3] = bone.rot.z;
    array[offset + 4] = bone.rot.w;

    array[offset + 5] = bone.life;

    if(bone.life === 0){
      array[offset + 6] = bone.pos.x;
      array[offset + 7] = bone.pos.y;
      array[offset + 8] = bone.pos.z;

      obj.ofs += 9;
    }else
      obj.ofs += 6;

    obj.cnt++;

    for(let child of bone.children){
      obj = this.getRagdollBoneData(child, array, obj);
    }

    return obj;
  }



  getNbones(bone, nBones = 0, curOffset = 0){

    let bones = nBones + 1;
    let offset = (bone.life === 0) ? curOffset + 9 : curOffset + 6;

    for(let c of bone.children){
        const ret = this.getNbones(c, bones, offset);
        bones = ret[0];
        offset = ret[1];
    }
    return [bones, offset];
  }

  getAnimId(def , id){

      if(id === 'idle'){
        return 0;
      }

      if(id === 'walk'){
        return 1;
      }

      if(id === 'wave'){
        return 2;
      }

  }

  isVisible(opos, pos){

  }
    
  getCars(track, startTime, lapTime, player, itime = null, floatArray = null, intArray = null) {
    var len = 2;



    const mok = Object.keys(track.movingObjects);

    const nBuss = (player === null) ? 0 : player.nBufferSents;

    const firstFrame = (player === null) ? true : (nBuss==0) ? true: false;

    let flags = 2;
    //if(firstFrame || (nBuss % 2)===0)
    {
      flags |= 1;
    }
    if(firstFrame || (nBuss % 2)===0){
      flags |= 4;
    }

    len += 1 + this.blastImpacts.length * 4;
    len += 1 + this.triggerEvents.length * 1;
    len += 1 + this.cars.length * 27;

    for (let n=0;n<this.cars.length;n++) {

      const car = this.cars[n];
      len += car.wheels.length * 12;
      len += car.contacts.length * 4;

      if(car.weapons.length>0)
        len++;

      if(flags & 4) {
        len += 1 + car.shots.length * 4;
      }

      len += car.newbonuses.length;
      len += car.newtechs.length;
    }

    let nNpcs = 0;

    len++;
    for (let n=0;n<this.npcs.length;n++) {

      const npc = this.npcs[n];

      if(npc==null) {

        if(!firstFrame)
          continue;

        len++;
      }else if(npc.ragdoll === true){

        if(!npc.active)
          continue;

        if(flags&1){
          const ret = this.getNbones(npc.skeleton.root);

          npc.nBones = ret[0];
          len += ret[1];
       }

        len+=2; // dialog
      }else{
        len+=2; // dialog
        len++;
        len +=npc.anims.length;
      }

      len += 9;
      nNpcs++;
    }


    len += 1 + this.characters.length * 10;
    len += 1 + this.woods.length * 7;
    len += 1 + this.interestPoints.length * 8;
    len += 1 + this.bonuses.length * 2;
    
    len += 1 + this.turrets.length;

    if(flags & 4) {

      for (let turret of this.turrets) {
        len += 1 + turret.shots.length * 4;
      } 
    }

    len ++;
    for (let n =0; n<this.drones.length; n++) {

      const drone = this.drones[n];
      //console.log('idrone '+n+' '+len);

      len += drone.pales.length * 8;
      if(flags & 4) {
        len += 1 + drone.shots.length * 4;
      }
      len += 8;
    }

    len += 1 + this.springs.length * 8;
    len += 1 + this.ropes.length * 8;


    len++; 
    let nspiders = 0;
    for (let n=0;n<track.spiders.length;n++) {

      const spider = track.spiders[n];
      if(firstFrame || spider.active || spider.skel.root.physicsBody.isActive()){

        //console.log('ispider '+n+' '+len);

        len += 2;

        if(flags & 1){
          let obj = {cnt : 0, firstFrame: firstFrame};
          obj = this.getActiveBones(spider.skel.root, obj);
          len += obj.cnt * 8;
        }
        if(flags&4) {
          len += 1;
          len += spider.pshots.length * 4;
        }
        nspiders++;
      }
    } 

    len += 1 + this.trackSegments.length * 6;
    len += 1 + mok.length * 4;
    
    let ncyls = 0;
    let ncubes = 0;
    let nXMeshes = 0;

    if(flags & 2){

      len++; 
      for (let n=0;n<track.cyls.length;n++) {
        const cyl = track.cyls[n];
        const a = cyl.physicsBody.isActive();
        if(firstFrame||a){
          len += 8;
          ncyls++;
        }
      }
      
      len++; 
      for (let n=0;n<track.cubes.length;n++) {
        const cube = track.cubes[n];
        const a = cube.physicsBody.isActive();
        if(firstFrame||a || (cube.moving === true)){

          //console.log('icube '+n+' '+len);
          len += 8;
          ncubes++;
        }
      }

      len += 1 + this.splits.length * 5;
    
      len++; 
  
      for(let xi = 0; xi< this.xmeshes.length; xi++){
        const a = this.xmeshes[xi].physicsBody.isActive();
        if((firstFrame)||(a)){
          nXMeshes++;
          len += 8;
        }
      }
    }
    
    if(floatArray === null) {
      floatArray = new Float32Array(len);
      intArray = new Uint32Array(floatArray.buffer);
    }
    else
      floatArray.fill(0)

    var ofs = 0;
    let totaltime = 0; 
    let time = itime !== null ?itime : this.clock.getElapsedTime();
    
    if(startTime !== undefined)
      totaltime = time - startTime;

 

    floatArray[ofs++] = flags;
    floatArray[ofs++] = totaltime;
    
    floatArray[ofs++] = this.blastImpacts.length;
    let impact;
    while(impact = this.blastImpacts.pop()){
      floatArray[ofs++] = impact.type;
      floatArray[ofs++] = impact.pos.x;
      floatArray[ofs++] = impact.pos.y;
      floatArray[ofs++] = impact.pos.z;
    }

    floatArray[ofs++] = this.triggerEvents.length;
    let event;
    while(event = this.triggerEvents.pop()){
      intArray[ofs++] = ((event.trig & 0xFFFF) | (event.state << 16));
    }
    
    floatArray[ofs++] = this.cars.length;
    for (let n = 0; n < this.cars.length; n++) {

      intArray[ofs++] = this.cars[n].netID;
      floatArray[ofs++] = this.cars[n].pos.x;
      floatArray[ofs++] = this.cars[n].pos.y;
      floatArray[ofs++] = this.cars[n].pos.z;

      floatArray[ofs++] = this.cars[n].quat.x;
      floatArray[ofs++] = this.cars[n].quat.y;
      floatArray[ofs++] = this.cars[n].quat.z;
      floatArray[ofs++] = this.cars[n].quat.w;

      if(startTime === 0){
        floatArray[ofs++] = -3;
      }else if(totaltime>3) {
        const remt =  totaltime - 3 - this.cars[n].lastLap ;
        floatArray[ofs++] = remt;
      } else {
        floatArray[ofs++] = -(3-totaltime);
      }

      const carDmg = parseInt(this.cars[n].damage + this.cars[n].initialdmg);
      const carPower = this.cars[n].power;

      floatArray[ofs++] = this.cars[n].speed;
      floatArray[ofs++] = this.cars[n].RPM;
      intArray[ofs++] = (((carDmg & 0xFFFF)<< 16) | (carPower & 0xFFFF)) ;
      floatArray[ofs++] = this.cars[n].curSeg;
      floatArray[ofs++] = this.cars[n].nLaps;
      intArray[ofs++] = this.cars[n].triggerIDX + 1;
      intArray[ofs++] = this.cars[n].blueprints;
      floatArray[ofs++] = this.cars[n].targetNode;

      const targetID = this.cars[n].selectedTargetId & 0x7F;
      const targetSID = this.cars[n].selectedSubsystemId & 0x7F;
      intArray[ofs++] = (targetID) | (targetSID<<7);
      if(this.cars[n].weapons.length>0){
   
        const headAngle = parseInt(this.cars[n].weapons[0].headAngle * 512 / (Math.PI * 2)) & 0x1FF;
        const cannonAngle = parseInt(this.cars[n].weapons[0].cannonAngle * 512 / (Math.PI * 2)) & 0x1FF;
          intArray[ofs++] = (headAngle << 14) | (cannonAngle << 23);
      }

      floatArray[ofs++] = this.cars[n].newAttachedObjects;
      this.cars[n].newAttachedObjects = 0;

      this.cars[n].newTakenObjects = 0;

      floatArray[ofs++] = this.cars[n].nitros - this.cars[n].usedNitros;
      floatArray[ofs++] = this.cars[n].nextNitro ? this.cars[n].nextNitro : 0;

      floatArray[ofs++] = this.cars[n].multiplier;
      floatArray[ofs++] = this.getCarBonus(this.cars[n]);
      floatArray[ofs++] = this.getCarTechs(this.cars[n]);

      floatArray[ofs++] = this.cars[n].contacts.length;

      let impact;
      while ((impact = this.cars[n].contacts.pop())) {
        floatArray[ofs++] = impact.pos.x();
        floatArray[ofs++] = impact.pos.y();
        floatArray[ofs++] = impact.pos.z();
        floatArray[ofs++] = impact.hit;
      }

      if(flags & 4) {
        floatArray[ofs++] = this.cars[n].shots.length;
        for(let i=0; i < this.cars[n].shots.length; i++) {
          floatArray[ofs++] = this.cars[n].shots[i].id;
          floatArray[ofs++] = this.cars[n].shots[i].pos.x;
          floatArray[ofs++] = this.cars[n].shots[i].pos.y;
          floatArray[ofs++] = this.cars[n].shots[i].pos.z;
        }
      }

      floatArray[ofs++] = this.cars[n].newbonuses.length;
      let nb;
      while(nb = this.cars[n].newbonuses.pop()){
        floatArray[ofs++] = nb;
      }
      
      floatArray[ofs++] = this.cars[n].newtechs.length;
      let nt;
      while(nt = this.cars[n].newtechs.pop()){
        floatArray[ofs++] = nt;
      }

      for (let i = 0; i < this.cars[n].wheels.length; i++) {
        floatArray[ofs++] = this.cars[n].wheels[i].pos.x;
        floatArray[ofs++] = this.cars[n].wheels[i].pos.y;
        floatArray[ofs++] = this.cars[n].wheels[i].pos.z;

        floatArray[ofs++] = this.cars[n].wheels[i].quat.x;
        floatArray[ofs++] = this.cars[n].wheels[i].quat.y;
        floatArray[ofs++] = this.cars[n].wheels[i].quat.z;
        floatArray[ofs++] = this.cars[n].wheels[i].quat.w;
        floatArray[ofs++] = this.cars[n].wheels[i].skidding;

        floatArray[ofs++] = this.cars[n].wheels[i].inContact;
        floatArray[ofs++] = this.cars[n].wheels[i].contactPoint.x;
        floatArray[ofs++] = this.cars[n].wheels[i].contactPoint.y;
        floatArray[ofs++] = this.cars[n].wheels[i].contactPoint.z;
      }
    }

    floatArray[ofs++] = nNpcs;
    for (let n = 0; n < this.npcs.length; n++) {

      //console.log('npc '+n+' '+ofs);
      if(this.npcs[n] === null){
         if(firstFrame){
            floatArray[ofs++] = n;
            floatArray[ofs++] = track.npcs[n].pos.x;
            floatArray[ofs++] = track.npcs[n].pos.y;
            floatArray[ofs++] = track.npcs[n].pos.z;
      
            floatArray[ofs++] = track.npcs[n].rot.x;
            floatArray[ofs++] = track.npcs[n].rot.y;
            floatArray[ofs++] = track.npcs[n].rot.z;
            floatArray[ofs++] = track.npcs[n].rot.w;
            floatArray[ofs++] = 0;
         }
      }else if(this.npcs[n].ragdoll){

        if(!this.npcs[n].active)
          continue;
  
        let ret = {ofs : ofs +11, cnt : 0, firstFrame: firstFrame};
        if(flags & 1) {
          ret  = this.getRagdollBoneData(this.npcs[n].skeleton.root, floatArray, ret);
        }
        floatArray[ofs++] = n;
        floatArray[ofs++] = track.npcs[n].ppos.x;
        floatArray[ofs++] = track.npcs[n].ppos.y;
        floatArray[ofs++] = track.npcs[n].ppos.z;
  
        floatArray[ofs++] = track.npcs[n].prot.x;
        floatArray[ofs++] = track.npcs[n].prot.y;
        floatArray[ofs++] = track.npcs[n].prot.z;
        floatArray[ofs++] = track.npcs[n].prot.w;

        floatArray[ofs++] = ret.cnt;


        floatArray[ofs++] = this.npcs[n].currentDialogLine;
        floatArray[ofs++] = this.npcs[n].currentDialogSubLine;

        ofs = ret.ofs;

      }else{
        floatArray[ofs++] = n;
        floatArray[ofs++] = this.npcs[n].physicsBody.userData.char.ppos.x;
        floatArray[ofs++] = this.npcs[n].physicsBody.userData.char.ppos.y;
        floatArray[ofs++] = this.npcs[n].physicsBody.userData.char.ppos.z;
  
        floatArray[ofs++] = this.npcs[n].physicsBody.userData.char.prot.x;
        floatArray[ofs++] = this.npcs[n].physicsBody.userData.char.prot.y;
        floatArray[ofs++] = this.npcs[n].physicsBody.userData.char.prot.z;
        floatArray[ofs++] = this.npcs[n].physicsBody.userData.char.prot.w;
  
        floatArray[ofs++] = this.npcs[n].anims.length;
        for(let a of this.npcs[n].anims){
          floatArray[ofs++] = this.getAnimId(track.npcs[n].avatarDef, a.a);
          floatArray[ofs++] = time - a.t;
        }

        floatArray[ofs++] = this.npcs[n].currentDialogLine;
        floatArray[ofs++] = this.npcs[n].currentDialogSubLine;
      }
    }

    floatArray[ofs++] = this.characters.length;
    for (let n = 0; n < this.characters.length; n++) {

      floatArray[ofs++] = this.characters[n].netID;
      floatArray[ofs++] = this.characters[n].physicsBody.userData.obj.pos.x;
      floatArray[ofs++] = this.characters[n].physicsBody.userData.obj.pos.y;
      floatArray[ofs++] = this.characters[n].physicsBody.userData.obj.pos.z;

      floatArray[ofs++] = this.characters[n].physicsBody.userData.obj.rot.x;
      floatArray[ofs++] = this.characters[n].physicsBody.userData.obj.rot.y;
      floatArray[ofs++] = this.characters[n].physicsBody.userData.obj.rot.z;
      floatArray[ofs++] = this.characters[n].physicsBody.userData.obj.rot.w;

      if(this.characters[n].anims){
        floatArray[ofs++] = this.getAnimId(this.characters[n].physicsBody.userData.obj.avatarDef, this.characters[n].anims[0].a);
        floatArray[ofs++] = time - this.characters[n].anims[0].t;
      }else{
        floatArray[ofs++] = 0;
        floatArray[ofs++] =  time;
      }
    }


    floatArray[ofs++] = this.woods.length;
    for (let n = 0; n < this.woods.length; n++) {
      floatArray[ofs++] = n;
      floatArray[ofs++] = this.woods[n].physicsBody.userData.obj.pos.x;
      floatArray[ofs++] = this.woods[n].physicsBody.userData.obj.pos.y;
      floatArray[ofs++] = this.woods[n].physicsBody.userData.obj.pos.z;

      floatArray[ofs++] = this.woods[n].physicsBody.userData.obj.rot.x;
      floatArray[ofs++] = this.woods[n].physicsBody.userData.obj.rot.y;
      floatArray[ofs++] = this.woods[n].physicsBody.userData.obj.rot.z;
      floatArray[ofs++] = this.woods[n].physicsBody.userData.obj.rot.w;
    }

    floatArray[ofs++] = this.interestPoints.length;

    for (let n = 0; n < this.interestPoints.length; n++) {
      floatArray[ofs++] = n;
      floatArray[ofs++] = this.interestPoints[n].LphysicsBody.userData.obj.pos.x;
      floatArray[ofs++] = this.interestPoints[n].LphysicsBody.userData.obj.pos.y;
      floatArray[ofs++] = this.interestPoints[n].LphysicsBody.userData.obj.pos.z;

      floatArray[ofs++] = this.interestPoints[n].LphysicsBody.userData.obj.rot.x;
      floatArray[ofs++] = this.interestPoints[n].LphysicsBody.userData.obj.rot.y;
      floatArray[ofs++] = this.interestPoints[n].LphysicsBody.userData.obj.rot.z;
      floatArray[ofs++] = this.interestPoints[n].LphysicsBody.userData.obj.rot.w;
    }

    floatArray[ofs++] = this.bonuses.length;

    for (let n = 0; n < this.bonuses.length; n++) {
      floatArray[ofs++] = this.bonuses[n].taken;
      floatArray[ofs++] = this.bonuses[n].size;
    }

    floatArray[ofs++] = this.turrets.length;
    for (let n = 0; n < this.turrets.length; n++) {
      floatArray[ofs++] = this.turrets[n].headAngle;
      if(flags&4){
        floatArray[ofs++] = this.turrets[n].shots.length;
        for(let i=0;i<this.turrets[n].shots.length;i++) {
          floatArray[ofs++] = this.turrets[n].shots[i].id;
          floatArray[ofs++] = this.turrets[n].shots[i].pos.x;
          floatArray[ofs++] = this.turrets[n].shots[i].pos.y;
          floatArray[ofs++] = this.turrets[n].shots[i].pos.z;
        }
      }
    }

    floatArray[ofs++] = this.drones.length;
    for(let n=0;n<this.drones.length;n++) {

      //console.log('drone '+n+' '+ofs);

      floatArray[ofs++] = n | (this.drones[n].id << 8);
      floatArray[ofs++] = this.drones[n].physicsBody.userData.obj.pos.x;
      floatArray[ofs++] = this.drones[n].physicsBody.userData.obj.pos.y;
      floatArray[ofs++] = this.drones[n].physicsBody.userData.obj.pos.z;

      floatArray[ofs++] = this.drones[n].physicsBody.userData.obj.rot.x;
      floatArray[ofs++] = this.drones[n].physicsBody.userData.obj.rot.y;
      floatArray[ofs++] = this.drones[n].physicsBody.userData.obj.rot.z;
      floatArray[ofs++] = this.drones[n].physicsBody.userData.obj.rot.w;


      for(let pale of this.drones[n].pales) {

        if(pale.physicsBody){
          floatArray[ofs++] = pale.physicsBody.userData.obj.pos.x;
          floatArray[ofs++] = pale.physicsBody.userData.obj.pos.y;
          floatArray[ofs++] = pale.physicsBody.userData.obj.pos.z;
    
          floatArray[ofs++] = pale.physicsBody.userData.obj.rot.x;
          floatArray[ofs++] = pale.physicsBody.userData.obj.rot.y;
          floatArray[ofs++] = pale.physicsBody.userData.obj.rot.z;
          floatArray[ofs++] = pale.physicsBody.userData.obj.rot.w;

          floatArray[ofs++] = pale.life;
        }else{
          floatArray[ofs++] = 0;
          floatArray[ofs++] = -2000;
          floatArray[ofs++] = 0;
    
          floatArray[ofs++] = 0;
          floatArray[ofs++] = 0;
          floatArray[ofs++] = 0;
          floatArray[ofs++] = 1;

          floatArray[ofs++] = -1;
        }
      } 
  

      if(flags&4){
        floatArray[ofs++] = this.drones[n].shots.length;
        for(let i=0; i < this.drones[n].shots.length; i++) {
          floatArray[ofs++] = this.drones[n].shots[i].id;
          floatArray[ofs++] = this.drones[n].shots[i].pos.x;
          floatArray[ofs++] = this.drones[n].shots[i].pos.y;
          floatArray[ofs++] = this.drones[n].shots[i].pos.z;
        }
      }
    }


    floatArray[ofs++] = this.springs.length;

    for (let n = 0; n < this.springs.length; n++) {
      floatArray[ofs++] = n;
      floatArray[ofs++] = this.springs[n].obj.physicsBody.userData.obj.pos.x;
      floatArray[ofs++] = this.springs[n].obj.physicsBody.userData.obj.pos.y;
      floatArray[ofs++] = this.springs[n].obj.physicsBody.userData.obj.pos.z;

      floatArray[ofs++] = this.springs[n].obj.physicsBody.userData.obj.rot.x;
      floatArray[ofs++] = this.springs[n].obj.physicsBody.userData.obj.rot.y;
      floatArray[ofs++] = this.springs[n].obj.physicsBody.userData.obj.rot.z;
      floatArray[ofs++] = this.springs[n].obj.physicsBody.userData.obj.rot.w;
      
    }

    floatArray[ofs++] = this.ropes.length;
    for (let n = 0; n < this.ropes.length; n++) {
      floatArray[ofs++] = n;
      floatArray[ofs++] = this.ropes[n].obj.physicsBody.userData.obj.pos.x;
      floatArray[ofs++] = this.ropes[n].obj.physicsBody.userData.obj.pos.y;
      floatArray[ofs++] = this.ropes[n].obj.physicsBody.userData.obj.pos.z;

      floatArray[ofs++] = this.ropes[n].obj.physicsBody.userData.obj.rot.x;
      floatArray[ofs++] = this.ropes[n].obj.physicsBody.userData.obj.rot.y;
      floatArray[ofs++] = this.ropes[n].obj.physicsBody.userData.obj.rot.z;
      floatArray[ofs++] = this.ropes[n].obj.physicsBody.userData.obj.rot.w;
      
    }



    
    floatArray[ofs++] = nspiders;
    for (let n = 0; n < track.spiders.length; n++) {
      
      if(firstFrame || track.spiders[n].active || track.spiders[n].skel.root.physicsBody.isActive()){

        //console.log('spider '+n+' '+ofs);

        let ret={ofs : ofs +1, cnt : 0, firstFrame: firstFrame, spider: track.spiders[n]};
        if(flags&1){
          ret = this.getBoneData(track.spiders[n].skel.root, floatArray, ret);
        }
        floatArray[ofs] = n | (ret.cnt << 8);
        ofs = ret.ofs;

        floatArray[ofs++] = track.spiders[n].sndBits;
        track.spiders[n].sndBits = 0;

        if(flags&4){

          floatArray[ofs++] = track.spiders[n].pshots.length;
          for (let i = 0; i < track.spiders[n].pshots.length; i++) {
            floatArray[ofs++] = track.spiders[n].pshots[i].id;
            floatArray[ofs++] = track.spiders[n].pshots[i].pos.x;
            floatArray[ofs++] = track.spiders[n].pshots[i].pos.y;
            floatArray[ofs++] = track.spiders[n].pshots[i].pos.z;
          }
        }
      }
    }

    floatArray[ofs++] = 0;
    /*
    floatArray[ofs++] = this.trackSegments.length;

    for (let n = 0; n < this.trackSegments.length; n++) {

      floatArray[ofs++] = this.trackSegments[n].center.x;
      floatArray[ofs++] = this.trackSegments[n].center.y;
      floatArray[ofs++] = this.trackSegments[n].center.z;

      floatArray[ofs++] = this.trackSegments[n].halfSize.x;
      floatArray[ofs++] = this.trackSegments[n].halfSize.y;
      floatArray[ofs++] = this.trackSegments[n].halfSize.z;

    }
    */


    floatArray[ofs++] = mok.length;
    for (let n = 0; n < mok.length; n++) {
      floatArray[ofs++] = track.movingObjects[mok[n]].rot.x;
      floatArray[ofs++] = track.movingObjects[mok[n]].rot.y;
      floatArray[ofs++] = track.movingObjects[mok[n]].rot.z;
      floatArray[ofs++] = track.movingObjects[mok[n]].rot.w;
    }


    let p = (time - this.lt)>1 ? true : false;

    if(flags & 2){

      floatArray[ofs++] = ncyls
      for (let n = 0; n < track.cyls.length; n++) {
  
        const a = track.cyls[n].physicsBody.isActive();
        if((firstFrame)||(a)){

          floatArray[ofs++] = n;
          floatArray[ofs++] = track.cyls[n].pos.x;
          floatArray[ofs++] = track.cyls[n].pos.y;
          floatArray[ofs++] = track.cyls[n].pos.z;
    
          floatArray[ofs++] = track.cyls[n].rot.x;
          floatArray[ofs++] = track.cyls[n].rot.y;
          floatArray[ofs++] = track.cyls[n].rot.z;
          floatArray[ofs++] = track.cyls[n].rot.w;
        }
      }
  
      floatArray[ofs++] = ncubes;
      for (let n = 0; n < track.cubes.length; n++) {

        const a = track.cubes[n].physicsBody.isActive();
        if((firstFrame)||(a) ||(track.cubes[n].moving === true)){

          //console.log('cube '+n+' '+ofs);
          
          floatArray[ofs++] = n;
          floatArray[ofs++] = track.cubes[n].pos.x;
          floatArray[ofs++] = track.cubes[n].pos.y;
          floatArray[ofs++] = track.cubes[n].pos.z;
    
          floatArray[ofs++] = track.cubes[n].rot.x;
          floatArray[ofs++] = track.cubes[n].rot.y;
          floatArray[ofs++] = track.cubes[n].rot.z;
          floatArray[ofs++] = track.cubes[n].rot.w;
        }
      }
      
      floatArray[ofs++] =this.splits.length;
      for(let split =0; split < this.splits.length;split++) {
        floatArray[ofs++] = this.splits[split].id;
        floatArray[ofs++] = this.splits[split].a;
        floatArray[ofs++] = this.splits[split].b;
        floatArray[ofs++] = this.splits[split].c;
        floatArray[ofs++] = this.splits[split].d;
      }
      this.splits=[];

      

      floatArray[ofs++] = nXMeshes;
      for (let n = 0; n < this.xmeshes.length; n++) {
        const a = this.xmeshes[n].physicsBody.isActive();
        if((firstFrame)||(a)){
          floatArray[ofs++] = n;
          floatArray[ofs++] = this.xmeshes[n].pos.x;
          floatArray[ofs++] = this.xmeshes[n].pos.y;
          floatArray[ofs++] = this.xmeshes[n].pos.z;

          floatArray[ofs++] = this.xmeshes[n].rot.x;
          floatArray[ofs++] = this.xmeshes[n].rot.y;
          floatArray[ofs++] = this.xmeshes[n].rot.z;
          floatArray[ofs++] = this.xmeshes[n].rot.w;
        }
      }
    }

   
    if(p){
      this.lt = time;
    }

    this.totalBufferSents++;

    if(player)
      player.nBufferSents++;

      return floatArray.buffer;
    }

    

    createGroundMesh(track, offset = {x:0,y:0,z:0}){

      const scale = new Ammo.btVector3(1.0, 1.0, 1.0);
      let ammoMesh3 = new Ammo.btTriangleMesh();
      const v1 = new Ammo.btVector3();
      const v2 = new Ammo.btVector3();
      const v3 = new Ammo.btVector3();

      let groundMesh;
      let groundGeo;
      if(track.groundGeo){
        groundGeo = track.groundGeo.toNonIndexed();
        groundMesh = groundGeo.attributes.position.array;
      }else{
        groundGeo = new THREE.BufferGeometry();
        groundGeo.setAttribute('position', new THREE.Float32BufferAttribute(track.groundMesh, 3));
        groundMesh= track.groundMesh;
      }

      track.minGroundY = 10000;
      
      for (let n = 0; n < groundMesh.length; n+=9) {
        v1.setValue(groundMesh[n+0] + offset.x, groundMesh[n+1] + offset.y, groundMesh[n+2] + offset.z);
        v2.setValue(groundMesh[n+3] + offset.x, groundMesh[n+4] + offset.y, groundMesh[n+5] + offset.z);
        v3.setValue(groundMesh[n+6] + offset.x, groundMesh[n+7] + offset.y, groundMesh[n+8] + offset.z);
        ammoMesh3.addTriangle(v1, v2, v3, true);

        track.minGroundY = Math.min(track.minGroundY,  groundMesh[n+4] + offset.y);
        track.minGroundY = Math.min(track.minGroundY,  groundMesh[n+1] + offset.y);
        track.minGroundY = Math.min(track.minGroundY,  groundMesh[n+7] + offset.y); 
      }

      Ammo.destroy(v1);
      Ammo.destroy(v2);
      Ammo.destroy(v3);

     

      const geo = groundGeo.clone();
      //geo.setAttribute('position', new THREE.Float32BufferAttribute(track.groundMesh, 3));
      geo.computeBoundsTree();
      const mat = new THREE.MeshBasicMaterial({side: THREE.DoubleSide});
      this.tGroundMesh = new THREE.Mesh(geo, mat);

      ammoMesh3.computeVertexNormals();
      let triangleShape3 = new Ammo.btBvhTriangleMeshShape(ammoMesh3, false, true);
      triangleShape3.setMargin(0.2);
      triangleShape3.setLocalScaling(scale);
      triangleShape3.GenerateInternalEdgeInfo();
      Ammo.destroy(scale);

      track.groundObj = { name: "groundMesh", shape: triangleShape3, mesh: ammoMesh3 };
    }

    createRoadPhy(track){
   
        let vertices = [];
        let texcoords = [];
        var lastpn = -1;

        if(track.trackPoints.length < 2)
          return;
        
          this.curvePointsL= [];
          this.curvePointsR= [];
          this.curvePointsC= [];
          this.curvePointsLU= [];
          this.curvePointsRU= [];

        for(let n=0;n<track.curvePointsL.length;n++){
        
          this.curvePointsL[n]= new THREE.Vector3(track.curvePointsL[n].x, track.curvePointsL[n].y, track.curvePointsL[n].z);
          this.curvePointsR[n]= new THREE.Vector3(track.curvePointsR[n].x, track.curvePointsR[n].y, track.curvePointsR[n].z);
          this.curvePointsC[n]= new THREE.Vector3(track.curvePointsC[n].x, track.curvePointsC[n].y, track.curvePointsC[n].z);
          this.curvePointsLU[n]= new THREE.Vector3(track.curvePointsLU[n].x, track.curvePointsLU[n].y, track.curvePointsLU[n].z);
          this.curvePointsRU[n]= new THREE.Vector3(track.curvePointsRU[n].x, track.curvePointsRU[n].y, track.curvePointsRU[n].z);
        }

        console.log(this.curvePointsL);
        console.log(this.curvePointsR);
        console.log(this.curvePointsC);
        console.log(this.curvePointsLU);
        console.log(this.curvePointsRU);

        this.curveL = new THREE.CatmullRomCurve3(this.curvePointsL, true, track.curveType, track.tensionL);
        this.curveR = new THREE.CatmullRomCurve3(this.curvePointsR, true, track.curveType, track.tensionR);
        this.curveC = new THREE.CatmullRomCurve3(this.curvePointsC, true, track.curveType, track.tensionC);        

        this.curveLU = new THREE.CatmullRomCurve3(this.curvePointsLU, true, track.curveType, track.tensionL);
        this.curveRU = new THREE.CatmullRomCurve3(this.curvePointsRU, true, track.curveType, track.tensionR);
        
        const last = track.trackPoints.length - 1;

        const cL = this.curveC.getLength();
        let uvfac = 0;
        const uv1_ = new THREE.Vector2(0 , 0);

        const nMSidePts = 6;
        const midCoord = new THREE.Vector3();

         for (let n = 0; n < track.trackPoints.length; n++) {
            let p1, p2;

            p1 = new THREE.Vector3(track.trackPoints[n].pos.x, track.trackPoints[n].pos.y, track.trackPoints[n].pos.z);

            if (n < track.trackPoints.length - 1) {
                p2 = new THREE.Vector3(track.trackPoints[n + 1].pos.x , track.trackPoints[n + 1].pos.y  , track.trackPoints[n + 1].pos.z);
            }else {
                p2 = new THREE.Vector3(track.trackPoints[0].pos.x     , track.trackPoints[0].pos.y      , track.trackPoints[0].pos.z);
            }

            track.trackPoints[n].line = new THREE.Line3(p1, p2);
        }
    
        for (let n = 0; n <= track.NPoints; n++) {

          const u = n / track.NPoints;
          const u1 = (n+1) / track.NPoints;

          const posL = this.curveL.getPoint(u);
          const posR = this.curveR.getPoint(u);

          let fac = 0;

          for(let m=0;m<nMSidePts;m++){

            const r = m / (nMSidePts -1);
            midCoord.lerpVectors(posL, posR, r);

            const uv = new THREE.Vector2(r , uvfac);
            const v = new Ammo.btVector3(midCoord.x, midCoord.y, midCoord.z);
            vertices.push(v);
            texcoords.push(uv);
          }          

   
          const latL = this.curveLU.getPoint(u);
          const latR = this.curveRU.getPoint(u);

          const v3 = new Ammo.btVector3(latL.x, latL.y, latL.z);
          const v4 = new Ammo.btVector3(latR.x, latR.y, latR.z);

          texcoords.push(uv1_);
          texcoords.push(uv1_);
          
          vertices.push(v3);
          vertices.push(v4);

          const pn = parseInt(Math.floor(n * last / (track.NPoints-1)));
          const pn2 = parseInt(Math.floor((n+1) * last / (track.NPoints-1)));
          const wallOffset = track.trackPoints[pn].noWalls ? 0.00001 : 10.0;
          lastpn = pn;

          const v5 = new Ammo.btVector3(latL.x, latL.y + wallOffset, latL.z);
          const v6 = new Ammo.btVector3(latR.x, latR.y + wallOffset, latR.z);

          texcoords.push(uv1_);
          texcoords.push(uv1_);

          vertices.push(v5);
          vertices.push(v6);


          const pts1 = this.curveC.getPoint(u);
          const pts2 = this.curveC.getPoint(u1);
          const dddd = pts1.distanceTo(pts2) / cL;

          uvfac += dddd;

          if ((!track.trackSegments[pn].looping)&&(!track.trackSegments[pn].worm)&&(!track.trackSegments[pn].tube)) {
    
            let min = [Math.min(posL.x, posR.x), Math.min(posL.y, posR.y), Math.min(posL.z, posR.z)];
            let max = [Math.max(posL.x, posR.x), Math.max(posL.y, posR.y), Math.max(posL.z, posR.z)];
    
            min = [Math.min(latL.x, min[0]), Math.min(latL.y, min[1]), Math.min(latL.z, min[2])];
            min = [Math.min(latR.x, min[0]), Math.min(latR.y, min[1]), Math.min(latR.z, min[2])];
    
            max = [Math.max(latL.x, max[0]), Math.max(latL.y, max[1]), Math.max(latL.z, max[2])];
            max = [Math.max(latR.x, max[0]), Math.max(latR.y, max[1]), Math.max(latR.z, max[2])];
    
            track.trackSegments[pn].min = [Math.min(track.trackSegments[pn].min[0], min[0]), Math.min(track.trackSegments[pn].min[1], min[1]), Math.min(track.trackSegments[pn].min[2], min[2])];
            track.trackSegments[pn].max = [Math.max(track.trackSegments[pn].max[0], max[0]), Math.max(track.trackSegments[pn].max[1], max[1]), Math.max(track.trackSegments[pn].max[2], max[2])];
    
            if((pn2<track.trackSegments.length)&&(pn2 != pn))
            {
              if((!track.trackSegments[pn2].looping)&&(!track.trackSegments[pn2].worm)&&(!track.trackSegments[pn2].tube))
              {
                track.trackSegments[pn2].min = [Math.min(track.trackSegments[pn2].min[0], min[0]), Math.min(track.trackSegments[pn2].min[1], min[1]), Math.min(track.trackSegments[pn2].min[2], min[2])];
                track.trackSegments[pn2].max = [Math.max(track.trackSegments[pn2].max[0], max[0]), Math.max(track.trackSegments[pn2].max[1], max[1]), Math.max(track.trackSegments[pn2].max[2], max[2])];
              }
            }
          }

        }
    
        //track.trackSegments[last].min = [Math.min(track.trackSegments[last].min[0], track.trackSegments[0].min[0]), Math.min(track.trackSegments[last].min[1], track.trackSegments[0].min[1]), Math.min(track.trackSegments[last].min[2], track.trackSegments[0].min[2])];
        //track.trackSegments[last].max = [Math.max(track.trackSegments[last].max[0], track.trackSegments[0].max[0]), Math.max(track.trackSegments[last].max[1], track.trackSegments[0].max[1]), Math.max(track.trackSegments[last].max[2], track.trackSegments[0].max[2])];
    
        for (let n = 0; n < track.trackPoints.length; n++) {
    
          if ((!track.trackSegments[n].looping)&&(!track.trackSegments[n].worm)&&(!track.trackSegments[n].tube)) 
          {
            track.trackSegments[n].halfSize = new THREE.Vector3((track.trackSegments[n].max[0] - track.trackSegments[n].min[0]) / 2.0 + 1.0, 40, (track.trackSegments[n].max[2] - track.trackSegments[n].min[2]) / 2.0+ 1.0);
            track.trackSegments[n].center = new THREE.Vector3(track.trackSegments[n].min[0] + track.trackSegments[n].halfSize.x, track.trackSegments[n].min[1] + track.trackSegments[n].halfSize.y / 2, track.trackSegments[n].min[2] + track.trackSegments[n].halfSize.z);
          }
        }
    
    
        
        let ammoMesh = new Ammo.btTriangleMesh();
        //let ammoMesh2 = new Ammo.btTriangleMesh();
        let ammoMesh4 = new Ammo.btTriangleMesh();

        const iStride = 4 + nMSidePts;
        const nVertices = vertices.length;

        track.tRoadGeo = new THREE.BufferGeometry();
        track.tRoadVertices = new Float32Array(nVertices * 3);
        track.tRoadUvs = new Float32Array(nVertices * 2);
        track.tRoadIndices = new Uint16Array(track.NPoints * nMSidePts * 6);

        track.tWallGeo = new THREE.BufferGeometry();
        //track.tWallVertices = new Float32Array(track.NPoints * vStride * 2);
        track.tWallIndices = new Uint16Array(track.NPoints * 6 * 2 );

        for (let n = 0; n < nVertices; n++) {
            track.tRoadVertices[n * 3 + 0] = vertices[n].x();
            track.tRoadVertices[n * 3 + 1] = vertices[n].y();
            track.tRoadVertices[n * 3 + 2] = vertices[n].z();

            track.tRoadUvs[n * 2 + 0] = texcoords[n].x;
            track.tRoadUvs[n * 2 + 1] = texcoords[n].y;
        }

        for (let n = 0; n < track.NPoints; n++) {
            let i1;
            let i2;
            let i3;
            let i4;

            for(let m=0;m<(nMSidePts-1);m++){
              i1 = n * iStride + 0 + m;
              i2 = n * iStride + 1 + m;

              i3 = (n + 1) * iStride + 0 + m;
              i4 = (n + 1) * iStride + 1 + m;

              ammoMesh.addTriangle(vertices[i1], vertices[i2], vertices[i3], true);
              ammoMesh.addTriangle(vertices[i2], vertices[i4], vertices[i3], true);

              track.tRoadIndices[(n * nMSidePts + m) * 6 + 0] = i1;
              track.tRoadIndices[(n * nMSidePts + m) * 6 + 1] = i2;
              track.tRoadIndices[(n * nMSidePts + m) * 6 + 2] = i3;
            
              track.tRoadIndices[(n * nMSidePts + m) * 6 + 3] = i2;
              track.tRoadIndices[(n * nMSidePts + m) * 6 + 4] = i4;
              track.tRoadIndices[(n * nMSidePts + m) * 6 + 5] = i3;
            }

            i1 = n * iStride + 0;
            i2 = n * iStride + nMSidePts;

            i3= (n + 1) * iStride + 0;
            i4= (n + 1) * iStride + nMSidePts;

            ammoMesh.addTriangle(vertices[i3], vertices[i2], vertices[i1], true);
            ammoMesh.addTriangle(vertices[i3], vertices[i4], vertices[i2], true);
            
            track.tWallIndices[n  * 12 + 0] = i3;
            track.tWallIndices[n  * 12 + 1] = i2;
            track.tWallIndices[n  * 12 + 2] = i1;

            track.tWallIndices[n  * 12 + 3] = i3;
            track.tWallIndices[n  * 12 + 4] = i4;
            track.tWallIndices[n  * 12 + 5] = i2;

            i1 = n * iStride + (nMSidePts -1);
            i2 = n * iStride + (nMSidePts -1) + 2;

            i3= (n + 1) * iStride + (nMSidePts -1);
            i4= (n + 1) * iStride + (nMSidePts -1) + 2;

            ammoMesh.addTriangle(vertices[i1], vertices[i2], vertices[i3], true);
            ammoMesh.addTriangle(vertices[i2], vertices[i4], vertices[i3], true);

            track.tWallIndices[n  * 12 + 6]  = i1;
            track.tWallIndices[n  * 12 + 7]  = i2;
            track.tWallIndices[n  * 12 + 8]  = i3;

            track.tWallIndices[n  * 12 + 9]  = i2;
            track.tWallIndices[n  * 12 + 10] = i4;
            track.tWallIndices[n  * 12 + 11] = i3;

            i1 = n * iStride + nMSidePts;
            i2 = n * iStride + nMSidePts + 2;

            i3 = (n + 1) * iStride + nMSidePts;
            i4 = (n + 1) * iStride + nMSidePts + 2;

            ammoMesh4.addTriangle(vertices[i3], vertices[i2], vertices[i1], true);
            ammoMesh4.addTriangle(vertices[i2], vertices[i3], vertices[i4], true);

            i1 = n * iStride + nMSidePts + 1;
            i2 = n * iStride + nMSidePts + 3;

            i3 = (n + 1) * iStride + nMSidePts + 1;
            i4 = (n + 1) * iStride + nMSidePts + 3;

            ammoMesh4.addTriangle(vertices[i1], vertices[i2], vertices[i3], true);
            ammoMesh4.addTriangle(vertices[i4], vertices[i3], vertices[i2], true);
        }

        track.tRoadMat = new THREE.MeshBasicMaterial({side : THREE.DoubleSide});

        track.tRoadGeo.setAttribute('position',	new THREE.Float32BufferAttribute(track.tRoadVertices, 3));
        track.tRoadGeo.setAttribute('uv3',	new THREE.Float32BufferAttribute(track.tRoadUvs, 2));
        track.tRoadGeo.index = new THREE.Uint16BufferAttribute(track.tRoadIndices, 1);
        track.tRoadGeo.computeBoundsTree();
        track.tRoadMesh = new THREE.Mesh(track.tRoadGeo, track.tRoadMat);

        this.tRoadMesh = track.tRoadMesh;

        track.tWallGeo.setAttribute('position',	new THREE.Float32BufferAttribute(track.tRoadVertices, 3));
        track.tWallGeo.index = new THREE.Uint16BufferAttribute(track.tWallIndices , 1);
        track.tWallGeo.computeBoundsTree();
        track.tWallMesh = new THREE.Mesh(track.tWallGeo, track.tRoadMat);
        this.tWallMesh = track.tWallMesh;

        ammoMesh.computeVertexNormals();
        //ammoMesh2.computeVertexNormals();
        ammoMesh4.computeVertexNormals();

        const scale = new Ammo.btVector3(1.0, 1.0, 1.0);

        let triangleShape = new Ammo.btBvhTriangleMeshShape(ammoMesh, true,true);
        triangleShape.setMargin(0.2);
        triangleShape.setLocalScaling(scale);
        //triangleShape.updateBound();


        /*
        let triangleShape2 = new Ammo.btBvhTriangleMeshShape(ammoMesh2, true,true);
        triangleShape2.setMargin(0.35);
        triangleShape2.setLocalScaling(scale);
        */


        let triangleShape3 = new Ammo.btBvhTriangleMeshShape(ammoMesh4, true,true);
        triangleShape3.setMargin(0.35);
        triangleShape3.setLocalScaling(scale);

        Ammo.destroy(scale);

        for (let n = 0; n < vertices.length; n++) {
            Ammo.destroy(vertices[n]);
        }

        
        this.roadObjPhy = { name: "TrackMesh", shape: triangleShape, mesh: ammoMesh };
        //track.wallObjPhy = { name: "WallMesh", shape: triangleShape2, mesh: ammoMesh2 };
        this.wallObjPhy2 = { name: "Wall2Mesh", shape: triangleShape3, mesh: ammoMesh4 };
    }
    
    addMesh(positions, position, quaternion, flip = false){

        let ammoMesh = new Ammo.btTriangleMesh();

        //const geo = object.geometry.toNonIndexed();

        for (let n = 0; n < positions.length / 3; n++) {

            if(flip){
              const v1 = new Ammo.btVector3(positions[n * 9 + 0], positions[n * 9 + 1], positions[n * 9 + 2]);
              const v2 = new Ammo.btVector3(positions[n * 9 + 3], positions[n * 9 + 4], positions[n * 9 + 5]);
              const v3 = new Ammo.btVector3(positions[n * 9 + 6], positions[n * 9 + 7], positions[n * 9 + 8]);
  
              ammoMesh.addTriangle(v3, v2, v1, true);
  
            }else{
              const v1 = new Ammo.btVector3(positions[n * 9 + 0], positions[n * 9 + 1], positions[n * 9 + 2]);
              const v2 = new Ammo.btVector3(positions[n * 9 + 3], positions[n * 9 + 4], positions[n * 9 + 5]);
              const v3 = new Ammo.btVector3(positions[n * 9 + 6], positions[n * 9 + 7], positions[n * 9 + 8]);
  
              ammoMesh.addTriangle(v1, v2, v3, true);
  
            }
        }

        //ammoMesh.computeVertexNormals();

        const scale = new Ammo.btVector3(1.0, 1.0, 1.0);

        let triangleShape = new Ammo.btBvhTriangleMeshShape(ammoMesh, true,true);
        triangleShape.setMargin(0.2);
        triangleShape.setLocalScaling(scale);

        Ammo.destroy(scale);

        return { shape: triangleShape, mesh: ammoMesh, pos: position, quat: quaternion };
    }

    removeShape(shape){

        if(!shape)
            return;
    
        if(shape.physicsBody){
            this.physicsUniverse.removeRigidBody(shape.physicsBody);
            Ammo.destroy(shape.physicsBody);
            shape.physicsBody = null;
        }
        
        if(shape.ammoData){
            Ammo.destroy(shape.ammoData.localInertia);
            Ammo.destroy(shape.ammoData.motionState);
            Ammo.destroy(shape.ammoData.transform);

            if(shape.ammoData.geometry)
                Ammo.destroy(shape.ammoData.geometry);

            shape.ammoData= null;
        }

        if(shape.shape){
            Ammo.destroy(shape.shape);
            shape.shape = null;
        }

        if(shape.mesh){
            Ammo.destroy(shape.mesh);
            shape.mesh = null;
        }
    }
    cars;
    woods;
    rigidBody_List;
    physicsUniverse;
    syncList;
    tmpTransformation;
  }
  
  export {PhysicMain, meshsInfos, frameTime, fps, worldGroup, worldMask, profiles};